/**
 * Claim Detail Page - Tab-Based Station Layout
 *
 * Top:  Signpost banner (product heading, subtitle, stats, stepper, actions)
 * Mid:  Tab bar (Overview | Resolution | Lab | Settlement | Inspection | LOA)
 * Body: Tab content - Overview shows calm 2-column card grid, other tabs
 *       show station-specific workspaces.
 */

import {
  Fragment,
  cloneElement,
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import type { ReactNode } from "react";
import {
  AutoReminderAction,
  useReminderStatus,
} from "@/shared/components/reminders";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import {
  Users,
  Pencil,
  X,
  Play,
  Pause,
  Check,
  Flag,
  ShieldAlert,
  UserPlus,
  Loader2,
  FileText,
  Shield,
  UserCircle,
  Home,
  Info,
  Car,
  Anchor,
  Package,
  Image,
  Mail,
  Plus,
  Clock,
  User,
  Cpu,
  StickyNote,
  Building,
  Paperclip,
  Layers,
  ClipboardCheck,
  FlaskConical,
  Camera,
  TestTube,
  Upload,
  ChevronDown,
  AlertTriangle,
  FileUp,
  MapPin,
  UserRound,
  Wrench,
  MessageSquareDiff,
  XCircle,
  Phone,
  Calendar,
  CheckCircle2,
  Circle,
  AlertCircle,
  Target,
  ListChecks,
  Maximize2,
  CreditCard,
  Timer,
  ShieldCheck,
  Banknote,
  Send,
  RotateCcw,
  ExternalLink,
  Lock,
  Tag,
  Contact,
  Building2,
} from "lucide-react";
import { useFormatting } from "@/admin/hooks/use-formatting";
import { AppShell } from "@/admin/components/app-shell";
import { DuplicateMediaPanel } from "@/admin/components/claims/duplicate-media-panel";
import { ConfigHelp } from "@/admin/components/config-help";
import { AccessLevelPanel } from "@/admin/components/access-level-panel";
import { EvidenceCard } from "@/admin/components/claims/evidence-card";
import { InspectionRequestsCard } from "@/admin/components/claims/inspection-requests-card";
import { EvidenceFileRow } from "@/shared/components/evidence/evidence-file-row";
import { AuthenticatedImage } from "@/admin/components/authenticated-image";
import { fileThumbnailUrl } from "@/shared/lib/authenticated-file";
import { ClaimDocumentationCard } from "@/admin/components/claims/claim-documentation-card";
import { ClaimPartnerCard } from "@/admin/components/claims/claim-partner-card";
import { resolvePartnerLock } from "@/admin/lib/claim-partner-lock";
import { ClaimCommunicationsCard } from "@/admin/components/claims/claim-communications-card";
import {
  useRecordCommunicationsActivity,
  type ActivityRow,
} from "@/admin/hooks/use-communications-activity";
import { ClaimTeamPanel } from "@/admin/components/claims/claim-team-panel";
import { useClaimTeam } from "@/admin/hooks/use-claim-team";
import { useExternalRoles } from "@/admin/hooks/use-external-roles";
import type { Attachment } from "@/admin/pages/claims/use-claim-attachments";
import { registrationPathFromClaim } from "@/admin/pages/registrations/registration-back";
import { ContactCustomerDialog } from "@/admin/components/claims/contact-customer-dialog";
import { ChildClaimsTab } from "@/admin/components/claims/child-claims-tab";
import { UmbrellaChildrenList } from "@/admin/components/claims/umbrella-children-list";
import { ClaimItemsSummaryCard } from "@/admin/components/claims/claim-items-summary-card";
import { SendPacketDialog } from "@/admin/components/claims/send-packet-dialog";
import { ReissueUploadLinkDialog } from "@/admin/components/claims/reissue-upload-link-dialog";
import { StatusBadge } from "@/admin/components/shared/status-badge";
import { DetailCard, FieldRow } from "@/admin/components/shared/detail-card";
import { DraggableCardGrid } from "@/admin/components/shared/draggable-card-grid";
import {
  CardDragHandle,
  NestedCardBoundary,
} from "@/shared/components/card-drag-handle";
import {
  isBlankSummaryValue,
  summaryFromItems,
  summaryFromRows,
  type CardSummary,
} from "@/admin/components/shared/card-summary";
import { resolveHoldingConsumerFields } from "@/admin/lib/customer-card";
import { Badge } from "@/shared/components/ui/badge";
import { EditableDetailCard } from "@/admin/components/shared/editable-detail-card";
import { EditFormDialog } from "@/admin/components/shared/edit-form-dialog";
import { ConfirmDeleteDialog } from "@/admin/components/confirm-delete-dialog";
import { recordTitleFontClass } from "@/shared/lib/record-title";
import { NotesPanel } from "@/admin/components/shared/notes-panel";
import { SettlementLineItemsTable } from "@/admin/components/claims/settlement-line-items-table";
import {
  SettlementCostSummaryCard,
  SettlementLedgerSplitCard,
  SettlementProrationCard,
} from "@/admin/components/claims/settlement-summary-card";
import { LOAApprovalPanel } from "@/admin/components/claims/loa-approval-panel";
import {
  ApprovalChangeBadge,
  ApprovalChangesSummary,
  WasValue,
} from "@/admin/components/claims/approval-changes-summary";
import { SettlementPaymentsTable } from "@/admin/components/claims/settlement-payments-table";
import {
  ClaimVouchersCard,
  type SupplierDefault,
} from "@/admin/components/claims/claim-vouchers-card";
import { titleCaseKey } from "@/shared/lib/humanise";
import {
  CLAIM_PAYEE_PROVIDER,
  defaultPayeeValue,
  useClaimPayeeProviders,
} from "@/admin/hooks/use-claim-payee-lookup";
import type { PayeeCandidate } from "@/admin/hooks/use-claim-payee-lookup";
import { DecisionMatrixWorkspace } from "@/admin/components/claims/decision-matrix-workspace";
import { AssessmentLineItemsTable } from "@/admin/components/claims/assessment-line-items-table";
import { PhotoReviewPanel } from "@/admin/components/claims/photo-review-panel";
import { SampleTrackingCard } from "@/admin/components/claims/sample-tracking-card";
import { LabTestResultsTable } from "@/admin/components/claims/lab-test-results-table";
import {
  LabRequestPanel,
  RequestPhotosDialog,
} from "@/admin/components/claims/lab-request-dialog";
import {
  useLabTasks,
  useDecisionMatrices,
  useClaimChecklist,
  useSetEvidenceCategoryApproval,
  useLabRequests,
  type LabTask,
} from "@/admin/hooks/use-lab-tasks";
import { RegistrationConfiguredFieldRows } from "@/admin/components/registration-configured-fields";
import { IdentifyProductDialog } from "@/admin/components/identify-product-dialog";
import { CustomerActionControls } from "@/admin/components/shared/customer-action-controls";
import { EntityActionMenu } from "@/admin/components/shared/entity-action-menu";
import {
  buildRegistrationConfiguredFieldItems,
  holdingRegistrationCardColumnApplies,
} from "@/admin/lib/registration-configured-fields";
import {
  registrationCardDisplayColumns,
  displayColumnLabel,
  formatEntityCardValue,
  hasRegistrationCardDisplay,
  resolveHoldingRegistrationCardValue,
} from "@/admin/lib/entity-card-display";
import { ActionEntryDialog } from "@/shared/components/action-entry-dialog";
import { ResourceLookupContextProvider } from "@/shared/components/ui/resource-lookup-context";
import { useResourceLookupProviders } from "@/admin/hooks/use-resource-lookup-providers";
import { FileViewerProvider } from "@/shared/components/file-viewer-dialog";
import { useFileViewer } from "@/shared/components/file-viewer-context";
import { getLatestConsumerMessageEntry } from "@/admin/lib/claim-message-state";
import {
  resolveNotificationViewTarget,
  useMarkNotificationsRead,
  useNotificationItemAction,
  useNotifications,
} from "@/admin/hooks/use-notifications";
import { NotificationsPanel } from "@/admin/components/notifications-panel";
import { usePermissions } from "@/admin/hooks/use-permissions";
import { SEND_REQUESTS } from "@/admin/lib/permissions";
import { ClaimTransfersCard } from "@/admin/components/claims/claim-transfers-card";
import { useUserPreference } from "@/admin/hooks/use-user-preference";
import { usePinnedView } from "@/admin/hooks/use-pinned-view";
import {
  PinMyViewBar,
  PinMyViewControls,
} from "@/admin/components/shared/pin-my-view";
import {
  RecordJourney,
  RecordTopPanel,
  type RecordJourneyNode,
  type RecordTopPanelAction,
  type RecordTopPanelFact,
} from "@/admin/components/shared/record-top-panel";
import { useOrg } from "@/admin/hooks/use-org";
import {
  useClaim,
  useClaimActionMetadata,
  useExecuteClaimAction,
  useEditClaimDirect,
  useToggleClaimFlag,
  useResolveClaimDuplicates,
  useResolveMediaMatch,
  useClaimContacts,
  useAddClaimContact,
  useUpdateClaimContact,
  useRemoveClaimContact,
  useClaimContactRoles,
  useCreateClaimContactRole,
  useAssignClaim,
  useClaimAssignablePartners,
  useAssignClaimToPartner,
  useAddClaimEvidence,
  useOrgMembers,
  useClaimChildren,
  type WorkflowAction,
  type ActionLogEntry,
  type ActionFieldMeta,
  type SubmittedReviewField,
  type ActionEffectPreview,
  type PricingGuidance,
  type ClaimContact as ApiClaimContact,
} from "@/admin/hooks/use-claims";
import { ClaimLetterPreviewDialog } from "@/admin/components/composer/record-composer-dialog";
import { PricingGuidancePanel } from "./pricing-guidance-panel";
import { ClaimCycleTimePanel } from "./claim-cycle-time-panel";
import {
  useCreateExceptionGrant,
  useExceptionReasons,
  useResolveWorkflowIssue,
  useWorkflowHistory,
  useWorkflowInstanceIssues,
} from "@/shared/hooks/use-workflows";
import { computeVisitedStateKeys } from "@/shared/lib/workflows/visited-states";
import { ExceptionModal } from "@/admin/components/workflow-issues/workflow-issue-resolve-modal";
import {
  parseEngineBlockError,
  type EngineBlock,
} from "@/shared/lib/workflows/engine-errors";
import type {
  WorkflowIssueReasonOption,
  WorkflowIssueResolvePayload,
} from "@/shared/lib/workflows/types";
import { useHolding, useUploadFile } from "@/admin/hooks/use-products";
import { useEntityTypeByKey, useEntityTypes } from "@/admin/hooks/use-entities";
import {
  normaliseCaptureEntries,
  type WorkflowState,
} from "@/admin/hooks/use-claim-workflows";
import {
  useInspections,
  useClaimInspectionEvidence,
  type InspectionRow,
} from "@/admin/hooks/use-inspections";
import { DocumentRequestInput } from "@/admin/components/claims/document-request-input";
import {
  normaliseClaimField,
  MetadataField,
  defineField,
  formatDisplayValue,
  formatPhoneDisplay,
  useForm,
  displayScalar,
  isRequiredValueProvided,
} from "@/shared/lib/forms";
import type { FormFieldMeta, FormField } from "@/shared/lib/forms";
import { CreatableSelect } from "@/shared/components/ui/creatable-select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/shared/components/ui/tabs";
import { IfEntity } from "@/admin/components/conditional-display";
import { useEntityRegistered } from "@/admin/hooks/use-entity-registration";
import {
  useFeatureFlag,
  useFeatureFlags,
  FLAGS,
} from "@/admin/hooks/use-feature-flag";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import {
  SearchableSelect,
  type SearchableSelectGroup,
} from "@/shared/components/ui/searchable-select";
import { Label } from "@/shared/components/ui/label";
import { Separator } from "@/shared/components/ui/separator";
import { SimpleTooltip } from "@/shared/components/ui/tooltip";
import { cn } from "@/shared/lib/utils";
import { extractApiErrorMessage, toastApiError } from "@/shared/lib/api-error";
import { computeWarrantyExpiresAt } from "@/shared/lib/warranty";
import type {
  ApprovalChanges,
  ClaimPayment,
  SettlementData,
  LOAContext,
  LOAData,
  LOADimension,
  LOAViewerState,
} from "@/admin/hooks/use-settlement";
import {
  useApproveLOA,
  useClaimPayments,
  useRejectLOA,
  useRequestChangesLOA,
} from "@/admin/hooks/use-settlement";
import type { CardFieldItem } from "@/admin/components/shared/editable-card-types";
import {
  DECISION_FIELDS,
  DECISION_ORDER,
  CLAIM_BOTTOM_FIELDS,
  SUPPLIER_CARD_FIELDS,
  WARRANTY_TYPE_LABELS,
  SENDER_ROLE_LABELS,
  buildClaimFieldItem,
  claimProductDisplayName,
  displayOnlyCardItems,
  mergeRoutedCardItems,
  productNameWithoutVariant,
  productRowsBesideDefectDetails,
  cardRowHasValue,
  COLOUR_ROW_LABEL,
  QUANTITY_ROW_LABEL,
  PRODUCT_NAME_COLUMN,
  CONSUMER_CARD_FIELDS,
  PRODUCT_CLAIM_FIELDS,
  hiddenFromClaimDetailsCodes,
  fieldUpdatePermissions,
  grantScopedEditConfig,
  readOnlyOnDefectDetailsCodes,
  labCardFields,
  defectCardFields,
  withColourBesideProduct,
  pickedUuid,
  startFromRegistration,
  supplierCardFields,
  codeForSemanticRole,
  settlementQuantityImpactedLabel,
  optionLabelFor,
  pinnedClaimDetailCodes,
  humanise,
  durationLabel,
  deriveRecordStatus,
  formatAttrValue,
  buttonActions,
  isEngineStep,
  pickNextStep,
  loaDecisionControls,
} from "./claim-detail-helpers";
import {
  defaultWarrantyUuidForHolding,
  findHoldingWarranty,
  isIssuedWarranty,
} from "@/admin/lib/warranty-record";
import { ClaimLabelsControl } from "./claim-labels-control";
import { useCanAddClaimLabels } from "@/admin/hooks/use-can-add-claim-labels";
import { CommunicationHoldControl } from "@/admin/components/claims/communication-hold-control";
import { isAddressLike, addressDisplay } from "@/shared/lib/formatting/address";
import { formatClaimId } from "@/admin/lib/formatting";

// Sentinel option value for the assignee picker's "Unassign" row.
const ASSIGNEE_UNASSIGN = "__unassign__";

// ── Action Icon Resolution ───────────────────────────────
//
// Frontend-only icon allowlist keyed by action-key fragments. CLAUDE.md
// explicitly allows "icons-by-name" registries as security/UX allowlist
// (vs "values-by-tenant" which is banned).

/**
 * When an exit check ran a different action than the one clicked (e.g. Proceed
 * to Settlement sent back for a new LOA), say where the claim went and why,
 * using the check's own title and message; a check with no copy just names the
 * stage. Returns whether it showed one.
 */
function toastDivertedAction(result: unknown, t: TFunction): boolean {
  const diverted = (
    result as {
      diverted?: {
        title?: string;
        message?: string;
        to_state_label?: string;
      } | null;
    } | null
  )?.diverted;
  if (!diverted) return false;
  if (!diverted.title) {
    toast.info(
      t("claims.action.diverted", "Moved to {{state}} instead", {
        state: diverted.to_state_label,
      }),
    );
    return true;
  }
  toast.warning(
    diverted.title,
    diverted.message ? { description: diverted.message } : undefined,
  );
  return true;
}

function resolveActionIcon(actionKey: string): typeof Play {
  const key = actionKey.toLowerCase();
  if (/approve|accept/i.test(key)) return Check;
  if (/reject|deny/i.test(key)) return XCircle;
  if (/hold|pause/i.test(key)) return Pause;
  if (/review|begin_review/i.test(key)) return Play;
  if (/request_info|request_changes/i.test(key)) return Send;
  if (/provide_info/i.test(key)) return Upload;
  if (/process_payment|payment/i.test(key)) return Banknote;
  if (/close|resolve/i.test(key)) return CheckCircle2;
  if (/return|revert/i.test(key)) return RotateCcw;
  if (/void|cancel/i.test(key)) return XCircle;
  return Play;
}

// ── State → Tab mapping ───────────────────────────────────
//
// Which tab a state belongs to is authored per state as the EAV attribute
// ``workspace_category``, read off
// ``workflow_instance.current_state_attribute_values``. That is the only
// source consulted - a tenant naming its states anything it likes lands on
// the right tab, and a state with no category simply leaves the tab where
// the user put it rather than jumping somewhere arbitrary.
//
// The hardcoded OC key table that used to live here (a PR #392 shortcut, and
// the Phase 2 anti-pattern its own comment flagged for removal) is gone: the
// attribute is seeded on every state, including the cascade-created
// ``loa_leadership_pending``.

// ── Resolution workspace constants ──────────────────────────

// Platform state vocabulary, carried on every WorkflowState as EAV and seeded
// per tenant. Reading these instead of matching state keys is what lets a
// second tenant name its states anything it likes and still get these
// surfaces - see isLoaState for the same mechanism on the LOA panel.
const STAGE_DOCUMENTATION_INTAKE = "documentation_intake";
const STAGE_EVALUATION = "evaluation";
const WORKSPACE_SETTLEMENT = "settlement";

/**
 * The intake stage: gathering photos and documents, before any lab work.
 *
 * Gates resolution-stage editing and the "before lab" reading of the lab
 * panel. Was a literal key set ("open", "awaiting_docs", "intake_review"),
 * which silently did nothing on any workflow that named those steps
 * differently.
 */
function isIntakeStage(attrs: Record<string, unknown>): boolean {
  return attrs?.stage === STAGE_DOCUMENTATION_INTAKE;
}

/**
 * A settlement step where an open punch item should hold the claim.
 *
 * Three clauses because the settlement workspace is wider than the gate. The
 * RS steps (``rs_material_labor``, ``rs_disposition``) are still assembling
 * the estimate and the closed states are terminal, so neither should raise a
 * "settlement blocked" banner. ``blocks_evidence`` is what separates them:
 * once evidence is locked the estimate is final, which is exactly when an
 * open punch item should hold the claim. Verified to select the same three
 * states the old key list did on all four OC claim workflows - and
 * ``allow_line_item_editing``, the obvious alternative, is seeded on Installed
 * only, so it would have widened the gate on the other three.
 */
function isSettlementGatedStage(attrs: Record<string, unknown>): boolean {
  return (
    attrs?.workspace_category === WORKSPACE_SETTLEMENT &&
    attrs?.stage === STAGE_EVALUATION &&
    attrs?.blocks_evidence === true
  );
}

// Per-state editable field lists used to drive the resolution panel
// now come from the EAV state attribute ``editable_fields`` (Phase 5).
// See ``currentStateAttributeValues.editable_fields`` below - read off
// as a ``string[]`` and converted to a Set at the call site.

/**
 * The defect's product, colourway and quantity, by MEANING rather than field.
 *
 * Which attribute holds each is the schema's business. A schema declares it
 * with `configuration_data.semantic_role`, and the platform code name is the
 * fallback for a schema that has not been tagged yet.
 */
const DEFECT_ROLE_SLOTS: Record<string, string> = {
  defect_quantity: "defect_quantity",
  product_name: "defect_product",
  color: "defect_colour",
};

/**
 * WPS-102 AC6: `quantity_impacted` is deliberately absent. The claimant is no
 * longer asked how much of the roof is affected - the lab's Quantity Approved
 * (`lab_coverage_determination` + `lab_square_count`) is the only quantity,
 * and it is what advises payment. The attribute stays in the schema so
 * historical claims keep their value.
 */
/**
 * Read the display label out of an ENTITY_SEARCH claim value.
 *
 * The shared resource picker stores the picked resource as an object
 * (`{ uuid, name }`), but a value typed in before the field became a picker,
 * or imported from a spreadsheet, is a bare string. Both are legitimate, so
 * both render.
 */
function resourceValueLabel(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    for (const key of ["name", "label", "display_name", "title"]) {
      const candidate = record[key];
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate.trim();
      }
    }
  }
  return "";
}

/**
 * Which Overview card an attribute belongs to.
 *
 * WPS-146 #4 asks for the Claim Details card to go. Claim Details is the
 * Overview's CATCH-ALL: it renders every attribute no other card claimed, so
 * simply hiding it takes 76 populated fields on Installed (60 on Job Stop, 40
 * on Uninstalled, 32 on Workmanship) off the screen the moment they populate.
 *
 * So instead of blindfolding the card, every attribute declares its home and
 * the catch-all empties on its own (it already renders behind
 * `otherClaimItems.length > 0`). A schema says where a field lives with
 * `configuration_data.overview_card`; nothing else about the card changes.
 *
 * Config, never a hardcoded field list - a tenant re-homes a field by
 * re-authoring its attribute, not by editing this file.
 */
type OverviewCardSlot = "context" | "defect" | "homeowner" | "none";

const OVERVIEW_CARD_SLOTS: ReadonlySet<string> = new Set([
  "context",
  "defect",
  "homeowner",
  "none",
]);

/**
 * The slot a field declares, or `null` when it has not been routed yet.
 *
 * `context_card: true` is the original WPS-203 spelling of `"context"` and
 * stays supported, so schemas authored before this routing existed keep
 * landing on Claim Context.
 */
function overviewCardSlot(field: {
  code_name: string;
  configuration_data?: unknown;
}): OverviewCardSlot | null {
  const config = field.configuration_data as Record<string, unknown> | undefined;
  const declared = config?.overview_card;
  if (typeof declared === "string" && OVERVIEW_CARD_SLOTS.has(declared)) {
    return declared as OverviewCardSlot;
  }
  if (config?.context_card) return "context";
  return null;
}

// Claim Context membership: the attributes a schema routes there.
function isClaimContextField(field: {
  code_name: string;
  configuration_data?: unknown;
}): boolean {
  return overviewCardSlot(field) === "context";
}

/**
 * A field's place among Defect Details' own fields (`overview_card_order`), or
 * `null`. These render in that order even before they have a value, because
 * the card is where they get set (WPS-175 #2(a): Minor directly under Major).
 * Other `defect` fields append once populated.
 */
function defectCardOrder(field: {
  code_name: string;
  configuration_data?: unknown;
}): number | null {
  if (overviewCardSlot(field) !== "defect") return null;
  const order = (field.configuration_data as Record<string, unknown> | undefined)
    ?.overview_card_order;
  return typeof order === "number" ? order : null;
}

/** Fields ordered by their Defect Details position (each must have one). */
function byDefectCardOrder<T extends { code_name: string; configuration_data?: unknown }>(
  a: T,
  b: T,
): number {
  return (defectCardOrder(a) ?? 0) - (defectCardOrder(b) ?? 0);
}

const RS_STATE_GUIDANCE: Record<
  string,
  { title: string; description: string }
> = {
  open: {
    title: "Intake Review",
    description:
      "Review the claim details, fill in intake fields, then send the document checklist to the homeowner.",
  },
  awaiting_docs: {
    title: "Awaiting Documents",
    description:
      "The homeowner has been sent a document checklist. Review any submissions below.",
  },
  intake_review: {
    title: "Document Review",
    description:
      "Review the submitted documents, confirm the defect categorisation, then route to lab or request more documents.",
  },
};

// ── Lab workspace constants ─────────────────────────────

/**
 * Fields the lab analyst records as core findings, in display order.
 *
 * WPS-151: the lab's Determination is `lab_coverage_determination`
 * (Full / Partial), NOT the shared `determination` field - that one is
 * Major / Minor defect severity set at Intake Review, and it stays with the
 * other classification fields. `lab_square_count` is shown (and optional) once
 * the coverage determination is Full or Partial - the gating is config-driven
 * (`visible_when`), see `labFindingItems` consumers.
 */
/**
 * WPS-151: the Lab Findings card no longer prompts for an edit reason, but
 * the claim PUT still requires one for the audit log. This is the reason
 * recorded on the technician's behalf.
 */
const LAB_FINDINGS_EDIT_REASON = "Lab findings recorded";

/** Sample & production tracking fields */
// Condition and Meets Min. Size were dropped (WPS-175 item #4): the lab judges
// a sample on the Lab Findings card, and two places to record the same
// judgement produced two answers.
const LAB_SAMPLE_FIELDS = new Set([
  "sample_received",
  "sample_received_date",
  // WPS-151 retest (Penny) 4.i: the Reviewed Date is mirrored off the
  // LabSample onto the claim (sync_sample_eav) so it shows on the Sample
  // Tracking card beside the Received Date - the two dates are symmetric.
  "sample_reviewed_date",
  "plant_code",
  "production_code",
]);

/**
 * Defect classification - set at intake, verified/updated at lab.
 *
 * WPS-151 moved `determination` (Major / Minor severity) here out of
 * LAB_FINDING_FIELDS. It is intake's classification call, so it belongs with
 * the other classification fields rather than in the lab's findings card -
 * and keeping it in this set is what stops it drifting into Intake Summary.
 */
const LAB_CLASSIFICATION_FIELDS = new Set([
  "major_category",
  "minor_category",
  "determination",
  "component_type",
  "product_impacted",
  // WPS-165: Plant Code is a lab-owned classification field, so keep it out of
  // the generic Intake Summary (it renders on the Defect Details card instead).
  "plant_code",
]);

/**
 * Lab-relevant fields known at module scope.
 *
 * The findings codes are deliberately absent: they are declared per tenant via
 * ``configuration_data.lab_card`` (see `labCardFields`) and so are only known
 * once a claim's schema metadata is in hand. Callers union this with the codes
 * `labCardFields` returns.
 */
const LAB_STATIC_FIELDS = new Set([
  ...LAB_SAMPLE_FIELDS,
  ...LAB_CLASSIFICATION_FIELDS,
]);

const LAB_GUIDANCE = {
  title: "Lab Analysis",
  description:
    "Review evidence and sample against product requirements, classify the defect, record findings, then approve or deny the claim.",
};

// ── Compact Workflow Stepper ──────────────────────────────
//
// Role labels + colours come from the per-state EAV attributes
// ``role_label`` and ``role_label_color`` (Phase 2 of the
// tenant-agnostic claims migration). No hardcoded state→role map
// any more - onboarding a new tenant means seeding their workflow's
// ``WorkflowStateAttributeValue`` rows, not editing this file.

function WorkflowStepper({
  states,
  currentStateKey,
  workflowInstanceUuid,
}: {
  states: WorkflowState[];
  currentStateKey: string;
  workflowInstanceUuid?: string;
}) {
  const { t } = useTranslation();
  // WPS-81: a claim can only be blocked at the state it currently sits in
  // (you cannot be parked at a future step). We fetch the OPEN issues for
  // this instance and mark the CURRENT node with a warning glyph + a hover
  // tooltip that spells out WHY the claim cannot progress. Open-only by
  // default (the hook's "blocking banner" mode), so resolved history never
  // lights up the stepper.
  const { data: blockingIssues = [] } =
    useWorkflowInstanceIssues(workflowInstanceUuid);
  // A node is "completed" only when the claim ACTUALLY visited it, never by
  // ordinal position. A claim can shortcut past earlier states (e.g. a
  // bulk-settle jump straight to Compensated); painting those states with a
  // green check would disagree with the engine's own history-based reopen
  // resolver. We read the same action-log history the engine records and mark
  // any prior-but-unvisited state as SKIPPED instead of completed.
  const { data: history, isPending: historyPending } =
    useWorkflowHistory(workflowInstanceUuid);
  // Only trust the visited-set once the history query has actually resolved.
  // Until then (or when there is no instance to query) fall back to the
  // position-based rail so we never flash prior states as "skipped" while the
  // action log is still loading.
  const historyReady = Boolean(workflowInstanceUuid) && !historyPending;
  const visitedStateKeys = useMemo(
    () =>
      computeVisitedStateKeys(history, {
        states,
        currentStateKey,
      }),
    [history, states, currentStateKey],
  );
  const currentState = states.find((s) => s.key === currentStateKey);
  // Which states form the line. The backend derives this from the
  // action graph and sends ``is_linear_stage``: side exits (On Hold and
  // friends, reachable from any state at any time), states nothing can
  // reach, and branch outcomes such as Denied are all excluded, while
  // the one closure the record reaches from the final stage is included
  // so the line ends where the work does. A state that loops back to an
  // earlier step (Awaiting Info) IS a stage - a claim genuinely sits
  // there. The claim's own state always shows, even when it is a branch
  // outcome. Payloads without the flag fall back to the previous
  // terminal-only rule.
  //
  // Order comes from the definition (the API already sorts by it); this
  // only floats the initial state to the front. Written as a difference
  // of the two flags so the comparator is a valid ordering - the old
  // `a.is_initial ? -1 : ...` form claimed a < b AND b < a whenever both
  // were initial, which is undefined behaviour for Array.sort.
  const visibleStates = states
    .filter((s) => {
      if (s.key === currentStateKey) return true;
      if (typeof s.is_linear_stage === "boolean") return s.is_linear_stage;
      return !s.is_terminal;
    })
    .sort(
      (a, b) => Number(Boolean(b.is_initial)) - Number(Boolean(a.is_initial)),
    );

  const currentIdx = visibleStates.findIndex((s) => s.key === currentStateKey);

  return (
    <RecordJourney
      nodes={visibleStates.map((state, idx): RecordJourneyNode => {
        const isCurrent = state.key === currentStateKey;
        const isPrior = idx < currentIdx;
        // Prior-and-visited => completed (green check); prior-but-never-visited
        // => skipped (dashed, minus glyph, no green) so a shortcut past a state
        // is never misread as "passed through". Before history loads, keep the
        // historical position-based behaviour (every prior completed).
        const isCompleted =
          isPrior && (!historyReady || visitedStateKeys.has(state.key));
        const isSkipped =
          isPrior && historyReady && !visitedStateKeys.has(state.key);
        const isTerminalError = state.is_terminal && state.key === "rejected";
        const isBlocked = isCurrent && blockingIssues.length > 0;
        return {
          key: state.key,
          label: state.label,
          status: isBlocked
            ? "blocked"
            : isCurrent
              ? "current"
              : isCompleted
                ? "completed"
                : isSkipped
                  ? "skipped"
                  : "future",
          tone: isCurrent
            ? isTerminalError
              ? "error"
              : currentState?.is_terminal
                ? "ok"
                : "default"
            : "default",
          meta:
            attributeText(state.attribute_values?.role_label) ?? null,
          metaColor:
            attributeText(state.attribute_values?.role_label_color) ??
            null,
          tooltip: isBlocked ? (
            <div className="flex flex-col gap-1">
              <span className="font-medium">
                {t(
                  "claims.override.blockedTitle",
                  "This action cannot progress yet",
                )}
              </span>
              <ul className="flex flex-col gap-0.5">
                {blockingIssues.map((issue) => (
                  <li key={issue.uuid}>
                    {issue.summary}
                    {issue.detail ? ` - ${issue.detail}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          ) : isSkipped ? (
            <span>
              {t(
                "claims.stepper.skipped",
                "Skipped - this claim did not pass through this stage",
              )}
            </span>
          ) : null,
        };
      })}
    />
  );
}

// ── Timeline Entry ────────────────────────────────────────

interface HistoryAttachment {
  uuid: string;
  file_url: string | null;
  file_name: string;
  file_type?: string;
  file_size?: number;
  mime_type: string;
}

/**
 * A letter someone sent by hand, on the claim's activity rail.
 *
 * Emails fired by a workflow action are already reported on that action's own
 * entry. A manual send has no action behind it, so without this it happened and
 * the record does not say so. Same rail, same dot, deliberately quieter than a
 * state change: it is a thing that happened, not a step the claim took.
 */
function SentLetterTimelineEntry({
  row,
  showConnector,
}: {
  row: ActivityRow;
  showConnector?: boolean;
}) {
  const { formatDate, formatTime } = useFormatting();
  const { t } = useTranslation();
  const who = row.counterparty_name || row.counterparty;
  const reminder = row.cause?.reminder;

  return (
    <div className="relative pb-[18px] pl-3">
      {showConnector && (
        <div className="absolute -bottom-1 left-[-15px] top-4 w-px bg-[var(--rule)]" />
      )}
      <div className="absolute left-[-20px] top-1 h-[11px] w-[11px] rounded-full border-[1.5px] border-[var(--ink-3)] bg-[var(--bg)]" />
      <div className="flex flex-col gap-0.5">
        <span className="flex flex-wrap items-center gap-1.5 text-[13px] text-[var(--ink)]">
          <Mail className="h-3 w-3 shrink-0 text-[var(--ink-3)]" />
          {reminder
            ? reminder.final
              ? t("claimComms.timelineFinalReminder", "Final reminder sent")
              : t("claimComms.timelineReminder", {
                  number: reminder.number,
                  of: reminder.of ?? reminder.number,
                  defaultValue: "Reminder {{number}} of {{of}} sent",
                })
            : t("claimComms.timelineSent", "Letter sent")}
          {who ? <span className="text-[var(--ink-2)]">{who}</span> : null}
        </span>
        {row.summary && (
          <span className="text-[12px] text-[var(--ink-2)]">{row.summary}</span>
        )}
        <span className="text-[11px] text-[var(--ink-3)]">
          {formatDate(row.occurred_at)} {formatTime(row.occurred_at)}
        </span>
      </div>
    </div>
  );
}

function TimelineEntry({
  entry,
  onClick,
  showConnector = false,
}: {
  entry: ActionLogEntry;
  onClick?: () => void;
  showConnector?: boolean;
}) {
  const { formatDate, formatTime } = useFormatting();
  const { t } = useTranslation();
  const initiator =
    entry.initiated_by.type === "SYSTEM"
      ? "System"
      : entry.initiated_by.user?.name ||
        entry.initiated_by.user?.email ||
        "Unknown";
  const isSystem = entry.initiated_by.type === "SYSTEM";
  const isContact = entry.action_key === "consumer_contact";
  const isNote = entry.action_key === "note" || isContact;
  const isDupResolve = entry.action_key === "resolve_duplicates";
  const isDupReopen = entry.action_key === "reopen_duplicates";
  const isDuplicateEvent = isDupResolve || isDupReopen;
  const isAssign = entry.action_key === "assign";
  const isEdit = entry.action_key === "edit_details";
  const isEvidenceAdd = entry.action_key === "evidence_added";
  const isEvidenceRemove = entry.action_key === "evidence_removed";
  // A transfer moved the claim to the property's new owner (WPS-157 §8). Logged
  // as an event with no action, so it has no state or action label of its own.
  const isOwnerUpdate = entry.action_key === "owner_updated_via_transfer";
  const params = entry.parameters as Record<string, unknown> | null;
  const senderRole = params?.sender_role as string | undefined;
  const attachments = Array.isArray(params?.attachments)
    ? (params.attachments as HistoryAttachment[])
    : [];
  const hasCapturedValues =
    entry.captured_values && Object.keys(entry.captured_values).length > 0;
  const emails = entry.emails_sent ?? [];
  // A skipped or failed send is logged on the move too; it was not sent.
  const notSentCount = emails.filter((e) => e.not_sent).length;
  const sentCount = emails.length - notSentCount;
  const hasEmails = emails.length > 0;
  const isClickable =
    (isNote && (params?.message || attachments.length > 0)) ||
    hasCapturedValues ||
    hasEmails;

  const dotColor = isContact
    ? "border-[var(--blue)]"
    : isNote
      ? "border-[var(--amber,#d97706)]"
      : isDupReopen
        ? "border-[var(--status-warn)]"
        : isAssign
          ? "border-[var(--blue)]"
          : "border-[var(--green)]";

  // Generic engine logs duplicate-review events with action_key only (no
  // to_state), so give them a friendly label here.
  const duplicateLabel = isDupResolve
    ? "Duplicates reviewed"
    : "Duplicate review re-opened";

  // A reopen captures a reason; show it without naming the field, so a tenant
  // that calls it something else still gets its note surfaced. Only when the
  // action captured exactly one text value - never guess between several.
  const reopenNoteCandidates = Object.values(entry.captured_values ?? {}).filter(
    (v): v is string => typeof v === "string" && v.trim() !== "",
  );
  const reopenNote =
    reopenNoteCandidates.length === 1 ? reopenNoteCandidates[0] : "";

  // Assignment entries are logged with action_key="assign" and the current
  // (unchanged) state, so their to_state_label would otherwise read as a
  // duplicate of the state name. Surface the actual ownership change instead.
  const newAssignee = (entry.captured_values as Record<string, unknown> | null)
    ?.new_assignee as string | null | undefined;
  const assignLabel = newAssignee
    ? t("claims.activityAssignedTo", "Assigned to {{newAssignee}}", {
        newAssignee,
      })
    : t("claims.activityUnassigned", "Unassigned");

  return (
    <div
      className={cn(
        "relative pb-[18px] pl-3",
        isClickable && "cursor-pointer group",
      )}
      onClick={isClickable ? onClick : undefined}
    >
      {/* Connector line - rendered per entry so the rail stops at the last
          dot instead of hanging below it. Centred on the left-[-20px] dot;
          -bottom-1 extends it into the next entry's dot for a seamless rail. */}
      {showConnector && (
        <div className="absolute -bottom-1 left-[-15px] top-4 w-px bg-[var(--rule)]" />
      )}
      <div
        className={cn(
          "absolute left-[-20px] top-1 h-[11px] w-[11px] rounded-full border-[1.5px] bg-[var(--bg)]",
          dotColor,
        )}
      />
      <div>
        <span
          className={cn(
            "text-[13px] font-normal text-[var(--ink)]",
            isClickable && "group-hover:underline",
          )}
        >
          {isContact && (
            <Mail className="mr-1.5 inline-block h-3.5 w-3.5 text-[var(--blue)]" />
          )}
          {isNote && !isContact && (
            <StickyNote className="mr-1.5 inline-block h-3.5 w-3.5 text-[var(--amber,#d97706)]" />
          )}
          {isDupResolve && (
            <CheckCircle2 className="mr-1.5 inline-block h-3.5 w-3.5 text-[var(--green)]" />
          )}
          {isDupReopen && (
            <Layers className="mr-1.5 inline-block h-3.5 w-3.5 text-[var(--status-warn)]" />
          )}
          {isAssign && (
            <UserPlus className="mr-1.5 inline-block h-3.5 w-3.5 text-[var(--blue)]" />
          )}
          {isEdit && (
            <Pencil className="mr-1.5 inline-block h-3.5 w-3.5 text-[var(--green)]" />
          )}
          {(isEvidenceAdd || isEvidenceRemove) && (
            <Paperclip
              className={cn(
                "mr-1.5 inline-block h-3.5 w-3.5",
                isEvidenceAdd ? "text-[var(--green)]" : "text-[var(--ink-3)]",
              )}
            />
          )}
          {isDuplicateEvent
            ? duplicateLabel
            : isAssign
              ? assignLabel
              : isEdit
                ? t("claims.activityDetailsEdited", "Claim details edited")
                : isEvidenceAdd
                  ? t("claims.activityEvidenceAdded", "Evidence added")
                  : isEvidenceRemove
                    ? t("claims.activityEvidenceRemoved", "Evidence removed")
                    : isOwnerUpdate
                      ? t("transfers.activityOwnerUpdated", "Ownership updated to new owner")
                      : entry.reverts_closure
                        ? entry.action_label || entry.to_state_label
                        : entry.to_state_label || entry.action_label}
        </span>
        {hasEmails && (
          <span className="ml-1.5 inline-flex items-center gap-1 text-[10px] text-[var(--blue)]">
            <Mail className="h-3 w-3" />
            {sentCount > 0 && (
              <>
                {sentCount} email{sentCount === 1 ? "" : "s"} sent
              </>
            )}
            {sentCount > 0 && notSentCount > 0 && ", "}
            {notSentCount > 0 &&
              t("claims.activityEmailsNotSent", "{{count}} not sent", {
                count: notSentCount,
              })}
          </span>
        )}
        {entry.reverts_closure && (
          <div className="mt-0.5 text-[11px] text-[var(--ink-3)]">
            {t("claims.activityReopenedFrom", "Reopened from")}{" "}
            {entry.from_state_label} {"->"} {entry.to_state_label}
            {reopenNote ? ` - ${reopenNote}` : ""}
          </div>
        )}
        <div className="mt-1 flex items-center gap-2">
          {isSystem ? (
            <Cpu className="h-3 w-3 text-[var(--ink-3)]" />
          ) : isNote && senderRole === "dealer" ? (
            <Building className="h-3 w-3 text-[var(--ink-3)]" />
          ) : (
            <User className="h-3 w-3 text-[var(--ink-3)]" />
          )}
          <span className="text-[11px] text-[var(--ink-3)]">{initiator}</span>
          {senderRole && (
            <span className="rounded bg-[var(--bg-2,#f5f5f5)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--ink-3)]">
              {SENDER_ROLE_LABELS[senderRole] || senderRole}
            </span>
          )}
          <span className="text-[10px] text-[var(--ink-3)]">
            {formatDate(entry.created_at)} {formatTime(entry.created_at)}
          </span>
        </div>
        {attachments.length > 0 && (
          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-[var(--ink-3)]">
            <Paperclip className="h-3 w-3" />
            <span>
              {attachments.length} attachment
              {attachments.length === 1 ? "" : "s"}
            </span>
            <span className="truncate">
              {attachments
                .map(
                  (attachment) =>
                    attachment.file_name || attachment.uuid.slice(0, 8),
                )
                .join(", ")}
            </span>
          </div>
        )}
        {entry.override && (
          <div className="mt-2 flex flex-col gap-1 rounded-md border-[0.5px] border-[var(--status-warn)] bg-[var(--status-warn-bg)] px-2 py-1.5">
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="h-3 w-3 shrink-0 text-[var(--status-warn)]" />
              <span className="text-[11px] font-medium text-[var(--ink-1)]">
                {t("claims.override.activityLabel", "Progression overridden")}
                {entry.override.granted_by
                  ? ` - ${entry.override.granted_by}`
                  : ""}
              </span>
            </div>
            {entry.override.reason_labels.length > 0 && (
              <span className="text-[11px] text-[var(--ink-2)]">
                {entry.override.reason_labels.join(", ")}
              </span>
            )}
            {entry.override.comment && (
              <span className="text-[11px] italic text-[var(--ink-3)]">
                {entry.override.comment}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Generic Station Placeholder ───────────────────────────

function GenericStationPanel({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-light)] px-6 py-12">
      <Layers className="mb-3 h-8 w-8 text-[var(--ink-3)]" />
      <p className="text-[13px] font-normal text-[var(--ink-2)]">
        {label} Station
      </p>
      <p className="mt-1 text-[11px] text-[var(--ink-3)]">
        Use the workflow actions above to progress this claim.
      </p>
    </div>
  );
}

// ── Auto-Proceed Countdown ──────────────────────────────

function AutoProceedCountdown({
  enteredAt,
  durationDays,
  label,
}: {
  enteredAt: string | null | undefined;
  durationDays: number;
  label?: string;
}) {
  if (!enteredAt) return null;
  const entered = new Date(enteredAt);
  const deadline = new Date(entered.getTime() + durationDays * 86400000);
  const now = new Date();
  const remainingMs = deadline.getTime() - now.getTime();
  const remainingDays = Math.max(0, Math.ceil(remainingMs / 86400000));
  const isExpired = remainingMs <= 0;

  const colorClass = isExpired
    ? "border-[var(--status-error)] bg-[var(--status-error-bg-subtle)] text-[var(--status-error)]"
    : remainingDays <= 3
      ? "border-[var(--status-error)] bg-[var(--status-error-bg-subtle)] text-[var(--status-error)]"
      : remainingDays <= 7
        ? "border-[var(--status-warning)] bg-[var(--status-warning-bg)] text-[var(--status-warning)]"
        : "border-[var(--green)] bg-[var(--green-bg)] text-[var(--green)]";

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border-[0.5px] px-2.5 py-0.5 text-[10px] font-medium",
        colorClass,
      )}
    >
      <Timer className="h-3 w-3" />
      {isExpired
        ? label
          ? `${label} - expired`
          : "Auto-proceed expired"
        : `${remainingDays}d remaining${label ? ` - ${label}` : ""}`}
    </span>
  );
}

// ── HO Approval Panel (LOA tab) ────────────────────────

function HOApprovalPanel({
  settlement,
  enteredAt,
  formatOptions,
}: {
  settlement: SettlementData | undefined;
  enteredAt: string | null | undefined;
  formatOptions: { locale: string; currency: string };
}) {
  const fmtMoney = (val: number) =>
    new Intl.NumberFormat(formatOptions.locale, {
      style: "currency",
      currency: formatOptions.currency,
    }).format(val);

  const lineItems = settlement?.line_items ?? [];
  const materialItems = lineItems.filter(
    (li) =>
      li.category === "MATERIAL" ||
      (li.item_detail && /material/i.test(li.item_detail)),
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Status banner */}
      <div className="flex items-start gap-3 rounded-2xl border-[0.5px] border-l-[3px] border-[var(--rule)] border-l-[#2980b9] bg-[var(--bg-light)] px-4 py-3">
        <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#2980b9]" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="text-[13px] font-medium text-[var(--ink)]">
              Awaiting Homeowner Response
            </p>
            <AutoProceedCountdown
              enteredAt={enteredAt}
              durationDays={14}
              label="auto-approve"
            />
          </div>
          <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
            The homeowner has been notified of the settlement offer. They have
            14 days to accept or deny. If no response, the claim auto-approves.
          </p>
        </div>
      </div>

      {/* Material quantities (what the homeowner sees - no prices) */}
      {materialItems.length > 0 && (
        <div className="overflow-hidden rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)]">
          <div className="flex items-center gap-2 border-b-[0.5px] border-[var(--rule)] px-5 py-3">
            <Package className="h-3.5 w-3.5 text-[var(--skeleton-base)]" />
            <h4 className="text-[13px] font-bold font-display text-[var(--ink)]">
              Material Offer (Homeowner View)
            </h4>
          </div>
          <div className="px-5 py-3">
            <p className="mb-2 text-[10px] text-[var(--ink-3)]">
              The homeowner sees quantities only - dollar amounts are hidden.
            </p>
            <div className="flex flex-col gap-1.5">
              {materialItems.map((li) => (
                <div
                  key={li.uuid}
                  className="flex items-center justify-between rounded-[6px] bg-[var(--bg-subtle)] px-3 py-2"
                >
                  <span className="text-[12px] text-[var(--ink)]">
                    {li.product_name || li.description}
                  </span>
                  <span className="text-[12px] font-medium tabular-nums text-[var(--ink)]">
                    {li.quantity} {li.uom || "units"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Settlement total (admin-only view) */}
      {settlement?.summary && (
        <div className="overflow-hidden rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-5 py-4">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ink-3)]">
            Settlement Total (Admin View)
          </p>
          <p className="font-display text-[24px] font-semibold text-[var(--ink)]">
            {fmtMoney(settlement.summary.settlement_total)}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Stage tab card grid (WPS-146) ───────────────────────

/** The stage tabs laid out in a StageCardGrid - the ones Lock layout can hold. */
const CARD_GRID_TABS = new Set([
  "lab",
  "inspection",
  "settlement",
  "loa",
  "payments",
]);

/** The claim page's Lock layout, for the stage tabs' grids. */
const ClaimLayoutLockContext = createContext(false);

/**
 * WPS-146: a stage tab's cards (Lab, Inspection, Settlement, LOA ...) move like
 * the Overview's, and Lock layout holds them still. Each tab keeps
 * its own arrangement (`storageKey`); the lock is the page's one Lock layout.
 * Banners stay above the cards (`data-reorder-static`); tables and review
 * panels take a full row (`data-card-wide`).
 */
function StageCardGrid({
  storageKey,
  children,
}: {
  storageKey: string;
  children: ReactNode;
}) {
  const locked = useContext(ClaimLayoutLockContext);
  // Movable, not hideable: these tabs hold working panels (the LOA review,
  // payments) whose hiding would carry to every claim.
  return (
    <DraggableCardGrid storageKey={storageKey} locked={locked} aligned>
      {children}
    </DraggableCardGrid>
  );
}

// ── Settlement Audit Panel ──────────────────────────────

function SettlementAuditPanel({
  settlement,
  claimUuid,
  currency,
  allowPaymentProcessing,
  trailingCards,
}: {
  settlement: SettlementData;
  claimUuid: string;
  currency: string;
  allowPaymentProcessing: boolean;
  /** WPS-146: the tab's other cards, in the same grid. */
  trailingCards?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <StageCardGrid storageKey="claim-detail-settlement-cards-v1">
      {/* Segregation of duties banner */}
      <div data-reorder-static className="flex items-start gap-3 rounded-2xl border-[0.5px] border-l-[3px] border-[var(--rule)] border-l-[#8b5cf6] bg-[var(--bg-light)] px-4 py-3">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#8b5cf6]" />
        <div>
          <p className="text-[13px] font-medium text-[var(--ink)]">
            Settlement Audit in Progress
          </p>
          <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
            Payment has been created. A different team member must review and
            approve the settlement before processing. Use the workflow actions
            above to approve or return to RS.
          </p>
        </div>
      </div>

      {/* Settlement components (read-only view) */}
      <SettlementLineItemsTable
        key="line-items"
        data-card-wide
        data-card-title={t("claims.card.lineItems", "Line Items")}
        claimUuid={claimUuid}
        lineItems={settlement.line_items}
        isEditable={false}
        currency={currency}
        summary={settlement.summary}
        pricesRefreshedAt={settlement.prices_refreshed_at}
        isLocked={settlement.prices_locked === true}
        defaultPaymentType={settlement.default_payment_type}
      />
      {/* WPS-146: the summary's cards are cards of the tab, so each one
          moves and stretches to its row on its own. */}
      <SettlementCostSummaryCard
        key="cost-summary"
        data-card-title={t("settlement.costSummary.title", "Cost Summary")}
        summary={settlement.summary}
        currency={currency}
      />
      <SettlementLedgerSplitCard
        key="ledger-split"
        data-card-title={t("settlement.ledgerSplit.title", "Ledger Split")}
        lineItems={settlement.line_items}
        currency={currency}
      />
      <SettlementProrationCard
        key="proration"
        data-card-title={t("settlement.proration.title", "Proration")}
        summary={settlement.summary}
        currency={currency}
      />
      <SettlementPaymentsTable
        key="payments"
        data-card-wide
        data-card-title={t("claims.card.payments", "Payments")}
        claimUuid={claimUuid}
        payments={settlement.payments}
        isEditable={false}
        allowPaymentProcessing={allowPaymentProcessing}
        currency={currency}
        paidThrough={settlement.paid_through}
      />
      {trailingCards}
    </StageCardGrid>
  );
}

// ── Compensate Panel ────────────────────────────────────

function CompensatePanel({
  settlement,
  claimUuid,
  currency,
  formatOptions,
  allowPaymentProcessing,
  trailingCards,
}: {
  settlement: SettlementData;
  claimUuid: string;
  currency: string;
  formatOptions: { locale: string; currency: string };
  allowPaymentProcessing: boolean;
  /** WPS-146: the tab's other cards, in the same grid. */
  trailingCards?: ReactNode;
}) {
  const { t } = useTranslation();
  // The vouchers module shows its own card on the tab; this one reads the
  // legacy per-payment voucher fields.
  const vouchersEnabled = useFeatureFlag(FLAGS.VOUCHERS);
  const fmtMoney = (val: number) =>
    new Intl.NumberFormat(formatOptions.locale, {
      style: "currency",
      currency: formatOptions.currency,
    }).format(val);

  const processedPayments = settlement.payments.filter(
    (p) => p.status === "PROCESSED",
  );
  // DRF serializes DecimalField as a string ("120.00"); coerce before
  // summing so the reduce doesn't concatenate.
  const totalPaid =
    processedPayments.length > 0
      ? processedPayments.reduce((sum, p) => sum + Number(p.amount), 0)
      : Number(settlement.summary.settlement_total);

  // Use a local extension type for voucher-specific payment attributes.
  type VoucherPayment = ClaimPayment & {
    voucher_number?: string | null;
    voucher_status?: string | null;
    voucher_expiry?: string | null;
  };
  const voucherPayments: VoucherPayment[] = settlement.payments.filter(
    (p) => p.payment_type === "VCH",
  );

  const VOUCHER_STATUS_COLORS: Record<string, string> = {
    CREATED: "border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-2)]",
    ACTIVE:
      "border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] text-[var(--status-ok)]",
    REDEEMED: "border-[#8b5cf6] bg-[#f5f3ff] text-[#8b5cf6]",
    PAID: "border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] text-[var(--status-ok)]",
    EXPIRED:
      "border-[var(--status-error)] bg-[var(--status-error-bg-subtle)] text-[var(--status-error)]",
    CANCELLED:
      "border-[var(--ink-3)] bg-[var(--bg-subtle)] text-[var(--ink-3)]",
  };

  return (
    <StageCardGrid storageKey="claim-detail-settlement-cards-v1">
      {/* Status banner */}
      <div data-reorder-static className="flex items-start gap-3 rounded-2xl border-[0.5px] border-l-[3px] border-[var(--rule)] border-l-[var(--green)] bg-[var(--bg-light)] px-4 py-3">
        <Banknote className="mt-0.5 h-4 w-4 shrink-0 text-[var(--green)]" />
        <div>
          <p className="text-[13px] font-medium text-[var(--ink)]">
            Payment Issued
          </p>
          <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
            Settlement payments have been processed. Total paid:{" "}
            <strong className="font-medium text-[var(--ink)]">
              {fmtMoney(totalPaid)}
            </strong>
            . Use the workflow actions above to close the claim once all
            payments are confirmed.
          </p>
        </div>
      </div>

      {/* Settlement breakdown stays visible after payment (WPS-123).
          Line Items + Settlement Summary (which carries the Category and
          Ledger breakdowns) render read-only so the full settlement picture
          remains available in the compensated and closed statuses, never
          disappearing once the claim is paid. */}
      <SettlementLineItemsTable
        key="line-items"
        data-card-wide
        data-card-title={t("claims.card.lineItems", "Line Items")}
        claimUuid={claimUuid}
        lineItems={settlement.line_items}
        isEditable={false}
        currency={currency}
        summary={settlement.summary}
        pricesRefreshedAt={settlement.prices_refreshed_at}
        isLocked={settlement.prices_locked === true}
        defaultPaymentType={settlement.default_payment_type}
      />
      {/* WPS-146: the summary's cards are cards of the tab, so each one
          moves and stretches to its row on its own. */}
      <SettlementCostSummaryCard
        key="cost-summary"
        data-card-title={t("settlement.costSummary.title", "Cost Summary")}
        summary={settlement.summary}
        currency={currency}
      />
      <SettlementLedgerSplitCard
        key="ledger-split"
        data-card-title={t("settlement.ledgerSplit.title", "Ledger Split")}
        lineItems={settlement.line_items}
        currency={currency}
      />
      <SettlementProrationCard
        key="proration"
        data-card-title={t("settlement.proration.title", "Proration")}
        summary={settlement.summary}
        currency={currency}
      />

      {/* Voucher tracking if applicable */}
      {!vouchersEnabled && voucherPayments.length > 0 && (
        <DetailCard
          key="voucher-status"
          title={t("claims.voucherStatus.title", "Voucher Status")}
          icon={<CreditCard className="h-3.5 w-3.5" />}
          collapsible={false}
        >
          <div className="flex flex-col gap-2 px-4 py-3">
            {voucherPayments.map((v) => (
              <div
                key={v.uuid}
                className="flex items-center justify-between rounded-[6px] bg-[var(--bg-subtle)] px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full border-[0.5px] px-2 py-0.5 text-[9px] font-medium",
                      VOUCHER_STATUS_COLORS[v.voucher_status] ??
                        VOUCHER_STATUS_COLORS.CREATED,
                    )}
                  >
                    {v.voucher_status || "CREATED"}
                  </span>
                  {v.voucher_number && (
                    <span className="text-[11px] font-mono text-[var(--ink-2)]">
                      #{v.voucher_number}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {v.voucher_expiry && (
                    <AutoProceedCountdown
                      enteredAt={v.created_at}
                      durationDays={Math.ceil(
                        (new Date(v.voucher_expiry).getTime() -
                          new Date(v.created_at).getTime()) /
                          86400000,
                      )}
                      label="expires"
                    />
                  )}
                  <span className="text-[12px] font-medium tabular-nums text-[var(--ink)]">
                    {fmtMoney(v.amount)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </DetailCard>
      )}

      {/* Payments table */}
      <SettlementPaymentsTable
        key="payments"
        data-card-wide
        data-card-title={t("claims.card.payments", "Payments")}
        claimUuid={claimUuid}
        payments={settlement.payments}
        isEditable={false}
        allowPaymentProcessing={allowPaymentProcessing}
        currency={currency}
        paidThrough={settlement.paid_through}
      />
      {trailingCards}
    </StageCardGrid>
  );
}

// ── Claim Action Dialog ──────────────────────────────────

/**
 * Settlement totals + proration the approval dialog confirms. These are
 * derived from the settlement line items and the tethered proration
 * formula - never hand-keyed - so "Submit for Approval" can't diverge
 * from the reorganised line items.
 */
type DialogItemDetailSubtotal = {
  item_detail: string;
  label: string;
  subtotal: number;
};

type DialogSettlementSummary = {
  material_subtotal?: number;
  labor_subtotal?: number;
  other_subtotal?: number;
  settlement_total?: number;
  material_proration_pct?: number | null;
  labor_proration_pct?: number | null;
  prorated_total?: number | null;
  // WPS-165 #12: the payable figure - prorated OC material + labor plus the
  // Non-OC Material and Other lines no formula prorates.
  payable_total?: number | null;
  proration_formula_name?: string | null;
  // WP1-61: per-Item-Detail breakdown for the dialog summary.
  item_detail_subtotals?: DialogItemDetailSubtotal[];
};

// Capture-field code_names whose values come from the settlement summary,
// not user input. When a summary is present these render read-only in the
// "Current Settlement" panel and the formula-derived values are submitted,
// so an approval cannot silently diverge from the line items + formula.
// WPS-61: proration_pct is derived too - OC dropped the editable Proration %
// (and its change-reason field), so the payout is always formula-driven.
const SETTLEMENT_DERIVED_FIELDS = new Set([
  "coverage_amount",
  "material_total",
  "labor_total",
  "settlement_total",
  "proration_pct",
]);

function deriveSettlementValue(
  code: string,
  s: DialogSettlementSummary,
): number | null {
  switch (code) {
    case "material_total":
      return s.material_subtotal ?? null;
    case "labor_total":
      return s.labor_subtotal ?? null;
    case "settlement_total":
      return s.settlement_total ?? null;
    case "coverage_amount":
      // WPS-165 #12: the payable total, not the prorated portion. Reading
      // ``prorated_total`` here dropped Non-OC Material and Other - lines that
      // are payable in full - so attaching a proration formula silently shrank
      // the approved coverage by exactly those lines.
      return s.payable_total ?? s.prorated_total ?? s.settlement_total ?? null;
    case "proration_pct":
      return s.material_proration_pct ?? s.labor_proration_pct ?? 100;
    default:
      return null;
  }
}

function ClaimActionDialog({
  open,
  action,
  claimUuid,
  workflowInstanceUuid,
  attributeValues,
  onOpenChange,
  onSuccess,
  onAddDocuments,
  onChecklistClaimant,
  settlementSummary,
  pricingGuidance,
  currentStateKey,
  payeeCandidates = [],
  approvalChanges = null,
}: {
  open: boolean;
  action: WorkflowAction | null;
  claimUuid: string;
  /** Parties this claim can pay, for a ``claim_payee`` ENTITY_SEARCH capture. */
  payeeCandidates?: PayeeCandidate[];
  /** On a claim back for approval, what moved since the last decision. */
  approvalChanges?: ApprovalChanges | null;
  workflowInstanceUuid?: string;
  attributeValues?: Record<string, unknown>;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  /**
   * WPS-175 #13: leave the dialog for the evidence surface. A blocked action
   * is often blocked on a missing document, and the fix lives on another tab -
   * without this the only ways out are an override the user may not be
   * entitled to, or Cancel and a hunt for the right tab.
   */
  onAddDocuments?: () => void;
  /**
   * WPS-175 #13: ask the claimant for the document instead of chasing it
   * yourself. ``send_checklist`` already exists as an action - the block
   * panel is simply where a user needs it, because that is where they
   * discover the document is missing.
   */
  onChecklistClaimant?: () => void;
  settlementSummary?: DialogSettlementSummary;
  pricingGuidance?: PricingGuidance | null;
  /** The claim's current state, so a self-looping action does not claim
    to move it somewhere it already is (WPS-175 #5). */
  currentStateKey?: string;
}) {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { formatCurrency, locale, currency } = useFormatting();
  // WPS-175 #15: the guidance the workflow author wrote for THIS action.
  //
  // Every action can carry a ``ui_hints.banner`` - the Send for Approval
  // action's says "Over the approval threshold - send this claim for
  // approval. Payment unlocks once a reviewer approves it." It was rendered
  // on the claim page but never in the dialog, so the moment someone
  // committed to the action all they were told was "Are you sure you want to
  // execute this action?" - which says nothing about what happens, who picks
  // it up, or what to do next. Tenant copy, authored per action, so this
  // reads correctly for a tenant whose approval flow is nothing like OC's.
  const actionGuidance = (action?.banner ?? "").trim();
  const actionMetadata = useClaimActionMetadata();
  // Provider context so ENTITY_SEARCH capture fields resolve inside the action
  // dialog - the Assign action's partner picker, and Record Payment's payee.
  const resourceLookupProviders = useClaimPayeeProviders(payeeCandidates);
  const executeAction = useExecuteClaimAction();
  const uploadFile = useUploadFile();
  const [fields, setFields] = useState<ActionFieldMeta[]>([]);
  // A read-only "approve as submitted" sign-off shows the partner's submitted
  // values (sourced server-side from their branch) instead of editable inputs.
  const [submittedReview, setSubmittedReview] = useState<
    SubmittedReviewField[] | null
  >(null);
  const [captured, setCaptured] = useState<Record<string, unknown>>({});
  // What the action's side-effects will do (e.g. the payment it creates).
  const [effectPreviews, setEffectPreviews] = useState<ActionEffectPreview[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  // Set of capture-field codes with an upload in flight. A set (not a single
  // string) so concurrent uploads across fields don't let one finishing
  // upload clear the guard while others are still running.
  const [uploadingFields, setUploadingFields] = useState<Set<string>>(
    () => new Set(),
  );
  const isUploading = uploadingFields.size > 0;
  const addUploading = useCallback((code: string) => {
    setUploadingFields((prev) => new Set(prev).add(code));
  }, []);
  const removeUploading = useCallback((code: string) => {
    setUploadingFields((prev) => {
      const next = new Set(prev);
      next.delete(code);
      return next;
    });
  }, []);
  // Inline errors for capture fields the action requires but that were left
  // empty. State-action capture fields are required by default server-side
  // (see ``_resolve_capture_fields_from_ui_hints``) and the label already
  // renders an asterisk, but nothing stopped the submit - so a claim could be
  // denied with no reason recorded. Keyed by code_name so each empty field
  // turns red with its own message.
  const [requiredErrors, setRequiredErrors] = useState<Record<string, string>>(
    {},
  );

  // WPS-81 override: when the engine refuses the action with a structured
  // block (an ``exception_required`` binding or open ``blocked_by_issue``
  // rows), we surface a why-blocked banner and - for authorised users - an
  // override affordance that reuses the platform's generic Exceptions flow.
  const [blockError, setBlockError] = useState<EngineBlock | null>(null);
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  // WPS-97: failures that are NOT a structured engine block (validation,
  // permissions, network, 500s) used to surface as a 3s toast behind the open
  // dialog and then vanish. They now render inline and stay put until the
  // user changes something or closes the dialog.
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Same for the capture-metadata fetch: it used to close the dialog with no
  // explanation at all, which read as "the button does nothing".
  const [loadError, setLoadError] = useState<string | null>(null);
  // The override affordance is gated on the SAME capability the generic
  // exceptions surface uses: the ``exceptions`` feature flag plus the
  // ``exceptions.approve`` permission (granted to RS/Leads). Non-authorised
  // users still see the why-blocked banner, just no override button.
  const canOverride =
    useFeatureFlag(FLAGS.EXCEPTIONS) && can("exceptions.approve");
  const createExceptionGrant = useCreateExceptionGrant();
  const resolveIssue = useResolveWorkflowIssue();
  // Only fetch the reason catalog when an authorised user has the dialog
  // open - keeps the exceptions endpoint quiet for everyone else.
  const { data: exceptionReasons = [] } = useExceptionReasons(
    canOverride && open,
  );

  // ``MetadataField``'s ``FileField`` branch calls ``onFileUpload`` to
  // actually upload the picked file - without it, the file picker
  // updates local state but no upload happens. Wire it to the shared
  // ``useUploadFile`` mutation here so picking a photo in an action
  // dialog (e.g. Submit Documents → photos) actually persists the
  // file and stashes its UUID on ``captured`` for submit.
  const handleActionFileUpload = useCallback(
    async (file: File, field: FormFieldMeta) => {
      addUploading(field.code_name);
      try {
        const uploaded = await uploadFile.mutateAsync({
          file,
          use_case: "CLAIM_EVIDENCE",
        });
        setCaptured((prev) => ({ ...prev, [field.code_name]: uploaded.uuid }));
        return {
          uuid: uploaded.uuid,
          file_name: uploaded.file_name,
          file_url: uploaded.file_url,
        };
      } catch (err) {
        toastApiError(err, "Failed to upload file");
        throw err;
      } finally {
        removeUploading(field.code_name);
      }
    },
    [uploadFile, addUploading, removeUploading],
  );

  useEffect(() => {
    if (!open || !action) {
      setFields([]);
      setSubmittedReview(null);
      setEffectPreviews([]);
      setCaptured({});
      setLoading(false);
      setBlockError(null);
      setOverrideModalOpen(false);
      setSubmitError(null);
      setLoadError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    actionMetadata
      .mutateAsync({ uuid: claimUuid, action_key: action.key })
      .then((result) => {
        if (cancelled) return;
        setFields(result.fields);
        setSubmittedReview(result.submitted_review ?? null);
        setEffectPreviews(result.effect_previews ?? []);
        // WPS-81: if the action is exception-bound, surface the why-blocked
        // banner + override control the moment the dialog finishes loading -
        // no dead "confirm then wait for a 400" round-trip. The engine still
        // enforces the binding on execute; this is the proactive preview.
        if (
          result.exception &&
          result.exception.code === "exception_required"
        ) {
          setBlockError({
            code: "exception_required",
            binding: result.exception.binding ?? null,
            message: result.exception.message ?? "",
          });
        }
        const prefill: Record<string, unknown> = {};
        for (const f of result.fields) {
          const code = f.code_name;
          if (settlementSummary && SETTLEMENT_DERIVED_FIELDS.has(code)) {
            // Source settlement money/proration from the formula-derived
            // summary, not the stored attribute (which defaults to 100%).
            const derived = deriveSettlementValue(code, settlementSummary);
            if (derived != null) prefill[code] = derived;
          } else if (
            f.configuration_data?.lookup_provider === CLAIM_PAYEE_PROVIDER
          ) {
            const value = defaultPayeeValue(payeeCandidates);
            if (value) prefill[code] = value;
          } else if (
            // A top-up's fields describe a new payment, not the claim's first.
            action.persist_captures !== false &&
            attributeValues &&
            attributeValues[code] != null
          ) {
            prefill[code] = attributeValues[code];
          }
        }
        // Backend-derived defaults (e.g. currency from pricing guidance)
        // fill fields the claim has no stored value for - still editable.
        for (const f of result.fields) {
          if (prefill[f.code_name] == null && f.default_value != null) {
            prefill[f.code_name] = f.default_value;
          }
        }
        setCaptured(prefill);
      })
      .catch((err: unknown) => {
        // WPS-97: previously this closed the dialog silently - the user saw
        // the dialog flash open and shut with nothing to read. Keep it open
        // and say what went wrong.
        if (cancelled) return;
        setLoadError(
          extractApiErrorMessage(err) ??
            t(
              "claims.action.loadFailed",
              "We could not load this action. Close the dialog and try again.",
            ),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, action?.key, claimUuid, payeeCandidates]);

  // Run the transition. ``exceptionGrantId`` (WPS-81) is threaded through
  // when an authorised user has overridden an ``exception_required`` block;
  // ``blocked_by_issue`` blocks are cleared by resolving the issues first,
  // so they resubmit with no grant. A structured engine block sets
  // ``blockError`` (surfaced as the why-blocked banner + override control)
  // instead of a generic toast; any other failure falls back to the toast.
  const submit = async (exceptionGrantId?: string) => {
    if (!action) return false;
    setSubmitError(null);
    try {
      const result = await executeAction.mutateAsync({
        uuid: claimUuid,
        action_key: action.key,
        ...(fields.length > 0 ? { captured_values: captured } : {}),
        ...(exceptionGrantId ? { exception_grant_id: exceptionGrantId } : {}),
      });
      setBlockError(null);
      // Config-authored confirmation ("Onsite inspection requested - the
      // Resolution Specialist has been notified") so the message names the
      // outcome rather than the machinery. Falls back to the generic line for
      // actions that have not authored one.
      if (!toastDivertedAction(result, t)) {
        toast.success(
          action.success_message || `Action "${action.label}" executed`,
        );
      }
      onOpenChange(false);
      onSuccess();
      return true;
    } catch (err: unknown) {
      const block = parseEngineBlockError(err);
      if (block) {
        setBlockError(block);
        return false;
      }
      // WPS-97: inline and persistent - the dialog is still open, so the
      // explanation belongs next to the control that failed, not in a toast
      // that expires behind it.
      setSubmitError(
        extractApiErrorMessage(err) ??
          t(
            "claims.action.submitFailed",
            'We could not complete "{{label}}". Nothing on the claim has changed.',
            // ``label`` is a non-empty CharField in practice, but a blank one
            // would render as `We could not complete ""`. Fall back to the key.
            { label: action.label || action.key },
          ),
      );
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!action) return;
    // Guard against submitting before an in-flight upload has stashed its
    // file UUID on ``captured`` - otherwise the file silently never lands
    // in evidence (the "uploaded photo did not appear" report).
    if (isUploading) return;
    // Every required capture field must carry a value before the action runs.
    const missing: Record<string, string> = {};
    for (const field of editableFields) {
      if (!field.required) continue;
      const value = captured[field.code_name];
      if (!isRequiredValueProvided(field, value)) {
        missing[field.code_name] = t(
          "validation.fieldRequired",
          "{{field}} is required",
          { field: field.display_name || field.code_name },
        );
      }
    }
    if (Object.keys(missing).length > 0) {
      setRequiredErrors(missing);
      return;
    }
    setRequiredErrors({});
    await submit();
  };

  // WPS-81 override confirm. For an ``exception_required`` binding we mint a
  // single-use ``ExceptionGrant`` (scoped to this claim's workflow instance +
  // the binding) and resubmit with its uuid. For ``blocked_by_issue`` we
  // resolve every open issue through the generic resolve flow (a grant does
  // not clear an open issue), then resubmit. Either way the reason codes and
  // note the approver entered are captured for audit.
  const handleOverrideConfirm = async (
    payload: WorkflowIssueResolvePayload,
  ) => {
    if (!action || !blockError) return;
    const reasonCodes = payload.reason_codes ?? [];
    const comment = payload.resolution_text ?? "";
    try {
      if (blockError.code === "exception_required") {
        const grant = await createExceptionGrant.mutateAsync({
          action_key: action.key,
          reason_codes: reasonCodes,
          comment,
          workflow_instance: workflowInstanceUuid ?? null,
          binding: blockError.binding?.uuid ?? null,
          context: { action_key: action.key, action_label: action.label },
        });
        setOverrideModalOpen(false);
        await submit(grant.uuid);
        return;
      }
      // blocked_by_issue. Two kinds arrive under this one code, and they
      // need different treatment:
      //
      //  * Persisted issues - raised out of band or by an ENTRY check, and
      //    genuinely in the database. Resolving each one clears the block.
      //  * A firing EXIT check - the document gate is one. Its issue rows
      //    are written inside the transition's transaction and the block
      //    raise unwinds it, so nothing survives to resolve. Resolving them
      //    failed with "No WorkflowIssue matches the given query" and the
      //    claim could not be overridden by any route.
      //
      // An exception grant covers both: the engine validates it, waives a
      // BLOCK+NONE exit check, and stamps the grant onto the resulting
      // WorkflowActionLog - so the waiver carries an audit trail naming who
      // approved it, which resolving an issue row never did.
      const resolvable = blockError.issues.filter((issue) => issue.uuid);
      let resolvedAny = false;
      for (const issue of resolvable) {
        try {
          await resolveIssue.mutateAsync({
            uuid: issue.uuid,
            payload: {
              resolution_kind: "exception_granted",
              resolution_text: comment,
              reason_codes: reasonCodes,
              captured_values: payload.captured_values ?? {},
            },
          });
          resolvedAny = true;
        } catch {
          // The row is not there - an EXIT check's issue never committed.
          // Fall through to the grant, which does not need it.
        }
      }
      const grant = resolvedAny
        ? null
        : await createExceptionGrant.mutateAsync({
            action_key: action.key,
            reason_codes: reasonCodes,
            comment,
            workflow_instance: workflowInstanceUuid ?? null,
            binding: null,
            context: { action_key: action.key, action_label: action.label },
          });
      setOverrideModalOpen(false);
      await submit(grant?.uuid);
    } catch (err: unknown) {
      toastApiError(err, "Failed to override this action");
    }
  };

  // Reason catalog for the override modal: the tenant's active exception
  // reasons, with the binding's suggested reason (exception_required) or the
  // issue's own reason options (blocked_by_issue) surfaced as the default.
  const overrideReasonOptions: WorkflowIssueReasonOption[] = exceptionReasons
    .filter((reason) => reason.is_active)
    .map((reason) => ({
      code: reason.key,
      label: reason.title,
      description: reason.description,
    }));
  const overrideDefaultReasonCode =
    blockError?.code === "exception_required"
      ? (blockError.binding?.suggested_exception_key ?? undefined)
      : (blockError?.issues[0]?.reason_options?.[0]?.code ?? undefined);
  // Merge any issue-supplied reason options into the catalog so the default
  // is always selectable even when it is not one of the tenant's rows.
  const mergedOverrideReasonOptions: WorkflowIssueReasonOption[] = (() => {
    if (blockError?.code !== "blocked_by_issue") {
      if (
        overrideDefaultReasonCode &&
        !overrideReasonOptions.some((o) => o.code === overrideDefaultReasonCode)
      ) {
        const title = blockError?.binding?.suggested_exception_title;
        return [
          {
            code: overrideDefaultReasonCode,
            label: title || overrideDefaultReasonCode,
          },
          ...overrideReasonOptions,
        ];
      }
      return overrideReasonOptions;
    }
    const byCode = new Map<string, WorkflowIssueReasonOption>();
    for (const opt of overrideReasonOptions) byCode.set(opt.code, opt);
    for (const issue of blockError.issues) {
      for (const opt of issue.reason_options ?? []) {
        if (!byCode.has(opt.code)) byCode.set(opt.code, opt);
      }
    }
    return [...byCode.values()];
  })();

  // When a settlement summary is available, the settlement money totals and
  // the proration % are shown read-only (auto-calculated from the line items +
  // tethered formula) and removed from the editable inputs. WPS-61: OC dropped
  // the editable Proration % and its change-reason field, so nothing on a
  // settlement action is user-editable - the payout is always formula-driven.
  const isDerivedField = (code: string) => SETTLEMENT_DERIVED_FIELDS.has(code);
  const hasDerivedSettlement =
    !!settlementSummary && fields.some((f) => isDerivedField(f.code_name));
  const editableFields = hasDerivedSettlement
    ? fields.filter((f) => !isDerivedField(f.code_name))
    : fields;
  const prorationPct =
    settlementSummary?.material_proration_pct ??
    settlementSummary?.labor_proration_pct;
  // WPS-165 #12: the payable figure the approver signs off on - see the
  // ``coverage_amount`` case above.
  const coverageAmount =
    settlementSummary?.payable_total ??
    settlementSummary?.prorated_total ??
    settlementSummary?.settlement_total;

  return (
    <ResourceLookupContextProvider providers={resourceLookupProviders}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{action?.label ?? "Execute Action"}</DialogTitle>
            {/* An action whose target IS the current state does not move the
              claim - it records something against it (requesting an onsite
              inspection, for instance). Announcing a move to the state it is
              already in reads as though the claim is about to change hands
              when nothing of the sort happens. */}
            {action?.target_state_key &&
              action.target_state_key !== currentStateKey && (
                <DialogDescription className="text-[12px]">
                  This will move the claim to{" "}
                  <strong className="font-normal">
                    {/* The workflow's own label for the destination. Falls back to
                    a de-underscored key only for a payload that predates
                    ``target_state_label`` - otherwise an approver reads
                    "rs material labor" instead of "Material & Labor Review". */}
                    {action.target_state_label ||
                      action.target_state_key.replace(/_/g, " ")}
                  </strong>
                </DialogDescription>
              )}
          </DialogHeader>

          {approvalChanges && (
            <ApprovalChangesSummary changes={approvalChanges} />
          )}

          {/* Settlement context - derived from the line items + tethered
            proration formula, shown read-only so the approver confirms
            (not re-keys) the numbers. The formula-derived values are what
            get submitted (see the prefill effect). */}
          {!loading && hasDerivedSettlement && settlementSummary && (
            <div className="rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2.5">
              <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-[var(--ink-3)]">
                Current Settlement
              </p>
              <p className="mb-1.5 text-[10px] text-[var(--ink-3)]">
                Auto-calculated from the line items &amp; proration formula -
                not editable.
              </p>
              {/* WP1-61: a simple, readable list grouped by Item Detail, then
                the Settlement Total and (when it applies) the proration
                coverage. One value per row, sized for easy reading. */}
              <div className="flex flex-col gap-2 text-[13px]">
                {(settlementSummary.item_detail_subtotals ?? []).map((row) => (
                  <div
                    key={row.item_detail}
                    className="flex items-baseline justify-between gap-4"
                  >
                    <span className="text-[var(--ink-2)]">{row.label}</span>
                    <span className="font-medium text-[var(--ink-1)]">
                      {formatCurrency(row.subtotal)}
                    </span>
                  </div>
                ))}
                <div className="flex items-baseline justify-between gap-4 border-t-[0.5px] border-t-[var(--rule)] pt-2">
                  <span className="font-medium text-[var(--ink-1)]">
                    Settlement Total
                  </span>
                  <span className="font-medium text-[var(--ink-1)]">
                    {formatCurrency(settlementSummary.settlement_total ?? 0)}
                  </span>
                </div>
                {prorationPct != null && (
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="text-[var(--ink-2)]">
                      Coverage after {prorationPct}% proration
                    </span>
                    <span className="font-medium text-[var(--ink-1)]">
                      {formatCurrency(coverageAmount ?? 0)}
                    </span>
                  </div>
                )}
              </div>
              {settlementSummary.proration_formula_name && (
                <p className="mt-2 text-[12px] text-[var(--ink-3)]">
                  Formula: {settlementSummary.proration_formula_name}
                </p>
              )}
            </div>
          )}

          {/* Receipt-pricing guidance - decision input for the amount being
            judged. Rendered when the action's config asks for the
            "pricing" panel; products without reference pricing get an
            explicit note instead of silence. */}
          {!loading && action?.guidance_panels?.includes("pricing") && (
            <PricingGuidancePanel guidance={pricingGuidance} showEmptyNote />
          )}

          {/* WPS-81: why-blocked banner. When the engine refuses the action it
            returns a structured block - either an ``exception_required``
            binding or a list of open ``blocked_by_issue`` rows. We spell out
            WHY the claim cannot progress, and - for users with the
            ``exceptions.approve`` capability - offer an override that captures
            a mandatory reason for audit. Everyone else sees the reasons only. */}
          {blockError && (
            <div className="flex flex-col gap-2 rounded-lg border-[0.5px] border-[var(--status-warn)] bg-[var(--status-warn-bg)] px-3 py-2.5">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--status-warn)]" />
                <div className="flex flex-col gap-1">
                  <p className="text-[12px] font-medium text-[var(--ink-1)]">
                    {t(
                      "claims.override.blockedTitle",
                      "This action cannot progress yet",
                    )}
                  </p>
                  {blockError.code === "exception_required" ? (
                    <p className="text-[12px] leading-[1.5] text-[var(--ink-2)]">
                      {blockError.message ||
                        t(
                          "claims.override.exceptionRequired",
                          "An exception is required before this action can continue.",
                        )}
                    </p>
                  ) : (
                    <ul className="flex flex-col gap-1">
                      {blockError.issues.map((issue) => (
                        <li
                          key={issue.uuid}
                          className="text-[12px] leading-[1.5] text-[var(--ink-2)]"
                        >
                          <span className="font-medium text-[var(--ink-1)]">
                            {issue.summary}
                          </span>
                          {issue.detail ? (
                            <span className="text-[var(--ink-3)]">
                              {" "}
                              - {issue.detail}
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {/* WPS-175 #13: the first answer to "this document is
                  missing" is to go and add it, not to override the check.
                  Offered to everyone, override or not. */}
                {/* WPS-175 #13, in the ticket's own order: add the document,
                  ask the claimant for it, cancel, or override with approval.
                  "Resolve & continue" names the FIRST of those, because that
                  is what the requirement calls the route to the Evidence tab -
                  it used to label the override, which read as though the only
                  way past a missing document was to waive it. */}
                {onAddDocuments && (
                  <Button
                    type="button"
                    onClick={() => {
                      onOpenChange(false);
                      onAddDocuments();
                    }}
                    variant="outline"
                    className="border-[0.5px] border-[var(--status-warn)] px-3 py-1.5 text-[12px] text-[var(--ink-1)]"
                  >
                    {t("claims.override.resolveCta", "Resolve & continue")}
                  </Button>
                )}
                {onChecklistClaimant && (
                  <Button
                    type="button"
                    onClick={() => {
                      onOpenChange(false);
                      onChecklistClaimant();
                    }}
                    variant="outline"
                    className="border-[0.5px] border-[var(--status-warn)] px-3 py-1.5 text-[12px] text-[var(--ink-1)]"
                  >
                    {t(
                      "claims.override.checklistCta",
                      "Checklist the claimant",
                    )}
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  variant="ghost"
                  className="px-3 py-1.5 text-[12px] text-[var(--ink-2)]"
                >
                  {t("claims.override.cancelCta", "Cancel")}
                </Button>
                {canOverride ? (
                  <Button
                    type="button"
                    onClick={() => setOverrideModalOpen(true)}
                    variant="outline"
                    className="border-[0.5px] border-[var(--status-warn)] px-3 py-1.5 text-[12px] text-[var(--ink-1)]"
                  >
                    {t("claims.override.overrideCta", "Override & continue")}
                  </Button>
                ) : (
                  <p className="text-[11px] text-[var(--ink-3)]">
                    {t(
                      "claims.override.noPermission",
                      "Contact a lead to override this step.",
                    )}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* WPS-97: the action's capture metadata failed to load. */}
          {loadError && (
            <div className="flex items-start gap-2 rounded-lg border-[0.5px] border-[var(--status-error)] bg-[var(--status-error-bg-subtle)] px-3 py-2.5">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--status-error)]" />
              <p className="text-[12px] leading-[1.5] text-[var(--ink-2)]">
                {loadError}
              </p>
            </div>
          )}

          {/* WPS-97: the transition itself was refused. Persists until the user
            retries or closes - it is the only account of what happened. */}
          {submitError && (
            <div className="flex items-start gap-2 rounded-lg border-[0.5px] border-[var(--status-error)] bg-[var(--status-error-bg-subtle)] px-3 py-2.5">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--status-error)]" />
              <div className="flex flex-col gap-1">
                <p className="text-[12px] font-medium text-[var(--ink-1)]">
                  {t(
                    "claims.action.failedTitle",
                    "This action did not complete",
                  )}
                </p>
                <p className="whitespace-pre-line text-[12px] leading-[1.5] text-[var(--ink-2)]">
                  {submitError}
                </p>
                {/* The engine's message states facts only (it is served to API
                  clients too), so the UI-specific next step lives here. */}
                <p className="text-[11px] text-[var(--ink-3)]">
                  {t(
                    "claims.action.failedHint",
                    "Refresh the page if the claim may have changed since you opened it.",
                  )}
                </p>
              </div>
            </div>
          )}

          {/* What confirming does, in the claim's own figures: e.g. the one
            payment an action creates, its payee and how it is paid. */}
          {!loading &&
            effectPreviews
              .filter((preview) => preview.kind === "payment")
              .map((preview, index) => (
                <div
                  key={`preview-${index}`}
                  className="flex items-start gap-2 rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-3 py-2.5"
                  data-testid="action-payment-preview"
                >
                  <Banknote className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--ink-2)]" />
                  <p className="text-[13px] leading-[1.5] text-[var(--ink)]">
                    {t("claims.actionDialog.paymentPreview", {
                      defaultValue:
                        "Creates one payment of {{amount}} to {{payee}} ({{recipientType}}, {{paymentType}}).",
                      amount: formatCurrency(Number(preview.amount ?? 0)),
                      payee: preview.payee ?? "",
                      recipientType: preview.recipient_type ?? "",
                      paymentType: preview.payment_type ?? "",
                    })}
                  </p>
                </div>
              ))}

          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
            </div>
          ) : submittedReview && submittedReview.length > 0 ? (
            // "Approve as submitted": the partner's figures are shown read-only
            // for review, not as editable inputs. Approving accepts them as-is
            // (the write is sourced server-side from the branch); to change a
            // figure the owner returns the branch instead.
            <div className="flex flex-col gap-2 py-2">
              <div className="divide-y-[0.5px] divide-[var(--rule)] rounded-lg border-[0.5px] border-[var(--rule)]">
                {submittedReview.map((f) => (
                  <div
                    key={f.code_name}
                    className="flex items-start justify-between gap-4 px-3 py-2"
                  >
                    <span className="text-[12px] text-[var(--ink-3)]">
                      {f.label}
                    </span>
                    <span className="text-right text-[13px] text-[var(--ink-1)]">
                      {formatDisplayValue(
                        defineField({
                          code_name: f.code_name,
                          display_name: f.label,
                          field_type:
                            f.field_type as FormFieldMeta["field_type"],
                          configuration_data: f.configuration_data,
                        }),
                        f.value,
                        { locale, currency },
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : editableFields.length > 0 ? (
            <div className="flex flex-col gap-4 py-2">
              {editableFields.map((field) =>
                // WPS-130: a composite widget, not a form control - the action
                // needs to say WHICH documents are wanted, which no standard
                // input expresses. Driven by config (``widget``), never by
                // action key, so any tenant can opt an action into it.
                field.configuration_data?.widget === "document_request" ? (
                  <DocumentRequestInput
                    key={field.code_name}
                    claimUuid={claimUuid}
                    value={captured[field.code_name]}
                    onChange={(v) =>
                      setCaptured((prev) => ({ ...prev, [field.code_name]: v }))
                    }
                  />
                ) : (
                  <MetadataField
                    key={field.code_name}
                    field={normaliseClaimField(field)}
                    value={captured[field.code_name]}
                    error={requiredErrors[field.code_name]}
                    onChange={(v) => {
                      setCaptured((prev) => ({
                        ...prev,
                        [field.code_name]: v,
                      }));
                      setRequiredErrors((prev) => {
                        if (!(field.code_name in prev)) return prev;
                        const next = { ...prev };
                        delete next[field.code_name];
                        return next;
                      });
                    }}
                    onFileUpload={handleActionFileUpload}
                    uploading={uploadingFields.has(field.code_name)}
                    labelVariant="compact"
                  />
                ),
              )}
            </div>
          ) : (
            // WPS-97: suppressed when the metadata load failed - the confirm
            // button is hidden in that case, so asking the user to confirm
            // leaves a question they have no way to answer.
            !loadError && (
              <p className="py-2 text-[13px] text-[var(--ink-2)]">
                {actionGuidance ||
                  t(
                    "claims.actionDialog.confirmExecute",
                    "Are you sure you want to execute this action?",
                  )}
              </p>
            )
          )}

          <DialogFooter>
            <Button
              onClick={() => onOpenChange(false)}
              variant="outline"
              className="px-4 py-2 text-[12px]"
            >
              Cancel
            </Button>
            {/* WPS-81: while a block is showing, the primary confirm is a dead
              click (it can only 400 again) - the override control in the
              banner is the CTA, so hide it until the block clears.
              WPS-97: same for a failed metadata load - we have no fields to
              submit, so confirming could only fail again. */}
            {!blockError && !loadError && (
              <Button
                onClick={handleSubmit}
                disabled={executeAction.isPending || loading || isUploading}
                variant="default"
                className="bg-[var(--green)] px-4 py-2 text-[12px] text-white disabled:opacity-70"
              >
                {executeAction.isPending
                  ? "Processing..."
                  : isUploading
                    ? "Uploading..."
                    : (action?.label ?? "Confirm")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* WPS-81 override: reuse the platform's generic Exceptions modal in
        preview mode so the override UX matches the rest of the product. The
        approver picks a mandatory reason + note; on confirm we mint a grant
        (exception_required) or resolve the open issues (blocked_by_issue),
        then resubmit. The modal bypasses its own permission gate, so the
        ``canOverride`` check above is the gate. */}
      {canOverride && blockError && (
        <ExceptionModal
          uuid={null}
          open={overrideModalOpen}
          onOpenChange={setOverrideModalOpen}
          preview={{
            context: {
              subject: {
                kind: t("claims.override.subjectKind", "Claim action"),
                title: action?.label ?? "",
                subtitle: action?.key ?? "",
              },
              meta_sections: [],
              trigger: {
                title: action?.label ?? "",
                description:
                  blockError.code === "exception_required"
                    ? blockError.message ||
                      t(
                        "claims.override.exceptionRequired",
                        "An exception is required before this action can continue.",
                      )
                    : t(
                        "claims.override.resolveTrigger",
                        "Resolve the blocking issues to let this claim progress.",
                      ),
                breakdown: (blockError.code === "blocked_by_issue"
                  ? blockError.issues
                  : []
                ).map((issue) => ({
                  label: issue.summary,
                  value: issue.detail || "",
                })),
              },
            },
            reasonOptions: mergedOverrideReasonOptions,
            defaultReasonCode: overrideDefaultReasonCode,
            // Always the override wording. This modal is only ever reached
            // from "Override & continue", so borrowing the "Resolve" label
            // gave the blocked-action dialog two buttons reading the same
            // thing - one of which waives the check with a recorded reason.
            confirmLabel: t("claims.override.overrideCta", "Override & continue"),
            // Nothing upstream captures why, so the waiver's note is mandatory.
            noteRequiredMessage: t(
              "claims.override.noteRequired",
              "Add a note saying why this step is overridden",
            ),
            notePlaceholder: t(
              "claims.override.notePlaceholder",
              "Why is this step being overridden? Saved to the claim's audit trail.",
            ),
            onConfirm: handleOverrideConfirm,
          }}
        />
      )}
    </ResourceLookupContextProvider>
  );
}
// ── Supplier Info Card (holdingless claims) ──────────────

/** For Uninstalled / Job Stop claims that have no holding, display
 *  supplier details from the claim's EAV attribute values instead
 *  of the Homeowner & Property card.  Uses standard FieldRow layout
 *  to match Customer / Product / Warranty cards. */
function SupplierInfoCard({ attrs }: { attrs: Record<string, unknown> }) {
  const { t } = useTranslation();
  // Typed text, or a supplier picked from the directory ({ uuid, name }).
  const supplierName = attributeText(attrs.supplier_name);
  // Handle both single name field (new seed) and split first/last (old seed)
  const contactName =
    (attrs.supplier_contact_name as string) ||
    [attrs.supplier_contact_first_name, attrs.supplier_contact_last_name]
      .filter(Boolean)
      .join(" ") ||
    undefined;
  const email = (attrs.supplier_contact_email ?? attrs.supplier_email) as
    string | undefined;
  const phone = (attrs.supplier_contact_phone ?? attrs.supplier_phone) as
    string | undefined;
  const asmName =
    (attrs.asm_name as string) ||
    [attrs.asm_first_name, attrs.asm_last_name].filter(Boolean).join(" ") ||
    undefined;

  // Nothing to show (e.g. a group claim, where supplier is captured per
  // home, not at the group level) - hide the card rather than render
  // an all-empty shell. Contractor and property no longer count towards this:
  // they have their own cards (WPS-175 #2), so a claim with a contractor but
  // no supplier yet must not resurrect an empty Supplier card.
  const hasAnything = supplierName || contactName || email || phone || asmName;
  if (!hasAnything) return null;

  return (
    <DetailCard
      title={t("claims.card.supplierInformation", "Supplier Information")}
      icon={<Building className="h-3.5 w-3.5" />}
    >
      {/* supplier_name retired from intake (WP1-92); only show the row for
          older claims that still carry it, rather than an empty "-". */}
      {supplierName && (
        <FieldRow
          label={t("claims.supplier", "Supplier")}
          value={supplierName}
        />
      )}
      <FieldRow label="Contact Name" value={contactName} />
      <FieldRow
        label="Email"
        value={email || undefined}
        link={!!email}
        onLinkClick={() => email && (window.location.href = `mailto:${email}`)}
      />
      <FieldRow label="Phone" value={formatPhoneDisplay(phone) || undefined} />
      {asmName && <FieldRow label="Area Sales Manager" value={asmName} />}

      {/* WPS-175 #2: contractor and property used to render here too, which is
          why one card could show a contractor while the Contractor card below
          said there was none. They are their own cards now - see
          ClaimPartnerCard. */}
    </DetailCard>
  );
}

/**
 * Contacts card for holdingless claims (Uninstalled / Job Stop).
 *
 * WPS-164 #20: when the tenant has the `claim_contact` entity registered, the
 * supplier is the claim's PRIMARY contact and the full Claim Contacts card is
 * shown (primary supplier + ASM/contractor secondaries + the managed
 * add/edit/remove list), replacing the old read-only Supplier Information card.
 * Tenants without that entity keep the original SupplierInfoCard, so nothing
 * changes for them. Self-contained (reads its own org context) so it can drop
 * in at every holdingless render site.
 */
function SupplierContactsCard({
  claim,
}: {
  claim: { uuid: string; attribute_values?: Record<string, unknown> | null };
}) {
  const hasClaimContacts = useEntityRegistered("claim_contact");
  const { orgUuid } = useOrg();
  const { can } = usePermissions();
  const attrs = claim.attribute_values ?? {};

  if (!hasClaimContacts) {
    return <SupplierInfoCard attrs={attrs} />;
  }

  // WPS-164 #20: let the RS manage additional contacts (ASM, contractor, ...)
  // via the shared Add/Edit/Remove flow. No inspection contact is passed, so
  // the inspection-contact pencil stays hidden (see showEdit in the card).
  const canManageContacts =
    can("claims.edit.global") ||
    can("claims.edit.acl") ||
    can("claims.manage_contacts");

  // WPS-175 #2: the supplier has its own editable Supplier Information card
  // now (Penny asks for Supplier and Contractor as separate cards), so it is
  // no longer this card's static primary - that would show the same supplier
  // twice on one page, the way the contractor did. `primary` is optional and
  // its block is guarded, so the card renders fine without one: it becomes the
  // ASM plus the managed add/edit/remove list, which is where the OTHER
  // contacts on the claim live.
  const asmName =
    (attrs.asm_name as string) ||
    [attrs.asm_first_name, attrs.asm_last_name].filter(Boolean).join(" ") ||
    undefined;
  // WPS-175 #2 asks for Contractor as its own card, and holdingless claims now
  // get the editable "Contractor contact (this claim)" card (it was gated on
  // `holding_uuid` for no reason - every field on it is a claim attribute).
  // Listing a read-only Contractor secondary here as well would show the same
  // contractor twice on one page, so the dedicated card is the single home.
  const secondary: StaticContactInfo[] = [
    { role: "Area Sales Manager", name: asmName },
  ];

  return (
    <ClaimContactsCard
      claimUuid={claim.uuid}
      orgUuid={orgUuid}
      secondary={secondary}
      editConfig={{
        isPending: false,
        canEdit: canManageContacts,
        // No inspection-contact field on holdingless claims; the pencil is
        // suppressed, so this is never invoked.
        onSave: async () => {},
      }}
    />
  );
}

// ── Consumer (end-user) card ─────────────────────────────
//
// Renders the end-consumer captured on the claim (e.g. the homeowner
// returning a recalled unit), kept deliberately separate from the
// "Customer" card — which holds the registration's owner and, for B2B
// tenants, is the account/dealer. Driven purely by ``end_user_*`` claim
// attributes: the card renders only when at least one is present, so
// tenants that don't capture an end-user never see it. The title is
// themable via the ``claim_consumer_card_label`` Tenant setting.
function consumerInfo(attrs: Record<string, unknown>) {
  const name =
    (attrs.end_user_name as string) ||
    [attrs.end_user_first_name, attrs.end_user_last_name]
      .filter(Boolean)
      .join(" ") ||
    undefined;
  const email = (attrs.end_user_email as string) || undefined;
  const phone = (attrs.end_user_phone as string) || undefined;
  const addressRaw = attrs.end_user_address;
  const address = addressRaw
    ? typeof addressRaw === "string"
      ? addressRaw
      : addressDisplay(addressRaw)
    : undefined;
  return { name, email, phone, address };
}

function ConsumerInfoCard({
  attrs,
  label,
}: {
  attrs: Record<string, unknown>;
  label: string;
}) {
  const { name, email, phone, address } = consumerInfo(attrs);
  if (!name && !email && !phone && !address) return null;

  return (
    <DetailCard title={label} icon={<Users className="h-3.5 w-3.5" />}>
      <FieldRow label="Name" value={name} />
      <FieldRow
        label="Email"
        value={email}
        link={!!email}
        onLinkClick={() => email && (window.location.href = `mailto:${email}`)}
      />
      <FieldRow label="Phone" value={formatPhoneDisplay(phone) || undefined} />
      <FieldRow label="Address" value={address} />
    </DetailCard>
  );
}

// ── Resolution Station Panel ─────────────────────────────

function ClaimResolutionPanel({
  claim,
  history,
  claimDetailItems,
  editConfig,
  currentStateKey,
  currentStateAttributeValues,
  currentStateEditableFields,
  onClaimDetailsSave,
  formatOptions,
  propertyRows,
  warrantyLabel,
  warrantyRows,
  resolvedConsumer,
  onNavigateRegistration,
  onTimelineEntryClick,
  partnerLocked = false,
}: {
  claim: {
    uuid: string;
    consumer_name?: string;
    consumer_email?: string;
    consumer_phone?: string;
    consumer_address?: unknown;
    vehicle_registration?: string;
    vehicle_make?: string;
    vehicle_model?: string;
    host_attributes?: Array<{
      code_name: string;
      display_name: string;
      value: string;
    }>;
    holding_uuid?: string;
    claim_schema_key?: string;
    attribute_values?: Record<string, unknown>;
    // The partner this claim concerns (WPS-163): the claim's own override if
    // set, otherwise the registration's. `partner_is_inherited` says which.
    partner?: import("@/admin/types/organisations").Partnership | null;
    partner_is_inherited?: boolean;
  };
  history: ActionLogEntry[];
  claimDetailItems: CardFieldItem[];
  editConfig:
    { isPending: boolean; canEdit: boolean; requireReason: true } | undefined;
  currentStateKey: string;
  currentStateAttributeValues: Record<string, unknown>;
  currentStateEditableFields: string[] | null;
  onClaimDetailsSave: (
    values: Record<string, unknown>,
    reason?: string,
  ) => Promise<void>;
  formatOptions: { locale: string; currency: string };
  propertyRows: Array<{ key: string; label: string; value: unknown }>;
  warrantyLabel: string;
  warrantyRows: Array<{ key: string; label: string; value: unknown }>;
  resolvedConsumer: {
    name?: string;
    email?: string;
    phone?: string;
    address?: string;
  };
  onNavigateRegistration?: () => void;
  onTimelineEntryClick?: (entry: ActionLogEntry) => void;
  /** The claim's workflow fixes its partner (`partner_locked`). */
  partnerLocked?: boolean;
}) {
  const { t } = useTranslation();
  const { formatDate } = useFormatting();
  const { can } = usePermissions();
  const isActiveRS = isIntakeStage(currentStateAttributeValues);
  // Resolution-stage work (editing claim fields, attaching evidence)
  // requires the RS stage permission on top of the claim being in an
  // RS state. Read access (the cards themselves) stays open to any
  // role with claims.view.global.
  const canActRs = can("claims.rs_actions");
  const rsActionsEnabled = isActiveRS && canActRs;
  // Guidance copy now lives in the per-state EAV attribute values
  // (Phase 2). Fall back to RS_STATE_GUIDANCE for tenants that
  // haven't populated the EAV rows yet.
  const guidanceTitle =
    (currentStateAttributeValues.guidance_title as string | undefined) ??
    RS_STATE_GUIDANCE[currentStateKey]?.title;
  const guidanceDescription =
    (currentStateAttributeValues.guidance_description as string | undefined) ??
    RS_STATE_GUIDANCE[currentStateKey]?.description;
  const guidance = guidanceTitle
    ? { title: guidanceTitle, description: guidanceDescription ?? "" }
    : undefined;

  // Filter fields to RS-relevant ones.
  // Phase 5: per-state editable field list is now computed server-side
  // by evaluating ``FieldVisibilityRule`` rows against the claim (the
  // predicate gates on ``entity:claim.current_state``) and surfaced as
  // ``workflowInstance.current_state_editable_fields`` which arrives
  // here as the ``currentStateEditableFields`` prop. The legacy EAV
  // path (``state.attribute_values.editable_fields``) was removed by
  // claims migration 0021.
  const editableFields = currentStateEditableFields
    ? new Set(currentStateEditableFields)
    : null;

  // Show ALL claim fields for display; mark non-editable ones as readOnly
  const displayItems = claimDetailItems.map((item) => ({
    ...item,
    readOnly: editableFields
      ? !editableFields.has(item.field.code_name)
      : item.readOnly,
  }));

  // Split into two logical groups for 2-column layout, each routed by the
  // schema (`configuration_data.overview_card`).
  const defectItems = displayItems
    .filter((item) => defectCardOrder(item.field) !== null)
    .sort((a, b) => byDefectCardOrder(a.field, b.field));
  const contextItems = displayItems.filter((item) =>
    isClaimContextField(item.field),
  );

  // For intake_review, only those fields are editable; show rest read-only
  const editableForRS = rsActionsEnabled ? editConfig : undefined;

  // WPS-142: the documentation-reminder card is driven by the REAL reminder
  // status (sent count, last/next send, cadence) from the AutoReminder surface -
  // never a hardcoded day timeline. It shows while a document request is open,
  // whatever the waiting stage is called; refetched as the stage changes.
  const { data: docReminderStatus } = useReminderStatus("claim", claim.uuid, {
    refreshOn: currentStateKey,
  });

  return (
    <div className="flex flex-col gap-4">
      {/* Guidance banner */}
      {guidance ? (
        <div className="flex items-start gap-3 rounded-2xl border-[0.5px] border-l-[3px] border-[var(--rule)] border-l-[#e67e22] bg-[var(--bg-light)] px-4 py-3">
          <FileText className="mt-0.5 h-4 w-4 shrink-0 text-[#e67e22]" />
          <div>
            <p className="text-[13px] font-medium text-[var(--ink)]">
              {guidance.title}
            </p>
            <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
              {guidance.description}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3 rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-4 py-3">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--green)]" />
          <div>
            <p className="text-[13px] font-medium text-[var(--ink)]">
              Intake Complete
            </p>
            <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
              This claim has moved past the resolution phase.
            </p>
          </div>
        </div>
      )}

      {/* ── Two-column: Defect Details + Claim Context ── */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <EditableDetailCard
          title={
            currentStateKey === "intake_review"
              ? "Defect Categorisation"
              : "Defect Details"
          }
          icon={<Layers className="h-3.5 w-3.5" />}
          items={
            defectItems.length > 0
              ? defectItems
              : [
                  {
                    field: defineField({
                      code_name: "product_impacted",
                      display_name: "Product Impacted",
                      field_type: "TEXT",
                    }),
                    value: null,
                    readOnly: !isActiveRS,
                  },
                  {
                    field: defineField({
                      code_name: "component_type",
                      display_name: "Component Type",
                      field_type: "TEXT",
                    }),
                    value: null,
                    readOnly: !isActiveRS,
                  },
                  {
                    field: defineField({
                      code_name: "major_category",
                      display_name: "Major Category",
                      field_type: "TEXT",
                    }),
                    value: null,
                    readOnly: !isActiveRS,
                  },
                  {
                    field: defineField({
                      code_name: "minor_category",
                      display_name: "Minor Category",
                      field_type: "TEXT",
                    }),
                    value: null,
                    readOnly: !isActiveRS,
                  },
                ]
          }
          formatOptions={formatOptions}
          editConfig={
            editableForRS
              ? { ...editableForRS, onSave: onClaimDetailsSave }
              : undefined
          }
        />

        {contextItems.length > 0 ? (
          <EditableDetailCard
            testId="claim-context-card"
            title={t("claims.card.claimContext", "Claim Context")}
            icon={<MapPin className="h-3.5 w-3.5" />}
            items={contextItems}
            formatOptions={formatOptions}
            editConfig={
              editableForRS
                ? { ...editableForRS, onSave: onClaimDetailsSave }
                : undefined
            }
          />
        ) : (
          <DetailCard
            title={t("claims.card.claimContext", "Claim Context")}
            icon={<MapPin className="h-3.5 w-3.5" />}
          >
            <div className="px-3 py-3 text-[11px] text-[var(--ink-3)]">
              No context fields for this stage
            </div>
          </DetailCard>
        )}
      </div>

      {/* Documentation-reminder schedule (an open document request) - real
          cadence from the AutoReminder status, not a hardcoded timeline. */}
      {docReminderStatus?.has_active_request &&
        docReminderStatus.configured && (
          <DetailCard
            title={t("claims.card.reminderSchedule", "Documentation reminders")}
            icon={<Clock className="h-3.5 w-3.5" />}
          >
            <FieldRow
              label={t("claims.reminder.remindersSent", "Reminders sent")}
              value={
                <span className="text-[var(--ink)]">
                  {docReminderStatus.sent_count}
                  {docReminderStatus.max_reminders != null &&
                    ` of ${docReminderStatus.max_reminders}`}
                </span>
              }
            />
            <FieldRow
              label={t("claims.reminder.lastSent", "Last reminder")}
              value={
                <span
                  className={
                    docReminderStatus.last_sent_at
                      ? "text-[var(--ink)]"
                      : "text-[var(--ink-3)]"
                  }
                >
                  {docReminderStatus.last_sent_at
                    ? formatDate(docReminderStatus.last_sent_at)
                    : t("claims.reminder.notYetSent", "Not yet sent")}
                </span>
              }
            />
            <FieldRow
              label={t("claims.reminder.nextSend", "Next reminder")}
              value={
                <span
                  className={
                    docReminderStatus.enabled && docReminderStatus.next_send_at
                      ? "text-[var(--ink)]"
                      : "text-[var(--ink-3)]"
                  }
                >
                  {!docReminderStatus.enabled
                    ? t("claims.reminder.cancelled", "Cancelled")
                    : docReminderStatus.is_capped
                      ? t("claims.reminder.complete", "No more reminders")
                      : docReminderStatus.next_send_at
                        ? formatDate(docReminderStatus.next_send_at)
                        : "-"}
                </span>
              }
            />
          </DetailCard>
        )}

      {/* ── Warranty / Supplier (evidence moved to the Evidence tab - WP1-56) ── */}
      <div className="grid grid-cols-1 gap-3">
        {/* Warranty or Supplier card */}
        {claim.holding_uuid ? (
          <DetailCard
            title={warrantyLabel}
            icon={<Shield className="h-3.5 w-3.5" />}
          >
            {warrantyRows.length > 0 ? (
              warrantyRows.map((row) => (
                <FieldRow key={row.key} label={row.label} value={row.value} />
              ))
            ) : (
              <div className="px-3 py-3 text-[11px] text-[var(--ink-3)]">
                No warranty information
              </div>
            )}
          </DetailCard>
        ) : (
          <SupplierContactsCard claim={claim} />
        )}
      </div>

      {/* Contractor this claim concerns. Inherited from the registration
        unless this claim overrides it, and assignable here for the cases that
        genuinely differ - a second contractor on a repair, or a claim with no
        registration to inherit from (WPS-163). */}
      <div className="grid grid-cols-1 gap-3">
        <ClaimPartnerCard
          claimUuid={claim.uuid}
          partner={claim.partner}
          isInherited={claim.partner_is_inherited}
          canEdit={!!editConfig?.canEdit}
          partnerLocked={partnerLocked}
        />
      </div>

      {/* ── Two-column: Homeowner & Property + Notes ── */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {claim.holding_uuid && (
          <DetailCard
            title={t("claims.card.homeownerProperty", "Homeowner & Property")}
            icon={<Home className="h-3.5 w-3.5" />}
          >
            <FieldRow label="Name" value={resolvedConsumer.name} />
            <FieldRow
              label="Email"
              value={resolvedConsumer.email}
              link={!!resolvedConsumer.email}
              onLinkClick={() =>
                resolvedConsumer.email &&
                (window.location.href = `mailto:${resolvedConsumer.email}`)
              }
            />
            <FieldRow
              label="Phone"
              value={formatPhoneDisplay(resolvedConsumer.phone) || undefined}
            />
            {propertyRows.length > 0 ? (
              propertyRows.map((row) => (
                <FieldRow key={row.key} label={row.label} value={row.value} />
              ))
            ) : (
              <>
                {resolvedConsumer.address && (
                  <FieldRow label="Address" value={resolvedConsumer.address} />
                )}
                {(claim.host_attributes ?? [])
                  .filter(
                    (a) =>
                      ![
                        "make",
                        "model",
                        "colour",
                        "vin",
                        "registration_number",
                      ].includes(a.code_name),
                  )
                  .map((a) => (
                    <FieldRow
                      key={a.code_name}
                      label={a.display_name}
                      value={a.value}
                    />
                  ))}
              </>
            )}
            {onNavigateRegistration && (
              <FieldRow
                label="Registration"
                value="View registration"
                link
                onLinkClick={onNavigateRegistration}
              />
            )}
          </DetailCard>
        )}

        <NotesPanel
          recordId={claim.uuid}
          recordType="claim"
          canAddInternal
          canAddCustomerVisible
          allowAttachments
        />

        {/* Activity timeline - a card in the same grid as the others, with a
            scrollable body so long histories scroll within the card. */}
        <DetailCard
          title={t("claims.card.activity", "Activity")}
          icon={<Clock className="h-3.5 w-3.5" />}
          collapsible={false}
        >
          {history.length > 0 ? (
            <div className="max-h-[420px] overflow-y-auto px-4 py-4">
              <div className="pl-5">
                {history.map((entry, index) => (
                  <TimelineEntry
                    key={entry.uuid}
                    entry={entry}
                    showConnector={index < history.length - 1}
                    onClick={() => onTimelineEntryClick?.(entry)}
                  />
                ))}
              </div>
            </div>
          ) : (
            <p className="px-4 py-3 text-[13px] text-[var(--ink-3)]">
              No activity yet
            </p>
          )}
        </DetailCard>
      </div>
    </div>
  );
}

// ── Lab Review Requirements (collapsible sections) ──────

// ── Lab Station Panel ────────────────────────────────────

function ClaimLabPanel({
  claim,
  claimDetailItems,
  classificationItems,
  labFindingItems,
  editConfig,
  currentStateKey,
  onClaimDetailsSave,
  evidence,
  formatOptions,
  currentStateAttributeValues,
  plantCodeOptions,
  sampleItems,
}: {
  claim: {
    uuid: string;
    consumer_name?: string;
    consumer_email?: string;
    consumer_phone?: string;
    product_category_name?: string;
  };
  claimDetailItems: CardFieldItem[];
  /**
   * WPS-165: schema-derived Defect Details card rows (from
   * `configuration_data.defect_card`), so fields like Minor Defect Category
   * and Plant Code render and stay editable even before they hold a value.
   */
  classificationItems: CardFieldItem[];
  /** WPS-151: schema-derived, so inputs are typed before any value exists. */
  labFindingItems: CardFieldItem[];
  editConfig:
    { isPending: boolean; canEdit: boolean; requireReason: true } | undefined;
  currentStateKey: string;
  currentStateAttributeValues: Record<string, unknown>;
  onClaimDetailsSave: (
    values: Record<string, unknown>,
    reason?: string,
  ) => Promise<void>;
  evidence: Array<{
    rowKey: string;
    fieldKey: string;
    uuid: string;
    file_url: string | null;
    file_name: string;
    mime_type?: string;
  }>;
  formatOptions: { locale: string; currency: string };
  /**
   * WPS-151 retest (Penny) C13: curated plant dropdown options for the Add
   * Sample popup, sourced from the claim schema's ``plant_code`` SELECT so the
   * capture surface offers the same list as the card. Undefined for a schema
   * with no plant options (the popup falls back to a free-text Plant Code).
   */
  plantCodeOptions?: { value: string; label: string }[];
  /**
   * Schema-derived Sample Tracking rows, so Plant Code is its dropdown even
   * before any sample field holds a value.
   */
  sampleItems: CardFieldItem[];
}) {
  const { t } = useTranslation();
  const [reviewOpen, setReviewOpen] = useState(false);
  // WPS-165: the Lab-tab "Evidence & Reports" card was removed to stop it
  // duplicating the Evidence tab (the single source of truth for claim files),
  // so the drag/upload/rename/remove plumbing it needed lives there now.
  // Dynamic, per-defect checklist resolved server-side (WP1-59) - replaces the
  // old hardcoded LAB_REVIEW_SECTIONS. Completion is satisfied-driven (evidence
  // uploaded), not a manual tick, so the card auto-reflects what's been received.
  const { data: claimChecklist } = useClaimChecklist(claim.uuid);
  const checklistItems = claimChecklist?.items ?? [];
  // WPS-140 #3: a reviewer marks an evidence category "Approved" once it has
  // received sufficient evidence; the Review Checklist below states it.
  const setCategoryApproval = useSetEvidenceCategoryApproval(claim.uuid);
  // WP1-59: Re-checklist + Request Additional Photos now live in the prominent
  // claim header (single, de-duplicated entry). This panel keeps the dynamic
  // checklist display + the awaiting-documents status below.
  // WP1-59: while a photo/checklist request is outstanding, the claim stays in
  // Lab Analysis but shows it's awaiting the claimant's documents.
  const { data: labRequests = [] } = useLabRequests(claim.uuid);
  const awaitingRequestedDocs = labRequests.some(
    (r) =>
      r.request_type === "ADDITIONAL_PHOTOS" &&
      (r.status === "PENDING" || r.status === "IN_PROGRESS"),
  );
  const checklistSections = [
    {
      key: "docs",
      label: "Documentation",
      icon: FileText,
      items: checklistItems.filter((i) => i.kind === "document"),
    },
    {
      key: "photos",
      label: "Required Photos",
      icon: Camera,
      items: checklistItems.filter((i) => i.kind === "photo"),
    },
  ].filter((s) => s.items.length > 0);
  const isActiveLab = currentStateKey === "lab_analysis";
  const { can } = usePermissions();
  // Stage-action gate: lab work affordances (edit, sample tracking, lab
  // requests, decision matrix, photo verification) require the lab
  // stage permission on top of being in the lab state. Presentation
  // (guidance banner, read-only cards) stays keyed on state alone so
  // any role with claims.view.global can still read the lab data.
  const canActLab = can("claims.lab_actions");
  const labActionsEnabled = isActiveLab && canActLab;
  // Back after the Evidence & Reports card went: the checklist now opens the
  // files behind each row, so the reviewer can see what they are approving.
  const { openFile } = useFileViewer();

  // Fetch structured lab task data
  const { data: labTasks = [] } = useLabTasks(claim.uuid);
  const activeTask =
    labTasks.find(
      (t: LabTask) => t.status !== "CANCELLED" && t.status !== "COMPLETE",
    ) ?? (labTasks[0] as LabTask | undefined);

  // Resolve matrix key from the claim's defect: its major, else its minor when
  // the major has no matrix of its own (e.g. Other > Dragontooth Alignment).
  const defectKeys = ["major_category", "minor_category"]
    .map(
      (codeName) =>
        claimDetailItems.find((item) => item.field.code_name === codeName)
          ?.value,
    )
    .filter((value): value is string => typeof value === "string" && !!value)
    .map((value) => value.toLowerCase().replace(/\s+/g, "_"));
  const { data: allMatrices = [] } = useDecisionMatrices();
  const matrixKey =
    (activeTask?.metadata as Record<string, string>)?.matrix_key ??
    defectKeys
      .map((key) => allMatrices.find((m) => m.defect_type === key)?.key)
      .find(Boolean) ??
    null;
  const isBeforeLab = isIntakeStage(currentStateAttributeValues);

  // `classificationItems` (the Defect Details card) is now schema-driven and
  // passed in as a prop (WPS-165); it is no longer filtered from
  // `claimDetailItems` here.
  // WPS-151 retest (Penny) C12: Square Count is revealed for a Full OR Partial
  // determination and is optional (the lab may record a square count on a
  // full-coverage decision too). That condition is NOT reimplemented here - it
  // is authored on the backend as `visible_when` (Full/Partial, no
  // `required_when`) in the attribute's `configuration_data`, and
  // `GeneratedInput` already evaluates it against the form's LIVE values. So the
  // field stays in the EDIT form (filtering it out would stop it appearing the
  // moment a coverage is picked), and `hideWhenEmptyDisplay` keeps it out of the
  // read-only rows until it actually holds a value - otherwise the card shows a
  // permanent empty "Square Count" row that Penny's mockup does not have.
  // `hideWhenEmptyDisplay` is declared per attribute in `lab_card`
  // (`hide_when_empty`), so the conditional Square Count row - and any other
  // tenant's equivalent - is already handled where the items are built.
  const findingItems = labFindingItems;
  // Which codes belong to the lab is the union of the static sets and whatever
  // the schema put on the lab card, derived from the items rather than named.
  const labFieldCodes = new Set<string>([
    ...LAB_STATIC_FIELDS,
    ...labFindingItems.map((item) => item.field.code_name),
  ]);
  const intakeItems = claimDetailItems.filter(
    (item) => !labFieldCodes.has(item.field.code_name),
  );

  const editableForLab = labActionsEnabled ? editConfig : undefined;
  const totalChecklistItems = checklistItems.length;
  const totalChecked = checklistItems.filter((i) => i.satisfied).length;

  // Lab banner copy now lives in per-state EAV (Phase 2). Fall back to
  // LAB_GUIDANCE constant for tenants that haven't seeded the values.
  const labGuidanceTitle =
    (currentStateAttributeValues.guidance_title as string | undefined) ??
    LAB_GUIDANCE.title;
  const labGuidanceDescription =
    (currentStateAttributeValues.guidance_description as string | undefined) ??
    LAB_GUIDANCE.description;

  return (
    <StageCardGrid storageKey="claim-detail-lab-cards-v1">
      {/* Guidance banner - fixed above the cards. */}
      <div data-reorder-static>
      {isActiveLab ? (
        <div className="flex items-start gap-3 rounded-2xl border-[0.5px] border-l-[3px] border-[var(--rule)] border-l-[var(--green)] bg-[var(--bg-light)] px-4 py-3">
          <FlaskConical className="mt-0.5 h-4 w-4 shrink-0 text-[var(--green)]" />
          <div>
            <p className="text-[13px] font-medium text-[var(--ink)]">
              {labGuidanceTitle}
              {claim.product_category_name && (
                <span className="ml-1.5 text-[11px] font-normal text-[var(--ink-3)]">
                  - {claim.product_category_name}
                </span>
              )}
            </p>
            <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
              {labGuidanceDescription}
            </p>
          </div>
        </div>
      ) : isBeforeLab ? (
        <div className="flex items-start gap-3 rounded-2xl border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-light)] px-4 py-3">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[var(--ink-3)]" />
          <div>
            <p className="text-[13px] font-medium text-[var(--ink-2)]">
              Pending Lab Analysis
            </p>
            <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
              This claim has not yet been routed to the lab.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3 rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-4 py-3">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--green)]" />
          <div>
            <p className="text-[13px] font-medium text-[var(--ink)]">
              Lab Analysis Complete
            </p>
            <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
              This claim has moved past the lab analysis phase.
            </p>
          </div>
        </div>
      )}
      </div>

      {/* WPS-146: every card on the tab moves (StageCardGrid), so the cards
          are listed one by one rather than in fixed two-column blocks. */}
        <EditableDetailCard
          key="defect-details"
          title={t("claims.defectDetails", "Defect Details")}
          icon={<Layers className="h-3.5 w-3.5" />}
          items={classificationItems}
          formatOptions={formatOptions}
          editConfig={
            editableForLab
              ? { ...editableForLab, onSave: onClaimDetailsSave }
              : undefined
          }
        />

        <EditableDetailCard
          key="sample-summary"
          title={t("claims.card.sampleTracking", "Sample Tracking")}
          icon={<TestTube className="h-3.5 w-3.5" />}
          editButtonTestId="sample-tracking-edit-btn"
          items={
            sampleItems.length > 0
              ? sampleItems
              : [
                  {
                    field: defineField({
                      code_name: "sample_received",
                      display_name: "Sample Received",
                      field_type: "BOOLEAN",
                    }),
                    value: null,
                    readOnly: !isActiveLab,
                  },
                  {
                    field: defineField({
                      code_name: "sample_received_date",
                      display_name: "Date Received",
                      field_type: "DATE",
                    }),
                    value: null,
                    readOnly: !isActiveLab,
                  },
                  {
                    field: defineField({
                      code_name: "sample_reviewed_date",
                      display_name: "Date Reviewed",
                      field_type: "DATE",
                    }),
                    value: null,
                    readOnly: !isActiveLab,
                  },
                  {
                    field: defineField({
                      code_name: "plant_code",
                      display_name: "Plant Code",
                      field_type: "TEXT",
                    }),
                    value: null,
                    readOnly: !isActiveLab,
                  },
                  {
                    field: defineField({
                      code_name: "production_code",
                      display_name: "Production Code",
                      field_type: "TEXT",
                    }),
                    value: null,
                    readOnly: !isActiveLab,
                  },
                ]
          }
          formatOptions={formatOptions}
          editConfig={
            editableForLab
              ? { ...editableForLab, onSave: onClaimDetailsSave }
              : undefined
          }
        />

      {/* WP1-59: awaiting-documents status (claim stays in Lab Analysis) */}
      {awaitingRequestedDocs && (
        <div data-reorder-static className="flex items-center gap-2 rounded-2xl border-[0.5px] border-[var(--amber,#d97706)] bg-[var(--bg-light)] px-3.5 py-2.5">
          <Clock className="h-3.5 w-3.5 shrink-0 text-[var(--amber,#d97706)]" />
          <span className="text-[11px] text-[var(--ink-2)]">
            Lab requested additional photos - awaiting documents from the
            claimant.
          </span>
        </div>
      )}

      {/* ── Review Requirements - collapsible checklist ── */}
      <div
        key="review-checklist"
        data-card-wide
        data-card-title={t("claims.checklist.title", "Review Checklist")}
        data-testid="lab-review-checklist"
        className="rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)]"
      >
        {/* WPS-146: the grip sits beside the header button - a press on a
            button never starts a drag. */}
        <div className="flex items-center">
          <CardDragHandle className="ml-3.5" />
          <button
          type="button"
          onClick={() => setReviewOpen((prev) => !prev)}
          className="flex w-full min-w-0 flex-1 cursor-pointer items-center gap-2.5 px-3.5 py-2.5"
        >
          <ClipboardCheck className="h-3.5 w-3.5 text-[var(--ink-3)]" />
          <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-2)]">
            Review Checklist
          </span>
          <span className="ml-auto flex items-center gap-2.5">
            {/* Progress fraction */}
            <span
              className={cn(
                "text-[11px] tabular-nums",
                totalChecked === totalChecklistItems
                  ? "font-medium text-[var(--green)]"
                  : "text-[var(--ink-3)]",
              )}
            >
              {totalChecked}/{totalChecklistItems}
            </span>
            {/* Progress bar */}
            <span className="relative h-[4px] w-16 overflow-hidden rounded-full bg-[var(--bg-subtle)]">
              <span
                className={cn(
                  "absolute inset-y-0 left-0 rounded-full transition-all duration-300",
                  totalChecked === totalChecklistItems
                    ? "bg-[var(--green)]"
                    : "bg-[color-mix(in_srgb,var(--green)_55%,transparent)]",
                )}
                style={{
                  width: `${totalChecklistItems > 0 ? (totalChecked / totalChecklistItems) * 100 : 0}%`,
                }}
              />
            </span>
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 text-[var(--ink-3)] transition-transform duration-200",
                reviewOpen && "rotate-180",
              )}
            />
          </span>
        </button>
        </div>

        {reviewOpen && (
          <div className="border-t-[0.5px] border-[var(--rule)]">
            {checklistSections.length === 0 ? (
              <div className="px-3.5 py-4 text-[11px] text-[var(--ink-3)]">
                No checklist requirements resolved for this claim's defect yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 divide-x-0 md:grid-cols-2 md:divide-x-[0.5px] md:divide-[var(--rule)]">
                {checklistSections.map((section) => {
                  const checked = section.items.filter(
                    (i) => i.satisfied,
                  ).length;
                  const allDone = checked === section.items.length;
                  return (
                    <div key={section.key} className="px-3.5 py-3">
                      {/* Section header with counter */}
                      <div className="mb-2 flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <section.icon
                            className={cn(
                              "h-3 w-3",
                              allDone
                                ? "text-[var(--green)]"
                                : "text-[var(--ink-3)]",
                            )}
                          />
                          <span
                            className={cn(
                              "text-[10px] font-medium uppercase tracking-[0.08em]",
                              allDone
                                ? "text-[var(--green)]"
                                : "text-[var(--ink-3)]",
                            )}
                          >
                            {section.label}
                          </span>
                        </div>
                        <span
                          className={cn(
                            "text-[10px] tabular-nums",
                            allDone
                              ? "text-[var(--green)]"
                              : "text-[var(--ink-3)]",
                          )}
                        >
                          {checked}/{section.items.length}
                        </span>
                      </div>

                      {/* Checklist items - satisfied = evidence uploaded (auto);
                          approved = a reviewer signed the category off (WPS-140 #3). */}
                      <ul className="flex flex-col gap-0.5">
                        {section.items.map((item) => (
                          <li key={item.key}>
                            <div
                              className="flex items-start justify-between gap-2 rounded-[4px] px-1 py-1"
                              title={item.description || undefined}
                            >
                              <span className="flex items-start gap-2">
                                {item.satisfied || item.approved ? (
                                  <CheckCircle2 className="mt-[1px] h-3.5 w-3.5 shrink-0 text-[var(--green)]" />
                                ) : (
                                  <AlertCircle className="mt-[1px] h-3.5 w-3.5 shrink-0 text-[var(--amber,#d97706)]" />
                                )}
                                <span className="text-[11px] leading-[16px] text-[var(--ink-2)]">
                                  {item.label}
                                </span>
                              </span>
                              <span className="flex shrink-0 items-center gap-2">
                                {item.approved ? (
                                  <span className="mt-[1px] rounded-full bg-[var(--green)]/10 px-1.5 py-[1px] text-[9px] font-semibold uppercase tracking-[0.08em] text-[var(--green)]">
                                    {t("claims.checklist.approved", "Approved")}
                                  </span>
                                ) : (
                                  <span
                                    className={cn(
                                      "mt-[1px] text-[9px] font-medium uppercase tracking-[0.08em]",
                                      item.satisfied
                                        ? "text-[var(--green)]"
                                        : "text-[var(--amber,#d97706)]",
                                    )}
                                  >
                                    {item.satisfied
                                      ? t(
                                          "claims.checklist.received",
                                          "Received",
                                        )
                                      : t(
                                          "claims.checklist.missing",
                                          "Missing",
                                        )}
                                  </span>
                                )}
                                {labActionsEnabled && (
                                  <Button
                                    type="button"
                                    data-testid="approve-category"
                                    aria-label={
                                      item.approved
                                        ? t(
                                            "claims.checklist.undoApprovalFor",
                                            "Undo approval for {{label}}",
                                            { label: item.label },
                                          )
                                        : t(
                                            "claims.checklist.approveLabel",
                                            "Approve {{label}}",
                                            { label: item.label },
                                          )
                                    }
                                    variant={
                                      item.approved ? "ghost" : "outline"
                                    }
                                    size="xs"
                                    disabled={setCategoryApproval.isPending}
                                    onClick={() =>
                                      setCategoryApproval.mutate({
                                        category: item.category,
                                        approved: !item.approved,
                                      })
                                    }
                                  >
                                    {item.approved
                                      ? t("claims.checklist.undo", "Undo")
                                      : t(
                                          "claims.checklist.approve",
                                          "Approve",
                                        )}
                                  </Button>
                                )}
                              </span>
                            </div>
                            {/* The evidence behind the row. Approving a
                                category while looking at the words "Structure
                                photos" is a signature on trust; the reviewer
                                should be able to see what they are signing. */}
                            {(item.files ?? []).length > 0 && (
                              <ul className="flex flex-wrap gap-1.5 pb-1 pl-[22px]">
                                {(item.files ?? []).map((file) => (
                                  <li key={file.uuid}>
                                    <Button
                                      variant="ghost"
                                      size="xs"
                                      onClick={() =>
                                        openFile({
                                          file_uuid: file.uuid,
                                          file_url: file.file_url,
                                          file_name: file.file_name,
                                        })
                                      }
                                      className="h-auto max-w-[190px] gap-1 px-1.5 py-0.5 text-[10px] font-light text-[var(--ink-3)] hover:text-[var(--green)]"
                                    >
                                      <Paperclip className="h-2.5 w-2.5 shrink-0" />
                                      <span className="truncate">
                                        {file.file_name}
                                      </span>
                                    </Button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
            {/* Read-only status; the request-documentation action lives in the header. */}
            {checklistSections.length > 0 && (
              <div className="border-t-[0.5px] border-[var(--rule)] px-3.5 py-2.5">
                <span className="text-[10px] text-[var(--ink-3)]">
                  {checklistItems.filter((i) => i.is_required && !i.satisfied)
                    .length > 0
                    ? t(
                        "claims.checklistItemsMissing",
                        '{{count}} item(s) still missing - use "Request additional documentation" at the top of the claim',
                        {
                          count: checklistItems.filter(
                            (i) => i.is_required && !i.satisfied,
                          ).length,
                        },
                      )
                    : t(
                        "claims.checklistAllReceived",
                        "All required items received",
                      )}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Decision Matrix Workspace (Evaluation tasks) ── */}
      {activeTask && activeTask.task_type === "EVALUATION" && (
        <DecisionMatrixWorkspace
          key="decision-matrix"
          data-card-wide
          data-card-title={t("claims.card.decisionMatrix", "Decision Matrix")}
          claimUuid={claim.uuid}
          taskUuid={activeTask.uuid}
          matrixKey={matrixKey}
          readOnly={!labActionsEnabled}
        />
      )}

      {/* ── Test Results (Testing tasks - Component Lab) ── */}
      {activeTask && activeTask.task_type === "TESTING" && (
        <LabTestResultsTable
          key="test-results"
          data-card-wide
          data-card-title={t("claims.card.testResults", "Test Results")}
          claimUuid={claim.uuid}
          taskUuid={activeTask.uuid}
          readOnly={!labActionsEnabled}
        />
      )}

      {/* ── Sample Tracking (structured) ── */}
      {/* Sample tracking is its own workflow (Requested -> Received ->
          Reviewed). A sample can be received / reviewed weeks after the
          claim leaves the lab state (samples are mailed in, up to 30 days),
          so its actions are gated on lab permission (``canActLab``) - NOT on
          the claim currently being in a lab state (``labActionsEnabled``).
          The WorkflowActionMenu is additionally server-gated to the actions
          valid from each sample's current state. */}
      <SampleTrackingCard
        key="samples"
        data-card-wide
        data-card-title={t("claims.card.samples", "Samples")}
        claimUuid={claim.uuid}
        taskUuid={activeTask?.uuid}
        readOnly={!canActLab}
        plantCodeOptions={plantCodeOptions}
      />

      {/* ── Photo Review Panel ── */}
      {evidence.length > 0 &&
        (() => {
          const isActiveRS = isIntakeStage(currentStateAttributeValues);
          // Annotation requires the matching stage permission: lab work in
          // the lab state, RS work in RS states.
          const canAnnotate =
            labActionsEnabled || (isActiveRS && can("claims.rs_actions"));
          return (
            <PhotoReviewPanel
              key="photo-review"
              data-card-wide
              data-card-title={t("claims.card.photoReview", "Photo Review")}
              claimUuid={claim.uuid}
              evidence={evidence.map((e) => ({
                uuid: e.uuid,
                file_url: e.file_url,
                file_name: e.file_name,
              }))}
              taskUuid={activeTask?.uuid}
              readOnly={!canAnnotate}
              userRole={isActiveLab ? "lab" : "rs"}
              canVerifyPhotos={labActionsEnabled}
              photosVerified={activeTask?.photos_verified}
              photosVerifiedAt={activeTask?.photos_verified_at}
              photosVerifiedByName={activeTask?.photos_verified_by_name}
            />
          );
        })()}

      {/* ── Lab Requests (Additional Photos / Inspection) ── */}
      <LabRequestPanel
        key="lab-requests"
        data-card-wide
        data-card-title={t("claims.card.labRequests", "Lab Requests")}
        claimUuid={claim.uuid}
      />

      {/* ── Lab Findings, Intake Summary, Notes ── */}
        <EditableDetailCard
          key="lab-findings"
          title={t("claims.card.labFindings", "Lab Findings")}
          icon={<FlaskConical className="h-3.5 w-3.5" />}
          items={findingItems.map((item) => ({
            ...item,
            readOnly: item.readOnly ?? !isActiveLab,
          }))}
          formatOptions={formatOptions}
          editConfig={
            editableForLab
              ? {
                  ...editableForLab,
                  // WPS-151: the lab records its findings as part of its
                  // routine review, so it is not asked to justify the edit.
                  // Scoped to this card - every other card keeps the prompt.
                  // The PUT still requires a reason for the audit log, so we
                  // supply a fixed one rather than sending an empty string
                  // (which the endpoint rejects) - the entry stays auditable,
                  // the technician just is not prompted.
                  requireReason: false,
                  onSave: (values) =>
                    onClaimDetailsSave(values, LAB_FINDINGS_EDIT_REASON),
                }
              : undefined
          }
        />

        {/* ── Intake context (read-only) ── */}
        {intakeItems.length > 0 && (
          <EditableDetailCard
            key="intake-summary"
            title={t("claims.card.intakeSummary", "Intake Summary")}
            icon={<FileText className="h-3.5 w-3.5" />}
            items={intakeItems}
            formatOptions={formatOptions}
            previewCount={4}
          />
        )}

        {/* Notes */}
        <NotesPanel
          key="notes"
          data-card-title={t("notes.title", "Notes")}
          recordId={claim.uuid}
          recordType="claim"
          canAddInternal
          canAddCustomerVisible
          allowAttachments
        />
    </StageCardGrid>
  );
}

// ── Inspection Station Panel ─────────────────────────────

const INSPECTION_RESULT_COLORS: Record<string, string> = {
  pass: "text-[var(--green)]",
  pass_with_training: "text-[#e67e22]",
  fail_component: "text-[var(--red)]",
  fail_workmanship: "text-[var(--red)]",
};

const INSPECTION_RESULT_BG: Record<string, string> = {
  pass: "bg-[var(--green)]/10 border-[var(--green)]/30 text-[var(--green)]",
  pass_with_training: "bg-[#e67e22]/10 border-[#e67e22]/30 text-[#e67e22]",
  fail_component: "bg-[var(--red)]/10 border-[var(--red)]/30 text-[var(--red)]",
  fail_workmanship:
    "bg-[var(--red)]/10 border-[var(--red)]/30 text-[var(--red)]",
};

const INSPECTION_TYPE_BADGE: Record<
  string,
  { label: string; className: string }
> = {
  PJW: {
    label: "Post-Job Walk",
    className: "bg-[#3498db]/10 border-[#3498db]/30 text-[#3498db]",
  },
  QCI: {
    label: "Quality Claim Inspection",
    className: "bg-[#e67e22]/10 border-[#e67e22]/30 text-[#e67e22]",
  },
  LJI: {
    label: "Large Job Inspection",
    className: "bg-[#8e44ad]/10 border-[#8e44ad]/30 text-[#8e44ad]",
  },
};

const INSPECTION_STATUS_BANNER: Record<
  string,
  {
    icon: typeof ClipboardCheck;
    color: string;
    borderColor: string;
    bgColor: string;
    title: string;
    description: string;
  }
> = {
  requested: {
    icon: ClipboardCheck,
    color: "text-[#e67e22]",
    borderColor: "border-l-[#e67e22]",
    bgColor: "bg-[#e67e22]/5",
    title: "Inspection Requested",
    description:
      "Waiting for scheduling - assign an inspector and set an appointment date.",
  },
  scheduled: {
    icon: Calendar,
    color: "text-[#e67e22]",
    borderColor: "border-l-[#e67e22]",
    bgColor: "bg-[#e67e22]/5",
    title: "Inspection Scheduled",
    description:
      "Inspector assigned and appointment confirmed. Waiting for field inspection.",
  },
  in_progress: {
    icon: Play,
    color: "text-[#3498db]",
    borderColor: "border-l-[#3498db]",
    bgColor: "bg-[#3498db]/5",
    title: "Inspection In Progress",
    description: "Inspector is on-site conducting the assessment.",
  },
  complete: {
    icon: CheckCircle2,
    color: "text-[var(--green)]",
    borderColor: "border-l-[var(--green)]",
    bgColor: "bg-[var(--green)]/5",
    title: "Inspection Complete",
    description: "Field inspection has been completed and results recorded.",
  },
  cancelled: {
    icon: XCircle,
    color: "text-[var(--ink-3)]",
    borderColor: "border-l-[var(--ink-3)]",
    bgColor: "bg-[var(--bg-light)]",
    title: "Inspection Cancelled",
    description: "This inspection was cancelled before completion.",
  },
};

const LIFECYCLE_STEPS = [
  { key: "requested", label: "Requested" },
  { key: "scheduled", label: "Scheduled" },
  { key: "in_progress", label: "In Progress" },
  { key: "complete", label: "Complete" },
];

const REPAIR_TIMELINE_STEPS = [
  { key: "repair_letter_sent", label: "Repair Letter Dispatched", icon: Mail },
  {
    key: "repair_assigned_fts",
    label: "Requirements Assigned to FTS",
    icon: UserCircle,
  },
  {
    key: "repair_submitted",
    label: "Contractor Completes Repair",
    icon: Wrench,
  },
  {
    key: "repair_validated",
    label: "FTS Validates Repair",
    icon: CheckCircle2,
  },
  { key: "repair_passed", label: "Passed Letter Issued", icon: FileText },
];

const ONSITE_CHECKLIST = [
  "Verify contractor credentials",
  "Photo-document all roof facets",
  "Measure shingle exposure & offset",
  "Check flashing & valley metal",
  "Inspect ventilation & ridge cap",
  "Review starter strip & drip edge",
  "Assess nail pattern & depth",
  "Document any punch-list items",
];

const EVIDENCE_SLOTS = [
  {
    key: "risk_assessment",
    label: "Risk Assessment",
    icon: Shield,
    accept:
      "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  {
    key: "inspection_assessment",
    label: "Inspection Assessment",
    icon: ClipboardCheck,
    accept:
      "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  {
    key: "inspection_photos",
    label: "Inspection Photos",
    icon: Camera,
    accept: "image/jpeg,image/png,image/webp",
    multi: true,
  },
  {
    key: "repair_photos",
    label: "Repair Photos",
    icon: Wrench,
    accept: "image/jpeg,image/png,image/webp",
    multi: true,
    repairOnly: true,
  },
  {
    key: "repair_certification",
    label: "Repair Certification",
    icon: FileText,
    accept: "application/pdf",
    repairOnly: true,
  },
];

function buildInspectionFieldItem(
  codeName: string,
  displayName: string,
  fieldType: string,
  value: unknown,
): CardFieldItem {
  const displayValue = isAddressLike(value)
    ? addressDisplay(value) || "-"
    : undefined;
  return {
    field: defineField({
      code_name: codeName,
      display_name: displayName,
      field_type: fieldType,
    }),
    value,
    displayValue,
  };
}

function ClaimInspectionPanel({
  claim,
  editConfig,
  onClaimDetailsSave,
  onEvidenceFilesSelected,
  isEvidenceUploading,
  evidence,
  canEdit,
  isTerminal,
  currentStateAttributeValues,
  formatOptions,
  leadingCards,
  trailingCards,
}: {
  /** WPS-146: the tab's other cards, which share this panel's card grid so
   *  every card on the tab can move. Each needs a stable `key`. */
  leadingCards?: ReactNode;
  trailingCards?: ReactNode;
  claim: { uuid: string; attribute_values: Record<string, unknown> };
  editConfig:
    { isPending: boolean; canEdit: boolean; requireReason: true } | undefined;
  onClaimDetailsSave: (
    values: Record<string, unknown>,
    reason?: string,
  ) => Promise<void>;
  onEvidenceFilesSelected: (files: File[]) => Promise<void>;
  isEvidenceUploading: boolean;
  evidence: Array<{
    rowKey: string;
    fieldKey: string;
    uuid: string;
    file_url: string | null;
    file_name: string;
    mime_type?: string;
  }>;
  canEdit: boolean;
  isTerminal: boolean;
  currentStateAttributeValues: Record<string, unknown>;
  formatOptions: { locale: string; currency: string };
}) {
  const { t } = useTranslation();
  const evidenceRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [punchOpen, setPunchOpen] = useState(false);
  const [repairOpen, setRepairOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const { openFile } = useFileViewer();
  const navigate = useNavigate();

  // Fetch Inspection entity records linked to this claim
  const { data: linkedInspections = [] } = useInspections({
    claim: claim.uuid,
  });

  const attrs = claim.attribute_values;
  const inspectionRequired = attrs.inspection_required;
  const hasInspection =
    inspectionRequired === true || inspectionRequired === "true";
  const inspectionStatus = (attrs.inspection_status as string) || "";
  const inspectionType = (attrs.inspection_type as string) || "";
  const inspectionResult = attrs.inspection_result as string | undefined;
  const repairRequired =
    attrs.repair_required === true || attrs.repair_required === "true";
  const isFailWorkmanship = inspectionResult === "fail_workmanship";
  const isCancelled = inspectionStatus === "cancelled";
  const isComplete = inspectionStatus === "complete" || !!inspectionResult;
  const isEditable = !isCancelled && !isComplete && hasInspection;

  // Editing the claim's inspection fields requires an inspections edit
  // permission on top of the claim-edit gate carried by ``editConfig``
  // (the save is a claim PATCH). This keeps Lab / RS - who hold
  // claims.edit but are inspection view-only - read-only here, while
  // Inspection Lead / Admin (both perms) can edit. The Inspector role
  // performs inspection work in the inspections module, not on this tab.
  const { can } = usePermissions();
  const canEditInspection =
    can("inspections.edit.global") || can("inspections.edit.acl");

  const editableConfig =
    isEditable && canEditInspection && editConfig
      ? { ...editConfig, onSave: onClaimDetailsSave }
      : undefined;

  // Build field items from attributes
  const schedulingItems: CardFieldItem[] = [
    buildInspectionFieldItem(
      "requested_date",
      "Requested Date",
      "DATE",
      attrs.requested_date,
    ),
    buildInspectionFieldItem(
      "scheduled_date",
      "Scheduled Date",
      "DATE",
      attrs.scheduled_date,
    ),
    buildInspectionFieldItem(
      "scheduled_time",
      "Scheduled Time",
      "TEXT",
      attrs.scheduled_time,
    ),
    buildInspectionFieldItem(
      "travel_miles",
      "Travel Miles",
      "NUMBER",
      attrs.travel_miles,
    ),
    buildInspectionFieldItem(
      "plant_distance",
      "Plant Distance (mi)",
      "NUMBER",
      attrs.plant_distance,
    ),
  ].filter((item) => item.value != null && item.value !== "");

  const contactItems: CardFieldItem[] = [
    buildInspectionFieldItem(
      "property_address",
      "Property Address",
      "TEXT",
      attrs.property_address,
    ),
    buildInspectionFieldItem(
      "appointment_contact_name",
      "Appointment Contact",
      "TEXT",
      attrs.appointment_contact_name,
    ),
    buildInspectionFieldItem(
      "appointment_contact_phone",
      "Contact Phone",
      "PHONE",
      attrs.appointment_contact_phone,
    ),
    // WPS-144: the homeowner's phone is the submitter contact's phone - it now
    // lives in the Claim Contacts card, not this inspection Property & Contact
    // panel, so it is intentionally not rendered here (was a duplicate leak).
    buildInspectionFieldItem(
      "roof_pitch",
      "Roof Pitch",
      "TEXT",
      attrs.roof_pitch,
    ),
  ].filter((item) => item.value != null && item.value !== "");

  const resultItems: CardFieldItem[] = [
    buildInspectionFieldItem(
      "inspection_result",
      "Result",
      "TEXT",
      attrs.inspection_result,
    ),
    buildInspectionFieldItem(
      "workmanship_failure_reason",
      "Failure Reasons",
      "TEXT",
      attrs.workmanship_failure_reason,
    ),
    buildInspectionFieldItem(
      "component_failure_detail",
      "Component Detail",
      "TEXT",
      attrs.component_failure_detail,
    ),
    buildInspectionFieldItem(
      "inspection_notes",
      "Inspector Notes",
      "TEXTAREA",
      attrs.inspection_notes,
    ),
    buildInspectionFieldItem(
      "repair_required",
      "Repair Required",
      "BOOLEAN",
      attrs.repair_required,
    ),
    buildInspectionFieldItem(
      "repair_completion_date",
      "Repair Completed",
      "DATE",
      attrs.repair_completion_date,
    ),
    buildInspectionFieldItem(
      "repair_cycle_time_days",
      "Repair Cycle Time",
      "TEXT",
      attrs.repair_cycle_time_days
        ? `${attrs.repair_cycle_time_days} days`
        : undefined,
    ),
  ].filter((item) => item.value != null && item.value !== "");

  const cancelItems: CardFieldItem[] = isCancelled
    ? [
        buildInspectionFieldItem(
          "cancellation_reason",
          "Cancellation Reason",
          "TEXT",
          attrs.cancellation_reason,
        ),
        buildInspectionFieldItem(
          "cancellation_date",
          "Cancellation Date",
          "DATE",
          attrs.cancellation_date,
        ),
        buildInspectionFieldItem(
          "cancelled_by",
          "Cancelled By",
          "TEXT",
          attrs.cancelled_by,
        ),
      ].filter((item) => item.value != null && item.value !== "")
    : [];

  const punchList = (attrs.inspection_punch_list ?? []) as Array<{
    description: string;
    status: string;
    assignee?: string;
    deadline?: string;
  }>;
  const hasOpenPunchItems = punchList.some(
    (item) => item.status === "open" || item.status === "in_progress",
  );
  const settlementBlocked =
    hasOpenPunchItems && isSettlementGatedStage(currentStateAttributeValues);

  const roofPitch = attrs.roof_pitch as string | undefined;
  const pitchNum = roofPitch ? parseInt(roofPitch.split("/")[0], 10) : 0;
  const isSteepPitch = pitchNum > 8;

  const inspectorName = attrs.inspector as string | undefined;
  const inspectorPhone = attrs.inspector_phone as string | undefined;
  const inspectorDesignation = attrs.inspector_designation as
    string | undefined;
  const inspectorInitials = inspectorName
    ? inspectorName
        .split(" ")
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "??";

  const inspectionTrigger = attrs.inspection_trigger as string | undefined;
  const inspectionRequestedBy = attrs.inspection_requested_by as
    string | undefined;

  const checklistRaw = attrs.inspection_checklist_completed as
    string | undefined;
  const completedChecks = checklistRaw
    ? checklistRaw
        .split(",")
        .map(Number)
        .filter((n) => !isNaN(n))
    : [];

  const repairSteps = REPAIR_TIMELINE_STEPS.map((step) => ({
    ...step,
    date: attrs[`${step.key}_date`] as string | undefined,
    done: !!attrs[`${step.key}_date`],
  }));
  const repairDoneCount = repairSteps.filter((s) => s.done).length;

  const bannerKey = isCancelled
    ? "cancelled"
    : isComplete
      ? "complete"
      : inspectionStatus || "requested";
  const banner =
    INSPECTION_STATUS_BANNER[bannerKey] ?? INSPECTION_STATUS_BANNER.requested;
  const BannerIcon = banner.icon;

  const lifecycleIndex = isCancelled
    ? -1
    : LIFECYCLE_STEPS.findIndex(
        (s) => s.key === (isComplete ? "complete" : inspectionStatus),
      );

  const typeBadge = INSPECTION_TYPE_BADGE[inspectionType];

  // ── Empty state (no legacy attribute-based inspection) ──
  // WPS-146: every state of the tab is one card grid, so the request and
  // document cards move whether or not an inspection exists yet.
  const inspectionGrid = (content: ReactNode) => (
    <StageCardGrid storageKey="claim-detail-inspection-cards-v1">
      {leadingCards}
      {content}
      {trailingCards}
    </StageCardGrid>
  );

  if (!hasInspection && linkedInspections.length === 0) {
    return inspectionGrid(
      <div
        key="no-inspection"
        data-card-wide
        data-card-title={t("claims.card.inspection", "Inspection")}
        className="relative flex flex-col items-center justify-center rounded-2xl border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-light)] px-6 py-10">
        <CardDragHandle className="absolute left-4 top-4" />
        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg-subtle)]">
          <ClipboardCheck className="h-5 w-5 text-[var(--ink-3)]" />
        </div>
        <p className="text-[13px] font-medium text-[var(--ink-2)]">
          No Inspection Requested
        </p>
        <p className="mt-1 max-w-[340px] text-center text-[11px] leading-relaxed text-[var(--ink-3)]">
          Inspections are triggered during intake review or lab analysis when
          field verification is needed.
        </p>
      </div>,
    );
  }

  // ── Only linked inspection entities (no legacy attributes) ──
  if (!hasInspection && linkedInspections.length > 0) {
    return inspectionGrid(
      <LinkedInspectionsList
        key="linked-inspections"
        data-card-wide
        data-card-title={t("claims.card.linkedInspections", "Linked Inspections")}
        inspections={linkedInspections}
        navigate={navigate}
      />,
    );
  }

  return inspectionGrid(
    <>
      {/* ── Hero: Status + Stepper + Inspector - always visible ── */}
      <div
        key="inspection-status"
        data-card-wide
        data-card-title={t("claims.card.inspectionStatus", "Inspection Status")}
        className={cn(
          "rounded-2xl border-[0.5px] border-l-[3px] border-[var(--rule)]",
          banner.borderColor,
          banner.bgColor,
        )}
      >
        {/* Top row: status + badges */}
        <div className="flex items-start gap-3 px-4 pt-3 pb-2">
          <CardDragHandle className="mt-0.5" />
          <BannerIcon className={cn("mt-0.5 h-4 w-4 shrink-0", banner.color)} />
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[13px] font-medium text-[var(--ink)]">
                {banner.title}
              </span>
              {typeBadge && (
                <span
                  className={cn(
                    "inline-flex items-center rounded-full border-[0.5px] px-2 py-0.5 text-[10px] font-medium",
                    typeBadge.className,
                  )}
                >
                  {inspectionType}
                </span>
              )}
              {isComplete && inspectionResult && (
                <span
                  className={cn(
                    "inline-flex items-center rounded-full border-[0.5px] px-2 py-0.5 text-[10px] font-medium",
                    INSPECTION_RESULT_BG[inspectionResult] ?? "",
                  )}
                >
                  {humanise(inspectionResult)}
                </span>
              )}
            </div>
            <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
              {isCancelled && attrs.cancellation_reason
                ? `Reason: ${humanise(attrs.cancellation_reason as string)}`
                : banner.description}
            </p>
          </div>
        </div>

        {/* Lifecycle stepper - compact horizontal bar */}
        {!isCancelled && (
          <div className="flex items-center gap-0 border-t-[0.5px] border-[var(--rule)]/50 px-4 py-2.5">
            {LIFECYCLE_STEPS.map((step, i) => {
              const isCurrent = i === lifecycleIndex;
              const isPast = i < lifecycleIndex;
              const isFuture = i > lifecycleIndex;
              return (
                <div key={step.key} className="flex flex-1 items-center">
                  <div className="flex items-center gap-1.5">
                    <div
                      className={cn(
                        "flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-medium",
                        isPast && "bg-[var(--green)] text-white",
                        isCurrent && "bg-[var(--ink)] text-white",
                        isFuture &&
                          "border-[1.5px] border-[var(--rule-strong)] text-[var(--ink-3)]",
                      )}
                    >
                      {isPast ? <Check className="h-2.5 w-2.5" /> : i + 1}
                    </div>
                    <span
                      className={cn(
                        "text-[10px]",
                        isPast && "font-medium text-[var(--green)]",
                        isCurrent && "font-medium text-[var(--ink)]",
                        isFuture && "text-[var(--ink-3)]",
                      )}
                    >
                      {step.label}
                    </span>
                  </div>
                  {i < LIFECYCLE_STEPS.length - 1 && (
                    <div
                      className={cn(
                        "mx-2 h-[1.5px] flex-1",
                        i < lifecycleIndex
                          ? "bg-[var(--green)]"
                          : "bg-[var(--rule)]",
                      )}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Inspector profile - inline in hero */}
        {inspectorName && (
          <div className="flex items-center gap-3 border-t-[0.5px] border-[var(--rule)]/50 px-4 py-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--ink)] text-[11px] font-medium text-white">
              {inspectorInitials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="truncate text-[12px] font-medium text-[var(--ink)]">
                {inspectorName}
              </p>
              {inspectorDesignation && (
                <p className="truncate text-[10px] text-[var(--ink-3)]">
                  {inspectorDesignation}
                </p>
              )}
            </div>
            {inspectorPhone && (
              <span className="flex shrink-0 items-center gap-1 text-[11px] text-[var(--ink-2)]">
                <Phone className="h-3 w-3" />
                {formatPhoneDisplay(inspectorPhone)}
              </span>
            )}
            {(inspectionTrigger || inspectionRequestedBy) && (
              <>
                <span className="h-4 w-[1px] bg-[var(--rule)]" />
                <span className="flex shrink-0 items-center gap-1 text-[10px] text-[var(--ink-3)]">
                  <Target className="h-3 w-3" />
                  {inspectionTrigger || inspectionRequestedBy}
                </span>
              </>
            )}
          </div>
        )}

        {/* Trigger criteria - shown only when no inspector yet */}
        {!inspectorName && (inspectionTrigger || inspectionRequestedBy) && (
          <div className="flex items-center gap-2 border-t-[0.5px] border-[var(--rule)]/50 px-4 py-2.5">
            <Target className="h-3 w-3 text-[var(--ink-3)]" />
            <span className="text-[11px] text-[var(--ink-2)]">
              {inspectionTrigger}
            </span>
            {inspectionRequestedBy && (
              <span className="text-[10px] text-[var(--ink-3)]">
                - {inspectionRequestedBy}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Alerts - only when relevant ── */}
      {isSteepPitch && (
        <div data-reorder-static className="flex items-center gap-2.5 rounded-2xl border-[0.5px] border-l-[3px] border-[var(--rule)] border-l-[var(--red)] bg-[var(--red)]/5 px-3.5 py-2.5">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-[var(--red)]" />
          <p className="text-[11px] text-[var(--ink)]">
            <span className="font-medium">Safety:</span> Roof pitch {roofPitch}{" "}
            exceeds 8/12 - excluded from standard scheduling pool.
          </p>
        </div>
      )}

      {settlementBlocked && (
        <div data-reorder-static className="flex items-center gap-2.5 rounded-2xl border-[0.5px] border-l-[3px] border-[var(--rule)] border-l-[var(--red)] bg-[var(--red)]/5 px-3.5 py-2.5">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-[var(--red)]" />
          <p className="text-[11px] text-[var(--ink)]">
            <span className="font-medium">Settlement blocked:</span>{" "}
            {
              punchList.filter(
                (i) => i.status === "open" || i.status === "in_progress",
              ).length
            }{" "}
            open punch list item(s) must be resolved first.
          </p>
        </div>
      )}

      {/* ── Scheduling + Contact - always visible ── */}
        {(schedulingItems.length > 0 || isEditable) && (
          <EditableDetailCard
            key="scheduling"
            title={t(
              "claims.card.schedulingAssignment",
              "Scheduling & Assignment",
            )}
            icon={<Calendar className="h-3.5 w-3.5" />}
            items={
              schedulingItems.length > 0
                ? schedulingItems
                : [
                    buildInspectionFieldItem(
                      "scheduled_date",
                      "Scheduled Date",
                      "DATE",
                      undefined,
                    ),
                  ]
            }
            formatOptions={formatOptions}
            editConfig={editableConfig}
          />
        )}

        {(contactItems.length > 0 || isEditable) && (
          <EditableDetailCard
            key="property-contact"
            title={t("claims.card.propertyContact", "Property & Contact")}
            icon={<MapPin className="h-3.5 w-3.5" />}
            items={
              contactItems.length > 0
                ? contactItems.map((item) => {
                    if (item.field.code_name === "roof_pitch" && isSteepPitch) {
                      return {
                        ...item,
                        displayValue: (
                          <span className="flex items-center gap-1.5">
                            <span>{String(item.value)}</span>
                            <span className="inline-flex items-center gap-0.5 rounded-[4px] bg-[var(--red)]/10 px-1.5 py-0.5 text-[9px] font-medium text-[var(--red)]">
                              <AlertTriangle className="h-2.5 w-2.5" />
                              Steep
                            </span>
                          </span>
                        ),
                      };
                    }
                    return item;
                  })
                : [
                    buildInspectionFieldItem(
                      "property_address",
                      "Property Address",
                      "TEXT",
                      undefined,
                    ),
                  ]
            }
            formatOptions={formatOptions}
            editConfig={editableConfig}
          />
        )}

      {/* ── Results Card (complete only) - always visible when present ── */}
      {isComplete && resultItems.length > 0 && (
        <EditableDetailCard
          key="inspection-results"
          title={t("claims.card.inspectionResults", "Inspection Results")}
          icon={<ClipboardCheck className="h-3.5 w-3.5" />}
          items={resultItems.map((item) => {
            if (item.field.code_name === "inspection_result" && item.value) {
              return {
                ...item,
                displayValue: (
                  <span
                    className={cn(
                      "font-medium",
                      INSPECTION_RESULT_COLORS[item.value as string] ?? "",
                    )}
                  >
                    {humanise(item.value as string)}
                  </span>
                ),
              };
            }
            if (
              item.field.code_name === "workmanship_failure_reason" &&
              item.value
            ) {
              const reasons = Array.isArray(item.value)
                ? (item.value as string[])
                : String(item.value)
                    .split(",")
                    .map((s) => s.trim());
              return {
                ...item,
                displayValue: (
                  <span className="flex flex-wrap gap-1">
                    {reasons.map((r) => (
                      <span
                        key={r}
                        className="inline-flex items-center rounded-[4px] bg-[var(--bg-subtle)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--ink-2)]"
                      >
                        {humanise(r)}
                      </span>
                    ))}
                  </span>
                ),
              };
            }
            return item;
          })}
          formatOptions={formatOptions}
        />
      )}

      {/* ── Collapsible: On-Site Checklist ── */}
      {!isCancelled && (inspectionStatus === "in_progress" || isComplete) && (
        <div
          key="onsite-checklist"
          data-card-wide
          data-card-title={t("claims.card.onSiteChecklist", "On-Site Checklist")}
          className="rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)]">
          {/* WPS-146: the grip sits beside the header button - a press on a
              button never starts a drag. */}
          <div className="flex items-center">
            <CardDragHandle className="ml-3.5" />
            <button
            type="button"
            onClick={() => setChecklistOpen((p) => !p)}
            className="flex w-full min-w-0 flex-1 cursor-pointer items-center gap-2.5 px-3.5 py-2.5"
          >
            <ListChecks className="h-3.5 w-3.5 text-[var(--ink-3)]" />
            <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-2)]">
              On-Site Checklist
            </span>
            <span className="ml-auto flex items-center gap-2.5">
              <span
                className={cn(
                  "text-[11px] tabular-nums",
                  completedChecks.length === ONSITE_CHECKLIST.length
                    ? "font-medium text-[var(--green)]"
                    : "text-[var(--ink-3)]",
                )}
              >
                {completedChecks.length}/{ONSITE_CHECKLIST.length}
              </span>
              <span className="relative h-[4px] w-16 overflow-hidden rounded-full bg-[var(--bg-subtle)]">
                <span
                  className={cn(
                    "absolute inset-y-0 left-0 rounded-full transition-all duration-300",
                    completedChecks.length === ONSITE_CHECKLIST.length
                      ? "bg-[var(--green)]"
                      : "bg-[#3498db]",
                  )}
                  style={{
                    width: `${ONSITE_CHECKLIST.length > 0 ? (completedChecks.length / ONSITE_CHECKLIST.length) * 100 : 0}%`,
                  }}
                />
              </span>
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 text-[var(--ink-3)] transition-transform duration-200",
                  checklistOpen && "rotate-180",
                )}
              />
            </span>
          </button>
          </div>
          {checklistOpen && (
            <div className="border-t-[0.5px] border-[var(--rule)]">
              <div className="grid grid-cols-1 gap-0 sm:grid-cols-2">
                {ONSITE_CHECKLIST.map((item, i) => {
                  const checked = completedChecks.includes(i);
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-2 px-3.5 py-1.5"
                    >
                      {checked ? (
                        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-[var(--green)]" />
                      ) : (
                        <Circle className="h-3.5 w-3.5 shrink-0 text-[var(--rule-strong)]" />
                      )}
                      <span
                        className={cn(
                          "text-[11px]",
                          checked
                            ? "text-[var(--ink-2)] line-through"
                            : "text-[var(--ink)]",
                        )}
                      >
                        {item}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Collapsible: Repair Follow-Up ── */}
      {isFailWorkmanship && repairRequired && (
        <div
          key="repair-follow-up"
          data-card-wide
          data-card-title={t("claims.card.repairFollowUp", "Repair Follow-Up")}
          className="rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)]">
          {/* WPS-146: the grip sits beside the header button - a press on a
              button never starts a drag. */}
          <div className="flex items-center">
            <CardDragHandle className="ml-3.5" />
            <button
            type="button"
            onClick={() => setRepairOpen((p) => !p)}
            className="flex w-full min-w-0 flex-1 cursor-pointer items-center gap-2.5 px-3.5 py-2.5"
          >
            <Wrench className="h-3.5 w-3.5 text-[var(--ink-3)]" />
            <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-2)]">
              Repair Follow-Up
            </span>
            <span className="ml-auto flex items-center gap-2.5">
              <span
                className={cn(
                  "text-[11px] tabular-nums",
                  repairDoneCount === repairSteps.length
                    ? "font-medium text-[var(--green)]"
                    : "text-[var(--ink-3)]",
                )}
              >
                {repairDoneCount}/{repairSteps.length}
              </span>
              <span className="relative h-[4px] w-16 overflow-hidden rounded-full bg-[var(--bg-subtle)]">
                <span
                  className={cn(
                    "absolute inset-y-0 left-0 rounded-full transition-all duration-300",
                    repairDoneCount === repairSteps.length
                      ? "bg-[var(--green)]"
                      : "bg-[#e67e22]",
                  )}
                  style={{
                    width: `${repairSteps.length > 0 ? (repairDoneCount / repairSteps.length) * 100 : 0}%`,
                  }}
                />
              </span>
              {attrs.repair_cycle_time_days && (
                <span className="flex items-center gap-1 rounded-full border-[0.5px] border-[var(--rule-strong)] px-1.5 py-0.5 text-[9px] text-[var(--ink-3)]">
                  <Clock className="h-2.5 w-2.5" />
                  {String(attrs.repair_cycle_time_days)}d
                </span>
              )}
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 text-[var(--ink-3)] transition-transform duration-200",
                  repairOpen && "rotate-180",
                )}
              />
            </span>
          </button>
          </div>
          {repairOpen && (
            <div className="border-t-[0.5px] border-[var(--rule)]">
              <div className="relative px-3.5 py-2.5">
                <div className="absolute bottom-5 left-[29px] top-5 w-[1.5px] bg-[var(--rule)]" />
                <div className="relative flex flex-col gap-2.5">
                  {repairSteps.map((step, i) => {
                    const StepIcon = step.icon;
                    return (
                      <div key={step.key} className="flex items-center gap-3">
                        <div
                          className={cn(
                            "relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
                            step.done
                              ? "bg-[var(--green)] text-white"
                              : i === repairDoneCount
                                ? "border-[2px] border-[var(--ink)] bg-[var(--bg)] text-[var(--ink)]"
                                : "border-[1.5px] border-[var(--rule-strong)] bg-[var(--bg)] text-[var(--ink-3)]",
                          )}
                        >
                          {step.done ? (
                            <Check className="h-2.5 w-2.5" />
                          ) : (
                            <StepIcon className="h-2.5 w-2.5" />
                          )}
                        </div>
                        <div className="flex flex-1 items-center justify-between">
                          <span
                            className={cn(
                              "text-[11px]",
                              step.done
                                ? "font-medium text-[var(--ink)]"
                                : "text-[var(--ink-3)]",
                            )}
                          >
                            {step.label}
                          </span>
                          {step.date ? (
                            <span className="flex items-center gap-1 text-[10px] text-[var(--ink-2)]">
                              {step.date}
                              <CheckCircle2 className="h-3 w-3 text-[var(--green)]" />
                            </span>
                          ) : (
                            <span className="text-[9px] text-[var(--ink-3)]">
                              Pending
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Collapsible: Evidence & Documentation ── */}
      <div
        key="inspection-evidence"
        data-card-wide
        data-card-title={t("claims.card.evidenceDocumentation", "Evidence & Documentation")}
        className="rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)]">
        {/* WPS-146: the grip sits beside the header button - a press on a
            button never starts a drag. */}
        <div className="flex items-center">
          <CardDragHandle className="ml-3.5" />
          <button
          type="button"
          onClick={() => setEvidenceOpen((p) => !p)}
          className="flex w-full min-w-0 flex-1 cursor-pointer items-center gap-2.5 px-3.5 py-2.5"
        >
          <Camera className="h-3.5 w-3.5 text-[var(--ink-3)]" />
          <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-2)]">
            Evidence & Documentation
          </span>
          <span className="ml-auto flex items-center gap-2.5">
            {evidence.length > 0 ? (
              <span className="rounded-full bg-[var(--green)]/10 px-1.5 py-0.5 text-[10px] font-medium text-[var(--green)]">
                {evidence.length} file{evidence.length !== 1 ? "s" : ""}
              </span>
            ) : (
              <span className="text-[10px] text-[var(--ink-3)]">No files</span>
            )}
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 text-[var(--ink-3)] transition-transform duration-200",
                evidenceOpen && "rotate-180",
              )}
            />
          </span>
        </button>
        </div>
        {evidenceOpen && (
          <div className="border-t-[0.5px] border-[var(--rule)]">
            {EVIDENCE_SLOTS.filter(
              (slot) => !slot.repairOnly || repairRequired,
            ).map((slot) => {
              const SlotIcon = slot.icon;
              const slotFiles = evidence.filter((f) => f.fieldKey === slot.key);
              const uploadEnabled = canEdit && !isTerminal && !isCancelled;
              return (
                <div
                  key={slot.key}
                  className="flex items-center justify-between border-b-[0.5px] border-[var(--rule)] px-3.5 py-2 last:border-b-0"
                >
                  <div className="flex items-center gap-2">
                    <SlotIcon className="h-3 w-3 text-[var(--ink-3)]" />
                    <span className="text-[11px] font-medium text-[var(--ink)]">
                      {slot.label}
                    </span>
                    {slotFiles.length > 0 && (
                      <span className="rounded-full bg-[var(--green)]/10 px-1.5 py-0.5 text-[9px] font-medium text-[var(--green)]">
                        {slotFiles.length}
                      </span>
                    )}
                    {slotFiles.length > 0 && (
                      <span className="flex items-center gap-1">
                        {slotFiles.map((file) => (
                          <button
                            key={file.rowKey}
                            type="button"
                            onClick={() =>
                              openFile({
                                file_uuid: file.uuid,
                                file_url: file.file_url,
                                file_name: file.file_name,
                                mime_type: file.mime_type,
                              })
                            }
                            className="flex cursor-pointer items-center gap-0.5 text-[10px] text-[var(--ink-3)] hover:text-[var(--ink)]"
                          >
                            <Paperclip className="h-2.5 w-2.5" />
                            {file.file_name || file.uuid.slice(0, 8)}
                          </button>
                        ))}
                      </span>
                    )}
                  </div>
                  {uploadEnabled && (
                    <>
                      <input
                        ref={(el) => {
                          evidenceRefs.current[slot.key] = el;
                        }}
                        type="file"
                        multiple={!!slot.multi}
                        accept={slot.accept}
                        className="hidden"
                        onChange={(event) => {
                          const files = Array.from(
                            event.currentTarget.files ?? [],
                          );
                          if (files.length > 0) onEvidenceFilesSelected(files);
                          event.currentTarget.value = "";
                        }}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="xs"
                        onClick={() => evidenceRefs.current[slot.key]?.click()}
                        disabled={isEvidenceUploading}
                        className="inline-flex cursor-pointer items-center gap-1 bg-transparent p-0 text-[10px] font-normal uppercase tracking-[0.08em] text-[var(--ink-2)] transition-opacity duration-150 hover:text-[var(--ink)] disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {isEvidenceUploading ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Upload className="h-3 w-3" />
                        )}
                        Upload
                      </Button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Collapsible: Punch List ── */}
      {(punchList.length > 0 || isEditable) && (
        <div
          key="punch-list"
          data-card-wide
          data-card-title={t("claims.card.punchList", "Punch List")}
          className="rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)]">
          {/* WPS-146: the grip sits beside the header button - a press on a
              button never starts a drag. */}
          <div className="flex items-center">
            <CardDragHandle className="ml-3.5" />
            <button
            type="button"
            onClick={() => setPunchOpen((p) => !p)}
            className="flex w-full min-w-0 flex-1 cursor-pointer items-center gap-2.5 px-3.5 py-2.5"
          >
            <ListChecks className="h-3.5 w-3.5 text-[var(--ink-3)]" />
            <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-2)]">
              Punch List
            </span>
            <span className="ml-auto flex items-center gap-2.5">
              {punchList.length > 0 ? (
                <span className="text-[11px] tabular-nums text-[var(--ink-3)]">
                  {
                    punchList.filter(
                      (i) => i.status === "resolved" || i.status === "verified",
                    ).length
                  }
                  /{punchList.length} resolved
                </span>
              ) : (
                <span className="text-[10px] text-[var(--ink-3)]">Empty</span>
              )}
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 text-[var(--ink-3)] transition-transform duration-200",
                  punchOpen && "rotate-180",
                )}
              />
            </span>
          </button>
          </div>
          {punchOpen && (
            <div className="border-t-[0.5px] border-[var(--rule)]">
              {punchList.length === 0 ? (
                <div className="px-3.5 py-3 text-center text-[11px] text-[var(--ink-3)]">
                  No items yet - added during or after inspection
                </div>
              ) : (
                punchList.map((item, i) => {
                  const isResolved =
                    item.status === "resolved" || item.status === "verified";
                  return (
                    <div
                      key={i}
                      className="flex items-start gap-2.5 border-b-[0.5px] border-[var(--rule)] px-3.5 py-2 last:border-b-0"
                    >
                      {isResolved ? (
                        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--green)]" />
                      ) : (
                        <Circle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--rule-strong)]" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p
                          className={cn(
                            "text-[11px]",
                            isResolved
                              ? "text-[var(--ink-2)] line-through"
                              : "text-[var(--ink)]",
                          )}
                        >
                          {item.description}
                        </p>
                        <div className="mt-0.5 flex items-center gap-2">
                          <StatusBadge
                            status={
                              isResolved
                                ? "active"
                                : item.status === "in_progress"
                                  ? "pending"
                                  : "inactive"
                            }
                          >
                            {humanise(item.status)}
                          </StatusBadge>
                          {item.assignee && (
                            <span className="text-[9px] text-[var(--ink-3)]">
                              {item.assignee}
                            </span>
                          )}
                          {item.deadline && (
                            <span className="text-[9px] text-[var(--ink-3)]">
                              Due {item.deadline}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Collapsible: Scheduling & Contact Details ── */}
      {isComplete &&
        (schedulingItems.length > 0 || contactItems.length > 0) && (
          <div
            key="inspection-details"
            data-card-wide
            data-card-title={t("claims.card.schedulingContactDetails", "Scheduling & Contact Details")}
            className="rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)]">
            {/* WPS-146: the grip sits beside the header button - a press on a
                button never starts a drag. */}
            <div className="flex items-center">
              <CardDragHandle className="ml-3.5" />
              <button
              type="button"
              onClick={() => setDetailsOpen((p) => !p)}
              className="flex w-full min-w-0 flex-1 cursor-pointer items-center gap-2.5 px-3.5 py-2.5"
            >
              <Clock className="h-3.5 w-3.5 text-[var(--ink-3)]" />
              <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-2)]">
                Scheduling & Appointment Details
              </span>
              <ChevronDown
                className={cn(
                  "ml-auto h-3.5 w-3.5 text-[var(--ink-3)] transition-transform duration-200",
                  detailsOpen && "rotate-180",
                )}
              />
            </button>
            </div>
            {detailsOpen && (
              <div className="border-t-[0.5px] border-[var(--rule)]">
                <div className="grid grid-cols-1 gap-0 divide-y-[0.5px] divide-[var(--rule)] md:grid-cols-2 md:divide-x-[0.5px] md:divide-y-0">
                  {schedulingItems.length > 0 && (
                    <div className="px-3.5 py-2">
                      {schedulingItems.map((item) => (
                        <div
                          key={item.field.code_name}
                          className="flex items-center justify-between py-1"
                        >
                          <span className="text-[10px] text-[var(--ink-3)]">
                            {item.field.display_name}
                          </span>
                          <span className="text-[11px] text-[var(--ink)]">
                            {String(item.value ?? "-")}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {contactItems.length > 0 && (
                    <div className="px-3.5 py-2">
                      {contactItems.map((item) => (
                        <div
                          key={item.field.code_name}
                          className="flex items-center justify-between py-1"
                        >
                          <span className="text-[10px] text-[var(--ink-3)]">
                            {item.field.display_name}
                          </span>
                          <span className="text-[11px] text-[var(--ink)]">
                            {String(item.value ?? "-")}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

      {/* ── Cancellation Details ── */}
      {isCancelled && cancelItems.length > 0 && (
        <EditableDetailCard
          key="cancellation"
          title={t("claims.card.cancellationDetails", "Cancellation Details")}
          icon={<XCircle className="h-3.5 w-3.5" />}
          items={cancelItems}
          formatOptions={formatOptions}
        />
      )}

      {/* ── Linked Inspection Entity Records ── */}
      {linkedInspections.length > 0 && (
        <LinkedInspectionsList
          key="linked-inspections"
          data-card-wide
          data-card-title={t("claims.card.linkedInspections", "Linked Inspections")}
          inspections={linkedInspections}
          navigate={navigate}
        />
      )}
    </>,
  );
}

/** Renders a list of Inspection entity records linked to the claim. */
function LinkedInspectionsList({
  inspections,
  navigate,
}: {
  inspections: InspectionRow[];
  navigate: (path: string) => void;
}) {
  const { formatDate } = useFormatting();
  return (
    <div className="rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)]">
      <div className="flex items-center gap-2 border-b-[0.5px] border-[var(--rule)] px-4 py-2.5">
        <CardDragHandle />
        <ClipboardCheck className="h-3.5 w-3.5 text-[var(--ink-3)]" />
        <span className="text-[12px] font-medium text-[var(--ink)]">
          Linked Inspections
        </span>
        <span className="rounded-full bg-[var(--bg-subtle)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--ink-3)]">
          {inspections.length}
        </span>
      </div>
      <div className="divide-y divide-[var(--rule)]/50">
        {inspections.map((insp) => (
          <button
            key={insp.uuid}
            type="button"
            onClick={() => navigate(`/inspections/${insp.uuid}`)}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-[var(--bg-light)]"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[12px] font-medium text-[var(--ink)]">
                  {insp.code || insp.uuid.slice(0, 8)}
                </span>
                <StatusBadge status={insp.lifecycle_status} />
                {insp.sub_type_label && (
                  <span className="rounded-full bg-[var(--bg-subtle)] px-1.5 py-0.5 text-[10px] text-[var(--ink-3)]">
                    {insp.sub_type_label}
                  </span>
                )}
              </div>
              <div className="mt-0.5 flex items-center gap-3 text-[11px] text-[var(--ink-3)]">
                {insp.inspector_name && (
                  <span className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {insp.inspector_name}
                  </span>
                )}
                {insp.scheduled_for && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(insp.scheduled_for)}
                  </span>
                )}
                {insp.property_city && insp.property_state && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {insp.property_city}, {insp.property_state}
                  </span>
                )}
              </div>
            </div>
            <ExternalLink className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Linked Inspection Documents ──────────────────────────
// Aggregates evidence uploaded on every inspection linked to this claim so the
// files captured in the field surface directly on the claim. Read-only: the
// documents are still owned/managed on their inspection record. Rows are
// grouped by inspection with a link back to each one.
function LinkedInspectionDocuments({ claimUuid }: { claimUuid: string }) {
  const { data: evidence = [], isPending } =
    useClaimInspectionEvidence(claimUuid);
  const { openFile } = useFileViewer();
  const navigate = useNavigate();
  const { formatDate } = useFormatting();
  const { t } = useTranslation();

  // Nothing to show yet, or nothing captured: render nothing rather than an
  // empty card (the claim-level "Inspection Documents" card already covers the
  // no-evidence case). Loading is gated first so we never flash an empty state.
  if (isPending || evidence.length === 0) return null;

  // Group evidence rows by their source inspection, preserving server order.
  const groups: Array<{
    inspection_uuid: string;
    inspection_code: string;
    items: typeof evidence;
  }> = [];
  const groupIndex = new Map<string, number>();
  for (const item of evidence) {
    let idx = groupIndex.get(item.inspection_uuid);
    if (idx === undefined) {
      idx = groups.length;
      groupIndex.set(item.inspection_uuid, idx);
      groups.push({
        inspection_uuid: item.inspection_uuid,
        inspection_code: item.inspection_code,
        items: [],
      });
    }
    groups[idx].items.push(item);
  }

  return (
    <div className="rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)]">
      <div className="flex items-center gap-2 border-b-[0.5px] border-[var(--rule)] px-4 py-2.5">
        <CardDragHandle />
        <FileText className="h-3.5 w-3.5 text-[var(--ink-3)]" />
        <span className="text-[12px] font-medium text-[var(--ink)]">
          {t("claims.inspectionEvidence.title", "Inspection Evidence")}
        </span>
        <span className="rounded-full bg-[var(--bg-subtle)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--ink-3)]">
          {evidence.length}
        </span>
      </div>
      <div className="divide-y divide-[var(--rule)]/50">
        {groups.map((group) => (
          <div key={group.inspection_uuid}>
            <button
              type="button"
              onClick={() => navigate(`/inspections/${group.inspection_uuid}`)}
              className="flex w-full items-center gap-2 px-4 py-2 text-left transition-colors hover:bg-[var(--bg-light)]"
            >
              <ClipboardCheck className="h-3 w-3 shrink-0 text-[var(--ink-3)]" />
              <span className="text-[11px] font-medium text-[var(--ink-2)]">
                {group.inspection_code || group.inspection_uuid.slice(0, 8)}
              </span>
              <ExternalLink className="h-3 w-3 shrink-0 text-[var(--ink-3)]" />
            </button>
            <ul>
              {group.items.map((item) => (
                <EvidenceFileRow
                  key={item.uuid}
                  fileName={
                    item.file?.file_name || t("common.document", "Document")
                  }
                  fileUuid={item.file?.uuid}
                  fileUrl={item.file?.file_url}
                  thumbnailUrl={item.file?.thumbnail_url}
                  thumbnail={
                    item.file?.uuid ? (
                      <AuthenticatedImage
                        src={item.file.thumbnail_url ?? item.file.file_url}
                        alt={
                          item.file.file_name ||
                          t("common.document", "Document")
                        }
                        className="h-full w-full object-cover"
                        fallback={null}
                      />
                    ) : undefined
                  }
                  badges={
                    item.kind ? (
                      <Badge variant="outline">{item.kind}</Badge>
                    ) : undefined
                  }
                  meta={
                    item.captured_at ? formatDate(item.captured_at) : undefined
                  }
                  onPreview={
                    item.file?.uuid || item.file?.file_url
                      ? () =>
                          openFile({
                            file_uuid: item.file?.uuid,
                            file_url: item.file?.file_url,
                            file_name:
                              item.file?.file_name ||
                              t("common.document", "Document"),
                          })
                      : undefined
                  }
                />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── LOA Station Panel ────────────────────────────────────

// WPS-175 #14: every state that IS an LOA sign-off stage, in one place.
//
// OC's Installed workflow currently carries TWO of them: the original bespoke
// `loa_pending`, and `loa_leadership_pending` from the generic cascade WPS-165
// authored to replace it. Until one is retired, claims exist in both, so both
// must resolve to the LOA surface - a single hardcoded key sent the other
// stage's claims to a tab that rendered nothing.
//
// Component-scope code should prefer `pending_state_key` off the LOA
// dimensions (see `isAtLoaStage`), which reads the answer from the seeded
// cascade. This set exists for module-scope maps that run before any claim is
// loaded and so cannot consult config. Delete the retired key from here when
// the duplicate stage goes.
// Which states are LOA states, and which of those await an INTERNAL decision,
// are both already answered by the state's own EAV attributes - the same
// mechanism behind `allow_payment_processing` and `allow_document_request`:
//
//   workspace_category === "loa"   -> this step is an LOA review step
//   cycle_class === "internal"     -> it awaits us, not the homeowner
//
// Reading them instead of matching key literals is what lets a second tenant
// name its states anything it likes and still get the LOA surface.
const LOA_WORKSPACE_CATEGORY = "loa";

function isLoaState(attrs: Record<string, unknown>): boolean {
  return attrs?.workspace_category === LOA_WORKSPACE_CATEGORY;
}

function isInternalLoaState(attrs: Record<string, unknown>): boolean {
  return isLoaState(attrs) && attrs?.cycle_class === "internal";
}
function LOAViewToggle({
  viewMode,
  onToggle,
}: {
  viewMode: "card" | "a4";
  onToggle: (mode: "card" | "a4") => void;
}) {
  return (
    <div className="flex items-center gap-0.5 rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-0.5">
      <button
        type="button"
        onClick={() => onToggle("card")}
        className={cn(
          "flex items-center gap-1 rounded-[4px] px-2 py-1 text-[10px] font-medium tracking-[0.04em] transition-colors",
          viewMode === "card"
            ? "bg-[var(--bg-light)] text-[var(--ink)] shadow-sm"
            : "text-[var(--ink-3)] hover:text-[var(--ink-2)]",
        )}
      >
        <CreditCard className="h-3 w-3" />
        Card
      </button>
      <button
        type="button"
        onClick={() => onToggle("a4")}
        className={cn(
          "flex items-center gap-1 rounded-[4px] px-2 py-1 text-[10px] font-medium tracking-[0.04em] transition-colors",
          viewMode === "a4"
            ? "bg-[var(--bg-light)] text-[var(--ink)] shadow-sm"
            : "text-[var(--ink-3)] hover:text-[var(--ink-2)]",
        )}
      >
        <Maximize2 className="h-3 w-3" />
        Full
      </button>
    </div>
  );
}

function ClaimLOAPanel({
  claim,
  currentStateKey,
  stateAttributes,
  availableActions,
  settlement,
  loa,
  warrantyType,
  productName,
  loaDimensions,
  formatOptions,
  semanticCodes,
  fieldMeta,
  suppressDecisionCard = false,
  hasLabWorkspace = true,
  loaViewer = null,
  approvalChanges = null,
  groupLines = false,
}: {
  claim: { uuid: string; attribute_values: Record<string, unknown> };
  currentStateKey: string;
  /** The current state's EAV values - see isLoaState. */
  stateAttributes: Record<string, unknown>;
  /**
   * The claim's live workflow actions. The LOA decision buttons read the
   * executing action's ``capture_fields`` from here so the reviewer's note is
   * posted under the code_names the action actually declares - a fixed key is
   * dropped by ``apply_captured_claim_values``. Keeps the panel tenant-agnostic:
   * it names no attribute, it forwards whatever the action captures.
   */
  availableActions: WorkflowAction[];
  /**
   * Whether this claim's WORKFLOW reaches a lab at all, derived from its
   * states' `workspace_category`. Defaults true so every existing caller keeps
   * today's behaviour.
   */
  hasLabWorkspace?: boolean;
  settlement: SettlementData | undefined;
  loa: LOAData | null;
  warrantyType: string;
  productName: string;
  /**
   * WPS-175 #14: the claim's LOA ladder as configured for this tenant. A
   * dimension only appears once its driving amount has reached the ladder's
   * lowest rung, so "does this claim need leadership approval at all" is
   * answered by the data - the approval limit lives in the seeded ladder, not
   * in this file.
   */
  loaDimensions: LOADimension[];
  formatOptions: { locale: string; currency: string };
  /**
   * WPS-102: which attribute holds the lab's verdict and the approved
   * quantity, resolved from `configuration_data.semantic_role`. The panel asks
   * by role so a tenant whose lab fields are named differently still renders
   * the right values - it names no attribute itself.
   */
  /** Schema field metadata, so Determination can show the option label. */
  fieldMeta?: { code_name: string; configuration_data?: unknown }[];
  semanticCodes: {
    labVerdict: string | null;
    approvedQuantity: string | null;
    approvedQuantityDetail: string | null;
    labNarrative: string | null;
    /**
     * WPS-215 AC4: which attribute holds the major defect category ("Granule
     * Loss", "Shading") that the Claim Context Determination row reports.
     */
    majorCategory: string | null;
  };
  // WPS-124: when the claim runs on the multi-dimension LOA cascade, the
  // LOAApprovalPanel above owns the completed/denied view with per-rung
  // approval records, so the flat "LOA Approval Complete" decision card here
  // is suppressed to avoid a redundant bare "Complete".
  suppressDecisionCard?: boolean;
  /** Whether the viewer can sign the level the claim is waiting on. */
  loaViewer?: LOAViewerState | null;
  /** What moved since the last approval decision, to mark on the lines. */
  approvalChanges?: ApprovalChanges | null;
  /**
   * A grouped claim approved on its lines' sum: the approver
   * sees each line's own settlement in place of the single-claim context.
   */
  groupLines?: boolean;
}) {
  const { t } = useTranslation();
  const { data: groupLineRows = [] } = useClaimChildren(
    groupLines ? claim.uuid : null,
  );
  const [viewMode, setViewMode] = useState<"card" | "a4">("card");
  const [actionRationale, setActionRationale] = useState("");
  const lineChanges = approvalChanges?.lines ?? [];
  // Open on a claim back for approval, so the marked lines are seen, until
  // the user toggles it (the changes can arrive after a cached first paint).
  const [lineItemsToggle, setLineItemsOpen] = useState<boolean | null>(null);
  const lineItemsOpen = lineItemsToggle ?? lineChanges.length > 0;
  const lineChangeByUuid = new Map(
    lineChanges.flatMap((c) => (c.after ? [[c.after.uuid, c] as const] : [])),
  );
  const removedLines = lineChanges.filter((c) => c.change === "removed");

  // WPS-175 #14: the banner used to claim "Pending Leadership Approval" for
  // every claim sitting in the LOA state, whether or not any approval was
  // actually outstanding. The LOA ladder answers that: the backend only emits
  // a dimension once its driving amount has reached that ladder's lowest rung
  // (`loa_panel.build_loa_dimension_states`), so an empty list - or one whose
  // dimensions are all approved - means nothing is waiting on leadership. The
  // approval limit itself stays where it is authored, in the seeded ladder.
  // WPS-175 #14: which state IS the LOA stage comes from the seeded cascade,
  // not from a constant in this file. This used to read
  // `currentStateKey === "loa_pending"`, and when OC's workflow began
  // diverting to a second cascade's pending state instead, every LOA surface
  // keyed off this flag - the banner, the decision panel, the
  // no-approval-required message - silently stopped rendering. Nothing threw;
  // the tab just went quiet. Falling back to the legacy key keeps claims
  // parked in the old state working while both stages coexist.
  const loaPendingStateKeys = new Set(
    loaDimensions
      .map((dimension) => dimension.pending_state_key)
      .filter((key): key is string => !!key),
  );
  // A state is an LOA stage if a cascade waits there OR it is one of the
  // known bespoke LOA states. It has to be the UNION, not a preference for
  // one source: on the Job Stop and Uninstalled workflows a claim parks in
  // `loa_pending` while their only cascade waits in `pending_approval`, so
  // consulting config alone answers "no" and the whole LOA surface - banner,
  // decision panel, message - silently disappears on the claim types that
  // most need it.
  const isAtLoaStage =
    loaPendingStateKeys.has(currentStateKey) ||
    isInternalLoaState(stateAttributes);
  // Deliberately NOT a single "below $X" number. The ladder has more than one
  // independent dimension and their lowest rungs differ by orders of
  // magnitude - claim amount starts at $17,001 but goodwill starts at $1, so
  // any goodwill dollar needs a Team Leader signature. Naming the minimum
  // across all of them would have printed "below the $0 threshold"; naming
  // only the claim-amount rung would tell a reviewer with goodwill applied
  // that no approval was needed when it was. What we can state truthfully is
  // the thing the backend actually decided: no dimension reached its lowest
  // rung. The per-dimension numbers stay where they are authored, in the
  // seeded ladder, and are visible in the tracks below.
  // WPS-175 #14: only the dimensions THIS stage can actually sign off.
  //
  // A workflow carries more than one cascade, and every dimension of every
  // cascade renders on the panel regardless of where the claim is. At the
  // pre-homeowner leadership stage that meant the pre-payment "Total Claim
  // Amount LOA" also read Pending - a second approval track the approver
  // standing there has no action for, and cannot clear. Two things saying
  // Pending when one is actionable is the same class of mismatch as a claim
  // being told approval was required when it was not. Each dimension names
  // the state its own cascade waits in, so match on that; a dimension from a
  // payload predating that field is kept rather than silently dropped.
  const dimensionsForThisStage = loaDimensions.filter(
    (dimension) =>
      !dimension.pending_state_key ||
      dimension.pending_state_key === currentStateKey,
  );
  const hasOutstanding = (dimensions: LOADimension[]) =>
    dimensions.some(
      (dimension) =>
        dimension.loa_status !== "APPROVED" &&
        (dimension.required_tier ?? -1) >= 0,
    );
  // Scoped to the stage that can actually sign them - but an EMPTY scope must
  // not read as "nothing to approve". A claim can park in a state no cascade
  // declares (`loa_pending`, while the cascades wait in `pending_approval` and
  // `loa_leadership_pending`), and then every dimension is filtered out: the
  // banner said "No Leadership Approval Required" and offered Continue on a
  // $61,489 claim with two dimensions still Pending, directly above it.
  // Falling back to the unscoped view keeps the scoping where it applies and
  // tells the truth where it does not.
  const isPendingLeadership =
    isAtLoaStage &&
    (dimensionsForThisStage.length > 0
      ? hasOutstanding(dimensionsForThisStage)
      : hasOutstanding(loaDimensions));

  // WPS-175 #14: who this claim is waiting on, by name. An approver works a
  // queue of these all day; "Pending Leadership Approval" tells them nothing
  // about whether the next move is theirs. The rung the ladder marks current
  // carries the role and the seeded approver name, so say both.
  const loaPendingApprover = (() => {
    for (const dimension of dimensionsForThisStage) {
      if (dimension.loa_status === "APPROVED") continue;
      const tiers = dimension.threshold_tiers ?? [];
      const current =
        tiers.find((tier) => tier.is_current_tier) ??
        tiers[dimension.required_tier ?? -1];
      if (!current) continue;
      const role = String(current.role_label ?? "").trim();
      const name = String(current.approver_name ?? "").trim();
      if (!role && !name) continue;
      return { role, name, label: name ? `${role} - ${name}` : role };
    }
    return null;
  })();

  // The cascade's own action keys, published on the LOA payload. A workflow
  // names these itself - BFS resolves its approval with `approve_payment`, the
  // roofing flows with `loa_approve` - so posting a fixed key failed with
  // "action not found on workflow version" on everything but roofing. Falls
  // back to the historical keys when a payload predates them.
  const loaDecision = (loaDimensions ?? []).find(
    (d) => d.approve_action_key || d.return_action_key,
  );
  const approveActionKey = loaDecision?.approve_action_key || "loa_approve";
  const returnActionKey =
    loaDecision?.return_action_key || "loa_request_changes";
  // Empty when the cascade has no Deny, so the button is not offered.
  const denyActionKey = loaDecision?.deny_action_key ?? "loa_deny";
  const approveMut = useApproveLOA(claim.uuid, approveActionKey);
  const denyMut = useRejectLOA(claim.uuid, denyActionKey);
  const requestChangesMut = useRequestChangesLOA(claim.uuid, returnActionKey);
  const decisionPending =
    approveMut.isPending || denyMut.isPending || requestChangesMut.isPending;
  // The server refuses a decision from anyone the waiting level does not name,
  // and an approval from whoever submitted the claim; mirror that here.
  const canSignLoa = !loaViewer || loaViewer.can_sign;
  const canDecideLoa = !loaViewer || loaViewer.reason !== "not_signer";
  // ...and never offer a decision this viewer may not run at all.
  const loaControls = loaDecisionControls({
    availableKeys: new Set(availableActions.map((a) => a.key)),
    approveKey: approveActionKey,
    returnKey: returnActionKey,
    denyKey: denyActionKey,
    canSign: canSignLoa,
    canDecide: canDecideLoa,
  });

  // Resolve an action's declared capture-field code_names from the live
  // metadata so the reviewer's note is stored under the fields the action
  // actually captures (e.g. ``loa_decision`` / ``deny_reason``) rather than a
  // fixed key the backend would silently drop. Tenant-agnostic: it forwards
  // whatever the seeded action declares, naming no attribute itself.
  const captureCodesFor = useCallback(
    (actionKey: string): string[] =>
      normaliseCaptureEntries(
        availableActions.find((a) => a.key === actionKey)?.capture_fields,
      ).map((e) => e.code_name),
    [availableActions],
  );

  const isActiveLOA = isLoaState(stateAttributes);
  // "Not there yet" - the LOA gate is still ahead, so the panel shows its
  // placeholder rather than a decision card. Three clauses, all config:
  // evidence is still open (the estimate is not final), we are not standing on
  // an LOA state, and no cascade is waiting here.
  //
  // Was a five-key literal list copied off the Installed workflow, so on the
  // other claim types every state it had never heard of - Uninstalled's
  // `rs_disposition`, Workmanship's `rs_review_repair` and
  // `claimant_action_required` - fell through to the past-LOA branch and
  // rendered a decision card for a decision nobody had made yet.
  const isBeforeLOA =
    stateAttributes?.blocks_evidence !== true &&
    !isActiveLOA &&
    !loaPendingStateKeys.has(currentStateKey);
  const attrs = claim.attribute_values;

  // WPS-102: the lab's values, resolved by semantic role rather than named.
  const labVerdict = semanticCodes.labVerdict
    ? attrs[semanticCodes.labVerdict]
    : null;
  const labNarrative = semanticCodes.labNarrative
    ? attrs[semanticCodes.labNarrative]
    : null;
  // "Approved" is the affirmative end of the verdict's own option set; the
  // schema supplies the values, this only decides which colour to paint.
  const isApproved = String(labVerdict ?? "")
    .toLowerCase()
    .startsWith("approve");
  const summary = settlement?.summary;
  const lineItems = settlement?.line_items ?? [];

  /**
   * WPS-215 AC3: Quantity Impacted reads the SETTLEMENT, not the claimant's
   * stated extent - what is being paid for, not what was asked for. The
   * squares derivation lives in the helper so it is unit-testable.
   */
  const quantityImpactedLabel = settlementQuantityImpactedLabel(
    lineItems,
    formatOptions.locale,
  );

  /**
   * WPS-215 AC4: Determination, "based on the claim major or concern" - the
   * major defect category (Granule Loss, Shading), not the lab's Full/Partial
   * coverage verdict.
   *
   * Asked for by semantic role so a tenant whose classification field is named
   * differently still renders, with the platform code name as the fallback for
   * a schema not yet tagged - the same contract as `DEFECT_ROLE_SLOTS`.
   *
   * NOTE: WPS-102 pointed this row at the lab's verdict. WPS-215 AC4 moves it
   * back to the major category by explicit instruction; the lab's verdict
   * still renders on the Lab Analysis section of this same card.
   */
  const majorCategoryValue = semanticCodes.majorCategory
    ? attrs[semanticCodes.majorCategory]
    : attrs.major_category;
  const determinationLabel =
    majorCategoryValue === null ||
    majorCategoryValue === undefined ||
    majorCategoryValue === ""
      ? "-"
      : (optionLabelFor(
          fieldMeta,
          semanticCodes.majorCategory ?? "major_category",
          majorCategoryValue,
        ) ?? humanise(String(majorCategoryValue)));

  const maxWidth = viewMode === "card" ? "max-w-[560px]" : "max-w-[820px]";

  const handleAction = useCallback(
    (action: "approve" | "request_changes" | "deny") => {
      if (!actionRationale.trim() && action !== "approve") {
        toast.error("Please provide a rationale.");
        return;
      }
      const rationale = actionRationale.trim();
      if (action === "approve") {
        approveMut.mutate(
          { rationale, captureFields: captureCodesFor(approveActionKey) },
          {
            onSuccess: () => {
              // WPS-175 #14: "LOA approved" is untrue on a claim that needed
              // no LOA. The same endpoint moves the claim either way, so the
              // confirmation has to say which thing just happened.
              toast.success(
                isPendingLeadership
                  ? t("claimLoa.approvedToast", "LOA approved.")
                  : t("claimLoa.movedToast", "Claim moved to the next stage."),
              );
              setActionRationale("");
            },
            onError: (err) => toastApiError(err, "Failed to approve"),
          },
        );
      } else if (action === "deny") {
        denyMut.mutate(
          { rationale, captureFields: captureCodesFor(denyActionKey) },
          {
            onSuccess: () => {
              toast.success(
                t("claimLoa.deniedToast", "Denied - claim returned to the RS."),
              );
              setActionRationale("");
            },
            onError: (err) => toastApiError(err, "Failed to deny"),
          },
        );
      } else if (action === "request_changes") {
        requestChangesMut.mutate(
          {
            rationale,
            captureFields: captureCodesFor(returnActionKey),
          },
          {
            onSuccess: () => {
              toast.success("Changes requested - claim returned to RS.");
              setActionRationale("");
            },
            onError: (err) => toastApiError(err, "Failed to request changes"),
          },
        );
      }
    },
    // `isPendingLeadership` decides which confirmation the approve branch
    // shows. Omitting it would leave the callback holding the value from the
    // render that created it, so a claim whose approval requirement changed
    // after mount would report the wrong outcome. The two action keys are
    // here for the same reason: they arrive with the LOA payload, so a
    // callback created before it loaded would post the fallback key.
    [
      t,
      actionRationale,
      approveMut,
      denyMut,
      requestChangesMut,
      captureCodesFor,
      isPendingLeadership,
      approveActionKey,
      returnActionKey,
      denyActionKey,
    ],
  );

  // Before-LOA: placeholder
  if (isBeforeLOA) {
    return (
      <div className="rounded-2xl border border-[var(--rule)] bg-[var(--bg)] px-5 pb-5 pt-4 shadow-[var(--shadow-card)]">
        <div className="mb-3 flex items-center justify-between">
          <span className="flex items-center gap-3">
            <CardDragHandle />
            <span className="text-[16px] font-medium text-[var(--ink)]">
            {t("claimLoa.reviewTitle", "LOA review")}
          </span>
          </span>
        </div>
        <div className={cn("mx-auto", maxWidth)}>
          <div className="flex flex-col items-center justify-center rounded-2xl border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-light)] px-6 py-12">
            <Shield className="mb-3 h-8 w-8 text-[var(--ink-3)]" />
            <p className="text-[13px] font-normal text-[var(--ink-2)]">
              {t("claimLoa.notStarted", "LOA review not started")}
            </p>
            <p className="mt-1 max-w-[320px] text-center text-[11px] text-[var(--ink-3)]">
              The claim must progress through resolution and material/labor
              review before LOA approval is triggered.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // HO Approval: dedicated panel with countdown
  if (currentStateKey === "ho_approval") {
    return (
      <div className="rounded-2xl border border-[var(--rule)] bg-[var(--bg)] px-5 pb-5 pt-4 shadow-[var(--shadow-card)]">
        <div className="mb-3 flex items-center justify-between">
          <span className="flex items-center gap-3">
            <CardDragHandle />
            <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink-3)]">
            HOMEOWNER APPROVAL
          </span>
          </span>
        </div>
        <HOApprovalPanel
          settlement={settlement}
          enteredAt={null}
          formatOptions={formatOptions}
        />
      </div>
    );
  }

  // Past-LOA: show decision card. Suppressed on cascade claims, where the
  // LOAApprovalPanel's per-rung records replace the flat "Complete" (WPS-124).
  if (!isActiveLOA && !isBeforeLOA) {
    if (suppressDecisionCard) return null;
    const rawDecision = loa?.decision ?? attrs.loa_decision;
    const decisionStr = rawDecision ? String(rawDecision).toLowerCase() : "";
    const isDenied =
      currentStateKey === "denied" ||
      decisionStr.includes("deny") ||
      decisionStr.includes("denied") ||
      decisionStr.includes("reject") ||
      loa?.loa_status === "REJECTED";
    const isChangesRequested =
      decisionStr.includes("request_changes") ||
      decisionStr.includes("changes");
    const Icon = isDenied ? XCircle : isChangesRequested ? RotateCcw : Check;
    const iconColor = isDenied
      ? "text-[var(--red)]"
      : isChangesRequested
        ? "text-[var(--amber)]"
        : "text-[var(--green)]";
    const title = isDenied
      ? "LOA Denied"
      : isChangesRequested
        ? "LOA Changes Requested"
        : "LOA Approval Complete";
    const fallback = isDenied
      ? "Denied - claim will not proceed to settlement."
      : isChangesRequested
        ? "Changes requested - returned for revision."
        : "Approved - proceeding to settlement.";

    return (
      <div className="rounded-2xl border border-[var(--rule)] bg-[var(--bg)] px-5 pb-5 pt-4 shadow-[var(--shadow-card)]">
        <div className="mb-3 flex items-center justify-between">
          <span className="flex items-center gap-3">
            <CardDragHandle />
            <span className="text-[16px] font-medium text-[var(--ink)]">
            {t("claimLoa.reviewTitle", "LOA review")}
          </span>
          </span>
        </div>
        <div className={cn("mx-auto", maxWidth)}>
          <div className="rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] p-4">
            <div className="flex items-start gap-3">
              <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", iconColor)} />
              <div>
                <p className="text-[13px] font-medium text-[var(--ink)]">
                  {title}
                </p>
                <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
                  {rawDecision
                    ? `Decision: ${humanise(String(rawDecision))}`
                    : fallback}
                </p>
                {(loa?.rationale || attrs.loa_rationale) && (
                  <p className="mt-1 text-[11px] text-[var(--ink-2)]">
                    {String(loa?.rationale || attrs.loa_rationale)}
                  </p>
                )}
                {loa?.decided_by && (
                  <p className="mt-1 text-[10px] text-[var(--ink-3)]">
                    by {loa.decided_by}
                    {loa.decided_at ? ` · ${loa.decided_at}` : ""}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Active LOA: full review card
  const fmtMoney = (val: unknown) => {
    if (val == null) return "-";
    const num = typeof val === "number" ? val : Number(val);
    if (isNaN(num)) return "-";
    return new Intl.NumberFormat(formatOptions.locale, {
      style: "currency",
      currency: formatOptions.currency,
    }).format(num);
  };

  // Fallback: use attribute-based settlement values when settlement object is sparse
  const materialTotal =
    summary?.material_subtotal ?? (Number(attrs.material_total) || 0);
  const laborTotal =
    summary?.labor_subtotal ?? (Number(attrs.labor_total) || 0);
  const otherTotal = summary?.other_subtotal ?? 0;
  const settlementTotal =
    summary?.settlement_total ?? (Number(attrs.settlement_total) || 0);
  const materialProrationPct =
    summary?.material_proration_pct ??
    (attrs.proration_pct != null ? Number(attrs.proration_pct) : null);
  const laborProrationPct =
    summary?.labor_proration_pct ?? materialProrationPct;
  const prorationPct = materialProrationPct;
  // The payable figure: prorated lines plus the lines no formula prorates.
  // ``prorated_total`` alone drops those, so it is only the fallback.
  const payableTotal =
    summary?.payable_total ??
    summary?.prorated_total ??
    (Number(attrs.coverage_amount) || null);

  const hasInspection = !!(attrs.inspection_result || attrs.inspection_notes);

  return (
    <div className="rounded-2xl border border-[var(--rule)] bg-[var(--bg)] px-5 pb-5 pt-4 shadow-[var(--shadow-card)]">
      {/* Header row */}
      <div className="mb-3 flex items-center justify-between">
        <span className="flex items-center gap-3">
          <CardDragHandle />
          <span className="text-[16px] font-medium text-[var(--ink)]">
            {t("claimLoa.reviewTitle", "LOA review")}
          </span>
        </span>
        <LOAViewToggle viewMode={viewMode} onToggle={setViewMode} />
      </div>

      <div className={cn("mx-auto flex flex-col gap-3", maxWidth)}>
        {/* WPS-119: the money comes FIRST. An approver's whole question is
          "do I authorise this amount?", and the amount used to sit below the
          status banner and below the decision buttons - so the screen asked
          for a decision before it showed the thing being decided. Ruby, on
          the BFS approval screen: "The most important information, the
          financial, is at the bottom." Decision-First means the decision's
          SUBJECT leads, then the decision, then the evidence. */}
        {/* 3. Hero total + LOA type badge + stat row */}
        {(summary || settlementTotal > 0) && (
          <div className="overflow-hidden rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)]">
            <div className="px-5 py-5 text-center">
              <p className="font-display text-[28px] font-semibold leading-none tracking-tight text-[var(--ink)]">
                {fmtMoney(settlementTotal)}
              </p>
              <p className="mt-1 text-[11px] text-[var(--ink-3)]">
                {/* WPS-215 AC1: the claim total is named in full here; a
                  LOA dimension driven by a slice labels itself as such. */}
                {t("claimLoa.totalClaimSettlement", "Total Claim Settlement")}
              </p>
              {loa?.loa_type ? (
                <span
                  className={cn(
                    "mt-2 inline-block rounded-full px-2.5 py-0.5 text-[10px] font-medium",
                    loa.loa_type === "WARRANTY"
                      ? "bg-[#dbeafe] text-[#1e40af]"
                      : "bg-[#fef3c7] text-[#92400e]",
                  )}
                >
                  {loa.loa_type === "WARRANTY"
                    ? "Warranty LOA"
                    : "Goodwill LOA"}
                </span>
              ) : (
                <span className="mt-2 inline-block rounded-full bg-[#dbeafe] px-2.5 py-0.5 text-[10px] font-medium text-[#1e40af]">
                  {warrantyType} LOA
                </span>
              )}
            </div>
            <div className="flex border-t-[0.5px] border-[var(--rule)]">
              <div className="flex-1 border-r-[0.5px] border-[var(--rule)] px-4 py-3 text-center">
                <p className="text-[14px] font-semibold text-[var(--ink)]">
                  {fmtMoney(materialTotal)}
                </p>
                <p className="text-[10px] text-[var(--ink-3)]">Material</p>
              </div>
              <div className="flex-1 border-r-[0.5px] border-[var(--rule)] px-4 py-3 text-center">
                <p className="text-[14px] font-semibold text-[var(--ink)]">
                  {fmtMoney(laborTotal)}
                </p>
                <p className="text-[10px] text-[var(--ink-3)]">Labor</p>
              </div>
              <div className="flex-1 px-4 py-3 text-center">
                <p className="text-[14px] font-semibold text-[var(--ink)]">
                  {fmtMoney(otherTotal)}
                </p>
                <p className="text-[10px] text-[var(--ink-3)]">Other</p>
              </div>
            </div>
          </div>
        )}

        {/* The line items ARE the breakdown of the figure above, so they sit
          with it: total, then what makes it up, then who signs it off, then
          the buttons. They used to sit six sections below the decision, which
          asked an approver to authorise a number and only afterwards showed
          them what they had authorised. */}
        {/* 7. Settlement Line Items - collapsible */}
        <div className="rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)]">
          <button
            type="button"
            onClick={() => setLineItemsOpen(!lineItemsOpen)}
            className="flex w-full cursor-pointer items-center justify-between px-5 py-3.5"
          >
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ink-3)]">
                Settlement Line Items
              </span>
              <span className="rounded-full bg-[var(--bg-subtle)] px-1.5 py-px text-[9px] text-[var(--ink-3)]">
                {lineItems.length > 0
                  ? `${lineItems.length} item${lineItems.length !== 1 ? "s" : ""}`
                  : `${[materialTotal > 0, laborTotal > 0, otherTotal > 0].filter(Boolean).length} categories`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-semibold text-[var(--ink)]">
                {fmtMoney(settlementTotal)}
              </span>
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 text-[var(--ink-3)] transition-transform duration-150",
                  lineItemsOpen && "rotate-180",
                )}
              />
            </div>
          </button>
          {lineItemsOpen && (
            <div className="border-t-[0.5px] border-[var(--rule)] px-5 pb-4 pt-3">
              {lineItems.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-[11px]">
                    <thead>
                      <tr className="border-b-[0.5px] border-[var(--rule)] text-[9px] font-semibold uppercase tracking-[0.06em] text-[var(--ink-3)]">
                        <th className="pb-2 pr-3" />
                        <th className="pb-2 pr-3">Description</th>
                        <th className="pb-2 pr-3 text-right">Qty</th>
                        <th className="pb-2 pr-3 text-right">Rate</th>
                        <th className="pb-2 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((li) => {
                        const change = lineChangeByUuid.get(li.uuid);
                        // A changed field's value at the last decision.
                        const was = (field: string) =>
                          change?.change === "changed" &&
                          change.fields?.includes(field)
                            ? change.before?.[field]
                            : null;
                        return (
                          <tr
                            key={li.uuid}
                            data-change={change?.change}
                            className={cn(
                              "border-b-[0.5px] border-[var(--rule)]",
                              change?.change === "added" &&
                                "bg-[var(--status-ok-bg)]",
                              change?.change === "changed" &&
                                "bg-[var(--status-warn-bg)]",
                            )}
                          >
                            <td className="py-2 pr-3">
                              <span
                                className={cn(
                                  "inline-block rounded-[3px] px-[5px] py-px text-[8px] font-semibold uppercase tracking-[0.04em]",
                                  li.category === "MATERIAL"
                                    ? "bg-[#dbeafe] text-[#1e40af]"
                                    : li.category === "LABOR"
                                      ? "bg-[#fef3c7] text-[#92400e]"
                                      : "bg-[#f3e8ff] text-[#6b21a8]",
                                )}
                              >
                                {/* "Lbr", not "Lab". Rendered uppercase, "Lab"
                                  is the laboratory everywhere else in this
                                  product - a Lab tab, a Lab queue, a Lab role
                                  badge - so a Labor line on a claim type that
                                  never sees a lab read as lab involvement.
                                  Patrick's BFS spec is explicit that there is
                                  none. "Lbr" is the conventional short form and
                                  collides with nothing. */}
                                {li.category === "MATERIAL"
                                  ? "Mat"
                                  : li.category === "LABOR"
                                    ? "Lbr"
                                    : "Oth"}
                              </span>
                            </td>
                            <td className="max-w-[200px] py-2 pr-3 text-[var(--ink-2)]">
                              {change && (
                                <span className="mr-1.5">
                                  <ApprovalChangeBadge change={change.change} />
                                </span>
                              )}
                              {was("description") != null && (
                                <WasValue>{was("description")}</WasValue>
                              )}
                              {li.description}
                            </td>
                            <td className="py-2 pr-3 text-right tabular-nums text-[var(--ink-2)]">
                              {was("quantity") != null && (
                                <WasValue>{was("quantity")}</WasValue>
                              )}
                              {li.quantity}
                            </td>
                            <td className="py-2 pr-3 text-right tabular-nums text-[var(--ink-2)]">
                              {was("unit_price") != null && (
                                <WasValue>{fmtMoney(was("unit_price"))}</WasValue>
                              )}
                              {fmtMoney(li.unit_price)}
                            </td>
                            <td className="py-2 text-right tabular-nums text-[var(--ink-2)]">
                              {was("extended_price") != null && (
                                <WasValue>
                                  {fmtMoney(was("extended_price"))}
                                </WasValue>
                              )}
                              {fmtMoney(li.extended_price)}
                            </td>
                          </tr>
                        );
                      })}
                      {/* Lines the last decision saw that are gone now. */}
                      {removedLines.map((c) => (
                        <tr
                          key={c.before?.uuid}
                          data-change="removed"
                          className="border-b-[0.5px] border-[var(--rule)] text-[var(--ink-3)]"
                        >
                          <td className="py-2 pr-3" />
                          <td className="max-w-[200px] py-2 pr-3">
                            <span className="mr-1.5">
                              <ApprovalChangeBadge change="removed" />
                            </span>
                            <span className="line-through">
                              {c.before?.description}
                            </span>
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums line-through">
                            {c.before?.quantity}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums line-through">
                            {fmtMoney(c.before?.unit_price)}
                          </td>
                          <td className="py-2 text-right tabular-nums line-through">
                            {fmtMoney(c.before?.extended_price)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td
                          colSpan={4}
                          className="border-t border-[var(--ink)] pt-2.5 text-[11px] font-semibold text-[var(--ink)]"
                        >
                          {prorationPct != null && prorationPct < 100
                            ? t("claimLoa.subtotal", "Subtotal")
                            : t("claimLoa.total", "Total")}
                        </td>
                        <td className="border-t border-[var(--ink)] pt-2.5 text-right text-[11px] font-semibold tabular-nums text-[var(--ink)]">
                          {fmtMoney(settlementTotal)}
                        </td>
                      </tr>
                      {prorationPct != null &&
                        prorationPct < 100 &&
                        payableTotal != null && (
                          <tr>
                            <td
                              colSpan={4}
                              className="pt-1 text-[11px] font-semibold text-[var(--ink)]"
                            >
                              {t("settlement.payableTotal", "Payable Total")}
                            </td>
                            <td className="pt-1 text-right text-[11px] font-semibold tabular-nums text-[var(--ink)]">
                              {fmtMoney(payableTotal)}
                            </td>
                          </tr>
                        )}
                    </tfoot>
                  </table>
                </div>
              ) : materialTotal > 0 || laborTotal > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-[11px]">
                    <thead>
                      <tr className="border-b-[0.5px] border-[var(--rule)] text-[9px] font-semibold uppercase tracking-[0.06em] text-[var(--ink-3)]">
                        <th className="pb-2 pr-3" />
                        <th className="pb-2 pr-3">Category</th>
                        <th className="pb-2 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {materialTotal > 0 && (
                        <tr className="border-b-[0.5px] border-[var(--rule)]">
                          <td className="py-2 pr-3">
                            <span className="inline-block rounded-[3px] bg-[#dbeafe] px-[5px] py-px text-[8px] font-semibold uppercase tracking-[0.04em] text-[#1e40af]">
                              Mat
                            </span>
                          </td>
                          <td className="py-2 pr-3 text-[var(--ink)]">
                            Material
                          </td>
                          <td className="py-2 text-right tabular-nums text-[var(--ink)]">
                            {fmtMoney(materialTotal)}
                          </td>
                        </tr>
                      )}
                      {laborTotal > 0 && (
                        <tr className="border-b-[0.5px] border-[var(--rule)]">
                          <td className="py-2 pr-3">
                            <span className="inline-block rounded-[3px] bg-[#fef3c7] px-[5px] py-px text-[8px] font-semibold uppercase tracking-[0.04em] text-[#92400e]">
                              Lab
                            </span>
                          </td>
                          <td className="py-2 pr-3 text-[var(--ink)]">Labor</td>
                          <td className="py-2 text-right tabular-nums text-[var(--ink)]">
                            {fmtMoney(laborTotal)}
                          </td>
                        </tr>
                      )}
                      {otherTotal > 0 && (
                        <tr className="border-b-[0.5px] border-[var(--rule)]">
                          <td className="py-2 pr-3">
                            <span className="inline-block rounded-[3px] bg-[#f3e8ff] px-[5px] py-px text-[8px] font-semibold uppercase tracking-[0.04em] text-[#6b21a8]">
                              Oth
                            </span>
                          </td>
                          <td className="py-2 pr-3 text-[var(--ink)]">Other</td>
                          <td className="py-2 text-right tabular-nums text-[var(--ink)]">
                            {fmtMoney(otherTotal)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td
                          colSpan={2}
                          className="border-t border-[var(--ink)] pt-2.5 text-[11px] font-semibold text-[var(--ink)]"
                        >
                          {prorationPct != null && prorationPct < 100
                            ? t("claimLoa.subtotal", "Subtotal")
                            : t("claimLoa.total", "Total")}
                        </td>
                        <td className="border-t border-[var(--ink)] pt-2.5 text-right text-[11px] font-semibold tabular-nums text-[var(--ink)]">
                          {fmtMoney(settlementTotal)}
                        </td>
                      </tr>
                      {prorationPct != null &&
                        prorationPct < 100 &&
                        payableTotal != null && (
                          <tr>
                            <td
                              colSpan={2}
                              className="pt-1 text-[11px] font-semibold text-[var(--ink)]"
                            >
                              {t("settlement.payableTotal", "Payable Total")}
                            </td>
                            <td className="pt-1 text-right text-[11px] font-semibold tabular-nums text-[var(--ink)]">
                              {fmtMoney(payableTotal)}
                            </td>
                          </tr>
                        )}
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="text-[11px] text-[var(--ink-3)]">
                  No line items entered yet. Settlement details will appear here
                  once added.
                </p>
              )}
              {prorationPct !== null && (
                <div className="mt-3 rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3.5 py-2.5">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[9px] font-semibold uppercase tracking-[0.06em] text-[var(--ink-3)]">
                      {t("claimLoa.prorationApplied", "Proration Applied")}
                    </span>
                    <span className="text-[13px] font-semibold text-[var(--ink)]">
                      {prorationPct}%
                    </span>
                  </div>
                  {laborProrationPct != null &&
                    laborProrationPct !== prorationPct && (
                      <div className="mt-0.5 flex items-baseline justify-between">
                        <span className="text-[9px] text-[var(--ink-3)]">
                          {t("claimLoa.laborProration", "Labor proration")}
                        </span>
                        <span className="text-[11px] font-medium text-[var(--ink-2)]">
                          {laborProrationPct}%
                        </span>
                      </div>
                    )}
                  <div className="mt-1.5 h-[5px] w-full overflow-hidden rounded-[3px] bg-[var(--bg-subtle)]">
                    <div
                      className="h-full rounded-[3px] bg-[var(--green)] transition-all duration-500"
                      style={{ width: `${Math.min(100, prorationPct)}%` }}
                    />
                  </div>
                  <div className="mt-1 flex justify-between text-[9px] text-[var(--ink-3)]">
                    <span>Subtotal: {fmtMoney(settlementTotal)}</span>
                    <span>
                      {t("settlement.payableTotal", "Payable Total")}:{" "}
                      {fmtMoney(payableTotal ?? settlementTotal)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 4. Claim Context - always open, and sitting with the money.
          It used to fold, because it carried six reference fields an approver
          needed on the claims that looked wrong and never read on the ones
          that did not. WPS-215 cut it to the three the approver is required to
          see (AC2-AC4), so folding now saves nothing and costs everything: a
          closed accordion means the card does NOT show Product, Quantity
          Impacted and Determination, which is the AC. Three fields on one row
          are cheaper to render than the chevron that would hide them.
          A grouped claim shows its properties instead: the total being
          approved is their sum, so the approver sees what each contributes. */}
        {groupLines ? (
          <div className="rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-5 pb-4 pt-3.5">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ink-3)]">
                {t("claimLoa.groupLines.title", "Properties")}
              </span>
              <span className="text-[11px] text-[var(--ink-3)]">
                {t("claimLoa.groupLines.count", {
                  count: groupLineRows.length,
                  defaultValue_one: "{{count}} property",
                  defaultValue_other: "{{count}} properties",
                })}
              </span>
            </div>
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 border-b border-[var(--rule)] pb-1.5 text-[10px] uppercase tracking-[0.06em] text-[var(--ink-3)]">
              <span>{t("claimLoa.groupLines.property", "Property")}</span>
              <span className="text-right">
                {t("claimLoa.groupLines.settlement", "Settlement")}
              </span>
              <span className="text-right">
                {t("claimLoa.groupLines.pays", "Pays")}
              </span>
            </div>
            {groupLineRows.map((line) => (
              <div
                key={line.uuid}
                className="grid grid-cols-[1fr_auto_auto] gap-x-6 border-b border-[var(--rule)] py-2 text-[12px] last:border-b-0"
              >
                <span className="text-[var(--ink)]">
                  {line.property_address || line.claim_number}
                </span>
                <span className="text-right text-[var(--ink-2)]">
                  {fmtMoney(line.settlement_total ?? 0)}
                </span>
                <span className="text-right font-medium text-[var(--ink)]">
                  {fmtMoney(line.payable_total ?? 0)}
                </span>
              </div>
            ))}
          </div>
        ) : (
        <div className="rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)]">
          <div className="flex w-full items-center justify-between px-5 pt-3.5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ink-3)]">
              Claim Context
            </span>
          </div>
          <div className="px-5 pb-5">
          {/* WPS-215 AC6: Product, Quantity Impacted and Determination, and
            nothing else. Geozone, Install Date and Warranty Class were
            reference an approver never acted on - they widened the card
            without changing a single decision. Three fields sit on one row,
            so the grid tracks the field count rather than wrapping a lone
            cell onto a second row with a rule drawn into empty space. */}
          <div className="grid grid-cols-1 sm:grid-cols-3">
            {[
              {
                // Keyed by a stable id, not the label: the label is a
                // translated string, so keying on it remounts the row on a
                // locale switch, and collides outright in any language where
                // two of these translate the same.
                id: "product",
                label: t("claimLoa.field.product", "Product"),
                value: productName || "-",
              },
              {
                id: "quantity_impacted",
                label: t("claimLoa.field.quantityImpacted", "Quantity Impacted"),
                value: quantityImpactedLabel,
              },
              {
                // Deliberately NOT highlighted. The highlight this row used to
                // carry meant "the lab has ruled" - a status worth colouring.
                // A major defect category is a neutral classification, and
                // painting "Cracking" in the accent colour makes routine
                // triage data read as an alert.
                id: "determination",
                label: t("claimLoa.field.determination", "Determination"),
                value: determinationLabel,
              },
            ].map((item, idx) => (
              <div
                key={item.id}
                className={cn(
                  "border-b border-[var(--rule)] py-2.5 last:border-b-0",
                  "sm:border-b-0 sm:px-3 sm:first:pl-0 sm:last:pr-0",
                  idx < 2 && "sm:border-r sm:border-[var(--rule)]",
                )}
              >
                <p className="text-[10px] text-[var(--ink-3)]">{item.label}</p>
                <p className="mt-0.5 text-[12px] font-medium text-[var(--ink)]">
                  {item.value}
                </p>
              </div>
            ))}
          </div>
          </div>
        </div>
        )}

        {/* 6. Inspection Report - only when there IS one. The comment here
          has always said "conditional, only when inspection data exists", but
          only the BODY was conditional: with no inspection the card still drew
          its frame, its heading and a "No report" pill. That is a card whose
          entire content is the news that it has no content, sitting between an
          approver and the decision. A claim type that never inspects - BFS -
          showed it on every single claim. */}
        {hasInspection && (
        <div className="rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-3.5 w-3.5 text-[var(--ink-3)]" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ink-3)]">
                Inspection Report
              </span>
            </div>
            {attrs.inspection_result ? (
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-medium",
                  String(attrs.inspection_result).startsWith("fail")
                    ? "bg-[#fef2f2] text-[#dc2626]"
                    : String(attrs.inspection_result) === "pass"
                      ? "bg-[#f0fdf4] text-[#059669]"
                      : "bg-[#fffbeb] text-[#d97706]",
                )}
              >
                <span
                  className={cn(
                    "h-1.5 w-1.5 rounded-full",
                    String(attrs.inspection_result).startsWith("fail")
                      ? "bg-[#ef4444]"
                      : String(attrs.inspection_result) === "pass"
                        ? "bg-[#10b981]"
                        : "bg-[#f59e0b]",
                  )}
                />
                {humanise(String(attrs.inspection_result))}
              </span>
            ) : (
              <span className="rounded-full bg-[var(--bg-subtle)] px-2.5 py-0.5 text-[10px] text-[var(--ink-3)]">
                No report
              </span>
            )}
          </div>
          {hasInspection ? (
            <>
              <div className="mt-3 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--rule)]">
                {[
                  { label: "Inspector", value: attrs.inspector },
                  { label: "Type", value: attrs.inspection_type },
                  { label: "Date", value: attrs.scheduled_date },
                  { label: "Roof Pitch", value: attrs.roof_pitch },
                ]
                  .filter((f) => f.value)
                  .map((field) => (
                    <div
                      key={field.label}
                      className="bg-[var(--bg-light)] px-3 py-2"
                    >
                      <p className="text-[9px] uppercase tracking-[0.06em] text-[var(--ink-3)]">
                        {field.label}
                      </p>
                      <p className="mt-0.5 text-[11px] font-medium text-[var(--ink)]">
                        {String(field.value)}
                      </p>
                    </div>
                  ))}
              </div>
              {(attrs.component_failure_detail || attrs.inspection_notes) && (
                <div className="mt-3 rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2.5">
                  {attrs.component_failure_detail && (
                    <p className="text-[11px] leading-relaxed text-[var(--ink-2)]">
                      {String(attrs.component_failure_detail)}
                    </p>
                  )}
                  {attrs.inspection_notes && attrs.component_failure_detail && (
                    <div className="my-2 border-t-[0.5px] border-[var(--rule)]" />
                  )}
                  {attrs.inspection_notes && (
                    <p className="text-[11px] leading-relaxed text-[var(--ink-3)]">
                      {String(attrs.inspection_notes)}
                    </p>
                  )}
                </div>
              )}
              {attrs.workmanship_failure_reason && (
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {String(attrs.workmanship_failure_reason)
                    .split(",")
                    .map((reason) => (
                      <span
                        key={reason}
                        className="rounded-full bg-[#fef2f2] px-2 py-0.5 text-[9px] font-medium text-[#991b1b]"
                      >
                        {humanise(reason.trim())}
                      </span>
                    ))}
                </div>
              )}
            </>
          ) : null}
        </div>
        )}

        {/* 1. Status banner - only when the approval track is NOT shown.
          The track opens with "Awaiting approval from <approver>", so beside
          it this banner is a third statement of the one fact the screen has
          already made twice. It stays for the states the track does not cover
          (no approval required, approved, denied), where it is the only thing
          saying where the claim stands. */}
        {!isPendingLeadership && (
        <div
          className={cn(
            "flex items-start gap-3 rounded-2xl border-[0.5px] border-l-[3px] bg-[var(--bg-light)] px-4 py-3",
            isPendingLeadership
              ? "border-[var(--rule)] border-l-[#c0392b]"
              : "border-[var(--rule)] border-l-[#2980b9]",
          )}
        >
          <Shield
            className={cn(
              "mt-0.5 h-4 w-4 shrink-0",
              isPendingLeadership ? "text-[#c0392b]" : "text-[#2980b9]",
            )}
          />
          <div>
            <p className="text-[13px] font-medium text-[var(--ink)]">
              {isPendingLeadership
                ? t("claimLoa.pendingLeadership", "Pending Leadership Approval")
                : isAtLoaStage
                  ? t(
                      "claimLoa.noApprovalRequired",
                      "No Leadership Approval Required",
                    )
                  : t(
                      "claimLoa.awaitingHomeOffice",
                      "Awaiting Home Office Approval",
                    )}
            </p>
            <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
              {isPendingLeadership
                ? loaPendingApprover
                  ? t("claimLoa.pendingLeadershipHintApprover", {
                      // Says WHO, not where to look. The old copy narrated
                      // the layout ("Decide below - the settlement totals and
                      // approval tracks follow"), which was a card
                      // apologising for its own ordering - and became wrong
                      // the moment the totals moved above it.
                      defaultValue: "Waiting on {{approver}}.",
                      approver: loaPendingApprover.label,
                    })
                  : t(
                      "claimLoa.pendingLeadershipHint",
                      "Approve, request changes, or deny.",
                    )
                : isAtLoaStage
                  ? t(
                      "claimLoa.noApprovalRequiredHint",
                      "LOA approval is not required - this settlement is below every approval threshold, on both claim amount and goodwill. Continue to the next step.",
                    )
                  : t(
                      "claimLoa.awaitingHomeOfficeHint",
                      "Submitted for home office review. Waiting for approval response.",
                    )}
            </p>
            {isAtLoaStage && !isPendingLeadership && (
              <div className="mt-2">
                {/* WPS-175 #14: the message alone left the claim stranded.
                  With no approval outstanding the Decision panel is hidden and
                  the header's LOA buttons are gone, so this is the only way
                  forward - one button, named for where it goes rather than
                  "Approve", because there is nothing to approve. */}
                <Button
                  size="xs"
                  onClick={() => handleAction("approve")}
                  disabled={decisionPending}
                  className="text-[12px]"
                >
                  {decisionPending
                    ? t("claimLoa.continuing", "Continuing...")
                    : t(
                        "claimLoa.continueToHomeowner",
                        "Continue to Homeowner Approval",
                      )}
                </Button>
              </div>
            )}
          </div>
        </div>
        )}

        {/* 2. Decision - FIRST, directly under the banner.
          WPS-175 #14: this panel used to sit last, below the ladder, the lab
          report, the inspection, the line items and the rationale. Approving
          is a full-time job - an approver works a queue of these and should
          not scroll a whole claim to reach the one control that is theirs.
          The evidence stays available below for the cases that need it; the
          default case is "decide", so decide comes first.

          Only rendered when approval is actually outstanding. A claim under
          every threshold used to be offered Approve / Request Changes / Deny
          directly beneath a panel saying no approval was required - three
          statements on one screen disagreeing, and a Lead asked to sign off
          something nobody needs. */}
        {isAtLoaStage && isPendingLeadership && (
          <div className="rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] p-4">
            <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ink-3)]">
              Decision
            </p>
            {loaViewer?.reason === "not_signer" && (
              <p className="mb-3 text-[12px] text-[var(--ink-2)]">
                {t(
                  "claimLoa.viewer.notSigner",
                  "Waiting on the {{role}}. Only people in that role can decide this level.",
                  { role: loaViewer.pending_role_label },
                )}
              </p>
            )}
            {loaViewer?.reason === "submitted" && (
              <p className="mb-3 text-[12px] text-[var(--ink-2)]">
                {t(
                  "claimLoa.viewer.submitted",
                  "You submitted this claim for approval, so another approver has to sign it.",
                )}
              </p>
            )}
            {loaViewer?.role_unfilled && (
              <p className="mb-3 text-[12px] text-[var(--ink-3)]">
                {t(
                  "claimLoa.viewer.roleUnfilled",
                  "Nobody holds the {{role}} role yet, so any approver can sign this level. Add people to it in Settings > Roles.",
                  {
                    role:
                      loaViewer.pending_role || loaViewer.pending_role_label,
                  },
                )}
              </p>
            )}
            {!loaControls.any &&
              loaViewer?.reason !== "not_signer" &&
              loaViewer?.reason !== "submitted" && (
                <p className="mb-3 text-[12px] text-[var(--ink-2)]">
                  {t(
                    "claimLoa.viewer.cannotDecide",
                    "An LOA approver has to decide this level.",
                  )}
                </p>
              )}
            {loaControls.any && (
            <>
            <textarea
              value={actionRationale}
              onChange={(e) => setActionRationale(e.target.value)}
              placeholder={
                loaControls.requestChanges && loaControls.deny
                  ? t(
                      "claimLoa.rationalePlaceholder",
                      "Enter rationale (required for Request Changes and Deny)…",
                    )
                  : loaControls.deny
                    ? t(
                        "claimLoa.rationalePlaceholderDeny",
                        "Enter rationale (required for Deny)…",
                      )
                    : t(
                        "claimLoa.rationalePlaceholderReturn",
                        "Enter rationale (required for Request Changes)…",
                      )
              }
              className="mb-3 w-full resize-none rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2 text-[12px] text-[var(--ink)] placeholder:text-[var(--ink-3)] focus:border-[var(--green)] focus:outline-none"
              rows={3}
            />
            <div className="flex gap-2">
              {loaControls.approve && (
              <Button
                size="sm"
                onClick={() => handleAction("approve")}
                className="bg-[var(--green)] text-white hover:bg-[var(--green)]/90"
                disabled={decisionPending}
              >
                <Check className="mr-1 h-3.5 w-3.5" />
                Approve
              </Button>
              )}
              {loaControls.requestChanges && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAction("request_changes")}
                disabled={decisionPending}
              >
                <MessageSquareDiff className="mr-1 h-3.5 w-3.5" />
                Request Changes
              </Button>
              )}
              {loaControls.deny && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAction("deny")}
                className="border-[var(--status-error)]/30 text-[var(--status-error)] hover:bg-[var(--status-error)]/5"
                disabled={decisionPending}
              >
                <XCircle className="mr-1 h-3.5 w-3.5" />
                Deny
              </Button>
              )}
            </div>
            </>
            )}
          </div>
        )}

        {/* 5. Lab Analysis - only where the workflow HAS a lab. It rendered
          unconditionally as a "mandatory workflow state", true of the roofing
          flows this panel was written for and false of one that skips lab
          entirely. On BFS it told the approver, every single time, that a lab
          report they were never going to receive had not arrived. */}
        {hasLabWorkspace && (
          <div className="rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-5 py-3.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FlaskConical className="h-3.5 w-3.5 text-[var(--ink-3)]" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ink-3)]">
                Lab Analysis
              </span>
            </div>
            {/* WPS-102: the lab's verdict, not intake's Major/Minor severity.
                This read `attrs.determination` - the severity set at Intake
                Review - so the badge said "Pending" on a claim the lab had
                already approved, and its "defect" substring test coloured a
                `major` / `minor` severity RED, reporting a lab failure that
                never happened. `lab_determination` is the Approve / Deny call
                (WPS-151), which is what a payment approver needs here. */}
            {labVerdict ? (
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-medium",
                  isApproved
                    ? "bg-[#f0fdf4] text-[#059669]"
                    : "bg-[#fef2f2] text-[#dc2626]",
                )}
              >
                <span
                  className={cn(
                    "h-1.5 w-1.5 rounded-full",
                    isApproved ? "bg-[#10b981]" : "bg-[#ef4444]",
                  )}
                />
                {humanise(String(labVerdict))}
              </span>
            ) : (
              <span className="rounded-full bg-[var(--bg-subtle)] px-2.5 py-0.5 text-[10px] text-[var(--ink-3)]">
                Pending
              </span>
            )}
          </div>
          {labNarrative ? (
            <>
              <p className="mt-2 text-[11px] leading-relaxed text-[var(--ink-2)]">
                {String(labNarrative)}
              </p>
              {(attrs.plant_code || attrs.production_code) && (
                <div className="mt-2 flex gap-4 text-[10px] text-[var(--ink-3)]">
                  {attrs.plant_code && (
                    <span>
                      Plant:{" "}
                      <strong className="text-[var(--ink-2)]">
                        {displayScalar(attrs.plant_code)}
                      </strong>
                    </span>
                  )}
                  {attrs.production_code && (
                    <span>
                      Production:{" "}
                      <strong className="text-[var(--ink-2)]">
                        {String(attrs.production_code)}
                      </strong>
                    </span>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="mt-2 text-[11px] text-[var(--ink-3)]">
              No lab report available yet. Lab analysis will appear here once
              submitted.
            </p>
          )}
          </div>
        )}

        {/* WPS-215 AC5: Evidence photos were removed from LOA Review. An
          approver who needs to look at evidence opens the claim's Evidence
          section, where the full set lives with its flags and categories - a
          six-thumbnail crop here was never enough to judge on, and it pushed
          the figure being approved further down the screen. */}

        {/* 8. RS Rationale (v4: styled blockquote with attribution) */}
        {attrs.loa_rationale && (
          <div className="rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-5 py-3.5">
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ink-3)]">
              RS Rationale
            </p>
            <div className="rounded-r-[6px] border-l-2 border-[var(--rule-strong)] bg-[var(--bg-light)] px-3.5 py-2">
              {attrs.loa_rationale_author && (
                <p className="mb-1 text-[10px] text-[var(--ink-3)]">
                  {String(attrs.loa_rationale_author)}
                </p>
              )}
              <p className="text-[12px] leading-relaxed text-[var(--ink-2)]">
                {String(attrs.loa_rationale)}
              </p>
            </div>
          </div>
        )}

        {/* 9a. Approval Routing - Tracks */}
        {loa && loa.approval_tracks.length > 0 && (
          <div className="rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] p-4">
            <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ink-3)]">
              Approval Tracks
            </p>
            <div className="flex flex-wrap gap-2">
              {loa.approval_tracks
                .filter((t) => t.triggered)
                .map((track) => (
                  <div
                    key={track.track}
                    className={cn(
                      "flex items-center gap-2 rounded-full border-[0.5px] px-3 py-1.5 text-[11px]",
                      track.status === "APPROVED"
                        ? "border-[var(--green)]/30 bg-[var(--green)]/5 text-[var(--green)]"
                        : track.status === "PENDING"
                          ? "border-[var(--status-warning)]/30 bg-[var(--status-warning)]/5 text-[var(--status-warning)]"
                          : "border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)]",
                    )}
                  >
                    <span className="font-medium">{track.label}</span>
                    <span className="text-[10px] opacity-70">
                      {fmtMoney(track.current_value)} /{" "}
                      {fmtMoney(track.threshold_value)}
                    </span>
                    {track.status === "APPROVED" && (
                      <Check className="h-3 w-3" />
                    )}
                    {track.status === "PENDING" && (
                      <Circle className="h-3 w-3" />
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* 9b. Approval Routing - Stepper */}
        {loa && loa.threshold_tiers.length > 0 && (
          <div className="rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] p-4">
            <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ink-3)]">
              Approval Chain
            </p>
            <div className="flex flex-col gap-0">
              {loa.threshold_tiers.map((tier, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="flex flex-col items-center">
                    <div
                      className={cn(
                        "flex h-5 w-5 items-center justify-center rounded-full",
                        tier.status === "APPROVED"
                          ? "bg-[var(--green)]"
                          : tier.is_current_tier
                            ? "animate-pulse border-[1.5px] border-[var(--status-warning)] bg-[var(--status-warning)]"
                            : "border-[1.5px] border-[var(--rule)] bg-[var(--bg)]",
                      )}
                    >
                      {tier.status === "APPROVED" && (
                        <Check className="h-2.5 w-2.5 text-white" />
                      )}
                      {tier.is_current_tier && tier.status === "PENDING" && (
                        <Circle className="h-2 w-2 text-white" />
                      )}
                    </div>
                    {idx < loa.threshold_tiers.length - 1 && (
                      <div
                        className={cn(
                          "my-0.5 h-4 w-[1.5px]",
                          tier.status === "APPROVED"
                            ? "bg-[var(--green)]"
                            : "bg-[var(--rule)]",
                        )}
                      />
                    )}
                  </div>
                  <div className="pb-3">
                    <p className="text-[12px] font-medium text-[var(--ink)]">
                      {tier.role_label}
                    </p>
                    <p className="text-[10px] text-[var(--ink-3)]">
                      {fmtMoney(tier.min_amount)}
                      {tier.max_amount != null
                        ? ` - ${fmtMoney(tier.max_amount)}`
                        : "+"}
                    </p>
                    {tier.approved_by && (
                      <p className="text-[10px] text-[var(--ink-3)]">
                        ✓ {tier.approved_by}
                        {tier.approved_at ? ` · ${tier.approved_at}` : ""}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────

// ── Sectioned-design Overview helpers (rendered only in the sectioned design) ──
// ── Claim Contacts card (WP1-57) ────────────────────────
//
// One card that gathers every party already known to the claim: the
// homeowner is always the PRIMARY contact; anyone else the claim carries
// (inspection appointment contact, a group's property manager, ...) is listed as
// a secondary. Tenant-agnostic - it renders whatever contacts are passed
// in; nothing is hardcoded per tenant.

type StaticContactInfo = {
  role: string;
  name?: string;
  email?: string;
  phone?: string;
};

type ContactKind = "homeowner" | "reference" | "internal" | "external";

/** Circular avatar whose tint is the internal / external / homeowner
 *  differentiator (WPS-141) - it replaces the old text badges as the primary
 *  signal: a soft gray = property owner / reference contact, the brand accent =
 *  internal team member, a soft amber = external party. The glyph reinforces it
 *  (Home / User / Mail). */
function ContactAvatar({ kind }: { kind: ContactKind }) {
  const config = {
    homeowner: {
      Icon: Home,
      className: "bg-[var(--status-info-bg)] text-[var(--status-info)]",
    },
    reference: {
      Icon: User,
      className: "bg-[var(--status-info-bg)] text-[var(--status-info)]",
    },
    internal: {
      Icon: User,
      className: "bg-[var(--accent)] text-[var(--accent-foreground)]",
    },
    external: {
      Icon: Mail,
      className: "bg-[var(--status-warn-bg)] text-[var(--status-warn)]",
    },
  }[kind];
  const { Icon } = config;
  return (
    <span
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${config.className}`}
    >
      <Icon className="h-4 w-4" />
    </span>
  );
}

/** Shared avatar + title / subtitle + trailing-actions row. Every contact - the
 *  static homeowner / reference blocks and the managed internal / external rows
 *  - renders through this so the card reads consistently: the avatar tint is the
 *  internal-vs-external signal, the title is the name, the subtitle is the email
 *  (phone when there is no email), and every other label is a badge after the
 *  name. */
function ContactRow({
  kind,
  name,
  email,
  phone,
  badges,
  portalAccess,
  actions,
  onClick,
}: {
  kind: ContactKind;
  name?: string;
  email?: string;
  phone?: string;
  badges?: ReactNode;
  portalAccess?: boolean;
  actions?: ReactNode;
  /** When set, the whole row is a click target that opens the editor; the row
   *  shows a hover highlight + pointer. The trailing action buttons and the
   *  email link stop propagation so they keep their own behaviour. */
  onClick?: () => void;
}) {
  const { t } = useTranslation();
  const clickable = typeof onClick === "function";
  return (
    <div
      className={`group relative flex items-start gap-3 border-b-[0.5px] border-b-[var(--rule-light)] px-4 py-3 transition-colors last:border-b-0 ${
        clickable ? "cursor-pointer hover:bg-[var(--bg-subtle)]" : ""
      }`}
      onClick={onClick}
    >
      <ContactAvatar kind={kind} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[13px] font-medium text-[var(--ink)]">
            {name || "-"}
          </span>
          {badges}
        </div>
        {email ? (
          <a
            href={`mailto:${email}`}
            onClick={(e) => e.stopPropagation()}
            className="mt-0.5 block truncate text-[12px] text-[var(--ink-3)] hover:text-[var(--green)] hover:underline"
          >
            {email}
          </a>
        ) : null}
        {phone ? (
          <p className="mt-0.5 truncate text-[12px] text-[var(--ink-3)]">
            {formatPhoneDisplay(phone) || phone}
          </p>
        ) : null}
        {portalAccess ? (
          <p className="mt-1 text-[10px] uppercase tracking-[0.08em] text-[var(--ink-3)]">
            {t("claimContacts.hasPortalAccess", "Has portal access")}
          </p>
        ) : null}
      </div>
      {actions ? (
        <div
          onClick={(e) => e.stopPropagation()}
          className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity duration-150 focus-within:opacity-100 group-hover:opacity-100"
        >
          {actions}
        </div>
      ) : null}
    </div>
  );
}

function ContactBlock({
  contact,
  primary,
}: {
  contact: StaticContactInfo;
  primary?: boolean;
}) {
  return (
    <ContactRow
      kind={primary ? "homeowner" : "reference"}
      name={contact.name}
      email={contact.email}
      phone={contact.phone}
      badges={
        primary ? (
          <Badge
            variant="secondary"
            className="text-[9.5px] uppercase tracking-[0.08em]"
          >
            Primary
          </Badge>
        ) : contact.role ? (
          <Badge
            variant="outline"
            className="text-[9.5px] uppercase tracking-[0.08em]"
          >
            {contact.role}
          </Badge>
        ) : null
      }
    />
  );
}

// ── Managed contacts (Claim Contacts API, WPS-141) ──────
//
// Replaces the old standalone "watchers" feature: internal team members and
// external parties are Claim Contacts, addable/editable/removable straight
// from this card when the tenant has the ``claim_contact`` entity
// registered. External contacts carry a view_only/collaborator permission
// controlling any portal access; internal contacts always have full access
// via their own login, so only Remove applies to them.

function getContactPermissionOptions(t: TFunction) {
  return [
    {
      value: "view_only",
      label: t("claimContacts.permissionViewOnly", "View only"),
    },
    {
      value: "collaborator",
      label: t("claimContacts.permissionCollaborator", "Collaborator"),
    },
  ];
}

/** Builds the shared Role field: a CreatableSelect so a typed label is
 *  persisted as a reusable ``ClaimContactRole`` and selected immediately. */
function buildContactRoleField({
  t,
  roleOptions,
  onCreateRole,
  initialValue = "",
}: {
  t: TFunction;
  roleOptions: { value: string; label: string }[];
  onCreateRole: (label: string, onCreated: (uuid: string) => void) => void;
  initialValue?: string;
}): FormField {
  const roleLabel = t("claimContacts.fieldRole", "Role");
  return {
    type: "custom",
    name: "role",
    label: roleLabel,
    value: initialValue,
    render: (formContext) => {
      const value = (formContext.values.role ?? "") as string;
      return (
        <div className="flex flex-col gap-1.5">
          <Label className="text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
            {roleLabel}
          </Label>
          <CreatableSelect
            value={value}
            onValueChange={(v) => formContext.setFieldValue("role", v)}
            onCreate={(label) =>
              onCreateRole(label, (uuid) =>
                formContext.setFieldValue("role", uuid),
              )
            }
            options={roleOptions}
            placeholder={t(
              "claimContacts.roleSelectPlaceholder",
              "Select or create a role",
            )}
            createLabel={t("claimContacts.roleCreateLabel", "Create new role")}
            createPlaceholder={t(
              "claimContacts.roleCreatePlaceholder",
              "Type a role and press Enter",
            )}
          />
        </div>
      );
    },
  };
}

function ManagedContactRow({
  contact,
  canEdit,
  onEdit,
  onRequestRemove,
  removing,
}: {
  contact: ApiClaimContact;
  canEdit: boolean;
  onEdit: () => void;
  onRequestRemove: () => void;
  removing: boolean;
}) {
  const { t } = useTranslation();
  // External contacts are editable, so their whole row opens the editor;
  // internal contacts have nothing editable here (name/email are their user
  // account, permission is always full), so the row is not a click target.
  const rowEditable = canEdit && !contact.is_internal;
  return (
    <ContactRow
      kind={contact.is_internal ? "internal" : "external"}
      name={contact.display_name}
      email={contact.display_email || undefined}
      phone={contact.display_phone || undefined}
      portalAccess={!contact.is_internal && contact.has_portal_access}
      onClick={rowEditable ? onEdit : undefined}
      badges={
        <>
          {contact.is_main_contact ? (
            <Badge
              variant="default"
              className="text-[9.5px] uppercase tracking-[0.08em]"
            >
              {t("claimContacts.mainContactBadge", "Primary")}
            </Badge>
          ) : null}
          {!contact.is_internal && contact.permission_label ? (
            <Badge
              variant="outline"
              className="text-[9.5px] uppercase tracking-[0.08em]"
            >
              {contact.permission_label}
            </Badge>
          ) : null}
          {contact.role_label ? (
            <Badge
              variant="secondary"
              className="text-[9.5px] uppercase tracking-[0.08em]"
            >
              {contact.role_label}
            </Badge>
          ) : null}
        </>
      }
      actions={
        canEdit ? (
          <>
            {!contact.is_internal ? (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={onEdit}
                aria-label={t("claimContacts.editAria", "Edit {{name}}", {
                  name: contact.display_name,
                })}
                className="text-[var(--ink-3)] hover:text-[var(--ink)]"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onRequestRemove}
              disabled={removing}
              aria-label={t("claimContacts.removeAria", "Remove {{name}}", {
                name: contact.display_name,
              })}
              className="text-[var(--ink-3)] hover:text-[var(--status-error)]"
            >
              <X className="h-4 w-4" />
            </Button>
          </>
        ) : null
      }
    />
  );
}

function AddClaimContactDialog({
  open,
  onOpenChange,
  claimUuid,
  orgUuid,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  claimUuid: string;
  orgUuid: string | null;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"internal" | "external">("internal");
  const { data: orgMembers = [] } = useOrgMembers(open ? orgUuid : null);
  const { data: roles = [] } = useClaimContactRoles();
  const createRole = useCreateClaimContactRole();
  const addContact = useAddClaimContact();

  const roleOptions = useMemo(
    () => roles.map((r) => ({ value: r.uuid, label: r.label })),
    [roles],
  );
  const handleCreateRole = useCallback(
    (label: string, onCreated: (uuid: string) => void) => {
      createRole.mutate(
        { label },
        {
          onSuccess: (role) => onCreated(role.uuid),
          onError: (err) =>
            toastApiError(
              err,
              t("claimContacts.failedToCreateRole", "Failed to create role"),
            ),
        },
      );
    },
    [createRole, t],
  );

  const fields = useMemo<FormField[]>(() => {
    const roleField = buildContactRoleField({
      t,
      roleOptions,
      onCreateRole: handleCreateRole,
    });
    if (mode === "internal") {
      return [
        {
          type: "select",
          name: "user",
          label: t("claimContacts.fieldTeamMember", "Team member"),
          value: "",
          placeholder: t(
            "claimContacts.selectTeamMemberPlaceholder",
            "Select a team member",
          ),
          options: orgMembers.map((m) => ({
            value: m.user_uuid,
            label: m.user_name
              ? `${m.user_name} (${m.user_email})`
              : m.user_email,
          })),
          required: t(
            "claimContacts.teamMemberRequired",
            "Please select a team member",
          ),
        },
        // No role field for internal team members - they are identified by
        // their platform role (RBAC). Role is an external-contact attribute
        // only (WPS-141).
      ];
    }
    return [
      {
        type: "text",
        name: "external_name",
        label: t("claimContacts.fieldName", "Name"),
        value: "",
        placeholder: t("claimContacts.namePlaceholder", "Full name"),
      },
      {
        type: "email",
        name: "external_email",
        label: t("claimContacts.fieldEmail", "Email"),
        value: "",
        placeholder: t("claimContacts.emailPlaceholder", "name@company.com"),
        required: t("claimContacts.emailRequired", "Email is required"),
      },
      {
        type: "phone",
        name: "external_phone",
        label: t("claimContacts.fieldPhone", "Phone"),
        value: "",
      },
      roleField,
      {
        type: "select",
        name: "permission",
        label: t("claimContacts.fieldPermission", "Permission"),
        value: "view_only",
        options: getContactPermissionOptions(t),
      },
      {
        type: "checkbox",
        name: "is_main_contact",
        label: t("claimContacts.fieldMainContact", "Main contact"),
        value: false,
      },
    ];
  }, [mode, orgMembers, roleOptions, handleCreateRole, t]);

  const { Form, FormFooter } = useForm(
    {
      name: `add-claim-contact-${claimUuid}-${mode}`,
      submit: "footer",
      fields,
    },
    async (rawValues) => {
      const values = rawValues as {
        user?: string;
        external_name?: string;
        external_email?: string;
        external_phone?: string;
        role?: string;
        permission?: string;
        is_main_contact?: boolean;
      };
      const payload =
        mode === "internal"
          ? { claimUuid, user: values.user }
          : {
              claimUuid,
              external_name: values.external_name,
              external_email: values.external_email,
              external_phone: values.external_phone,
              role: values.role || null,
              permission: values.permission,
              is_main_contact: Boolean(values.is_main_contact),
            };
      await new Promise<void>((resolve, reject) => {
        addContact.mutate(payload, {
          onSuccess: () => {
            toast.success(t("claimContacts.contactAdded", "Contact added"));
            onOpenChange(false);
            resolve();
          },
          onError: (err) => {
            toastApiError(
              err,
              t("claimContacts.failedToAddContact", "Failed to add contact"),
            );
            reject(err);
          },
        });
      });
    },
    [claimUuid, mode, orgMembers, roleOptions, handleCreateRole, t],
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setMode("internal");
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("claimContacts.addContact", "Add Contact")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "claimContacts.addDialogDescription",
              "Add a team member or an external party to this claim.",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={mode === "internal" ? "default" : "outline"}
            size="sm"
            className="flex-1 text-[10px] uppercase tracking-[0.1em]"
            onClick={() => setMode("internal")}
          >
            {t("claimContacts.fieldTeamMember", "Team member")}
          </Button>
          <Button
            type="button"
            variant={mode === "external" ? "default" : "outline"}
            size="sm"
            className="flex-1 text-[10px] uppercase tracking-[0.1em]"
            onClick={() => setMode("external")}
          >
            {t("claimContacts.modeExternal", "External")}
          </Button>
        </div>
        <Form className="flex flex-col gap-3" />
        <DialogFooter className="mt-2">
          {FormFooter && (
            <FormFooter
              onCancel={() => onOpenChange(false)}
              submitText={t("claimContacts.addSubmit", "Add contact")}
            />
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditClaimContactDialog({
  open,
  onOpenChange,
  claimUuid,
  contact,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  claimUuid: string;
  /** External contact being edited; the dialog renders nothing when null. */
  contact: ApiClaimContact | null;
}) {
  const { t } = useTranslation();
  const { data: roles = [] } = useClaimContactRoles();
  const createRole = useCreateClaimContactRole();
  const updateContact = useUpdateClaimContact();

  const roleOptions = useMemo(
    () => roles.map((r) => ({ value: r.uuid, label: r.label })),
    [roles],
  );
  const handleCreateRole = useCallback(
    (label: string, onCreated: (uuid: string) => void) => {
      createRole.mutate(
        { label },
        {
          onSuccess: (role) => onCreated(role.uuid),
          onError: (err) =>
            toastApiError(
              err,
              t("claimContacts.failedToCreateRole", "Failed to create role"),
            ),
        },
      );
    },
    [createRole, t],
  );

  const fields = useMemo<FormField[]>(() => {
    if (!contact) return [];
    return [
      {
        type: "text",
        name: "external_name",
        label: t("claimContacts.fieldName", "Name"),
        value: contact.external_name ?? "",
      },
      {
        type: "email",
        name: "external_email",
        label: t("claimContacts.fieldEmail", "Email"),
        value: contact.external_email ?? "",
        required: t("claimContacts.emailRequired", "Email is required"),
      },
      {
        type: "phone",
        name: "external_phone",
        label: t("claimContacts.fieldPhone", "Phone"),
        value: contact.external_phone ?? "",
      },
      buildContactRoleField({
        t,
        roleOptions,
        onCreateRole: handleCreateRole,
        initialValue: contact.role ?? "",
      }),
      {
        type: "select",
        name: "permission",
        label: t("claimContacts.fieldPermission", "Permission"),
        value: contact.permission ?? "view_only",
        options: getContactPermissionOptions(t),
      },
      {
        type: "checkbox",
        name: "is_main_contact",
        label: t("claimContacts.fieldMainContact", "Main contact"),
        value: contact.is_main_contact ?? false,
      },
    ];
  }, [contact, roleOptions, handleCreateRole, t]);

  const { Form, FormFooter } = useForm(
    {
      name: `edit-claim-contact-${contact?.uuid ?? "none"}`,
      submit: "footer",
      fields,
    },
    async (rawValues) => {
      if (!contact) return;
      const values = rawValues as {
        external_name?: string;
        external_email?: string;
        external_phone?: string;
        role?: string;
        permission?: string;
        is_main_contact?: boolean;
      };
      await new Promise<void>((resolve, reject) => {
        updateContact.mutate(
          {
            claimUuid,
            contactUuid: contact.uuid,
            external_name: values.external_name,
            external_email: values.external_email,
            external_phone: values.external_phone,
            role: values.role || null,
            permission: values.permission,
            is_main_contact: Boolean(values.is_main_contact),
          },
          {
            onSuccess: () => {
              toast.success(
                t("claimContacts.contactUpdated", "Contact updated"),
              );
              onOpenChange(false);
              resolve();
            },
            onError: (err) => {
              toastApiError(
                err,
                t(
                  "claimContacts.failedToUpdateContact",
                  "Failed to update contact",
                ),
              );
              reject(err);
            },
          },
        );
      });
    },
    [claimUuid, contact, roleOptions, handleCreateRole, t],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("claimContacts.editDialogTitle", "Edit Contact")}
          </DialogTitle>
        </DialogHeader>
        {contact ? <Form className="flex flex-col gap-3" /> : null}
        <DialogFooter className="mt-2">
          {FormFooter && (
            <FormFooter
              onCancel={() => onOpenChange(false)}
              submitText={t("claimContacts.saveChanges", "Save changes")}
            />
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClaimContactsCard({
  claimUuid,
  orgUuid,
  primary,
  secondary,
  editConfig,
  inspectionContact,
}: {
  claimUuid: string;
  orgUuid: string | null;
  primary?: StaticContactInfo;
  secondary: StaticContactInfo[];
  /** When present, a pencil edits the inspection contact (a claim field);
   *  also gates the managed-contacts Add/Edit/Remove actions. */
  editConfig?: {
    isPending: boolean;
    canEdit: boolean;
    onSave: (values: Record<string, unknown>, reason?: string) => Promise<void>;
  };
  /** Current inspection-contact values for the edit form. */
  inspectionContact?: { name?: string; phone?: string };
}) {
  const { t } = useTranslation();
  // Managed contacts (WPS-141) are a distinct capability from the read-only
  // primary/secondary blocks above - gated entirely on entity registration
  // so a tenant without the `claim_contact` entity sees the card exactly as
  // it rendered before this feature existed.
  const hasManagedContacts = useEntityRegistered("claim_contact");
  const canEdit = !!editConfig && editConfig.canEdit;
  const { data: contacts = [], isPending: contactsPending } = useClaimContacts(
    hasManagedContacts ? claimUuid : null,
  );
  const removeContact = useRemoveClaimContact();

  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<ApiClaimContact | null>(
    null,
  );
  const [confirmRemoveUuid, setConfirmRemoveUuid] = useState<string | null>(
    null,
  );

  const hasPrimary =
    primary && (primary.name || primary.email || primary.phone);
  const realSecondary = secondary.filter((c) => c.name || c.email || c.phone);

  // WPS-164: a contact shown as the static primary/secondary (e.g. the supplier
  // on an uninstalled claim) may also exist as a managed Collaborator row -
  // created at claim creation so it receives packet emails. Filter those out of
  // the managed list so the same person is not rendered twice.
  const staticEmails = new Set(
    [primary?.email, ...secondary.map((c) => c.email)]
      .filter(Boolean)
      .map((e) => (e as string).trim().toLowerCase()),
  );
  const managedContacts = contacts.filter(
    (c) =>
      !c.external_email ||
      !staticEmails.has(c.external_email.trim().toLowerCase()),
  );

  const handleRemoveManaged = () => {
    if (!confirmRemoveUuid) return;
    removeContact.mutate(
      { claimUuid, contactUuid: confirmRemoveUuid },
      {
        onSuccess: () => {
          toast.success(t("claimContacts.contactRemoved", "Contact removed"));
          setConfirmRemoveUuid(null);
        },
        onError: (err) =>
          toastApiError(
            err,
            t(
              "claimContacts.failedToRemoveContact",
              "Failed to remove contact",
            ),
          ),
      },
    );
  };

  // The pencil edits the inspection contact (an installed-claim field). Only
  // offer it when there is an inspection contact to edit, so holdingless
  // claims (uninstalled/job-stop) that reuse this card for the supplier get
  // the managed-contacts Add without an irrelevant inspection-contact editor.
  // A claim can nominate who it is corresponded with, and that is not always
  // the customer: a BFS claim is filed and chased by a third-party reviewer,
  // and its spec forbids any claim communication reaching the property owner.
  // Where such a contact exists it IS the primary one, so badging the customer
  // "Primary" would name the wrong person - on BFS, the one person who must
  // not be contacted. Server-side this already decides who receives claim
  // email (``resolve_main_contact``); this makes the card agree with it.
  const nominatedMainContact = managedContacts.some((c) => c.is_main_contact);
  const showEdit = canEdit && !!inspectionContact;
  const showAdd = canEdit && hasManagedContacts;
  // The empty state renders only once loading has settled, per the
  // load->error->empty->content rule - never flash "no contacts" while the
  // managed-contacts query is still in flight.
  const showManagedEmpty =
    hasManagedContacts && !contactsPending && managedContacts.length === 0;
  const showEmptyState =
    !hasPrimary &&
    realSecondary.length === 0 &&
    (!hasManagedContacts || showManagedEmpty) &&
    !(hasManagedContacts && contactsPending);

  if (
    !hasPrimary &&
    realSecondary.length === 0 &&
    !canEdit &&
    !hasManagedContacts
  )
    return null;

  return (
    <>
      <DetailCard
        title={t("claims.card.claimContacts", "Claim Contacts")}
        icon={<Users className="h-3.5 w-3.5" />}
        trailing={
          showEdit ? (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => setEditOpen(true)}
              aria-label="Edit inspection contact"
              className="text-[var(--ink-3)] hover:text-[var(--ink)]"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          ) : undefined
        }
      >
        {hasPrimary && primary ? (
          <ContactBlock contact={primary} primary={!nominatedMainContact} />
        ) : null}
        {realSecondary.map((c, i) => (
          <ContactBlock key={`sec-${c.role}-${i}`} contact={c} />
        ))}
        {hasManagedContacts && contactsPending ? (
          <div className="px-4 py-[9px] text-[12px] text-[var(--ink-3)]">
            {t("claimContacts.loadingContacts", "Loading contacts...")}
          </div>
        ) : null}
        {hasManagedContacts && !contactsPending
          ? managedContacts.map((contact) => (
              <ManagedContactRow
                key={contact.uuid}
                contact={contact}
                canEdit={canEdit}
                onEdit={() => setEditingContact(contact)}
                onRequestRemove={() => setConfirmRemoveUuid(contact.uuid)}
                removing={
                  removeContact.isPending && confirmRemoveUuid === contact.uuid
                }
              />
            ))
          : null}
        {showEmptyState ? (
          <div className="px-4 py-[9px] text-[12px] text-[var(--ink-3)]">
            {t(
              "claimContacts.noContactsYet",
              "No additional contacts captured yet.",
            )}
          </div>
        ) : null}
        {showAdd ? (
          <div className="px-4 py-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddOpen(true)}
              className="flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-[6px] border-[0.5px] border-dashed border-[var(--rule-strong)] bg-[var(--bg)] text-[11px] uppercase tracking-[0.06em] text-[var(--ink-2)] transition-all duration-150 hover:border-[var(--green)] hover:bg-[var(--green-bg)] hover:text-[var(--ink)]"
            >
              <Plus className="h-3.5 w-3.5" />
              {t("claimContacts.addContact", "Add Contact")}
            </Button>
          </div>
        ) : null}
      </DetailCard>

      {showEdit && editConfig ? (
        <EditFormDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          title={t(
            "claims.card.editInspectionContact",
            "Edit Inspection Contact",
          )}
          fields={[
            defineField({
              code_name: "appointment_contact_name",
              display_name: "Inspection Contact",
              field_type: "TEXT",
            }),
            defineField({
              code_name: "appointment_contact_phone",
              display_name: "Contact Phone",
              field_type: "PHONE",
            }),
          ]}
          values={{
            appointment_contact_name: inspectionContact?.name ?? "",
            appointment_contact_phone: inspectionContact?.phone ?? "",
          }}
          onSave={editConfig.onSave}
          isPending={editConfig.isPending}
          requireReason={false}
        />
      ) : null}

      {hasManagedContacts ? (
        <>
          <AddClaimContactDialog
            open={addOpen}
            onOpenChange={setAddOpen}
            claimUuid={claimUuid}
            orgUuid={orgUuid}
          />
          <EditClaimContactDialog
            open={editingContact !== null}
            onOpenChange={(next) => {
              if (!next) setEditingContact(null);
            }}
            claimUuid={claimUuid}
            contact={editingContact}
          />
          {/* Confirm before removing a managed contact (destructive action). */}
          <ConfirmDeleteDialog
            open={confirmRemoveUuid !== null}
            onOpenChange={(open) => {
              if (!open) setConfirmRemoveUuid(null);
            }}
            title={t("claimContacts.removeContactTitle", "Remove contact?")}
            description={t(
              "claimContacts.removeContactDescription",
              "This removes the contact from this claim. They will no longer receive updates.",
            )}
            confirmLabel={t("claimContacts.removeConfirmLabel", "Remove")}
            isPending={removeContact.isPending}
            onConfirm={handleRemoveManaged}
          />
        </>
      ) : null}
    </>
  );
}

// ── Overview section heading (WP1-57) ───────────────────
//
// A labelled hairline divider that groups the Overview cards into readable
// bands ("The Claim", "People & Property", "Coverage") instead of one flat
// run of identical cards. Spans the full grid width so the cards beneath it
// flow back into the two-column rhythm. Tenant-agnostic - it only labels
// groups, never claim content.

/**
 * A stored attribute as plain text for headers and summaries. Attribute values
 * can be structured (`{value, display}`, `{label}`), which React cannot render
 * as a child; this reads the human label and never returns an object.
 */
function attributeText(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "string") return value || undefined;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (typeof value === "object" && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    const label = record.display ?? record.label ?? record.name ?? record.value;
    return typeof label === "string" || typeof label === "number"
      ? String(label) || undefined
      : undefined;
  }
  return undefined;
}

function OverviewSectionHeading({
  label,
  className,
}: {
  label: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        // Span the full width whether the parent is a CSS grid (col-span) or a
        // masonry multi-column container ([column-span:all]); the inapplicable
        // rule is simply ignored by the other layout mode.
        "col-span-1 flex items-center gap-3 md:col-span-2 [column-span:all]",
        className,
      )}
    >
      <span className="text-[14px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
        {label}
      </span>
      <div className="h-px flex-1 bg-[var(--rule)]" />
    </div>
  );
}

/**
 * Read-only preview of a potential-duplicate claim, shown in a modal so the
 * reviewer can eyeball a sibling claim without leaving the claim they are
 * currently working. Reuses the standard claim-detail hook/endpoint - no new
 * API surface. Fully generic: the essentials come from the claim detail
 * payload and the tenant-configured attribute values are rendered with their
 * metadata labels (falling back to a humanised key).
 */
function DuplicateClaimPreviewDialog({
  previewUuid,
  matchedOn,
  onClose,
  onOpenFull,
}: {
  previewUuid: string | null;
  matchedOn: string[];
  onClose: () => void;
  onOpenFull: (uuid: string) => void;
}) {
  const { data, isLoading, isError } = useClaim(previewUuid);
  const { formatDate, locale, formatCurrency } = useFormatting();
  const { t } = useTranslation();

  const dup = data?.claim;
  const labelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of data?.metadata?.editable_field_metadata ?? []) {
      map.set(f.code_name, f.display_name);
    }
    return map;
  }, [data?.metadata?.editable_field_metadata]);

  // Tenant-configured claim attributes (issue type, date of incident, amount,
  // ...) - rendered generically. Empty values and the labels chip are dropped.
  const attributeItems = useMemo(() => {
    if (!dup) return [] as Array<{ key: string; label: string; value: string }>;
    return Object.entries(dup.attribute_values ?? {})
      .filter(([key]) => key !== "labels")
      .map(([key, value]) => ({
        key,
        label: labelMap.get(key) ?? titleCaseKey(key),
        value: formatAttrValue(value, locale, key, formatCurrency, t),
      }))
      .filter((item) => item.value && item.value !== "-");
  }, [dup, labelMap, locale, formatCurrency, t]);

  return (
    <Dialog
      open={previewUuid !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-[var(--status-warn)]" />
            {dup?.reference ?? "Potential duplicate"}
          </DialogTitle>
          <DialogDescription className="text-[13px] text-[var(--ink-2)]">
            Read-only preview of a matched claim. You are still on the current
            claim.
          </DialogDescription>
        </DialogHeader>

        {isError ? (
          <div className="flex items-center justify-center gap-2 py-8">
            <AlertCircle className="h-4 w-4 shrink-0 text-[var(--status-error)]" />
            <p className="text-[13px] text-[var(--ink-2)]">
              Failed to load this claim. Close and try again, or open the full
              claim.
            </p>
          </div>
        ) : isLoading || !dup ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
          </div>
        ) : (
          <div className="flex flex-col gap-3 py-1">
            <div className="flex flex-wrap items-center gap-2">
              {dup.claim_state ? (
                <StatusBadge status={dup.claim_state}>
                  {humanise(dup.claim_state)}
                </StatusBadge>
              ) : null}
              {matchedOn.length > 0 ? (
                <span className="text-[11px] text-[var(--ink-3)]">
                  Matched on{" "}
                  {matchedOn
                    .map((ref) => ref.replace("consumer_", ""))
                    .join(", ")}
                </span>
              ) : null}
            </div>

            <Separator />

            <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
              <PreviewRow label="Customer" value={dup.consumer_name} />
              <PreviewRow label="Product" value={dup.product_name} />
              <PreviewRow label="Claim type" value={dup.claim_schema_name} />
              <PreviewRow
                label="Created"
                value={dup.created_at ? formatDate(dup.created_at) : "-"}
              />
              {attributeItems.map((item) => (
                <PreviewRow
                  key={item.key}
                  label={item.label}
                  value={item.value}
                />
              ))}
            </dl>
          </div>
        )}

        <DialogFooter>
          <Button
            onClick={onClose}
            variant="outline"
            className="px-4 py-2 text-[12px]"
          >
            Close
          </Button>
          <Button
            onClick={() => previewUuid && onOpenFull(previewUuid)}
            disabled={!previewUuid}
            className="px-4 py-2 text-[12px]"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open full claim
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Compact label/value pair used inside the duplicate-preview modal. */
function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[10px] uppercase tracking-[0.08em] text-[var(--ink-3)]">
        {label}
      </dt>
      <dd className="text-[13px] text-[var(--ink)]">{value || "-"}</dd>
    </div>
  );
}

// Keyed wrapper: remount on :uuid change so navigating claim -> claim (a child
// item in the Items tab, a duplicate chip, etc.) loads the new claim fresh
// instead of showing the previous one until a manual refresh.
export function ClaimDetailPage() {
  const { uuid } = useParams<{ uuid: string }>();
  // WPS-175 #2(b): ENTITY_SEARCH fields resolve their options through this
  // registry. Without it a picker degrades to an empty <Select>, which is what
  // Product Name did on the Defect Details edit dialog ("No results found") -
  // only ClaimActionDialog carried the providers, so the claim's own edit
  // dialogs had nothing to search. Provided here rather than inside
  // ClaimDetailPageInner so the page's JSX gains no nesting level.
  const resourceLookupProviders = useResourceLookupProviders();
  return (
    <ResourceLookupContextProvider providers={resourceLookupProviders}>
      <ClaimDetailPageInner key={uuid ?? ""} />
    </ResourceLookupContextProvider>
  );
}

function ClaimDetailPageInner() {
  const { t } = useTranslation();
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  // Optional ``?tab=`` lets callers land on a specific tab (e.g. a freshly
  // created claim opens on Overview rather than the state's workspace).
  const tabParam = searchParams.get("tab") ?? "";
  const { data, isLoading, refetch } = useClaim(uuid ?? null);
  const { data: linkedHolding } = useHolding(data?.claim?.holding_uuid ?? null);
  // Only a workflow that puts its main contact in the header needs the
  // contacts here; for it this is the query the Claim Contacts card already
  // runs, and every other claim makes no request.
  const headerShowsMainContact =
    data?.metadata?.workflow_metadata?.header_contact === "main_contact";
  const { data: claimContacts = [], isPending: claimContactsPending } =
    useClaimContacts(headerShowsMainContact ? (uuid ?? null) : null);
  const { data: hostEntity } = useEntityTypeByKey("host");
  const { data: holdingEntity } = useEntityTypeByKey("holding");
  const { data: warrantyEntity } = useEntityTypeByKey("warranty");
  const { data: entityTypes = [] } = useEntityTypes();
  const { can } = usePermissions();
  const { formatCurrency, formatDate, formatDateNumeric, locale, currency } =
    useFormatting();
  // Same partner-card label the card itself uses, for the hide/restore control.
  const { data: externalRoles = [] } = useExternalRoles();
  const { openFile } = useFileViewer();
  const hasProductIdentification = useFeatureFlag(FLAGS.PRODUCT_IDENTIFICATION);
  const [identifyProductOpen, setIdentifyProductOpen] = useState(false);
  const [dupDialogOpen, setDupDialogOpen] = useState(false);
  const [dupNote, setDupNote] = useState("");
  // UUID of the potential-duplicate claim being previewed in a modal (null =
  // closed). Clicking a match opens a read-only preview so the reviewer stays
  // on the current claim instead of navigating away.
  const [previewDupUuid, setPreviewDupUuid] = useState<string | null>(null);
  // WPS-164 #29: some actions (e.g. Initiate Scrap) run straight from the
  // action bar with no capture dialog or confirmation window.
  const executeImmediate = useExecuteClaimAction();

  const workflowStates: WorkflowState[] = useMemo(
    () => data?.metadata?.workflow_states ?? [],
    [data?.metadata?.workflow_states],
  );

  // ── Tab visibility ────────────────────────────────────
  //
  // Each non-Overview tab gates on either a Tier 2 entity (Lab,
  // Inspection, Settlement - they own a dedicated data table) or a
  // workflow workspace category (Resolution, LOA - they're workflow
  // workspaces, not entities). For tenants without any of those -
  // e.g. 12 Assured shipping the default claims system - only
  // Overview survives, in which case the tab bar is suppressed
  // entirely and the Overview content renders directly. Hooks live
  // here (before any early returns) so React's hook ordering rule
  // is satisfied even when ``data`` is still loading.
  const hasLabEntity = useEntityRegistered("lab_analysis");
  const hasInspectionEntityRegistered = useEntityRegistered("inspection");
  const hasInspectionsFlag = useFeatureFlag(FLAGS.INSPECTIONS);
  const hasInspectionEntity =
    hasInspectionEntityRegistered && hasInspectionsFlag;
  // Settlement is opt-in, not tenant-specific: any tenant that registers a
  // ``settlement_line_item`` entity, switches on the ``settlement`` flag and
  // tags a workflow state ``workspace_category: "settlement"`` gets it, with
  // no code change. Owens Corning and Teknor Apex both run it today. Requiring
  // BOTH the entity and the flag is what keeps it off a tenant that wants
  // neither. (Two separate hook calls - never short-circuit a hook behind &&.)
  const hasSettlementEntityRegistered = useEntityRegistered(
    "settlement_line_item",
  );
  const hasSettlementFlag = useFeatureFlag(FLAGS.SETTLEMENT);
  const hasVouchersFlag = useFeatureFlag(FLAGS.VOUCHERS);
  const hasSettlementEntity =
    hasSettlementEntityRegistered && hasSettlementFlag;
  // Assessment is internal-only - gated by claims.view_decisions so it never
  // shows to external partners (the endpoints + serializer enforce the same
  // server-side). Two separate hook calls - never short-circuit a hook behind &&.
  const hasAssessmentEntityRegistered = useEntityRegistered(
    "assessment_line_item",
  );
  const hasAssessmentFlag = useFeatureFlag(FLAGS.ASSESSMENT);
  const hasAssessmentEntity =
    hasAssessmentEntityRegistered && hasAssessmentFlag;
  // The sectioned claim design (merged Overview, Claim Contacts, section
  // bands, Evidence tab): a tenant setting served in the org context. Other
  // tenants render the classic single-screen overview.
  const { tenantSettings: designSettings } = useOrg();
  const sectionedDesign = designSettings?.claim_detail_design === "sectioned";
  // Evidence is its own tab only in the sectioned design; elsewhere Overview
  // is the only tab so the tab bar drops entirely.
  const hasEvidenceTab = sectionedDesign;
  // WP1-67: cycle-time + stage/status visibility, gated by ``cycletime``.
  const hasCycleTime = useFeatureFlag(FLAGS.CYCLETIME);
  // Frozen at first render so the awaiting_docs follow-up "done" comparisons
  // stay pure (matches the standard ClaimResolutionPanel's captured clock).
  const [followUpNowMs] = useState(() => Date.now());
  const workspaceCategories = useMemo(() => {
    const set = new Set<string>();
    for (const s of workflowStates) {
      const cat = s.attribute_values?.workspace_category;
      if (typeof cat === "string" && cat) set.add(cat);
    }
    return set;
  }, [workflowStates]);
  const hasResolutionWorkspace = workspaceCategories.has("resolution");
  const hasLoaWorkspace = workspaceCategories.has("loa");
  // WPS-119: the Lab entity is registered per TENANT, but whether a claim ever
  // reaches a lab is a property of its WORKFLOW. A BFS claim skips lab
  // analysis entirely (Patrick: "it shall skip the lab analysis stage and go
  // straight to Evaluation"), so the tab was a live-looking link to a panel
  // that can never hold anything - a click every reviewer pays once.
  //
  // Derived, not configured: a workflow that uses the lab tags a state
  // `workspace_category: "lab"`, exactly as the Settlement tab already reads
  // `hasSettlementWorkspace`. Verified across all nine OC claim workflows -
  // every one with a lab state tags it, so nothing that genuinely uses the lab
  // is dimmed, and a workflow that adds a lab stage lights its tab up with no
  // code or config change.
  const hasLabWorkspace = workspaceCategories.has("lab");
  // WP1-65: an active Levels-of-Approval cascade (Warranty / Business Goodwill)
  // surfaces the LOA tab even when the workflow tags no state
  // workspace_category="loa", so the cascade panel is never orphaned.
  const loaDimensions = data?.metadata?.loas as LOADimension[] | undefined;
  const hasLoaCascade = (loaDimensions?.length ?? 0) > 0;
  const showLoaTab = hasLoaWorkspace || hasLoaCascade;
  // WP1-65: how many LOA dimensions are awaiting an approval decision. Drives a
  // highlighted counter badge on the LOA tab so the pending approvals are not
  // hidden behind the tab.
  const loaPendingCount =
    loaDimensions?.filter((d) => d.loa_status === "PENDING").length ?? 0;
  // Assessment tab: gated by the registered entity + flag, the presence of a
  // state tagged workspace_category="assessment" (only an umbrella workflow
  // carries it - never a slug), and claims.view_decisions (internal-only).
  const hasAssessmentWorkspace = workspaceCategories.has("assessment");
  const hasAssessmentTab =
    hasAssessmentEntity &&
    hasAssessmentWorkspace &&
    can("claims.view_decisions");
  // A group claim: an umbrella parent filed for a group (the server sends its
  // group only then). It lists its property lines; no claim type is named.
  const isGroupClaim = !!data?.claim?.group && !data?.claim?.parent_claim_uuid;
  // What a group claim's lines cover, named by its group type's members.
  const groupMembersLabel =
    data?.claim?.group?.member_kind === "registration"
      ? t("claims.group.registrations", "{{holdingNounPlural}}")
      : t("claims.group.properties", "Properties");
  // Any umbrella parent (group or multi-item) surfaces its children. Superset
  // of isGroupClaim: a claim with children is a parent, no schema literal.
  const isUmbrellaParent =
    isGroupClaim || (data?.claim?.child_claim_count ?? 0) > 0;
  // For a group claim, Lab only appears once the inspection team has requested
  // it (or the claim is/was in the lab stage) - it is not a default tab.
  const labStateKey =
    data?.metadata?.workflow_instance?.current_state_key ?? "";
  const labRequested =
    labStateKey === "lab" ||
    (data?.history ?? []).some((e) => e.action_key === "request_lab");
  const showLabTab = hasLabEntity && (!isGroupClaim || labRequested);
  // Shown but inert. Hiding it would leave a reviewer who knows the Lab tab
  // exists wondering where it went; dimming answers the question in place.
  // `TabsTrigger` renders `disabled` at 50% opacity with pointer events off,
  // so this needs no styling of its own.
  const labTabDisabled = showLabTab && !hasLabWorkspace;
  const hasSettlementWorkspace = workspaceCategories.has("settlement");
  // The payment ledger is offered by registering the entity, not by a flag -
  // a claim recording what it paid is not an optional module.
  const hasPaymentLedger = useEntityRegistered("claim_payment");
  // Two shapes of tenant: payment+settlement keeps its payments inside the
  // Settlement tab (alongside the line items that produced them), payment-only
  // gets this tab. Keyed on whether the Settlement tab actually RENDERS, not on
  // the workspace tag alone - a tenant with a tagged state but no settlement
  // flag shows neither tab, and its payments would be unreachable.
  const hasPaymentsTab =
    hasPaymentLedger && !(hasSettlementEntity && hasSettlementWorkspace);
  // Who this claim could pay, read off the claim we already hold - no lookup
  // endpoint. Order is deliberate: the customer first, because on a claim with
  // a single candidate the picker pre-fills it.
  const payeeCandidates = useMemo<PayeeCandidate[]>(() => {
    const claim = data?.claim;
    if (!claim) return [];
    const out: PayeeCandidate[] = [];
    if (claim.consumer_name?.trim()) {
      out.push({
        uuid: `consumer:${claim.uuid}`,
        name: claim.consumer_name.trim(),
        recipient_type: "CUSTOMER",
        secondary: claim.consumer_email || undefined,
      });
    }
    const dealer = claim.dealer_name?.trim();
    if (dealer) {
      out.push({
        uuid: `dealer:${claim.uuid}`,
        name: dealer,
        recipient_type: "DEALER",
      });
    }
    const contractorName =
      claim.contractor?.downstream_organisation?.name?.trim();
    if (contractorName && contractorName !== dealer) {
      out.push({
        uuid: `contractor:${claim.contractor?.uuid ?? claim.uuid}`,
        name: contractorName,
        recipient_type: "CONTRACTOR",
      });
    }
    return out;
  }, [data?.claim]);
  const { data: claimPayments } = useClaimPayments(uuid ?? "", hasPaymentsTab);
  const visibleTabKeys = useMemo(
    () =>
      [
        "overview",
        !sectionedDesign && hasResolutionWorkspace ? "resolution" : null,
        showLabTab ? "lab" : null,
        hasInspectionEntity ? "inspection" : null,
        // The Settlement tab is a workspace-flow concept (staged station
        // panels, payments, RS permissions). Workflows without a
        // settlement workspace surface the same line items inline on the
        // Overview instead - one surface, no duplication.
        hasSettlementEntity && hasSettlementWorkspace ? "settlement" : null,
        // The payment ledger gets its own tab for every tenant that has one.
        // Suppressed where a settlement workspace already owns a payments
        // table on its own tab - one surface, no duplication.
        hasPaymentsTab ? "payments" : null,
        hasAssessmentTab ? "assessment" : null,
        showLoaTab ? "loa" : null,
        isUmbrellaParent ? "children" : null,
        // Evidence is a sectioned-design tab (WP1-56). Elsewhere it is
        // hidden, leaving Overview as the sole tab so the tab bar drops away.
        hasEvidenceTab ? "evidence" : null,
      ].filter((x): x is string => x !== null),
    [
      sectionedDesign,
      hasResolutionWorkspace,
      showLabTab,
      hasInspectionEntity,
      hasSettlementEntity,
      hasSettlementWorkspace,
      hasPaymentsTab,
      hasAssessmentTab,
      showLoaTab,
      isUmbrellaParent,
      hasEvidenceTab,
    ],
  );
  const showTabBar = visibleTabKeys.length > 1;
  // Visible and selectable are not the same thing: a dimmed tab stays in the
  // bar but must never be resolved as the landing tab, or the reviewer opens
  // the claim on a panel they are not allowed to click back out of.
  const selectableTabKeys = useMemo(
    () => visibleTabKeys.filter((key) => !(labTabDisabled && key === "lab")),
    [visibleTabKeys, labTabDisabled],
  );

  // ``hasLOAStates`` / ``hasLab`` were introduced by Ruby's PRs #392 /
  // #395 for un-gated tab rendering. The assimilation merge replaced
  // them with the entity-presence gates ``hasLoaWorkspace`` and
  // ``hasLabEntity`` (driven by ``<IfEntity name="..." />`` and
  // ``useEntityRegistered``), so neither helper is consumed any more.
  // Left removed deliberately.

  const [activeAction, setActiveAction] = useState<WorkflowAction | null>(null);
  // WPS-175 #13: the action a user abandoned to go and add a document.
  // "Add documents on the Evidence tab" used to clear activeAction, which
  // discarded what they were doing - they landed on Evidence with no way
  // back and had to find the action again and retype its capture fields.
  // The requirement says the claim should continue once the document is
  // added; this is what carries them there.
  // One-shot: open the Evidence tab's upload dialog on arrival from a blocked
  // action. Reset by the card once consumed, so closing it stays closed.
  const [autoOpenEvidenceAdd, setAutoOpenEvidenceAdd] = useState(false);
  const [returnToAction, setReturnToAction] = useState<WorkflowAction | null>(
    null,
  );
  const [flagDialogOpen, setFlagDialogOpen] = useState(false);
  const [flagReason, setFlagReason] = useState("");
  const [activeTab, setActiveTab] = useState<string>("");

  const editFieldsDirect = useEditClaimDirect();
  const uploadFile = useUploadFile();
  const addClaimEvidence = useAddClaimEvidence();
  const toggleFlag = useToggleClaimFlag();
  // A letter the workflow will send (e.g. a release), opened to read first.
  const [previewLetter, setPreviewLetter] = useState<{
    template_id: number;
    name: string;
  } | null>(null);
  const resolveDuplicates = useResolveClaimDuplicates();
  const resolveImageMatch = useResolveMediaMatch();
  const assignClaim = useAssignClaim();
  // ``claim`` is destructured from ``data`` further down (after the loading
  // guard); read the uuid off ``data`` here so these hooks stay above any
  // early return without a TDZ reference to ``claim``.
  const assignablePartners = useClaimAssignablePartners(
    data?.claim?.uuid ?? "",
  );
  const assignToPartner = useAssignClaimToPartner(data?.claim?.uuid ?? "");
  // WPS-160: when the tenant runs the Claim Team card (advanced_routing + a slot
  // catalogue), assignment is consolidated there - the standalone header Assign
  // control is retired and partner assignment folds into the card. Read the team
  // here so the header can show the ACTIVE-STAGE owner by slot.
  const advancedRouting = useFeatureFlags()["advanced_routing"] === true;
  const claimTeam = useClaimTeam(data?.claim?.uuid ?? "", advancedRouting);
  const teamSlots = claimTeam.data?.slots ?? [];
  const hasClaimTeam = advancedRouting && teamSlots.length > 0;
  // Until the team query settles we cannot tell whether assignment lives in
  // the Claim Team card, so the header's standalone Assign control waits
  // rather than flashing in and out.
  const claimTeamPending = advancedRouting && claimTeam.isPending;
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignmentPendingLabel, setAssignmentPendingLabel] = useState<
    string | null
  >(null);
  const [contactEntry, setContactEntry] = useState<ActionLogEntry | null>(null);
  const [contactCustomerOpen, setContactCustomerOpen] = useState(false);
  const [sendPacketOpen, setSendPacketOpen] = useState(false);
  const [reissueOpen, setReissueOpen] = useState(false);
  // WPS-146: label picker and access dialog open from the top panel's More
  // actions menu, so their triggers no longer crowd the header row.
  const [labelPickerOpen, setLabelPickerOpen] = useState(false);
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const claimLabelsConfigured = useCanAddClaimLabels(true);
  const evidenceInputRef = useRef<HTMLInputElement>(null);

  const claimHistory = data?.history ?? [];
  const claimUuidForBanner = data?.claim?.uuid ?? null;
  // Emails fired BY a workflow action already appear on that action's entry
  // (``emails_sent``). A letter someone sent by hand has no action to hang off,
  // so it would be missing from the record entirely - these are the ones the
  // timeline needs, identified by having no workflow action behind them.
  // Declared here with the other unconditional hooks; the hook disables itself
  // until the uuid resolves.
  // Per-user, server-backed so both follow the user across devices.
  // Folding is how a busy Overview gets quieter without hiding anything from
  // anyone else; pinning is how an arrangement someone settled on stops moving.
  const foldedCards = useUserPreference<string[]>(
    "claim-detail-folded-cards",
    [],
  );
  const { orgUuid, tenantSettings, isPartnerAccess } = useOrg();
  // Full cards unless the tenant opts into Sections (Tenant.settings). The
  // per-user switch still overrides either.
  const cardsView = usePinnedView("claim-detail", {
    defaultLayout:
      tenantSettings?.claim_detail_default_layout === "sections"
        ? "sections"
        : "cards",
  });
  const isFolded = (id: string) => foldedCards.value.includes(id);
  const toggleFold = (id: string) =>
    foldedCards.setValue(
      foldedCards.value.includes(id)
        ? foldedCards.value.filter((k) => k !== id)
        : [...foldedCards.value, id],
    );

  const { data: claimComms } = useRecordCommunicationsActivity(
    "claim",
    claimUuidForBanner ?? undefined,
    { page_size: 50 },
  );
  const manualSends = (claimComms?.results ?? []).filter(
    (row) => !row.cause?.workflow_action_log_id,
  );
  const latestConsumerMessageEntry =
    getLatestConsumerMessageEntry(claimHistory);

  // The claim's notifications for the current user are surfaced in a persistent
  // section (NotificationsPanel). We deliberately do NOT mark them read on open -
  // they stay unread (and keep driving the claims-list dot and new/unopened
  // emphasis, per-user and cross-device) until the user explicitly marks them
  // read here, or the standing condition clears itself. See
  // docs/notification-infrastructure.md.
  const { data: unreadNotificationsData } = useNotifications(
    claimUuidForBanner
      ? {
          record_type: "claim",
          record_uuid: claimUuidForBanner,
          status: "unread",
        }
      : undefined,
    { enabled: !!claimUuidForBanner },
  );
  // Read notifications are the collapsible "history". Fetched up front (not
  // lazily) so the panel knows history exists and stays visible once every
  // notification has been read: the section should only disappear when the
  // claim has no notifications at all (read or unread), never merely because
  // unread has dropped to zero.
  const { data: readNotificationsData } = useNotifications(
    claimUuidForBanner
      ? {
          record_type: "claim",
          record_uuid: claimUuidForBanner,
          status: "read",
        }
      : undefined,
    { enabled: !!claimUuidForBanner },
  );
  const unreadNotifications = unreadNotificationsData?.results ?? [];
  const readNotifications = readNotificationsData?.results ?? [];
  const markNotificationsRead = useMarkNotificationsRead();
  const notificationItemAction = useNotificationItemAction();
  const evidenceSectionRef = useRef<HTMLDivElement>(null);

  const markNotificationRead = (uuid: string) => {
    notificationItemAction.mutate(
      { uuid, action: "read" },
      {
        onError: (err) =>
          toastApiError(err, "Failed to mark notification read"),
      },
    );
  };

  const markAllNotificationsRead = () => {
    if (!claimUuidForBanner) return;
    markNotificationsRead.mutate(
      { record_type: "claim", record_uuid: claimUuidForBanner },
      {
        onError: (err) =>
          toastApiError(err, "Failed to mark notifications read"),
      },
    );
  };

  // Dismiss (archive) a notification so it never resurfaces on any surface.
  const dismissNotification = (uuid: string) => {
    notificationItemAction.mutate(
      { uuid, action: "archive" },
      {
        onError: (err) => toastApiError(err, "Failed to dismiss notification"),
      },
    );
  };

  const scrollToEvidence = () => {
    // Where a claim's evidence lives depends on the claim: when the tenant
    // shows an Evidence tab it is the whole surface, so open it rather than
    // sending the reader to Overview - which is where "View documents" landed
    // them, on a tab that does not hold the documents.
    if (visibleTabKeys.includes("evidence")) {
      setActiveTab("evidence");
      return;
    }
    // Otherwise the rows are inline on Overview; if the user is on another tab
    // the anchor is unmounted, so switch first, then scroll once it mounts.
    if (evidenceSectionRef.current) {
      evidenceSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      return;
    }
    setActiveTab("overview");
    window.setTimeout(() => {
      evidenceSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const { data: orgMembers } = useOrgMembers(assignOpen ? orgUuid : null);

  // Themable claim-detail labels + title variant (defaults preserve
  // today's wording/typography for every tenant; tenants opt in via
  // Tenant.settings). All generic - no tenant-specific branches.
  const customerCardLabel =
    tenantSettings?.claim_customer_card_label || "Customer";
  const consumerCardLabel =
    tenantSettings?.claim_consumer_card_label || "Consumer";
  // Label now flows through the translation layer (global default "Evidence",
  // per-tenant override e.g. Teknor "Documentation"). A pre-existing
  // claim_evidence_card_label tenant setting still wins for back-compat.
  const evidenceCardLabel =
    tenantSettings?.claim_evidence_card_label || t("claims.evidenceCardLabel");
  const dealerRowLabel = tenantSettings?.claim_dealer_row_label || "Dealer";
  const titleFontClass = recordTitleFontClass(
    tenantSettings?.record_title_variant,
  );

  // WPS-142 #17: the "Request additional documentation" dialog. (The WP1-59
  // header re-checklist action was removed - #16.)
  const [requestPhotosOpen, setRequestPhotosOpen] = useState(false);

  const handleClaimDetailsSave = async (
    values: Record<string, unknown>,
    reason?: string,
  ) => {
    if (!uuid) return;
    try {
      await editFieldsDirect.mutateAsync({
        uuid,
        attribute_values: values,
        // A card that does not ask for a reason still needs one: the edit
        // endpoint requires it for the audit entry.
        edit_reason:
          reason || t("claims.editReason.default", "Claim details updated"),
      });
      toast.success("Claim updated");
    } catch (err: unknown) {
      toastApiError(err, "Failed to update claim");
    }
  };

  const handleDecisionSave = async (
    values: Record<string, unknown>,
    reason?: string,
  ) => {
    if (!uuid) return;
    try {
      await editFieldsDirect.mutateAsync({
        uuid,
        attribute_values: values,
        edit_reason: reason ?? "",
      });
      toast.success("Decision updated");
    } catch (err: unknown) {
      toastApiError(err, "Failed to update decision");
    }
  };

  const handleDupDialogChange = (open: boolean) => {
    setDupDialogOpen(open);
    if (!open) setDupNote("");
  };

  const handleResolveDuplicates = async () => {
    if (!uuid) return;
    try {
      await resolveDuplicates.mutateAsync({ uuid, note: dupNote });
      toast.success("Duplicates marked as reviewed");
      handleDupDialogChange(false);
    } catch (err: unknown) {
      toastApiError(err, "Failed to resolve duplicates");
    }
  };

  // Acknowledge a single sibling (UAT FR-006). Tracks which chip is in flight
  // so only that control shows the pending state.
  const [reviewingDupUuid, setReviewingDupUuid] = useState<string | null>(null);
  const handleResolveOneDuplicate = async (
    duplicateClaimUuid: string,
    reference: string,
  ) => {
    if (!uuid) return;
    setReviewingDupUuid(duplicateClaimUuid);
    try {
      await resolveDuplicates.mutateAsync({ uuid, duplicateClaimUuid });
      toast.success(`${reference} marked as reviewed`);
    } catch (err: unknown) {
      toastApiError(err, "Failed to mark duplicate reviewed");
    } finally {
      setReviewingDupUuid(null);
    }
  };

  const handleEvidenceFilesSelected = async (files: File[]) => {
    if (!uuid || files.length === 0) return;
    try {
      for (const file of files) {
        const uploaded = await uploadFile.mutateAsync({
          file,
          use_case: "CLAIM_EVIDENCE",
        });
        await addClaimEvidence.mutateAsync({
          uuid,
          file_uuid: uploaded.uuid,
        });
      }
      toast.success(
        files.length === 1 ? "Evidence added" : "Evidence files added",
      );
      await refetch();
    } catch (err: unknown) {
      toastApiError(err, "Failed to add evidence");
    }
  };

  // WPS-175: what this claim still needs, so the next-step line can say so
  // WITHOUT the user having to attempt an action and be refused. Declared
  // here, above the early return, so the hook order is stable.
  const { data: nextStepChecklist } = useClaimChecklist(uuid);

  // ── Loading / not found ───────────────────────────────

  if (isLoading) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <p className="py-20 text-center text-[13px] text-[var(--ink-3)]">
            Loading...
          </p>
        </main>
      </AppShell>
    );
  }

  if (!data || !data.claim) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <p className="py-20 text-center text-[13px] text-[var(--ink-3)]">
            Claim not found
          </p>
        </main>
      </AppShell>
    );
  }

  const { claim, metadata, history } = data;
  const previewLetterList =
    (metadata.preview_letters as
      | { template_id: number; name: string }[]
      | undefined) ?? [];

  // Assignee picker options, grouped by role so a tenant with role groups (or
  // external partner roles) reads as distinct sections. A leading "Unassign"
  // group appears only when the claim is currently assigned. Names only - no
  // avatars, per the shared searchable-select list treatment.
  const assigneeGroups: SearchableSelectGroup[] = (() => {
    const byRole = new Map<
      string,
      { value: string; label: string; searchText: string }[]
    >();
    for (const m of orgMembers ?? []) {
      const role = m.role_name || t("claims.assigneeGroupMembers", "Members");
      const list = byRole.get(role) ?? [];
      list.push({
        value: m.user_uuid,
        label: m.user_name || m.user_email,
        searchText: `${m.user_name ?? ""} ${m.user_email ?? ""}`.trim(),
      });
      byRole.set(role, list);
    }
    const groups: SearchableSelectGroup[] = [...byRole.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([label, options]) => ({ label, options }));
    // Partners group: assigning the claim to a partner (one entry per
    // capability their role holds) forks the partner's branch workflow + parks
    // the claim. The capability comes from the partner's role - no separate
    // picker. Value is a sentinel the change handler routes to the partner
    // assign endpoint.
    const partnerOptions = (assignablePartners.data ?? []).map((p) => ({
      value: `partner::${p.partnership_uuid}::${p.capability}`,
      label: `${p.org_name} - ${p.capability_label}`,
      searchText: `${p.org_name} ${p.capability_label} ${p.role_name}`.trim(),
    }));
    if (partnerOptions.length > 0) {
      groups.push({
        label: t("claims.assigneeGroupPartners", "Partners"),
        options: partnerOptions,
      });
    }
    if (claim.assigned_to) {
      groups.unshift({
        label: t("claims.assigneeGroupCurrent", "Current"),
        options: [
          {
            value: ASSIGNEE_UNASSIGN,
            label: t("claims.assigneeActionUnassign", "Unassign"),
          },
        ],
      });
    }
    return groups;
  })();

  const handleAssigneeChange = (value: string) => {
    // Partner assignment: value is ``partner::<partnership_uuid>::<capability>``.
    // Forks the partner's branch workflow (capability from their role) + parks
    // the claim - the partner then works it from their own desk.
    if (value.startsWith("partner::")) {
      const [partnershipUuid, capability] = value
        .slice("partner::".length)
        .split("::");
      if (!partnershipUuid || !capability) return;
      const partner = (assignablePartners.data ?? []).find(
        (p) =>
          p.partnership_uuid === partnershipUuid && p.capability === capability,
      );
      setAssignmentPendingLabel(
        partner
          ? `${partner.org_name} - ${partner.capability_label}`
          : t("claims.assigneeStateAssigning", "Assigning..."),
      );
      assignToPartner.mutate(
        { partnership_uuid: partnershipUuid, capability },
        {
          onSuccess: () =>
            toast.success(
              t("claims.partnerAssignSuccess", "Assigned to {{name}}", {
                name: partner
                  ? `${partner.org_name} (${partner.capability_label})`
                  : t("claims.assigneeGroupPartners", "Partners"),
              }),
            ),
          onError: (err) =>
            toastApiError(
              err,
              t("claims.partnerAssignFailed", "Failed to assign to partner"),
            ),
          onSettled: () => {
            setAssignmentPendingLabel(null);
            refetch();
          },
        },
      );
      return;
    }
    if (value === ASSIGNEE_UNASSIGN) {
      if (!claim.assigned_to) return;
      setAssignmentPendingLabel(
        t("claims.assigneeStateUnassigning", "Unassigning..."),
      );
      assignClaim.mutate(
        { uuid: claim.uuid, assigned_to_uuid: null },
        {
          onSuccess: () =>
            toast.success(
              t("claims.assigneeSuccessUnassigned", "Claim unassigned"),
            ),
          onError: (err) =>
            toastApiError(
              err,
              t("claims.assigneeErrorUnassignFailed", "Failed to unassign"),
            ),
          onSettled: () => setAssignmentPendingLabel(null),
        },
      );
      return;
    }
    if (value === claim.assigned_to?.uuid) return;
    const member = (orgMembers ?? []).find((m) => m.user_uuid === value);
    setAssignmentPendingLabel(
      member?.user_name ||
        member?.user_email ||
        t("claims.assigneeStateAssigning", "Assigning..."),
    );
    assignClaim.mutate(
      { uuid: claim.uuid, assigned_to_uuid: value },
      {
        onSuccess: (result) =>
          toast.success(
            t("claims.assigneeSuccessAssigned", "Assigned to {{name}}", {
              name: result.assigned_to?.name ?? t("common.user", "user"),
            }),
          ),
        onError: (err) =>
          toastApiError(
            err,
            t("claims.assigneeErrorAssignFailed", "Failed to assign"),
          ),
        onSettled: () => setAssignmentPendingLabel(null),
      },
    );
  };

  // WPS-160: partner assignment folded into the Claim Team card. A partner
  // assignment is capability-based (it forks the partner's branch workflow),
  // not a same-role slot write, so it rides in the card footer rather than a
  // slot row. Reuses the same options + handler as the retired header control;
  // null (hidden) when the org has no assignable partners.
  const partnerAssignOptions = (assignablePartners.data ?? []).map((p) => ({
    value: `partner::${p.partnership_uuid}::${p.capability}`,
    label: `${p.org_name} - ${p.capability_label}`,
    searchText: `${p.org_name} ${p.capability_label} ${p.role_name}`.trim(),
  }));
  const partnerAssignFooter =
    hasClaimTeam && partnerAssignOptions.length > 0 ? (
      <SearchableSelect
        value=""
        onValueChange={handleAssigneeChange}
        groups={[
          {
            label: t("claims.assigneeGroupPartners", "Partners"),
            options: partnerAssignOptions,
          },
        ]}
        searchPlaceholder={t(
          "claims.assigneeSearchPlaceholder",
          "Search people...",
        )}
        emptyMessage={t("claims.assigneeEmptyMessage", "No members found")}
        disabled={assignToPartner.isPending}
        contentClassName="min-w-[240px]"
        renderTrigger={
          <Button
            variant="outline"
            size="sm"
            disabled={assignToPartner.isPending}
            className="flex w-full cursor-pointer items-center justify-center gap-1 rounded-[6px] px-3 py-1.5 text-[11px]"
          >
            <UserPlus className="h-3 w-3" />
            {t("claims.assignToPartner", "Assign to partner")}
          </Button>
        }
      />
    ) : null;

  // What the claim has SENT (letters, emails, SMS) - distinct from the
  // Activity card beside it, which is the workflow history. Renders the
  // per-record communications timeline (WPS-175 requirement 3).
  const communicationsCard = (
    <ClaimCommunicationsCard
      claimUuid={claim.uuid}
      folded={isFolded("communications")}
      onToggleFold={() => toggleFold("communications")}
    />
  );

  // Activity timeline rendered as a standard card so it flows in the same card
  // container as the others (not a full-width row at the bottom). Shared by
  // both Overview layout branches.
  const activityCard = (
    <DetailCard
      title={t("claims.card.activity", "Activity")}
      icon={<Clock className="h-3.5 w-3.5" />}
      collapsible={false}
      folded={isFolded("activity")}
      onToggleFold={() => toggleFold("activity")}
    >
      {history.length > 0 || manualSends.length > 0 ? (
        <div className="max-h-[420px] overflow-y-auto px-4 py-4">
          <div className="pl-5">
            {(() => {
              // One chronological record. Both sources carry an ISO timestamp,
              // so they interleave rather than sitting in two lists a reader
              // has to merge in their head.
              const merged = [
                ...history.map((entry) => ({
                  at: entry.created_at,
                  key: `log-${entry.uuid}`,
                  entry,
                  comm: null as (typeof manualSends)[number] | null,
                })),
                ...manualSends.map((row) => ({
                  at: row.occurred_at,
                  key: `comm-${row.source}-${row.uuid}`,
                  entry: null,
                  comm: row,
                })),
                // Newest first: the question a reader opens this with is
                // "what just happened", and answering it should not mean
                // scrolling to the bottom of a long-running claim.
              ].sort((a, b) => (a.at > b.at ? -1 : a.at < b.at ? 1 : 0));
              return merged.map((item, index) =>
                item.entry ? (
                  <TimelineEntry
                    key={item.key}
                    entry={item.entry}
                    showConnector={index < merged.length - 1}
                    onClick={() => setContactEntry(item.entry!)}
                  />
                ) : (
                  <SentLetterTimelineEntry
                    key={item.key}
                    row={item.comm!}
                    showConnector={index < merged.length - 1}
                  />
                ),
              );
            })()}
          </div>
        </div>
      ) : (
        <p className="px-4 py-3 text-[13px] text-[var(--ink-3)]">
          No activity yet
        </p>
      )}
    </DetailCard>
  );
  const workflowInstance = metadata.workflow_instance;
  const availableActions = metadata.available_actions ?? [];
  // WPS-175 #13: "checklist the claimant" is one of the routes out of a
  // missing-document block. The action already exists on the workflow -
  // this just finds it so the block panel can offer it. Undefined when the
  // state does not have it, and the button then does not render.
  // WPS-175 #14: the LOA decision had three controls on one screen - these
  // header buttons, the next-step line's button, and the Decision panel at
  // the foot of the LOA tab. Three routes to one irreversible decision is
  // three chances to take it by accident, and none of them said which was
  // the real one. The Decision panel keeps it: it is the only one that
  // captures the rationale the deny and request-changes paths require.
  // The keys come from the cascade's own payload - a workflow names them.
  const loaDecisionActionKeys = new Set(
    ((metadata.loas as LOADimension[] | undefined) ?? []).flatMap(
      (dimension) =>
        [
          dimension.approve_action_key,
          dimension.return_action_key,
          dimension.deny_action_key,
        ].filter((key): key is string => !!key),
    ),
  );
  const checklistAction = availableActions.find(
    (a) => a.key === "send_checklist" || a.key === "request_more_docs",
  );

  // WPS-175: what this claim is waiting for, in one line.
  //
  // Not in any requirement. It is here because the screen showed eleven
  // workflow states and nine same-weight action buttons and never said what
  // the reader was being asked to do - the LOA panel even says "review the
  // totals and continue" when no button is called Continue. A claim should
  // be able to state its own next step.
  //
  // The primary action is the one the workflow marks primary, else the
  // lowest-order STATE action (see pickNextStep).
  // A plain derivation, NOT useMemo: this sits after an early return, so a
  // hook here would be called conditionally and break the hook order.
  const offeredActions = buttonActions(availableActions);
  const nextStepPrimary = pickNextStep(offeredActions);
  const flagActionLabel = claim.is_flagged ? "Remove Flag" : "Flag Claim";
  const isTerminal = workflowInstance?.current_state_is_terminal ?? false;
  const canEdit = can("claims.edit.global") && !isTerminal;
  // Labels stay editable once the claim closes (WPS-300): a tag for finding
  // the claim, not a change to what was decided.
  const canLabel = can("claims.edit.global");
  // Duplicate review uses the same permission the backend enforces
  // (ClaimResolveDuplicatesView -> _ClaimContactPermission: edit.global OR
  // edit.acl), so any role that can edit the claim - including a non-admin
  // role holding only edit.acl on its assigned claims - can clear its
  // duplicate review. The gate previously required edit.global only, which
  // hid "Mark reviewed" from edit.acl-only roles.
  const canReviewDuplicates =
    can("claims.edit.global") || can("claims.edit.acl");
  // Assigning/reassigning a claim is an edit op: the backend resolves the
  // claim under the "edit" permission (ClaimAssignView -> get_claim_by_uuid,
  // permission="edit"), so a view-only role hitting Assign gets a "not found"
  // error. Gate the control on the same permission the server enforces.
  const canAssignClaim = can("claims.edit.global") || can("claims.edit.acl");
  const settlement = metadata.settlement as SettlementData | undefined;
  const currentStateKey = workflowInstance?.current_state_key ?? "";
  const currentStateAttributeValues =
    (workflowInstance?.current_state_attribute_values as
      Record<string, unknown> | undefined) ?? {};
  // WPS-175 #14: either LOA sign-off stage counts, asked of the state's own
  // attributes rather than a key list - see isInternalLoaState.
  const atLoaDecision = isInternalLoaState(currentStateAttributeValues);
  const headerActions = atLoaDecision
    ? offeredActions.filter((a) => !loaDecisionActionKeys.has(a.key))
    : offeredActions;
  const nextStepOwner =
    attributeText(currentStateAttributeValues?.role_label) || null;
  // Required and not yet satisfied. Named in the next-step line because a
  // user who cancels out of the block message otherwise has no way to see
  // what is outstanding - the claim only told them when they tried again.
  const nextStepOutstanding = (nextStepChecklist?.items ?? [])
    // Mirrors the backend gate: a mailed sample never holds the claim.
    .filter(
      (item) => item.is_required && !item.satisfied && item.kind !== "sample",
    )
    .map((item) => item.label);
  // WP1-59 / WPS-142 #12: prominent header "Request additional documentation"
  // action. Shown wherever the workflow opts the state in via the
  // ``allow_document_request`` state attribute (Lab Analysis + Material & Labor
  // Review) - never a hardcoded state key - and the user holds a review-stage
  // action capability. The endpoint enforces the same lab-OR-rs capability
  // server-side. Hooks (useClaimChecklist/useRequestChecklist/useState) are
  // declared at the top of the component (before the loading early-return);
  // these are derived.
  const labChecklistActionsEnabled =
    Boolean(currentStateAttributeValues.allow_document_request) &&
    (can("claims.lab_actions") || can("claims.rs_actions"));
  // Per-payment "Mark Processed" is gated by both the state opting in
  // (allow_payment_processing EAV) AND the actor holding the pay-claim
  // permission - so a read-only role on a settlement-audit claim sees
  // the payments table but not the processing buttons.
  const allowPaymentProcessing =
    Boolean(currentStateAttributeValues.allow_payment_processing) &&
    can("claims.pay_claim");
  // Which settlement panel this state shows (``settlement_panel`` EAV).
  const settlementPanel = currentStateAttributeValues.settlement_panel as
    | string
    | undefined;
  // The 'Reissue upload link' action is offered ONLY at the state that issues
  // the consumer evidence upload link (the intake / "Awaiting Documents"
  // state), not at every pre-decision state. Opt-in and data-driven via the
  // ``allow_evidence_reissue`` state EAV a workflow seeds on that state; no
  // hardcoded state keys. Workflows that never issue a consumer upload link
  // (e.g. a group claim) simply do not set it, so the action never shows.
  const evidenceReissueAllowed = Boolean(
    currentStateAttributeValues.allow_evidence_reissue,
  );
  // Entity-gated card visibility. Each related-entity card renders only when
  // the actor holds that entity's view permission, so a partner role (e.g.
  // Inspector/Assessor/Repairer) configured with a restricted entity set sees
  // only its allowed cards - Consumer by default; Product/Warranty hidden
  // unless granted. Internal roles hold all of these, so their view is
  // unchanged. (Decision/Settlement stay gated by claims.view_decisions.)
  // Consumer card stays visible to all who can open the claim (the default
  // partner-visible card); Product/Warranty/Decision are entity-gated below.
  const canViewProductCard =
    can("products.view.global") || can("products.view.acl");
  const canViewWarrantyCard = can("warranties.view");
  // Decision + financial cards (Decision, Pricing Guidance, Supplier Info) are
  // internal-only: partner roles never hold claims.view_decisions, so the
  // cards are hidden entirely (server-side strip blanks the values too).
  // Not a hook: this sits after the component's early returns, and the set is
  // cheap enough that memoising it is not worth the ordering constraint.
  const overviewHiddenCards = new Set(
    (() => {
      const wf = (metadata.workflow_metadata ?? {}) as Record<string, unknown>;
      const hidden = wf.overview_hidden_cards;
      return Array.isArray(hidden)
        ? hidden.filter((c): c is string => typeof c === "string" && !!c)
        : [];
    })(),
  );
  // WPS-166: the curated set is handed to the grid as its DEFAULT hidden cards
  // rather than being applied by each card's own render condition, so a user
  // can put one back. Order is stable so the memo inside the grid does not
  // churn on every render.
  // A workflow that curates the Product card away keeps it on screen while the
  // holding is unidentified: that card carries the ONLY "Identify product"
  // entry point, so hiding it there would strand the claim with no way to name
  // the product. Keyed off the holding's own flag, never a tenant or claim
  // type. (No OC workflow curates it today - WPS-149 renders the Installed
  // Components on it - but the guard belongs with the mechanism, not the
  // config that happens to use it.)
  const overviewDefaultHiddenCards = [...overviewHiddenCards]
    .filter((card) => !(card === "product" && linkedHolding?.unidentified))
    .sort();
  // A workflow that fixes the partner (`partner_locked`) shows the partner card
  // read-only and leaves the holding actions that would swap the partner out of
  // the Product actions menu. The action keys come from the backend.
  const partnerLock = resolvePartnerLock(metadata?.partner_lock);
  const holdingActionExclusions = partnerLock.hidden_holding_action_keys
    .length
    ? partnerLock.hidden_holding_action_keys
    : undefined;
  // Each workflow curates its own defaults, so a user's hide/restore choices are
  // kept per workflow: restoring a card on one claim type must not unhide it on
  // another.
  const overviewCardScope = workflowInstance?.workflow_uuid || undefined;
  // Cards a workflow pins to a column (`overview_pinned_cards`, card key ->
  // column index); none by default, so every other workflow packs freely.
  const overviewPinnedColumns: Record<string, number> = (() => {
    const wf = (metadata.workflow_metadata ?? {}) as Record<string, unknown>;
    const pins = wf.overview_pinned_cards;
    if (!pins || typeof pins !== "object" || Array.isArray(pins)) return {};
    return Object.fromEntries(
      Object.entries(pins as Record<string, unknown>).filter(
        (entry): entry is [string, number] =>
          Number.isInteger(entry[1]) && (entry[1] as number) >= 0,
      ),
    );
  })();
  // Hard hide for cards outside the grid's restore control (e.g. BFS contractor contact).
  const isOverviewCardHidden = (card: string) => overviewHiddenCards.has(card);

  // WPS-175 #2: the Decision card renders from four places in this component.
  //
  // WPS-166 splits what used to be one flag into its two different jobs.
  // PERMISSION is a hard gate - a partner must never receive the decision, so
  // the card is not rendered at all and the serializer strips the values too.
  // The workflow's `overview_hidden_cards` is only a DEFAULT: the card is
  // rendered and hidden by the grid, so someone who wants it back can restore
  // it. Folding the two together (as this did) made the curated default
  // permanent and unrecoverable.
  const canViewDecisionCards = can("claims.view_decisions");
  const requestedDefaultTab =
    (currentStateAttributeValues.workspace_category as string | undefined) ??
    "overview";
  // Fall back to Overview if the workflow's currently-configured
  // workspace_category isn't represented by any visible tab (e.g.
  // current state has workspace_category="lab" but the lab_analysis
  // entity isn't registered for this tenant).
  // An explicit ``?tab=`` request wins (when it maps to a visible tab), then
  // the state's workspace category, then Overview.
  const defaultTab = selectableTabKeys.includes(tabParam)
    ? tabParam
    : selectableTabKeys.includes(requestedDefaultTab)
      ? requestedDefaultTab
      : "overview";

  // ── Derive claim detail / decision field items ────────

  // One source for the schema's field metadata, in the two shapes this
  // component needs: `fieldMeta` is the list, `metadataMap` is the index over
  // it. Helpers that scan for a tag or a declared role take the list;
  // existence and field_type lookups take the map.
  const fieldMeta = metadata.editable_field_metadata ?? [];
  const metadataMap = new Map<string, ActionFieldMeta>();
  for (const f of fieldMeta) {
    metadataMap.set(f.code_name, f);
  }

  // WPS-144: attributes tagged ``configuration_data.contact_card`` (the
  // submitter's name / email / phone) are surfaced in the Claim Contacts card,
  // so they must NOT also render in the generic Claim Details card or the
  // inspection Property & Contact panel. Config-driven and tenant-agnostic -
  // the set is whatever the schema tags, never a hardcoded field list.
  const contactCardCodes = new Set<string>();
  for (const f of fieldMeta) {
    const card = (f.configuration_data as Record<string, unknown> | undefined)
      ?.contact_card;
    if (card && typeof card === "object") contactCardCodes.add(f.code_name);
  }

  // WPS-102 AC6: attributes the schema tags as hidden from Claim Details are
  // surfaced on their own card instead (the lab's Quantity Approved replaced
  // the claimant's Quantity Impacted). See `hiddenFromClaimDetailsCodes`.
  const hiddenFromClaimDetails = hiddenFromClaimDetailsCodes(fieldMeta);

  // WPS-175: attributes a seed has retired are deactivated rather than
  // deleted, so historic values survive - but they must not keep rendering as
  // live claim fields. The submit form already skips them on the same flag;
  // without this the Claim Details card still listed Purchase Amount long
  // after the schema dropped it.
  const deprecatedCodes = new Set<string>();
  for (const f of fieldMeta) {
    const config = f.configuration_data as Record<string, unknown> | undefined;
    if (config?.deprecated) deprecatedCodes.add(f.code_name);
  }

  // WPS-102 AC5: fields the schema pins to the top of Claim Details, so the
  // lab's decision is not buried under "Show N more" at settlement.
  const pinnedDetailCodes = pinnedClaimDetailCodes(fieldMeta);
  const schemaOrder = new Map<string, number>(
    fieldMeta.map((f, i) => [f.code_name, i]),
  );

  // The lab card's composition is the schema's, not this file's: which
  // attributes appear, in what order, which are mandatory, and which are
  // read-only context all come from `configuration_data.lab_card`.
  const labCardSpec = labCardFields(fieldMeta);
  // WPS-204 #7: the Supplier card's rows are the schema's `supplier_card` tags.
  const supplierCardSpec = supplierCardFields(fieldMeta);
  const supplierCardCodes = new Set(supplierCardSpec.map((f) => f.code_name));

  // WPS-102: which attribute plays which part, from the schema's
  // `semantic_role` tags. Panels ask by role so no view names an attribute.
  const labSemanticCodes = {
    labVerdict: codeForSemanticRole(fieldMeta, "lab_verdict"),
    approvedQuantity: codeForSemanticRole(fieldMeta, "approved_quantity"),
    approvedQuantityDetail: codeForSemanticRole(
      fieldMeta,
      "approved_quantity_detail",
    ),
    labNarrative: codeForSemanticRole(fieldMeta, "lab_narrative"),
    // WPS-215 AC4: the major defect category the LOA Review card reports as
    // the Determination. No schema tags `defect_major` yet, so the panel falls
    // back to the platform code name - same contract as DEFECT_ROLE_SLOTS.
    majorCategory: codeForSemanticRole(fieldMeta, "defect_major"),
  };

  // WPS-164 5A: an outcome or lab verdict the schema marks `show_while_open`
  // (a lab deny) is announced on the still-open claim, with the field tagged as
  // its reason.
  const decisionNotice = (() => {
    const values = (claim.attribute_values ?? {}) as Record<string, unknown>;
    const optionOf = (code: string | null | undefined) => {
      if (!code) return undefined;
      const options = metadataMap.get(code)?.configuration_data?.options as
        | {
            value?: unknown;
            label?: string;
            notice_label?: string;
            show_while_open?: boolean;
          }[]
        | undefined;
      return options?.find((o) => String(o.value) === String(values[code]));
    };
    const outcome = ["outcome", codeForSemanticRole(fieldMeta, "lab_verdict")]
      .map(optionOf)
      .find((o) => o?.show_while_open);
    if (!outcome) return null;
    const reasonCode = codeForSemanticRole(fieldMeta, "decision_reason");
    const reasonValue = reasonCode ? values[reasonCode] : undefined;
    const reason =
      optionOf(reasonCode)?.label ??
      (reasonValue != null && reasonValue !== "" ? String(reasonValue) : "");
    return {
      outcome: (
        outcome.notice_label ??
        outcome.label ??
        String(outcome.value)
      ).toLowerCase(),
      reason,
    };
  })();

  const isHoldingless = !claim.holding_uuid;
  const claimDetailEntries = Object.entries(claim.attribute_values)
    .filter(([key]) => !DECISION_FIELDS.has(key))
    // `labels` is a triage tag rendered as header chips (see
    // ClaimLabelsControl), not a generic claim-detail form field.
    .filter(([key]) => key !== "labels")
    // WPS-145: the communication hold has its own switch on the Overview
    // (CommunicationHoldControl). Keyed off the API-supplied code_name so
    // no attribute name is hardcoded here.
    .filter(([key]) => key !== claim.communication_hold?.code_name)
    // For holdingless claims, supplier/contractor/property fields are
    // rendered by the SupplierInfoCard - filter them from the generic
    // Claim Details card so info isn't duplicated.
    .filter(([key]) => !isHoldingless || !SUPPLIER_CARD_FIELDS.has(key))
    // Supplier card rows render there, on every claim type (WPS-204 #7).
    .filter(([key]) => !supplierCardCodes.has(key))
    // End-consumer fields render in the Consumer card, and claim-level
    // product fields (serial number) on the Product card — filter both
    // out of the generic Claim Details list to avoid duplication.
    .filter(([key]) => !CONSUMER_CARD_FIELDS.has(key))
    .filter(([key]) => !PRODUCT_CLAIM_FIELDS.has(key))
    // Submitter contact fields belong to the Claim Contacts card (WPS-144).
    .filter(([key]) => !contactCardCodes.has(key))
    // Schema-tagged hidden fields (WPS-102).
    .filter(([key]) => !hiddenFromClaimDetails.has(key))
    // Retired attributes (WPS-175).
    .filter(([key]) => !deprecatedCodes.has(key))
    .sort(([a], [b]) => {
      // Pinned first, bottom-fields last, everything else in payload order.
      const rank = (code: string) =>
        pinnedDetailCodes.has(code)
          ? -1
          : CLAIM_BOTTOM_FIELDS.has(code)
            ? 1
            : 0;
      const byRank = rank(a) - rank(b);
      if (byRank !== 0) return byRank;
      // Within the pinned block, follow the order the schema authored, so the
      // lab reads Determination -> Quantity Approved -> Square Count rather
      // than whatever order the payload happened to serialise them in.
      if (pinnedDetailCodes.has(a)) {
        return (schemaOrder.get(a) ?? 0) - (schemaOrder.get(b) ?? 0);
      }
      return 0;
    });

  const claimDetailItems: CardFieldItem[] = claimDetailEntries.map(
    ([key, value]) => buildClaimFieldItem(key, value, metadataMap),
  );

  // WPS-151: the Lab Findings card is built from the claim SCHEMA, not from
  // `claim.attribute_values`. The API omits attributes that have no value
  // yet, so filtering `claimDetailItems` left the card empty on a fresh
  // claim and it fell back to untyped placeholder rows - which is why
  // Determination rendered as a text box instead of its Full / Partial
  // dropdown. Reading `metadataMap` (every schema attribute, with
  // field_type + options) means the inputs are correctly typed from the
  // start. Order here is the order Penny asked for.
  // Defect Classification is built from the SCHEMA for the same reason Lab
  const labFindingItems: CardFieldItem[] = labCardSpec
    .filter((spec) => metadataMap.has(spec.code_name))
    .map((spec) => {
      const code = spec.code_name;
      const values = claim.attribute_values as
        Record<string, unknown> | undefined;
      let item = buildClaimFieldItem(code, values?.[code] ?? null, metadataMap);

      // Mandatory on this card only - schema-level `required` is validated
      // against the whole schema on every write and would block claim
      // creation (the WPS-151 `lab_findings` regression).
      if (spec.required && !item.field.required) {
        item = { ...item, field: { ...item.field, required: true } };
      }

      // Context the lab reads rather than edits, and rows that should vanish
      // rather than render empty.
      if (spec.readOnly) item = { ...item, readOnly: true };
      if (spec.hideWhenEmpty) item = { ...item, hideWhenEmptyDisplay: true };

      // WPS-151: a field may inherit its STARTING value from another attribute
      // via `configuration_data.default_from` - the lab's Determination opens on
      // the claimant's Quantity Impacted so the technician confirms or revises
      // rather than answering from scratch. Config-driven: the source field is
      // whatever `default_from` names, so this reads for any future pairing and
      // hardcodes no attribute names.
      //
      // Applied as `editValue`, not `value`: the read-only row must keep showing
      // what the lab actually recorded, or the card would assert a determination
      // nobody made - which is the claimed-vs-determined conflation this whole
      // field split exists to avoid.
      const defaultFrom = (
        item.field.configuration_data as Record<string, unknown> | undefined
      )?.default_from;
      if (
        typeof defaultFrom === "string" &&
        (item.value === null || item.value === undefined || item.value === "")
      ) {
        const inherited = values?.[defaultFrom];
        if (inherited !== null && inherited !== undefined && inherited !== "") {
          return { ...item, editValue: inherited };
        }
      }
      return item;
    });

  // WPS-178: resolve the two role slots against what THIS schema declares
  // (`configuration_data.semantic_role`), falling back to the platform code
  // name. Uninstalled answers `defect_product` with `product_variant`;
  // Installed / Workmanship / Job Stop answer it with `product_name`.
  const defectRoleCodes: Record<string, string | null> = {};
  for (const [slot, role] of Object.entries(DEFECT_ROLE_SLOTS)) {
    const declared = codeForSemanticRole(fieldMeta, role);
    defectRoleCodes[slot] =
      declared && metadataMap.has(declared)
        ? declared
        : metadataMap.has(slot)
          ? slot
          : null;
  }
  const defectProductCode = defectRoleCodes.product_name;
  const defectColourCode = defectRoleCodes.color;
  const defectQuantityCode = defectRoleCodes.defect_quantity;
  // WPS-258: on either Defect Details card, an unset Product / Colour starts
  // from the registration, shown and offered in the edit popup.
  const claimProductUuid = pickedUuid(
    defectProductCode
      ? (claim.attribute_values as Record<string, unknown> | undefined)?.[
          defectProductCode
        ]
      : null,
  );
  const fromRegistration = (item: CardFieldItem): CardFieldItem =>
    startFromRegistration(
      item,
      { product: defectProductCode, colour: defectColourCode },
      claim.registration_product_pick,
      claimProductUuid,
    );

  // WPS-165: the Lab-tab "Defect Details" card (renamed from "Defect
  // Classification") is built from the schema the same way as Lab Findings,
  // via `configuration_data.defect_card`, so a field with no value yet - Minor
  // Defect Category, Plant Code - still renders and is editable during Lab
  // Analysis rather than being silently absent (the reported failure).
  const defectCardSpec = withColourBesideProduct(
    defectCardFields(fieldMeta),
    defectProductCode,
    defectColourCode,
  );
  const classificationItems: CardFieldItem[] = defectCardSpec
    .filter((spec) => metadataMap.has(spec.code_name))
    .map((spec) => {
      const code = spec.code_name;
      const values = claim.attribute_values as
        Record<string, unknown> | undefined;
      let item = buildClaimFieldItem(code, values?.[code] ?? null, metadataMap);
      if (spec.required && !item.field.required) {
        item = { ...item, field: { ...item.field, required: true } };
      }
      if (spec.readOnly) item = { ...item, readOnly: true };
      if (spec.hideWhenEmpty) item = { ...item, hideWhenEmptyDisplay: true };
      return fromRegistration(item);
    });

  // Sample Tracking is schema-built too: on a claim's first Lab entry no sample
  // field has a value, and the untyped fallback rendered Plant Code as free text.
  const sampleItems: CardFieldItem[] = [...LAB_SAMPLE_FIELDS]
    .filter((code) => metadataMap.has(code))
    .map((code) =>
      buildClaimFieldItem(
        code,
        (claim.attribute_values as Record<string, unknown> | undefined)?.[
          code
        ] ?? null,
        metadataMap,
      ),
    );

  // The sectioned design groups claim attributes into Defect Details +
  // Claim Context + a catch-all "Claim Details" so no attribute is dropped.
  // Computed unconditionally (cheap) but only rendered in the sectioned design.
  // WPS-175 #2: cards a workflow curates off its Overview, authored on
  // `Workflow.metadata.overview_hidden_cards`. Config rather than a claim-type
  // branch - this asks whether a card is hidden and never names a schema.

  // WPS-175 #2(a): built from the SCHEMA's ordered Defect Details fields
  // (`overview_card_order`), not by filtering `claim.attribute_values`. Same reason as the Lab Findings card
  // above - the API omits attributes that have no value yet, so Minor Defect
  // Category simply did not appear until somebody had already set it, which is
  // impossible when this card is where you set it. Building from the schema
  // also means the input is correctly typed (its dependent Minor options key
  // off Major) rather than falling back to an untyped placeholder row.
  // Fields the schema marks read-only on this card (WPS-178 #2 uses it for the
  // contractor's defect `reason`), declared rather than matched by name.
  const defectReadOnlyCodes = readOnlyOnDefectDetailsCodes(fieldMeta);
  const defectItems: CardFieldItem[] = fieldMeta
    .filter((f) => defectCardOrder(f) !== null && metadataMap.has(f.code_name))
    .sort(byDefectCardOrder)
    .map((f) => f.code_name)
    .map((code) => {
      const values = claim.attribute_values as
        Record<string, unknown> | undefined;
      const item = buildClaimFieldItem(
        code,
        values?.[code] ?? null,
        metadataMap,
      );
      return defectReadOnlyCodes.has(code)
        ? { ...item, readOnly: true, hideWhenEmptyDisplay: true }
        : item;
    });
  const contextItems = claimDetailItems.filter((i) =>
    isClaimContextField(i.field),
  );
  // WPS-146 #4: fields the schema routes to a card other than Claim Context.
  // `defect` appends to Defect Details (the lab's own findings, sample dates,
  // plant and production codes - the card that already owns the defect);
  // `homeowner` appends to Homeowner & Property (the mailing address the
  // cheque goes to). Both cards render these AFTER their declared order, so a
  // routed field never displaces a field the card was designed around.
  // `defectItems` already holds the card's own ordered fields; exclude them so
  // none renders twice.
  const defectItemCodes = new Set(defectItems.map((i) => i.field.code_name));
  const routedDefectItems = claimDetailItems.filter(
    (i) =>
      defectCardOrder(i.field) === null &&
      !defectItemCodes.has(i.field.code_name) &&
      overviewCardSlot(i.field) === "defect",
  );
  const routedHomeownerItems = claimDetailItems.filter(
    (i) => overviewCardSlot(i.field) === "homeowner",
  );
  // What is left over. A field lands here only while it has no declared home -
  // which is exactly what the catch-all is for, and why this card must never
  // be hidden instead of emptied: an attribute added tomorrow is visible the
  // day it populates, not silently lost. Once every attribute a schema
  // populates is routed, this list is empty and the card does not render.
  const otherClaimItems = claimDetailItems.filter(
    (i) =>
      !defectItemCodes.has(i.field.code_name) &&
      overviewCardSlot(i.field) === null,
  );

  // Follow-up cadence for awaiting_docs - rendered in the sectioned design's
  // Overview (preserved from the old Resolution tab). Standard renders its own
  // copy inside ClaimResolutionPanel, so this is only read in that branch.
  const followUpChecklistEntry = history.find(
    (e) => e.action_key === "send_checklist",
  );
  const followUpMilestones = followUpChecklistEntry
    ? (() => {
        const sent = new Date(followUpChecklistEntry.created_at);
        const at = (days: number) =>
          new Date(sent.getTime() + days * 86_400_000);
        return [
          { day: 0, label: "Checklist sent", date: sent, done: true },
          {
            day: 21,
            label: "First follow-up",
            date: at(21),
            done: followUpNowMs > at(21).getTime(),
          },
          {
            day: 60,
            label: "Final notice",
            date: at(60),
            done: followUpNowMs > at(60).getTime(),
          },
          {
            day: 75,
            label: "Auto-close",
            date: at(75),
            done: followUpNowMs > at(75).getTime(),
          },
        ];
      })()
    : null;

  const decisionEntries = Object.entries(claim.attribute_values)
    .filter(([key]) => DECISION_FIELDS.has(key))
    .sort(([a], [b]) => {
      const ai = DECISION_ORDER.indexOf(a);
      const bi = DECISION_ORDER.indexOf(b);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

  const decisionItemEntries = [...decisionEntries];
  const existingDecisionKeys = new Set(decisionEntries.map(([k]) => k));
  // Phase 5: editable-field source moved from per-state EAV to
  // FieldVisibilityRule evaluation; the API surfaces it as
  // ``workflowInstance.current_state_editable_fields``.
  const editableFieldsFromRules =
    workflowInstance?.current_state_editable_fields ?? [];
  for (const key of DECISION_ORDER) {
    if (
      !existingDecisionKeys.has(key) &&
      (metadataMap.has(key) || editableFieldsFromRules.includes(key))
    ) {
      decisionItemEntries.push([key, ""]);
    }
  }

  // TA-55: a value the approval gate already judged is read-only; a
  // correction goes through Revise Decision so the gate runs again.
  const lockedDecisionFields = new Set(
    workflowInstance?.current_state_locked_fields ?? [],
  );
  const decisionItems: CardFieldItem[] = decisionItemEntries.map(
    ([key, value]) => {
      const item = buildClaimFieldItem(key, value, metadataMap, {
        positive: key === "approved_amount",
      });
      return lockedDecisionFields.has(key) ? { ...item, readOnly: true } : item;
    },
  );
  // What the Decision card shows: the decision, or a note that none is made.
  const decisionCardItems: CardFieldItem[] =
    decisionItems.length > 0
      ? decisionItems
      : [
          {
            field: defineField({
              code_name: "_awaiting",
              display_name: "Status",
              field_type: "TEXT",
            }),
            value: "Awaiting review decision",
            readOnly: true,
          },
        ];

  // ── Derive host / product / warranty rows ──────────

  const configuredHostRows = linkedHolding
    ? registrationCardDisplayColumns(hostEntity).map((column) => ({
        key: column.id,
        label: displayColumnLabel(column),
        value: formatEntityCardValue(
          resolveHoldingRegistrationCardValue(linkedHolding, column, {
            entityTypes,
          }),
        ),
      }))
    : [];
  // Drive host-card visibility from the entity-display signal so the
  // card only surfaces for tenants that actually model a host. It shows
  // when either:
  //   1. The host entity has a configured registration_card display, OR
  //   2. The claim payload carries legacy host fallback data.
  // Tenants without a host concept (no host entity configured, no
  // payload data) see nothing; tenants with a configured host entity
  // are unaffected.
  const hasConfiguredHostDisplay = hasRegistrationCardDisplay(hostEntity);
  const hasLegacyHostData =
    Boolean(claim.vehicle_registration) ||
    Boolean(claim.vehicle_make) ||
    Boolean(claim.vehicle_model) ||
    Boolean(claim.vehicle_colour) ||
    Boolean(claim.vehicle_vin) ||
    (claim.host_attributes ?? []).length > 0;
  const showHostCard = hasConfiguredHostDisplay || hasLegacyHostData;
  const configuredProductRows = linkedHolding
    ? registrationCardDisplayColumns(holdingEntity)
        .filter((column) => !column.id.startsWith("transaction_attribute:"))
        // Scope template-projected HOLDING captures (e.g. the installed-
        // component questions defined on the roofing template) to holdings on
        // that template, so a non-roofing claim's Product card does not render
        // them as empty rows (WPS-149).
        .filter((column) =>
          holdingRegistrationCardColumnApplies(
            column,
            linkedHolding,
            entityTypes,
          ),
        )
        .map((column) => ({
          key: column.id,
          label: displayColumnLabel(column),
          // Coerce to a plain string (same as the host rows) so partner/
          // organisation values like the dealer render as clean text
          // rather than split, mis-styled badge pills.
          value: formatEntityCardValue(
            resolveHoldingRegistrationCardValue(linkedHolding, column, {
              entityTypes,
            }),
          ),
        }))
    : [];

  // WPS-165 (6-1): the Overview "Defect Details" card must also show the
  // defective product's Product / Colour / Squares. These describe the
  // registration, not the claim schema, so they are NOT in `claim.attribute_values`
  // (hence absent from `defectItems`). Source them from the same
  // tenant-configured registration card the Product card renders
  // (`configuredProductRows`) for Colour/Squares, and the claim's product name
  // for Product, as read-only rows (they are edited on the registration, not
  // here). `configuredProductRows` is used so a tenant that labels these
  // differently still resolves - no hardcoded attribute value.
  const _defectRegRow = (re: RegExp): string => {
    const match = configuredProductRows?.find(
      (r) =>
        r &&
        typeof r.label === "string" &&
        re.test(r.label) &&
        r.value != null &&
        String(r.value).trim() !== "",
    );
    return match ? String(match.value) : "";
  };
  const _defectHoldingAttr = (codeName: string): string => {
    const av = linkedHolding?.attribute_values as unknown;
    let raw: unknown = "";
    if (Array.isArray(av)) {
      raw = (av as Array<{ code_name?: string; value?: unknown }>).find(
        (a) => a?.code_name === codeName,
      )?.value;
    } else if (av && typeof av === "object") {
      raw = (av as Record<string, unknown>)[codeName];
    }
    return raw == null || raw === "" ? "" : String(raw);
  };
  const defectRegistrationRows: CardFieldItem[] = [];
  // `code` is a stable, locale-independent key for the synthetic (read-only)
  // field; `label` is the translated, user-facing display name.
  const _pushDefectRow = (code: string, label: string, value: string) => {
    if (value && value.trim()) {
      defectRegistrationRows.push({
        field: defineField({
          code_name: `__defect_${code}`,
          display_name: label,
          field_type: "TEXT",
        }),
        value,
        readOnly: true,
      });
    }
  };
  // WPS-178: only fall back to the synthetic read-only registration row when
  // the schema has no real, editable field playing that role. A schema that
  // does renders the editable one instead - the read-only pair was exactly why
  // the edit popup had nothing to offer. Keyed off the declared role, so a
  // tenant whose product field is named anything at all still resolves.
  if (!defectProductCode) {
    _pushDefectRow(
      "product",
      t("claims.product", "Product"),
      claim.product_line_name ||
        claim.product_name ||
        claim.product_short_name ||
        "",
    );
  }
  // Only a tenant whose Product card has a colour row has colours; for anyone
  // else the product's name is not a colour and must not be shown as one.
  if (
    !defectColourCode &&
    configuredProductRows.some((row) => COLOUR_ROW_LABEL.test(row.label))
  ) {
    _pushDefectRow(
      "colour",
      t("claims.colour", "Colour"),
      _defectRegRow(COLOUR_ROW_LABEL) || claim.product_short_name || "",
    );
  }
  if (!defectQuantityCode) {
    _pushDefectRow(
      "quantity",
      t("claims.quantity", "Quantity"),
      _defectHoldingAttr("roof_total_squares") ||
        _defectRegRow(QUANTITY_ROW_LABEL),
    );
  }
  // WPS-151 #2: the claim's own Quantity starts from the registration
  // attribute its schema names in `default_from_registration`, until edited.
  const _defectQuantitySource = defectQuantityCode
    ? metadataMap.get(defectQuantityCode)?.configuration_data
        ?.default_from_registration
    : undefined;
  const _defectQuantityFallback =
    typeof _defectQuantitySource === "string"
      ? _defectHoldingAttr(_defectQuantitySource)
      : "";
  // WPS-178: a claim that has not overridden Product / Colour yet still shows
  // the registration's values, so replacing the old read-only rows with
  // editable ones does not blank the card.
  //
  // Set `displayValue`, never `value`: these are ENTITY_SEARCH pickers whose
  // form value must stay an object (or null). Writing the registration's plain
  // string into `value` makes the card read correctly but fails the edit
  // form's schema ("expected object, received string") and blocks every save.
  // WPS-258: where the registration's product is known as a picker value, the
  // popup opens on it (`fromRegistration`); this string is the fallback.
  const _defectProductFallback =
    claim.product_line_name ||
    claim.product_name ||
    claim.product_short_name ||
    "";
  // Keyed by the role-resolved code names, so this never names an attribute.
  const _defectRegFallback: Record<string, string> = {};
  if (defectProductCode) {
    _defectRegFallback[defectProductCode] = _defectProductFallback;
  }
  if (defectColourCode) {
    _defectRegFallback[defectColourCode] =
      _defectRegRow(COLOUR_ROW_LABEL) || claim.product_short_name || "";
  }
  if (defectQuantityCode) {
    _defectRegFallback[defectQuantityCode] = _defectQuantityFallback;
  }
  const defectItemsWithFallback: CardFieldItem[] = defectItems.map((item) => {
    const started = fromRegistration(item);
    if (started !== item) return started;
    const fallback = _defectRegFallback[item.field.code_name];
    const isEmpty =
      item.value == null ||
      (typeof item.value === "string" && item.value.trim() === "");
    if (!fallback || !isEmpty) return item;
    // A number can open the popup on the registration's figure, so the edit
    // starts from what the card shows; a picker cannot (see above).
    const startsFromFallback =
      item.field.code_name === defectQuantityCode &&
      Number.isFinite(Number(fallback));
    return startsFromFallback
      ? { ...item, displayValue: fallback, editValue: Number(fallback) }
      : { ...item, displayValue: fallback };
  });

  const defectDetailItems: CardFieldItem[] = [
    ...defectRegistrationRows,
    ...defectItemsWithFallback,
    ...routedDefectItems,
  ];

  // WPS-203 AC2: on the merged Overview the Product card drops what Defect
  // Details already shows, and is not rendered once nothing is left.
  const defectDetailsShown =
    claimDetailItems.length > 0 &&
    defectDetailItems.length > 0 &&
    !overviewHiddenCards.has("defect-details");
  const defectShowsSlot = (synthetic: string, code: string | null) =>
    defectDetailsShown &&
    defectDetailItems.some(
      (item) =>
        (item.field.code_name === `__defect_${synthetic}` ||
          (code !== null && item.field.code_name === code)) &&
        (cardRowHasValue(item.displayValue) ||
          (item.value != null && typeof item.value === "object") ||
          cardRowHasValue(item.value)),
    );
  const fallbackProductRows = [
    {
      key: PRODUCT_NAME_COLUMN,
      label: "Product",
      value: claim.product_short_name || claim.product_name || "",
    },
    {
      key: "product_template",
      label: "Template",
      value: claim.product_template_name || "",
    },
    {
      key: "product_type",
      label: "Type",
      value: claim.product_type
        ? claim.product_type.charAt(0).toUpperCase() +
          claim.product_type.slice(1).toLowerCase()
        : "",
    },
    {
      key: "product_category",
      label: "Category",
      value: claim.product_category_name || "",
    },
  ].filter((row) => row.key === PRODUCT_NAME_COLUMN || row.value);
  const overviewProductRows = productRowsBesideDefectDetails(
    configuredProductRows.length > 0
      ? configuredProductRows
      : fallbackProductRows,
    {
      product: defectShowsSlot("product", defectProductCode),
      colour: defectShowsSlot("colour", defectColourCode),
      quantity: defectShowsSlot("quantity", defectQuantityCode),
    },
  );
  // An unidentified product keeps the card: it holds "Identify product".
  const showOverviewProductCard =
    Boolean(claim.holding_uuid) &&
    canViewProductCard &&
    (Boolean(linkedHolding?.unidentified) ||
      overviewProductRows.some((row) => cardRowHasValue(row.value)));

  // Single resolver for the customer/homeowner card. Read the consumer's
  // details - including the address - off the SAME holding the registration
  // page renders (Entity -> resolver -> display), so both surfaces show
  // identical values. Falls back to the flat claim.consumer_* snapshot only for
  // holdingless claims, which have no holding to resolve from.
  const consumerCard = linkedHolding
    ? resolveHoldingConsumerFields(linkedHolding)
    : null;
  const resolvedConsumerName =
    consumerCard?.fullName ||
    [claim.consumer_first_name, claim.consumer_last_name]
      .filter(Boolean)
      .join(" ") ||
    claim.consumer_name ||
    undefined;
  const resolvedConsumerEmail =
    consumerCard?.email || claim.consumer_email || undefined;
  const resolvedConsumerPhone =
    consumerCard?.phone || claim.consumer_phone || undefined;
  const resolvedConsumerAddress =
    consumerCard?.address ||
    (claim.consumer_address
      ? typeof claim.consumer_address === "string"
        ? claim.consumer_address
        : addressDisplay(claim.consumer_address)
      : "") ||
    undefined;

  // Trailing actions for the Customer card, shared by both render branches
  // (host-based "Homeowner & Property" and the standard "Customer" card) so the
  // Contact button + Edit-customer menu are defined once. A plain const (not a
  // hook) since this sits below the loading/early-return guard. The
  // EntityActionMenu renders nothing unless the tenant has seeded a consumer
  // action AND the user holds its permission (e.g. consumer.edit_customer), so
  // it stays tenant-agnostic; it refetches the claim on success since the
  // consumer name/email/phone are surfaced on the claim.
  // The standard Customer card shows the customer's edit as the platform pen,
  // last after the menu (CustomerActionControls), like every editable card.
  const standardCustomerCardTrailing = (
    <div className="flex items-center gap-2">
      <Button
        onClick={() => setContactCustomerOpen(true)}
        variant="outline"
        size="xs"
        className="flex cursor-pointer items-center gap-1.5 rounded-[6px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] text-[10px] uppercase tracking-[0.06em] text-[var(--ink)] transition-all duration-150 hover:border-[var(--green)] hover:bg-[var(--green-bg)]"
      >
        <Mail className="h-3 w-3" />
        Contact
      </Button>
      {consumerCard?.uuid && (
        <CustomerActionControls
          consumerUuid={consumerCard.uuid}
          triggerLabel={t("claims.customerActions", "Customer actions")}
          triggerTestId="claim-customer-menu"
          editButtonTestId="claim-customer-edit-btn"
          onActionComplete={() => refetch()}
        />
      )}
    </div>
  );

  const customerCardTrailing = (
    <div className="flex items-center gap-2">
      <Button
        onClick={() => setContactCustomerOpen(true)}
        variant="outline"
        size="xs"
        className="flex cursor-pointer items-center gap-1.5 rounded-[6px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] text-[10px] uppercase tracking-[0.06em] text-[var(--ink)] transition-all duration-150 hover:border-[var(--green)] hover:bg-[var(--green-bg)]"
      >
        <Mail className="h-3 w-3" />
        Contact
      </Button>
      {consumerCard?.uuid && (
        <EntityActionMenu
          entityTypeKey="consumer"
          resourceUuid={consumerCard.uuid}
          triggerLabel={t("claims.customerActions", "Customer actions")}
          onActionComplete={() => refetch()}
        />
      )}
    </div>
  );

  const holdingWarranty = linkedHolding
    ? findHoldingWarranty(
        linkedHolding,
        defaultWarrantyUuidForHolding(linkedHolding),
      )
    : null;
  const claimWarrantyFallback = holdingWarranty
    ? null
    : ((claim.warranties ?? [])[0] ?? null);
  const issuedWarranty =
    holdingWarranty && isIssuedWarranty(holdingWarranty)
      ? holdingWarranty
      : null;
  const pendingIssues = linkedHolding?.pending_exceptions ?? [];
  const statusWorkflowState =
    linkedHolding?.workflow_states?.find(
      (ws) => ws.workflow_key === "lifecycle_status",
    ) ?? null;
  const holdingStatusKey = linkedHolding?.status?.toLowerCase() ?? null;
  const statusWorkflowStateKey =
    statusWorkflowState?.state_key?.toLowerCase() ?? null;
  const hasStatusWorkflowIssues = pendingIssues.some((issue) => {
    if (issue.workflow_key === "lifecycle_status") return true;
    if (issue.workflow_key && issue.workflow_key !== "lifecycle_status")
      return false;
    return issue.issue_kind === "approval_required";
  });
  const isRegistrationActive =
    statusWorkflowStateKey === "active" ||
    statusWorkflowStateKey === "registered" ||
    (!statusWorkflowState &&
      (holdingStatusKey === "active" || holdingStatusKey === "registered"));
  const activeSyntheticWarranty =
    Boolean(holdingWarranty) &&
    !issuedWarranty &&
    isRegistrationActive &&
    !hasStatusWorkflowIssues;

  const fallbackWarrantyType = claimWarrantyFallback
    ? WARRANTY_TYPE_LABELS[claimWarrantyFallback.warranty_type] ||
      humanise(claimWarrantyFallback.warranty_type) ||
      "Warranty"
    : "Warranty";
  const rawWarrantyType = holdingWarranty
    ? WARRANTY_TYPE_LABELS[holdingWarranty.warranty_type] ||
      humanise(holdingWarranty.warranty_type) ||
      "Warranty"
    : fallbackWarrantyType;
  const warrantyType =
    activeSyntheticWarranty && holdingWarranty
      ? holdingWarranty.class_name || "Warranty"
      : rawWarrantyType;
  const warrantyClassName =
    holdingWarranty?.class_name ||
    claimWarrantyFallback?.class_name ||
    warrantyType;
  const warrantyTier =
    holdingWarranty && activeSyntheticWarranty
      ? holdingWarranty.option_display_name?.toLowerCase().trim() ===
        "pending warranty"
        ? "Registered warranty"
        : holdingWarranty.option_display_name || "Registered warranty"
      : warrantyType;
  const warrantyStatus = holdingWarranty
    ? activeSyntheticWarranty
      ? deriveRecordStatus(linkedHolding?.status, "Registered")
      : deriveRecordStatus(linkedHolding?.status, holdingWarranty.status)
    : claimWarrantyFallback?.status || "No warranty";
  const warrantyActiveFrom = holdingWarranty
    ? (holdingWarranty.active_from ?? null)
    : (claimWarrantyFallback?.active_from ?? null);
  const _warrantyForExpiry = holdingWarranty ?? claimWarrantyFallback;
  const warrantyExpires = _warrantyForExpiry
    ? computeWarrantyExpiresAt(_warrantyForExpiry)
    : null;
  const warrantyDocuments = issuedWarranty?.documents ?? [];
  const warrantyForDuration = holdingWarranty ?? claimWarrantyFallback;
  const configuredWarrantyRows = linkedHolding
    ? buildRegistrationConfiguredFieldItems({
        holding: linkedHolding,
        columns: registrationCardDisplayColumns(warrantyEntity),
        entityTypes,
        locale,
        currency,
      })
    : [];

  // Partner-branch cards (owner view). One Customer-style card per branch,
  // rendered INSIDE the same card grid as Claim Details / Customer so it is the
  // same size. Only present once an extended (partner) workflow has actually
  // been initiated on the claim - i.e. at least one branch exists; never before.
  const partnerBranches = metadata.partner_branches?.branches ?? [];
  const partnerBranchOpenCount = metadata.partner_branches?.open_count ?? 0;
  const showPartnerWork = !isPartnerAccess && partnerBranches.length > 0;
  const partnerBranchCards = showPartnerWork
    ? partnerBranches.map((b, i) => (
        <EditableDetailCard
          key={`partner-${b.capability}-${i}`}
          title={`${b.capability_label} Partner`}
          icon={
            b.capability === "repair" ? (
              <Wrench className="h-3.5 w-3.5" />
            ) : (
              <ClipboardCheck className="h-3.5 w-3.5" />
            )
          }
          trailing={
            <Button
              variant="outline"
              size="sm"
              disabled={!b.partner_contact_email}
              onClick={() =>
                b.partner_contact_email &&
                (window.location.href = `mailto:${b.partner_contact_email}`)
              }
            >
              <Mail className="mr-1.5 h-3 w-3" />
              Contact Partner
            </Button>
          }
          formatOptions={{ locale, currency }}
          items={[
            {
              field: defineField({
                code_name: "partner_status",
                display_name: "Status",
                field_type: "TEXT",
              }),
              value: b.state_label,
              displayValue: (
                <StatusBadge status={b.state_label} color={b.state_color} />
              ),
            },
            ...(b.partner_org_name
              ? [
                  {
                    field: defineField({
                      code_name: "partner_org",
                      display_name: "Partner",
                      field_type: "TEXT",
                    }),
                    value: b.partner_org_name,
                  },
                ]
              : []),
            ...b.fields.map((f) => ({
              field: defineField({
                code_name: f.code_name,
                display_name: f.label,
                field_type: f.field_type as FormFieldMeta["field_type"],
                configuration_data: f.configuration_data,
              }),
              value: f.value,
            })),
          ]}
        />
      ))
    : null;

  // ── Edit config ───────────────────────────────────────

  const editConfig =
    canEdit && !isTerminal
      ? {
          isPending: editFieldsDirect.isPending,
          canEdit: true,
          requireReason: true as const,
        }
      : undefined;

  // WPS-204 #7: built from the schema, so a claim type shows the card exactly
  // when its schema tags supplier fields, and a retired field never renders.
  const supplierInformationItems: CardFieldItem[] = supplierCardSpec
    .filter((spec) => metadataMap.has(spec.code_name))
    .map((spec) => {
      const values = claim.attribute_values as
        Record<string, unknown> | undefined;
      let item = buildClaimFieldItem(
        spec.code_name,
        values?.[spec.code_name] ?? null,
        metadataMap,
      );
      if (spec.readOnly) item = { ...item, readOnly: true };
      if (spec.hideWhenEmpty) item = { ...item, hideWhenEmptyDisplay: true };
      return item;
    });
  // A voucher redemption starts from the claim's Supplier card values.
  const voucherSupplierDefaults: SupplierDefault[] = supplierCardSpec.map(
    (spec) => ({
      code_name: spec.code_name,
      value: (claim.attribute_values as Record<string, unknown> | undefined)?.[
        spec.code_name
      ],
      field_type: metadataMap.get(spec.code_name)?.field_type,
      configuration_data: metadataMap.get(spec.code_name)?.configuration_data,
    }),
  );
  // WPS-204 #7: supplier fields may declare an update grant; its holders keep
  // the Supplier card editable at every stage, closed claims included.
  const supplierEditConfig = grantScopedEditConfig(
    fieldUpdatePermissions(
      supplierInformationItems.map((item) => item.field.code_name),
      metadataMap,
    ),
    can,
    editConfig,
    can("claims.edit.global")
      ? {
          isPending: editFieldsDirect.isPending,
          canEdit: true,
          requireReason: true as const,
        }
      : undefined,
  );

  // ── Evidence extraction ───────────────────────────────

  type AttributeEvidenceFile = {
    uuid: string;
    file_url: string | null;
    file_name: string;
    mime_type?: string;
    category?: string;
    file_size?: number | null;
    file_type?: string;
    thumbnail_url?: string | null;
  };

  const evidence: Array<
    AttributeEvidenceFile & { rowKey: string; fieldKey: string }
  > = [];

  // File attributes arrive in the canonical ``{display, value: {uuid,
  // file_url, file_size, ...}}`` envelope (common/uploaded_file.py); older
  // payloads were the bare inner object. Accept both shapes and keep size /
  // type / thumbnail so Evidence does not show "0 B" for intake photos.
  const asEvidenceFile = (item: unknown): AttributeEvidenceFile | null => {
    if (!item || typeof item !== "object") return null;
    const rec = item as Record<string, unknown>;
    const inner =
      rec.value && typeof rec.value === "object"
        ? (rec.value as Record<string, unknown>)
        : rec;
    if ("file_url" in inner && typeof inner.uuid === "string") {
      return inner as AttributeEvidenceFile;
    }
    return null;
  };

  for (const [key, value] of Object.entries(claim.attribute_values)) {
    const pushFile = (file: AttributeEvidenceFile, index?: number) => {
      evidence.push({
        rowKey: `${key}-${file.uuid}-${index ?? 0}`,
        fieldKey: key,
        ...file,
      });
    };

    const single = asEvidenceFile(value);
    if (single) {
      pushFile(single);
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => {
        const file = asEvidenceFile(item);
        if (file) pushFile(file, index);
      });
    }
  }

  // Attribute-captured evidence (claim "photos" field, admin "+" uploads,
  // evidence-at-intake wizard fields like failed_hose_photos) is stored on
  // claim.attribute_values, not the attachments registry. Project it into the
  // Attachment shape so the rich EvidenceCard can show it next to session/iris
  // uploads in a single card, categorised the same way.
  //
  // The category is derived from the field's own metadata - the ClaimAttribute
  // display_name (e.g. failed_hose_photos -> "Photos of the cut hose ends"),
  // falling back to a humanised code_name. This mirrors how iris-link uploads
  // carry their requirement label, and keeps the label generic (no
  // tenant-specific map) - any tenant's intake fields group by their own
  // configured display names. Read-only - it has no attachment delete
  // endpoint, and uploader is left blank (source isn't recorded here).
  const attributeEvidence: Attachment[] = evidence.map((file) => {
    const fieldMeta = metadataMap.get(file.fieldKey);
    const categoryLabel =
      fieldMeta?.display_name?.trim() || titleCaseKey(file.fieldKey);
    const mime = file.mime_type ?? "";
    const isImage = mime.startsWith("image/");
    return {
      uuid: file.uuid,
      subject_type: "claim",
      subject_uuid: claim.uuid,
      // A file filed under a category on the Evidence tab reads there; one
      // nobody filed reads under the field it answers.
      category: file.category || categoryLabel,
      file_url: file.file_url,
      thumbnail_url:
        file.thumbnail_url ?? (isImage ? file.file_url : null),
      file_name: file.file_name,
      file_type: file.file_type ?? "",
      // Backend already resolves size on the file envelope; do not zero it out
      // or intake photos render as "0 B" next to real attachment sizes.
      file_size: typeof file.file_size === "number" ? file.file_size : 0,
      mime_type: mime,
      requirement_key: "",
      document_option_key: "",
      uploader: { kind: "", email: "", name: "" },
      uploaded_at: "",
      is_deleted: false,
      deleted_at: null,
      deleted_by_email: null,
      deleted_reason: "",
    };
  });

  // ── Banner computed values ────────────────────────────

  const handleFlagDialogChange = (open: boolean) => {
    setFlagDialogOpen(open);
    if (!open) setFlagReason("");
  };

  const handleFlagSubmit = async () => {
    try {
      await toggleFlag.mutateAsync({
        uuid: claim.uuid,
        reason: claim.is_flagged ? undefined : flagReason,
      });
      toast.success(claim.is_flagged ? "Flag removed" : "Claim flagged");
      handleFlagDialogChange(false);
    } catch (err: unknown) {
      toastApiError(err, "Failed to update flag");
    }
  };

  const daysOpen = Math.max(
    0,
    Math.floor(
      (new Date().getTime() - new Date(claim.created_at).getTime()) /
        86_400_000,
    ),
  );
  const evidenceCount = evidence.length;
  const isEvidenceUploading =
    uploadFile.isPending || addClaimEvidence.isPending;


  // ── Record top panel (WPS-146) ────────────────────────
  // Where each header control sits. Every action keeps the gating it had:
  // the workflow's offered actions, the document-request and evidence-reissue
  // state opt-ins, and the partner rule. Only placement changes - the one
  // primary action is the step the workflow already picks (pickNextStep), and
  // everything else moves into the shared Actions menu, record-ending
  // actions last.
  // The integer pk the ACL endpoints key on. The detail serializer sends it,
  // but ClaimDetail does not declare it.
  const claimObjectId = (claim as { id?: number | null }).id ?? null;
  const runHeaderAction = (action: (typeof headerActions)[number]) => {
    // WPS-164 #29: execute_immediately actions (e.g. Initiate Scrap) run
    // directly - no dialog, no confirm.
    if (action.execute_immediately) {
      executeImmediate.mutate(
        { uuid: claim.uuid, action_key: action.key },
        {
          onSuccess: (result) => {
            if (!toastDivertedAction(result, t)) {
              toast.success(
                t("claims.actionExecuted", 'Action "{{label}}" executed', {
                  label: action.label,
                }),
              );
            }
          },
          onError: () =>
            toast.error(
              t("claims.actionExecuteError", 'Could not run "{{label}}"', {
                label: action.label,
              }),
            ),
        },
      );
      return;
    }
    setActiveAction(action);
  };
  // At most two workflow actions show as buttons, however many the workflow
  // offers, and only ones that move the claim on (the set the next step is
  // picked from): the one it marks primary (`ui_hints.variant`) first, then by
  // order. The rest sit in Actions, refusals last, so a busier workflow never
  // fills the header.
  const forwardHeaderActions = headerActions
    .filter(
      (a) =>
        !a.destructive &&
        a.applicability !== "GLOBAL" &&
        a.moves_claim !== false &&
        a.next_step !== false,
    )
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  const markedPrimary = forwardHeaderActions.find((a) => a.primary);
  const shownHeaderActions = (
    markedPrimary
      ? [markedPrimary, ...forwardHeaderActions.filter((a) => a !== markedPrimary)]
      : forwardHeaderActions
  ).slice(0, 2);
  const shownHeaderKeys = new Set(shownHeaderActions.map((a) => a.key));
  const headerActionLabel = (action: (typeof headerActions)[number]) =>
    action.performed && action.performed_label
      ? action.performed_label
      : action.label;
  const headerMenuActions: RecordTopPanelAction[] = [
    ...headerActions
      .filter((action) => !shownHeaderKeys.has(action.key))
      .map((action) => {
        const ActionIcon = resolveActionIcon(action.key);
        return {
          key: action.key,
          label: headerActionLabel(action),
          icon:
            action.performed && action.performed_label ? (
              <Check />
            ) : (
              <ActionIcon />
            ),
          onSelect: () => runHeaderAction(action),
          disabled: !action.actionable || executeImmediate.isPending,
          // Config-authored (`ui_hints.variant: "destructive"`): refusing and
          // record-ending routes show in red and go last in the menu.
          destructive: Boolean(action.destructive),
        };
      }),
    ...(labChecklistActionsEnabled
      ? [
          {
            // WPS-142 #17: the single "Request additional documentation"
            // action. The dialog pre-checks the outstanding items.
            key: "request-additional-documentation",
            label: t(
              "claims.requestAdditionalDocuments",
              "Request additional documentation",
            ),
            icon: <Camera />,
            onSelect: () => setRequestPhotosOpen(true),
          },
        ]
      : []),
    ...(can(SEND_REQUESTS) && !isTerminal && evidenceReissueAllowed
      ? [
          {
            key: "reissue-upload-link",
            label: t("claims.reissueUploadLink", "Reissue upload link"),
            icon: <RotateCcw />,
            onSelect: () => setReissueOpen(true),
          },
        ]
      : []),
    // Controls about the claim itself, after the workflow actions. Letter
    // previews and Flag are internal-only, hidden from partners working the
    // claim on their desk.
    ...(!isPartnerAccess
      ? previewLetterList.map((letter) => ({
          // A letter the workflow will send (e.g. a release), opened to read first.
          key: `preview-letter-${letter.template_id}`,
          label: t("claims.previewLetter", "Preview {{name}}", {
            name: letter.name,
          }),
          icon: <FileText />,
          onSelect: () => setPreviewLetter(letter),
          section: "record" as const,
        }))
      : []),
    ...(!isPartnerAccess
      ? [
          {
            key: "flag",
            label: claim.is_flagged
              ? t("claims.topPanel.removeFlag", "Remove flag")
              : t("claims.topPanel.flag", "Flag claim"),
            icon: <Flag />,
            onSelect: () => handleFlagDialogChange(true),
            disabled: toggleFlag.isPending,
            section: "record" as const,
          },
        ]
      : []),
    ...(claimLabelsConfigured && canLabel
      ? [
          {
            key: "labels",
            label: t("claims.topPanel.labels", "Labels"),
            icon: <Tag />,
            onSelect: () => setLabelPickerOpen(true),
            section: "record" as const,
          },
        ]
      : []),
    ...(claimObjectId != null
      ? [
          {
            key: "access",
            label: t("claims.topPanel.manageAccess", "Manage access"),
            icon: <Lock />,
            onSelect: () => setAccessDialogOpen(true),
            section: "record" as const,
          },
        ]
      : []),
  ];

  // The claim's own answer to "what now?" (WPS-175), shown as the first fact.
  // A decision notice replaces it: one status message at a time.
  // The next-step fact names the claim's forward step: the same action the
  // primary button offers. An action that leaves the claim where it is (a
  // top-up payment) can be picked as "next" by the workflow without being a
  // forward step, so it never shows here.
  const nextStepVisible = Boolean(
    nextStepPrimary && !isTerminal && !atLoaDecision && !decisionNotice,
  );
  const guidanceTitle =
    attributeText(currentStateAttributeValues.guidance_title) ||
    null;
  const guidanceDescription =
    attributeText(currentStateAttributeValues.guidance_description) ||
    null;
  // When the panel already states the stage's guidance, the Overview does not
  // repeat it as a second card below the tabs.
  const panelShowsGuidance = nextStepVisible && Boolean(guidanceTitle);

  // Facts - each gates on the data that gives it meaning, as before.
  // WPS-160: the person who owns the CURRENT stage, resolved server-side.
  const assignedLabelValue =
    claim.current_stage_assignee?.name ?? claim.assigned_to?.name ?? null;
  // WPS-165 #13/#14: the settled payment's reference and post date sit with
  // the settlement amount - captured together, shown together. Most recent
  // settled payment wins; internal, so not for partners.
  const settledPayment = (settlement?.payments ?? claimPayments ?? [])
    .filter(
      (p) => p.status === "PROCESSED" && p.settlement_reference && p.post_date,
    )
    .sort((a, b) => (b.post_date ?? "").localeCompare(a.post_date ?? ""))[0];
  // A grouped claim paying its properties through one payment has
  // no lines of its own: its settlement figure is what it pays.
  const ownSettlementTotal = Number(settlement?.summary?.settlement_total ?? 0);
  const settlementFigure =
    isUmbrellaParent && ownSettlementTotal === 0
      ? (settlement?.payments ?? [])
          .filter(
            (p) =>
              !p.is_voided && p.status !== "CANCELLED" && p.status !== "FAILED",
          )
          .reduce((sum, p) => sum + Number(p.amount || 0), 0)
      : ownSettlementTotal;
  // A workflow whose claims are corresponded with someone other than the
  // registration's owner names it in ``workflow_metadata.header_contact``:
  // "main_contact" shows the claim's main external contact (OC's BFS: the
  // installer's contact, never the property owner - WPS-119). Absent, the
  // header shows the owner as it always has.
  const mainContact =
    metadata?.workflow_metadata?.header_contact === "main_contact"
      ? claimContacts.find(
          (contact) => contact.is_main_contact && !contact.is_internal,
        )
      : undefined;
  // Until the contacts arrive, show no one rather than flash the owner the
  // header is configured not to name.
  const headerContactLoading = headerShowsMainContact && claimContactsPending;
  const headerContactName = mainContact
    ? mainContact.display_name
    : headerContactLoading
      ? ""
      : claim.consumer_name;
  const headerContactEmail = mainContact
    ? mainContact.display_email
    : headerContactLoading
      ? ""
      : claim.consumer_email;
  const headerFacts: RecordTopPanelFact[] = [
    ...(nextStepVisible && nextStepPrimary
      ? [
          {
            key: "next-step",
            label: nextStepOwner
              ? t("claims.topPanel.nextStepFor", {
                  defaultValue: "Next step, waiting on {{role}}",
                  role: nextStepOwner,
                })
              : t("claims.topPanel.nextStep", "Next step"),
            value: guidanceTitle ?? nextStepPrimary.label,
            detail:
              nextStepOutstanding.length > 0
                ? t("claims.topPanel.stillNeeds", {
                    defaultValue: "Still needs {{items}}.",
                    items: nextStepOutstanding.slice(0, 3).join(", "),
                  })
                : null,
            info: guidanceDescription,
            emphasis: true,
            testId: "claim-next-step",
          },
        ]
      : []),
    ...(!isPartnerAccess && assignedLabelValue != null
      ? [
          {
            key: "assigned",
            label: t("claims.topPanel.assignedTo", "Assigned to"),
            value: assignedLabelValue,
            testId: "claim-assigned-to",
          },
        ]
      : []),
    ...(headerContactName || headerContactEmail
      ? [
          {
            key: "contact",
            label: t("claims.topPanel.contact", "Contact"),
            value: headerContactName || headerContactEmail,
            info: headerContactName ? headerContactEmail : null,
          },
        ]
      : []),
    ...(workflowInstance
      ? [
          {
            key: "days-open",
            label: t("claims.topPanel.daysOpen", "Days open"),
            value: daysOpen,
          },
        ]
      : []),
    ...(evidenceCount > 0
      ? [
          {
            key: "evidence",
            label: t("claims.topPanel.evidence", "Evidence"),
            value: t("claims.topPanel.evidenceFiles", {
              defaultValue: "{{count}} files",
              count: evidenceCount,
            }),
          },
        ]
      : []),
    ...(settlement?.visible
      ? [
          {
            key: "settlement",
            label: t("claims.topPanel.settlement", "Settlement"),
            value: formatCurrency(settlementFigure),
            emphasis: true,
          },
        ]
      : []),
    ...(!isPartnerAccess && settledPayment
      ? [
          {
            key: "payment-reference",
            label: t(
              "claims.detail.paymentReferenceLabel",
              "Payment Reference",
            ),
            value: settledPayment.settlement_reference,
          },
          {
            key: "post-date",
            label: t("claims.detail.postDateLabel", "Post Date"),
            value: formatDateNumeric(settledPayment.post_date!),
          },
        ]
      : []),
  ];

  // The sections view of the Overview (the platform default) shows each card
  // as one calm section. Field cards summarise themselves from their own
  // fields; these cards hold people, a contractor or a warranty instead, so
  // the page says what their one-line summary is. Read from the same data the
  // cards render, nothing new.
  const openTeamSlots = teamSlots.filter((slot) => !slot.assignee);
  const filledTeamSlots = teamSlots.filter(
    (slot) => slot.assignee && !slot.primary,
  );
  const endUser = consumerInfo(claim.attribute_values ?? {});
  const consumerRows = [
    { key: "name", label: t("claims.homeowner.name", "Name"), value: endUser.name },
    { key: "email", label: t("claims.homeowner.email", "Email"), value: endUser.email },
    {
      key: "phone",
      label: t("claims.homeowner.phone", "Phone"),
      value: formatPhoneDisplay(endUser.phone) || undefined,
    },
    { key: "address", label: t("claims.homeowner.address", "Address"), value: endUser.address },
  ];
  const overviewSummaries: Record<string, CardSummary | null> = {
    "claim-team": {
      icon: <Users />,
      value:
        teamSlots.find((slot) => slot.primary)?.assignee?.name ??
        assignedLabelValue ??
        t("claims.unassigned", "Unassigned"),
      detail:
        [
          ...filledTeamSlots.map(
            (slot) => `${slot.assignee?.name} (${slot.label})`,
          ),
          openTeamSlots.length > 0
            ? t("claims.sections.rolesOpen", {
                defaultValue: "{{count}} roles open",
                count: openTeamSlots.length,
              })
            : null,
        ]
          .filter(Boolean)
          .join(", ") || null,
    },
    "claim-contacts": resolvedConsumerName
      ? {
          icon: <Contact />,
          value: resolvedConsumerName,
          // Wraps after the "@", never mid-word, in a narrow section.
          detail: claim.consumer_email ? (
            <>
              {claim.consumer_email.slice(0, claim.consumer_email.indexOf("@") + 1)}
              <wbr />
              {claim.consumer_email.slice(claim.consumer_email.indexOf("@") + 1)}
            </>
          ) : null,
        }
      : null,
    "claim-partner": claim.partner
      ? {
          icon: <Building2 />,
          value: claim.partner.downstream_organisation.name,
          detail: claim.partner.role_name || null,
        }
      : { empty: true },
    warranty: {
      value: warrantyClassName,
      detail: [warrantyStatus, warrantyTier !== warrantyClassName ? warrantyTier : null]
        .filter(Boolean)
        .join(", "),
    },
    product: summaryFromRows(
      overviewProductRows.map((row) => ({
        key: row.key,
        label: row.label,
        value: row.value,
      })),
    ),
    // Cards with no field items of their own for the grid to read (an empty or
    // wrapped field card, plain field rows, a bespoke card) are summarised from
    // the data they render, so Sections never renders one hidden to learn
    // whether it is empty.
    "claim-details":
      claimDetailItems.length > 0 || claim.group
        ? null
        : { empty: true },
    decision: summaryFromItems(decisionCardItems, { locale, currency }),
    customer: summaryFromRows([
      {
        key: "name",
        label: t("claims.homeowner.name", "Name"),
        value: resolvedConsumerName,
      },
      {
        key: "email",
        label: t("claims.homeowner.email", "Email"),
        value: resolvedConsumerEmail,
      },
      {
        key: "phone",
        label: t("claims.homeowner.phone", "Phone"),
        value: formatPhoneDisplay(resolvedConsumerPhone) || undefined,
      },
      {
        key: "address",
        label: t("claims.homeowner.address", "Address"),
        value: resolvedConsumerAddress,
      },
    ]),
    consumer: summaryFromRows(consumerRows),
  };
  const overviewSections = cardsView.layout === "sections";
  const claimNotesPanel = (
    <NotesPanel
      recordId={claim.uuid}
      recordType="claim"
      canAddInternal
      canAddCustomerVisible
      allowAttachments
      onNoteAdded={() => refetch()}
    />
  );
  // WPS-146: the Settlement tab's vouchers card sits in the tab's card grid.
  const settlementVouchersCard =
    hasVouchersFlag && can("voucher.view") ? (
      <ClaimVouchersCard
        key="vouchers"
        data-card-wide
        data-card-title={t("claims.card.vouchers", "Vouchers")}
        claimUuid={claim.uuid}
        supplierDefaults={voucherSupplierDefaults}
      />
    ) : null;
  const overviewAside = [
    {
      key: "activity",
      title: t("claims.card.activity", "Activity"),
      // Always open beside the sections: a saved fold would leave the side
      // panel showing an empty header.
      node: cloneElement(activityCard, { folded: false, onToggleFold: undefined }),
    },
  ];
  // The standard Overview keeps Notes and Communications below its grid
  // rather than inside it, so its side panel carries them directly.
  const overviewAsideWithNotes = [
    ...overviewAside,
    {
      key: "communications",
      title: t("claimComms.title", "Communications"),
      node: communicationsCard,
    },
    { key: "notes", title: t("notes.title", "Notes"), node: claimNotesPanel },
  ];
  // What the subtitle leads with, and the claim type after it. The type is
  // read from the schema key: short keys are acronyms (BFS), longer ones are
  // words (Installed, Job stop). Skipped when the subtitle already leads with
  // the schema's own name, so it is never said twice.
  const subtitleLead =
    claimProductDisplayName(claim.product_line_name, claim.product_short_name) ||
    claim.product_name ||
    attributeText(claim.attribute_values?.supplier_name) ||
    claim.claim_schema_name;
  const schemaKeyWords = (claim.claim_schema_key ?? "").replace(/_/g, " ");
  const claimTypeLabel =
    claim.attribute_values?.claim_type &&
    schemaKeyWords &&
    subtitleLead !== claim.claim_schema_name
      ? schemaKeyWords.length <= 3
        ? schemaKeyWords.toUpperCase()
        : schemaKeyWords.charAt(0).toUpperCase() + schemaKeyWords.slice(1)
      : null;
  // The status pill's hover: the state's helper and the lifecycle stage
  // (WP1-67), so the phase is one hover away instead of another chip.
  const statusTooltip =
    hasCycleTime &&
    (workflowInstance?.current_status?.helper ||
      workflowInstance?.current_stage?.label)
      ? [
          workflowInstance?.current_stage?.label
            ? t("claims.topPanel.stage", {
                defaultValue: "Stage: {{stage}}",
                stage: workflowInstance.current_stage.label,
              })
            : null,
          workflowInstance?.current_status?.helper ?? null,
          workflowInstance?.current_stage?.helper ?? null,
        ]
          .filter(Boolean)
          .join(" · ")
      : null;
  const panelStatusClass =
    "rounded-full px-2.5 py-0.5 text-[13px] font-medium normal-case tracking-normal";
  const headerChipClass =
    "inline-flex items-center gap-1 rounded-full border border-[var(--panel-rule)] bg-[var(--bg)] px-2.5 py-0.5 text-[13px] font-medium text-[var(--ink-2)]";

  return (
    <FileViewerProvider>
      <AppShell>
        <main className="page-container min-h-full py-6">
          {/* ── Record top panel (WPS-146) ───────────────────── */}
          <RecordTopPanel
            backLabel={t("claims.backToClaims", "Back to claims")}
            onBack={() => navigate("/claims")}
            title={claim.claim_number || formatClaimId(claim.uuid)}
            titleClassName={titleFontClass}
            status={
              workflowInstance ? (
                statusTooltip ? (
                  <SimpleTooltip content={statusTooltip}>
                    <span className="inline-flex cursor-help">
                      <StatusBadge
                        status={workflowInstance.current_state_label}
                        color={workflowInstance.current_state_color}
                        className={panelStatusClass}
                      />
                    </span>
                  </SimpleTooltip>
                ) : (
                  <StatusBadge
                    status={workflowInstance.current_state_label}
                    color={workflowInstance.current_state_color}
                    className={panelStatusClass}
                  />
                )
              ) : null
            }
            chips={
              <>
                {/* WP1-67: "Reopened" tag, derived from workflow history. */}
                {hasCycleTime && workflowInstance?.reopened && (
                  <SimpleTooltip content="This claim was closed and reopened - it resumed at the stage it was closed from.">
                    <span className={cn(headerChipClass, "cursor-help")}>
                      <RotateCcw className="h-3.5 w-3.5" />
                      {(workflowInstance.reopen_count ?? 0) > 1
                        ? `Reopened ×${workflowInstance.reopen_count}`
                        : "Reopened"}
                    </span>
                  </SimpleTooltip>
                )}
                {claim.is_flagged && (
                  <span
                    className={cn(
                      headerChipClass,
                      "border-[var(--status-error)] bg-[var(--status-error-bg-subtle)] text-[var(--status-error)]",
                    )}
                  >
                    <Flag className="h-3.5 w-3.5" />
                    Flagged
                  </span>
                )}
                {metadata.workflow_metadata?.is_priority ? (
                  <span
                    className={cn(
                      headerChipClass,
                      "border-red-700 bg-red-50 font-semibold text-red-700",
                    )}
                  >
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Priority -{" "}
                    {String(metadata.workflow_metadata?.sla_days ?? 7)} day SLA
                  </span>
                ) : null}
                <ClaimLabelsControl
                  claimUuid={claim.uuid}
                  labels={
                    Array.isArray(claim.attribute_values?.labels)
                      ? (claim.attribute_values.labels as string[])
                      : []
                  }
                  canEdit={canLabel}
                  closed={isTerminal}
                  hideAddTrigger
                  pickerOpen={labelPickerOpen}
                  onPickerOpenChange={setLabelPickerOpen}
                />
              </>
            }
            subtitle={
              <>
                {/* WPS-146 #3: what this claim is, directly under the number. */}
                {subtitleLead}
                {claim.consumer_name && <> &middot; {claim.consumer_name}</>}
                {claim.dealer_name && <> &middot; {claim.dealer_name}</>}
                {/* WPS-203: a claim with no registration may name only its
                  product line, and still says which claim type it is. */}
                {!claim.holding_uuid &&
                !claim.dealer_name &&
                claim.claim_schema_name &&
                (claim.product_name ||
                  claim.product_short_name ||
                  claim.product_line_name) ? (
                  <> &middot; {claim.claim_schema_name}</>
                ) : (
                  claimTypeLabel && <> &middot; {claimTypeLabel}</>
                )}
              </>
            }
            quietControls={
              <>
                {/* The documentation reminder is managed from the claim while a
                  document request is open; the action renders nothing
                  otherwise. */}
                {!isTerminal && (
                  <AutoReminderAction
                    resourceType="claim"
                    resourceUuid={claim.uuid}
                  />
                )}
                {/* The internal assignee is internal-only - hidden from
                  partners working the claim on their desk. Flag, labels and
                  access live in Actions. */}
                {!isPartnerAccess && (
                  <>
                    {/* WPS-160: with the Claim Team card active, assignment
                      lives in the card, so the standalone control is hidden.
                      Tenants without the card keep it. */}
                    {canAssignClaim && !hasClaimTeam && !claimTeamPending && (
                      <SearchableSelect
                        value={claim.assigned_to?.uuid ?? ""}
                        onValueChange={handleAssigneeChange}
                        onOpenChange={setAssignOpen}
                        groups={assigneeGroups}
                        disabled={assignClaim.isPending}
                        searchPlaceholder={t(
                          "claims.assigneeSearchPlaceholder",
                          "Search people...",
                        )}
                        emptyMessage={t(
                          "claims.assigneeEmptyMessage",
                          "No members found",
                        )}
                        loading={assignOpen && !orgMembers}
                        contentClassName="min-w-[240px]"
                        renderTrigger={
                          <Button
                            variant="outline"
                            disabled={assignClaim.isPending}
                            className="h-8 cursor-pointer gap-1.5 rounded-[8px] border-[var(--panel-rule)] bg-[var(--bg)] px-3 text-[13px] text-[var(--ink)]"
                          >
                            {assignClaim.isPending ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <UserPlus className="h-3.5 w-3.5" />
                            )}
                            {assignmentPendingLabel ??
                              claim.assigned_to?.name ??
                              "Assign"}
                          </Button>
                        }
                      />
                    )}
                  </>
                )}
                <AccessLevelPanel
                  resourceType="claim"
                  objectId={claimObjectId}
                  hideTrigger
                  open={accessDialogOpen}
                  onOpenChange={setAccessDialogOpen}
                />
                {!isPartnerAccess && (
                  <ClaimLetterPreviewDialog
                    claimUuid={claim.uuid}
                    letter={previewLetter}
                    onClose={() => setPreviewLetter(null)}
                  />
                )}
                <ConfigHelp
                  sections={[
                    "claim.detail_fields",
                    "display.claim_host_card",
                    "display.warranty_card",
                  ]}
                  returnLabel={t("claims.detail.title", {
                    defaultValue: "Claim",
                  })}
                  params={{
                    warrantyTemplateUuid:
                      issuedWarranty?.warranty_template?.uuid ?? null,
                  }}
                  size="icon-sm"
                  className="size-8 rounded-[8px] border border-solid border-[var(--panel-rule)] bg-[var(--bg)] hover:bg-[var(--bg-subtle)]"
                />
              </>
            }
            menuActions={headerMenuActions}
            primaryAction={
              shownHeaderActions.length > 0 ? (
                <>
                  {shownHeaderActions.map((action) => {
                    const ActionIcon = resolveActionIcon(action.key);
                    return (
                      <Button
                        key={action.key}
                        variant={action.primary ? "default" : "outline"}
                        onClick={() => runHeaderAction(action)}
                        disabled={
                          !action.actionable || executeImmediate.isPending
                        }
                        className={cn(
                          "h-8 cursor-pointer gap-1.5 rounded-[8px] px-3 text-[13px]",
                          !action.primary &&
                            "border-[var(--panel-rule)] bg-[var(--bg)] text-[var(--ink)]",
                        )}
                      >
                        {action.performed && action.performed_label ? (
                          <Check className="size-3.5" />
                        ) : (
                          <ActionIcon className="size-3.5" />
                        )}
                        {headerActionLabel(action)}
                      </Button>
                    );
                  })}
                </>
              ) : null
            }
            notice={
              decisionNotice && !isTerminal ? (
                <div className="flex basis-full items-center gap-2.5 rounded-xl border border-[var(--status-error)] bg-[var(--bg)] px-4 py-3">
                  <XCircle className="h-4 w-4 shrink-0 text-[var(--status-error)]" />
                  <span className="text-[16px] font-medium text-[var(--ink)]">
                    {decisionNotice.reason
                      ? t("claims.decisionNotice.withReason", {
                          defaultValue: "This claim was {{outcome}}: {{reason}}",
                          outcome: decisionNotice.outcome,
                          reason: decisionNotice.reason,
                        })
                      : t("claims.decisionNotice.plain", {
                          defaultValue: "This claim was {{outcome}}.",
                          outcome: decisionNotice.outcome,
                        })}
                  </span>
                </div>
              ) : null
            }
            facts={headerFacts}
            journey={
              workflowInstance && workflowStates.length > 0 ? (
                <WorkflowStepper
                  states={workflowStates}
                  currentStateKey={workflowInstance.current_state_key}
                  workflowInstanceUuid={workflowInstance.uuid}
                />
              ) : null
            }
          />

          {/* A home under a claim that pays as one is
            settled and paid there, so say so and link back. */}
          {claim.paid_by_parent && (
            <div
              className="mb-4 flex items-center justify-between gap-3 rounded-2xl border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg-subtle)] px-4 py-3"
              data-testid="paid-by-parent"
            >
              <div className="flex items-start gap-2.5">
                <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--ink-2)]" />
                <div className="flex flex-col gap-0.5">
                  <span className="text-[14px] font-medium text-[var(--ink)]">
                    {t("claims.paidByParent.title", {
                      claimNumber: claim.paid_by_parent.claim_number,
                      defaultValue: "Part of {{claimNumber}}",
                    })}
                    {claim.paid_by_parent.name
                      ? ` · ${claim.paid_by_parent.name}`
                      : ""}
                  </span>
                  <span className="text-[13px] text-[var(--ink-2)]">
                    {t(
                      "claims.paidByParent.body",
                      "This home is approved and paid through that claim's one payment. Add what is owed here; pay it there.",
                    )}
                  </span>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/claims/${claim.paid_by_parent!.uuid}`)}
              >
                {t("claims.paidByParent.open", {
                  claimNumber: claim.paid_by_parent.claim_number,
                  defaultValue: "Open {{claimNumber}}",
                })}
              </Button>
            </div>
          )}

          {/* Flag detail banner */}
          {claim.is_flagged && claim.flag_reason && (
            <div className="mb-4 rounded-2xl border-[0.5px] border-[var(--status-error)] bg-[var(--status-error-bg-subtle)] px-4 py-2.5">
              <div className="flex items-center gap-2">
                <Flag className="h-3 w-3 shrink-0 text-[var(--status-error)]" />
                <span className="text-[11px] text-[var(--ink-2)]">
                  {claim.flag_reason}
                </span>
              </div>
            </div>
          )}

          {/* Persistent notifications section - the current user's unread
            notifications for this claim (customer replied, added evidence, new
            claim awaiting review, open workflow issue). They stay visible and
            unread until explicitly marked read / dismissed, or until the
            standing condition clears itself. Read ones collapse into history.
            Driven by the notification infrastructure. */}
          <NotificationsPanel
            unread={unreadNotifications}
            read={readNotifications}
            onMarkRead={markNotificationRead}
            onMarkAllRead={markAllNotificationsRead}
            onDismiss={dismissNotification}
            markAllPending={markNotificationsRead.isPending}
            renderItemAction={(notification) => {
              // What "View" opens comes from the notification's own action data
              // (see resolveNotificationViewTarget). null = nothing to open, so
              // no button at all rather than a dead disabled one (TA-22).
              const target = resolveNotificationViewTarget(notification, {
                hasMessageToOpen: Boolean(latestConsumerMessageEntry),
              });
              if (!target) return null;

              return (
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => {
                    if (target === "evidence") {
                      scrollToEvidence();
                    } else if (latestConsumerMessageEntry) {
                      setContactEntry(latestConsumerMessageEntry);
                    }
                  }}
                  className="cursor-pointer"
                >
                  View
                </Button>
              );
            }}
          />

          {/* Action prominence banners - config-authored via the action's
            ui_hints.banner (e.g. the approval policy flags claims over
            the threshold until they are sent for approval). */}
          {availableActions
            .filter((action) => action.actionable && action.banner)
            .map((action) => (
              <div
                key={action.key}
                data-testid="action-banner"
                className="mb-4 flex items-center justify-between gap-3 rounded-2xl border-[0.5px] border-[var(--status-warn)] bg-[var(--bg-subtle)] px-4 py-2.5"
              >
                <div className="flex items-center gap-2">
                  <ShieldAlert className="h-3 w-3 shrink-0 text-[var(--status-warn)]" />
                  <span className="text-[11px] text-[var(--ink-2)]">
                    {action.banner}
                  </span>
                </div>
                {/* Deliberately NOT `action.label`: the header already
                  carries the action under its real name, and a second control
                  with the same accessible name is a screen-reader ambiguity
                  and a strict-mode collision for every spec that targets an
                  action by name. The banner copy beside it says what is
                  needed. An engine step's banner is guidance only: running it
                  from here would skip the form of the action that diverts to
                  it (WPS-235: Submit for Approval and its rationale). */}
                {!isEngineStep(action) && (
                  <Button
                    variant="outline"
                    size="xs"
                    onClick={() => setActiveAction(action)}
                    className="cursor-pointer border-[var(--status-warn)] text-[var(--status-warn)]"
                  >
                    {t("claims.actionBanner.cta", "Continue")}
                  </Button>
                )}
              </div>
            ))}

          {/* Duplicate-media findings. Internal-only, exactly like the duplicate
            callout below - an external partner working the claim must never
            see a fraud finding. Absent entirely when the capability is off. */}
          {!isPartnerAccess && claim.media_matches ? (
            <DuplicateMediaPanel
              review={claim.media_matches}
              onPreviewClaim={(claimUuid) => setPreviewDupUuid(claimUuid)}
              canResolve={canReviewDuplicates}
              isResolving={resolveImageMatch.isPending}
              onResolve={async ({ matchUuid, outcome, note }) => {
                await resolveImageMatch.mutateAsync({
                  uuid: claim.uuid,
                  matchUuid,
                  outcome,
                  note,
                });
                toast.success("Outcome recorded.");
              }}
            />
          ) : null}

          {/* Potential duplicate callout - sibling claims whose consumer /
            holding matches on the tenant-configured refs (email/phone/name/
            address). Matched on each claim's frozen submission-time snapshot
            (TA-35), so editing the account later never moves the flag; works
            for voice-agent claims with no human at creation. Each sibling can be
            reviewed individually (UAT FR-006): a chip acknowledged shows a
            "Reviewed" marker, the rest keep their per-chip control. The
            callout stays visible while any sibling is still unreviewed, and
            re-surfaces automatically if a new unreviewed sibling appears
            later. */}
          {(() => {
            // Duplicate detection is an internal fraud/payment-review surface -
            // never shown to partners working the claim on their desk.
            if (isPartnerAccess) return null;
            const dups = claim.potential_duplicates ?? [];
            const unreviewed = dups.filter((d) => !d.reviewed);
            if (dups.length === 0 || unreviewed.length === 0) return null;
            return (
              <div className="mb-4 flex flex-col gap-2 rounded-2xl border-[0.5px] border-[var(--status-warn)] bg-[var(--bg-subtle)] px-4 py-2.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2">
                    <Layers className="mt-px h-3 w-3 shrink-0 text-[var(--status-warn)]" />
                    <span className="text-[11px] text-[var(--ink-2)]">
                      Potential duplicate - this customer matches{" "}
                      {dups.length === 1
                        ? "1 other claim"
                        : `${dups.length} other claims`}
                      {unreviewed.length !== dups.length
                        ? ` (${unreviewed.length} not yet reviewed)`
                        : ""}
                      . Review each before paying.
                    </span>
                  </div>
                  {canReviewDuplicates && unreviewed.length > 1 && (
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={() => handleDupDialogChange(true)}
                      disabled={resolveDuplicates.isPending}
                      className="shrink-0 cursor-pointer border-[var(--status-warn)] text-[11px] text-[var(--status-warn)]"
                    >
                      <Check className="h-3 w-3" />
                      Mark all reviewed
                    </Button>
                  )}
                </div>
                <div className="flex flex-col gap-1.5 pl-5">
                  {dups.map((dup) => (
                    <div
                      key={dup.claim_uuid}
                      className="flex flex-wrap items-center gap-2"
                    >
                      {/* Opens the read-only preview dialog in place (PR #860)
                        so the reviewer keeps the claim they are working on;
                        the dialog itself offers "Open full claim". */}
                      <Button
                        variant="link"
                        onClick={() => setPreviewDupUuid(dup.claim_uuid)}
                        className="inline-flex h-auto cursor-pointer items-center gap-1 p-0 text-[11px] text-[var(--status-warn)] hover:underline"
                      >
                        <ExternalLink className="h-3 w-3 shrink-0" />
                        {dup.reference}
                        {dup.claim_state ? ` (${dup.claim_state})` : ""}
                      </Button>
                      <span className="text-[11px] text-[var(--ink-3)]">
                        matched on{" "}
                        {(dup.matched_on ?? [])
                          .map((ref) =>
                            ref.replace("consumer_", "").replace("field:", ""),
                          )
                          .join(", ")}
                      </span>
                      {dup.reviewed ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-[var(--status-ok)]">
                          <CheckCircle2 className="h-3 w-3 shrink-0" />
                          Reviewed
                        </span>
                      ) : (
                        canReviewDuplicates && (
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={() =>
                              handleResolveOneDuplicate(
                                dup.claim_uuid,
                                dup.reference,
                              )
                            }
                            disabled={
                              resolveDuplicates.isPending &&
                              reviewingDupUuid === dup.claim_uuid
                            }
                            className="h-auto cursor-pointer px-1.5 py-0.5 text-[11px] text-[var(--status-warn)]"
                          >
                            <Check className="h-3 w-3" />
                            {resolveDuplicates.isPending &&
                            reviewingDupUuid === dup.claim_uuid
                              ? "Marking..."
                              : "Mark reviewed"}
                          </Button>
                        )
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* WPS-145: while the hold is ON it stops being a setting and
              becomes a state of the claim, so it leads the page above the tab
              bar - visible from Settlement, LOA, Evidence and everywhere else,
              not just Overview - and carries its own off-switch. The quiet
              Overview row below renders only while the hold is OFF, so the
              control is never in two places at once. */}
          {claim.communication_hold?.active ? (
            <CommunicationHoldControl
              claimUuid={claim.uuid}
              hold={claim.communication_hold}
              variant="banner"
            />
          ) : null}

          {/* ── Tab Bar + Content ───────────────────────────── */}
          {/*
          Merge of PR #392 (controlled-tabs via activeTab/setActiveTab so
          auto-tab navigation works after workflow transitions) and our
          existing entity-presence gating from `useEntityRegistered`.
          Auto-tab navigation after a transition reads the new state's
          `workspace_category` EAV attribute - no state-key table.
        */}
          <ClaimLayoutLockContext.Provider value={cardsView.pinned}>
          <Tabs
            value={activeTab || defaultTab}
            onValueChange={setActiveTab}
            className="cv-uniform"
          >
            {showTabBar && (
              <div className="relative">
              <TabsList variant="line">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                {/* OC merges Resolution into Overview (WP1-57); standard keeps it. */}
                {!sectionedDesign && hasResolutionWorkspace && (
                  <TabsTrigger value="resolution">Resolution</TabsTrigger>
                )}
                {hasLabEntity && (
                  <TabsTrigger
                    value="lab"
                    disabled={labTabDisabled}
                    title={
                      labTabDisabled
                        ? t(
                            "claims.labNotUsedOnThisClaimType",
                            "Lab analysis is not part of this claim type",
                          )
                        : undefined
                    }
                  >
                    Lab
                  </TabsTrigger>
                )}
                {hasInspectionEntity && (
                  <TabsTrigger value="inspection">Inspection</TabsTrigger>
                )}
                {hasSettlementEntity && (
                  <TabsTrigger value="settlement">Settlement</TabsTrigger>
                )}
                {hasPaymentsTab && (
                  <TabsTrigger value="payments">
                    {t("claims.tab.payments", "Payments")}
                  </TabsTrigger>
                )}
                {hasAssessmentTab && (
                  <TabsTrigger value="assessment">Assessment</TabsTrigger>
                )}
                {showLoaTab && (
                  <TabsTrigger
                    value="loa"
                    badge={
                      loaPendingCount > 0 ? (
                        <Badge
                          variant="default"
                          title={t(
                            "claims.approvalsPending",
                            "Approvals pending",
                          )}
                          className="h-[18px] min-w-[18px] rounded-full px-1 text-[11px] leading-none"
                        >
                          {loaPendingCount}
                        </Badge>
                      ) : undefined
                    }
                  >
                    LOA
                  </TabsTrigger>
                )}
                {isUmbrellaParent && (
                  <TabsTrigger value="children">
                    {isGroupClaim ? groupMembersLabel : "Items"}
                  </TabsTrigger>
                )}
                {hasEvidenceTab && (
                  <TabsTrigger value="evidence">
                    {t("claims.evidenceTab")}
                  </TabsTrigger>
                )}
                {/* WP1-67: cycle-time reporting view. Sits LAST (after the
                  workflow-stage tabs) because it's a read-only data surface,
                  not a step in the claim flow. Flag-gated + client-agnostic. */}
                {hasCycleTime && (
                  <TabsTrigger value="cycletime">Insights</TabsTrigger>
                )}
              </TabsList>
              {/* How the Overview is laid out (Sections or Full cards, Pin my
                  view) sits beside the tabs, so it costs no row of its own.
                  WPS-146: Lock layout shows on every tab whose cards move;
                  Sections / Full cards is the Overview's alone. */}
              {(activeTab || defaultTab) === "overview" ? (
                <PinMyViewControls
                  view={cardsView}
                  className="mb-4 lg:absolute lg:-top-2 lg:right-0 lg:mb-0"
                />
              ) : CARD_GRID_TABS.has(activeTab || defaultTab) ? (
                <PinMyViewControls
                  view={{ ...cardsView, layout: undefined, setLayout: undefined }}
                  className="mb-4 lg:absolute lg:-top-2 lg:right-0 lg:mb-0"
                />
              ) : null}
              </div>
            )}

            {/* WP1-67: "Insights" tab - the full cycle-time story (total, our
              work vs awaiting customer, by stage / status / person). Lives in
              its own tab, not on Overview, so Overview stays calm. Flag-gated
              + client-agnostic. */}
            {hasCycleTime && (
              <TabsContent value="cycletime">
                <ClaimCycleTimePanel cycleTime={metadata.cycle_time} />
              </TabsContent>
            )}

            {/* ── Overview Tab ─────────────────────────────── */}
            <TabsContent value="overview">
              <PinMyViewBar view={cardsView} controlsElsewhere={showTabBar} />
              {/* Partner Work - owner view of the branches forked off this
                  claim (Assign to partner). While any branch is open the claim
                  is parked (read-only); the banner explains why forward actions
                  are hidden. Partners never see this (they see their own
                  workflow). */}
              {showPartnerWork && partnerBranchOpenCount > 0 ? (
                <div className="mb-4 rounded-md border-[0.5px] border-l-[3px] border-[var(--rule)] border-l-[var(--status-warn)] bg-[var(--bg-light)] px-3 py-2 text-[12px] text-[var(--ink-2)]">
                  Parked - awaiting {partnerBranchOpenCount} partner branch
                  {partnerBranchOpenCount > 1 ? "es" : ""}. Forward actions
                  resume when they complete.
                </div>
              ) : null}
              {/* Multi-item (umbrella) parents carry no product/customer of
                  their own, so lead the Overview with a summary of the line
                  items. Group claims already get their own parent overview
                  below; this covers generic multi-item parents. Shown purely on
                  data presence (child_claim_count > 0), so only tenants using
                  multi-item claims ever see it. */}
              {isUmbrellaParent && !isGroupClaim && (
                <div className="mb-4">
                  <ClaimItemsSummaryCard
                    parentUuid={claim.uuid}
                    onViewAll={() => setActiveTab("children")}
                  />
                </div>
              )}
              {sectionedDesign ? (
                <>
                  {/* State guidance (e.g. "Claim Received") so a freshly opened
                claim greets the user on Overview with what to do next. */}
                  {currentStateAttributeValues.guidance_title &&
                  !panelShowsGuidance ? (
                    <div className="mb-4 flex items-start gap-3 rounded-2xl border-[0.5px] border-l-[3px] border-[var(--rule)] border-l-[#e67e22] bg-[var(--bg-light)] px-4 py-3">
                      <FileText className="mt-0.5 h-4 w-4 shrink-0 text-[#e67e22]" />
                      <div>
                        <p className="text-[13px] font-medium text-[var(--ink)]">
                          {currentStateAttributeValues.guidance_title as string}
                        </p>
                        {currentStateAttributeValues.guidance_description ? (
                          <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
                            {
                              currentStateAttributeValues.guidance_description as string
                            }
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                  {/* Hidden file input shared by the Evidence "Add" buttons. */}
                  <input
                    ref={evidenceInputRef}
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,video/mp4,video/quicktime"
                    className="hidden"
                    onChange={(event) => {
                      const files = Array.from(event.currentTarget.files ?? []);
                      if (files.length > 0) handleEvidenceFilesSelected(files);
                      event.currentTarget.value = "";
                    }}
                  />
                  {/* Scroll anchor for the "View" action on customer-evidence
                notifications - lands at the top of the detail cards, where
                the Claim Details card (and its evidence rows) begins. */}
                  <div ref={evidenceSectionRef} className="scroll-mt-4" />
                  {isGroupClaim ? (
                    /* Group claim Overview (the group card, Claim Details, Supplier,
                 Decision, Notes, Activity). Masonry columns pack the cards
                 tightly regardless of their height instead of leaving
                 whitespace beside a short card in a row-aligned grid. */
                    <>
                      <DraggableCardGrid
                        storageKey="claim-detail-umbrella-cards-v1"
                        locked={cardsView.pinned}
                        view={cardsView.layout}
                        summaries={overviewSummaries}
                        asideKeys={["communications", "notes"]}
                        asideExtras={overviewAside}
                        appendCards={overviewSections ? undefined : overviewAside}
                        hideable
                        aligned
                        pinnedColumns={overviewPinnedColumns}
                        defaultHiddenKeys={overviewDefaultHiddenCards}
                        hiddenScope={overviewCardScope}
                        className="columns-1 gap-6 md:columns-2"
                        itemClassName="mb-6 break-inside-avoid"
                      >
                        {/* WPS-145: the claim's communication hold. This grid carries no
                            "The Claim" band, so the hold leads the Overview here. Same control
                            and same rules as the banded layout - the tenant's label and the
                            permission gating the switch both come from the API. */}
                        {claim.communication_hold && !claim.communication_hold.active ? (
                          <div data-reorder-static>
                            <CommunicationHoldControl
                              claimUuid={claim.uuid}
                              hold={claim.communication_hold}
                            />
                          </div>
                        ) : null}
                        <EditableDetailCard
                          key="claim-details"
                          title={t("claims.card.claimDetails", "Claim Details")}
                          icon={<FileText className="h-3.5 w-3.5" />}
                          items={[
                            ...claimDetailItems,
                            // The group's member count lives on Claim Details (read-only).
                            ...(claim.group
                              ? [
                                  {
                                    field: defineField({
                                      code_name: "_group_members",
                                      display_name: groupMembersLabel,
                                      field_type: "TEXT",
                                    }),
                                    value: String(
                                      claim.group.member_count,
                                    ),
                                    readOnly: true,
                                  },
                                ]
                              : []),
                          ]}
                          formatOptions={{ locale, currency }}
                          editConfig={
                            editConfig
                              ? {
                                  ...editConfig,
                                  onSave: handleClaimDetailsSave,
                                }
                              : undefined
                          }
                        />

                        {claim.group ? (
                          <DetailCard
                            title={t(
                              "claims.card.group",
                              "{{groupNoun}}",
                            )}
                            icon={<Building className="h-3.5 w-3.5" />}
                          >
                            <FieldRow
                              label={t("claims.card.groupName", "Name")}
                              value={claim.group.name}
                            />
                            <FieldRow
                              label="Address"
                              value={claim.group.address || undefined}
                            />
                            <FieldRow
                              label="Property Manager"
                              value={
                                claim.group.manager_name ||
                                claim.group.manager_email ||
                                undefined
                              }
                            />
                          </DetailCard>
                        ) : null}

                        {/* WPS-160 Claim Team - internal-only; self-hides unless
                          the tenant is on advanced_routing with a slot
                          catalogue. */}
                        {!isPartnerAccess && (
                          <ClaimTeamPanel
                            key="claim-team"
                            data-card-title={t("claimTeam.title", "Claim Team")}
                            claimUuid={claim.uuid}
                            footer={partnerAssignFooter}
                          />
                        )}

                        <ClaimDocumentationCard
                          key="documentation"
                          data-card-title={t("claims.documentationTitle", "Documentation")}
                          claimType={claim.claim_schema_key ?? ""}
                          claimUuid={claim.uuid}
                          canEdit={canEdit && !isTerminal}
                        />

                        {canViewDecisionCards && (
                          <Fragment key="decision-group">
                            <SupplierContactsCard
                              key="supplier"
                              data-card-title={t("claims.card.claimContacts", "Claim Contacts")}
                              claim={claim}
                            />

                            <EditableDetailCard
                              key="decision"
                              title={t("claims.card.decision", "Decision")}
                              icon={<Shield className="h-3.5 w-3.5" />}
                              items={decisionCardItems}
                              formatOptions={{ locale, currency }}
                              editConfig={
                                editConfig
                                  ? {
                                      ...editConfig,
                                      onSave: handleDecisionSave,
                                    }
                                  : undefined
                              }
                            />
                          </Fragment>
                        )}

                        <NotesPanel
                          key="notes"
                          data-card-title={t("notes.title", "Notes")}
                          recordId={claim.uuid}
                          recordType="claim"
                          canAddInternal
                          canAddCustomerVisible
                          allowAttachments
                          onNoteAdded={() => refetch()}
                        />

                        <div key="communications" data-card-title={t("claimComms.title", "Communications")}>{communicationsCard}</div>
                      </DraggableCardGrid>
                      
                    </>
                  ) : (
                    <>
                      {/* Masonry columns (not a row-aligned grid) so cards of differing
                heights pack against each other without leaving whitespace.
                Section headings span both columns via [column-span:all]. */}
                      <>
                        <DraggableCardGrid
                          storageKey="claim-detail-hoa-cards-v1"
                          locked={cardsView.pinned}
                          view={cardsView.layout}
                          summaries={overviewSummaries}
                          asideKeys={["communications", "notes"]}
                          asideExtras={overviewAside}
                          appendCards={overviewSections ? undefined : overviewAside}
                          hideable
                          aligned
                          pinnedColumns={overviewPinnedColumns}
                          defaultHiddenKeys={overviewDefaultHiddenCards}
                          hiddenScope={overviewCardScope}
                          className="columns-1 gap-6 md:columns-2"
                          itemClassName="mb-6 break-inside-avoid"
                        >
                          <div data-reorder-divider>
                            <OverviewSectionHeading label="The Claim" />
                          </div>
                          {/* WPS-145: the claim's communication hold, under the
                              "The Claim" band where Owens Corning asked for it.
                              Rendered only when the tenant has configured a hold;
                              the label, helper text, and the permission gating the
                              switch all come from the API. Marked
                              ``data-reorder-static`` so it renders full width and
                              is never draggable, and kept OUTSIDE the Claim Details
                              card on purpose - that card is gated on the claim
                              being non-terminal, and the hold has to stay flippable
                              at any point in the claim, including after it closes. */}
                          {claim.communication_hold && !claim.communication_hold.active ? (
                            <div data-reorder-static>
                              <CommunicationHoldControl
                                claimUuid={claim.uuid}
                                hold={claim.communication_hold}
                              />
                            </div>
                          ) : null}
                          {/* The group - the umbrella claim's "who/where" card, the
                  analogue of the customer/property card on a normal claim.
                  Same single-column size as the other Overview cards. */}
                          {claim.group ? (
                            <DetailCard
                              key="hoa-community"
                              folded={isFolded("hoa-community")}
                              onToggleFold={() => toggleFold("hoa-community")}
                              title={t(
                                "claims.card.group",
                                "{{groupNoun}}",
                              )}
                              icon={<Building className="h-3.5 w-3.5" />}
                            >
                              <FieldRow
                                label={t("claims.card.groupName", "Name")}
                                value={claim.group.name}
                              />
                              <FieldRow
                                label="Address"
                                value={claim.group.address || undefined}
                              />
                              <FieldRow
                                label="Property Manager"
                                value={
                                  claim.group.manager_name ||
                                  claim.group.manager_email ||
                                  undefined
                                }
                              />
                              <FieldRow
                                label={groupMembersLabel}
                                value={String(
                                  claim.group.member_count,
                                )}
                              />
                            </DetailCard>
                          ) : null}
                          {/* Claim attributes - WP1-57 presents them in the clearer
                  Defect Details / Claim Context groups the old Resolution tab
                  used, with a catch-all "Claim Details" card for anything
                  else, so no field is lost. */}
                          {claimDetailItems.length === 0 ? (
                            <DetailCard
                              key="claim-details"
                              folded={isFolded("claim-details")}
                              onToggleFold={() => toggleFold("claim-details")}
                              title={t(
                                "claims.card.claimDetails",
                                "Claim Details",
                              )}
                              icon={<FileText className="h-3.5 w-3.5" />}
                            >
                              <div className="px-3 py-3 text-[11px] text-[var(--ink-3)]">
                                No claim details recorded
                              </div>
                            </DetailCard>
                          ) : (
                            <Fragment key="claim-details-group">
                              {defectDetailItems.length > 0 && (
                                <EditableDetailCard
                                  key="defect-details"
                                  editButtonTestId="defect-details-edit-btn"
                                  title={t(
                                    "claims.defectDetails",
                                    "Defect Details",
                                  )}
                                  icon={<Layers className="h-3.5 w-3.5" />}
                                  items={defectDetailItems}
                                  formatOptions={{ locale, currency }}
                                  editConfig={
                                    editConfig
                                      ? {
                                          ...editConfig,
                                          // WPS-178 #2: no "Provide reason"
                                          // prompt on Defect Details.
                                          // Correcting the product or
                                          // colourway is a factual fix, not a
                                          // decision needing justification.
                                          // Scoped to this card - every other
                                          // editable card keeps its edit
                                          // reason.
                                          requireReason: false,
                                          // The claim edit endpoint requires an
                                          // edit_reason regardless (it writes
                                          // the AuditLog entry), so supply a
                                          // standing one instead of dropping
                                          // the audit trail server-side. The
                                          // user is not asked; the change is
                                          // still recorded.
                                          onSave: (values, reason) =>
                                            handleClaimDetailsSave(
                                              values,
                                              reason ||
                                                t(
                                                  "claims.defectDetails.editReason",
                                                  "Defect details updated",
                                                ),
                                            ),
                                        }
                                      : undefined
                                  }
                                />
                              )}
                              {contextItems.length > 0 && (
                                <EditableDetailCard
                                  key="claim-context"
                                  testId="claim-context-card"
                                  title={t(
                                    "claims.card.claimContext",
                                    "Claim Context",
                                  )}
                                  icon={<MapPin className="h-3.5 w-3.5" />}
                                  items={contextItems}
                                  formatOptions={{ locale, currency }}
                                  editConfig={
                                    editConfig
                                      ? {
                                          ...editConfig,
                                          onSave: handleClaimDetailsSave,
                                        }
                                      : undefined
                                  }
                                />
                              )}
                              {otherClaimItems.length > 0 && (
                                <EditableDetailCard
                                  key="claim-details-other"
                                  title={t(
                                    "claims.card.claimDetails",
                                    "Claim Details",
                                  )}
                                  icon={<FileText className="h-3.5 w-3.5" />}
                                  items={otherClaimItems}
                                  formatOptions={{ locale, currency }}
                                  editConfig={
                                    editConfig
                                      ? {
                                          ...editConfig,
                                          onSave: handleClaimDetailsSave,
                                        }
                                      : undefined
                                  }
                                />
                              )}
                            </Fragment>
                          )}

                          {/* WPS-160 Claim Team - internal-only; self-hides
                  unless the tenant is on advanced_routing with a slot
                  catalogue. */}
                          {!isPartnerAccess && (
                            <ClaimTeamPanel
                              key="claim-team"
                              data-card-title={t("claimTeam.title", "Claim Team")}
                              claimUuid={claim.uuid}
                              footer={partnerAssignFooter}
                            />
                          )}

                          {/* Supplier Information (WPS-175 #2, WPS-204 #7): under
                  Claim Team, editable so the RS fills the supplier in on the
                  call. Shown whenever the schema tags supplier fields. */}
                          {supplierInformationItems.length > 0 && (
                            <EditableDetailCard
                              key="supplier-information"
                              title={t(
                                "claims.card.supplierInformation",
                                "Supplier Information",
                              )}
                              icon={<Building className="h-3.5 w-3.5" />}
                              items={supplierInformationItems}
                              formatOptions={{ locale, currency }}
                              editConfig={
                                supplierEditConfig
                                  ? {
                                      ...supplierEditConfig,
                                      onSave: handleClaimDetailsSave,
                                    }
                                  : undefined
                              }
                            />
                          )}

                          {/* Decision - internal only. External partner (dealer)
                  roles lack ``claims.view_decisions`` so the card (and the
                  decision attribute_values behind it) never surface to
                  them; the backend strips the same fields from the
                  payload, so this is defence in depth, not the only gate. */}
                          {canViewDecisionCards && (
                            <div
                              key="decision"
                              data-card-title={t(
                                "claims.card.decision",
                                "Decision",
                              )}
                              className="flex flex-col gap-2.5 [&>:first-child]:flex-1"
                            >
                              <EditableDetailCard
                                title={t("claims.card.decision", "Decision")}
                                icon={<Shield className="h-3.5 w-3.5" />}
                                items={
                                  decisionItems.length > 0
                                    ? decisionItems
                                    : [
                                        {
                                          field: defineField({
                                            code_name: "_awaiting",
                                            display_name: "Status",
                                            field_type: "TEXT",
                                          }),
                                          value: "Awaiting review decision",
                                          readOnly: true,
                                        },
                                      ]
                                }
                                formatOptions={{ locale, currency }}
                                editConfig={
                                  editConfig
                                    ? {
                                        ...editConfig,
                                        onSave: handleDecisionSave,
                                      }
                                    : undefined
                                }
                              />
                              {/* Receipt-pricing guidance beside the amounts being
                      judged - decision input, present whenever the tenant
                      has pricing guidance configured for this claim. */}
                              {/* Part of the Decision card: no grip or hide of its own. */}
                              <NestedCardBoundary>
                                <PricingGuidancePanel guidance={claim.pricing_guidance} />
                              </NestedCardBoundary>
                            </div>
                          )}


                          {/* Homeowner & Property, holdingless variant (WPS-175 #2).
                  The card below reads the registration's consumer and its
                  host attributes - a Job Stop has neither. A contractor files
                  it over defective stock in their own yard and often does not
                  yet know whose roof it was destined for, so these arrive
                  later in the flow and the card has to be editable to collect
                  them. Reads the claim's own attributes instead. */}
                          {/* A claim can legitimately arrive with nothing
                            registered behind it - the absence is normal, not
                            missing data. Said once, quietly, because someone
                            who does not know that reads an empty property card
                            as a broken one. */}
                          {!claim.holding_uuid && (
                            <p
                              key="no-registration-note"
                              data-reorder-static
                              className="flex items-start gap-2 px-1 text-[14px] leading-[1.45] text-[var(--ink-2)]"
                            >
                              <Info className="mt-[2px] h-3 w-3 shrink-0" />
                              <span>
                                {t(
                                  "claims.noRegistrationNote",
                                  "No registration is linked to this claim. Some claims are raised without one. If the item was registered separately, it can be linked from the registration record.",
                                )}
                              </span>
                            </p>
                          )}
                          {!claim.holding_uuid && (
                            <EditableDetailCard
                              key="homeowner-property"
                              title={t(
                                "claims.card.homeownerProperty",
                                "Homeowner & Property",
                              )}
                              icon={<Home className="h-3.5 w-3.5" />}
                              editButtonTestId="holdingless-homeowner-edit-btn"
                              items={mergeRoutedCardItems(
                                // WPS-146 #4: schema-routed rows (mailing
                                // address, named contact) follow the card's
                                // own - see the holding-linked card below. A
                                // routed field the card already lists (the
                                // property address) takes its slot, once.
                                [
                                  {
                                    field: defineField({
                                      code_name: "homeowner_name",
                                      display_name: t(
                                        "claims.homeowner.name",
                                        "Name",
                                      ),
                                      field_type: "TEXT",
                                    }),
                                    value: claim.attribute_values?.homeowner_name,
                                  },
                                  {
                                    field: defineField({
                                      code_name: "homeowner_email",
                                      display_name: t(
                                        "claims.homeowner.email",
                                        "Email",
                                      ),
                                      field_type: "EMAIL",
                                    }),
                                    value:
                                      claim.attribute_values?.homeowner_email,
                                  },
                                  {
                                    field: defineField({
                                      code_name: "homeowner_phone",
                                      display_name: t(
                                        "claims.homeowner.phone",
                                        "Phone",
                                      ),
                                      field_type: "PHONE",
                                    }),
                                    value:
                                      claim.attribute_values?.homeowner_phone,
                                  },
                                  {
                                    field: defineField({
                                      code_name: "property_address",
                                      display_name: t(
                                        "claims.homeowner.propertyAddress",
                                        "Property Address",
                                      ),
                                      field_type: "ADDRESS",
                                    }),
                                    value:
                                      claim.attribute_values?.property_address,
                                  },
                                ],
                                routedHomeownerItems,
                              )}
                              formatOptions={{ locale, currency }}
                              editConfig={
                                editConfig
                                  ? {
                                      ...editConfig,
                                      // Adding the homeowner is capturing who
                                      // they are, not a decision to justify.
                                      requireReason: false,
                                      onSave: handleClaimDetailsSave,
                                    }
                                  : undefined
                              }
                            />
                          )}

                          {/* Claim Contacts - holdingless claims (Uninstalled /
                  Job Stop). WPS-175 #2 puts it directly below Homeowner &
                  Property: the supplier and the other contacts read as a
                  follow-on from whose property it is, not ahead of it. Only the
                  DEFAULT order - cards are drag-reorderable per user
                  (DraggableCardGrid), so an RS can arrange it their own way. */}
                          {isHoldingless && (
                            <SupplierContactsCard
                              key="supplier"
                              data-card-title={t("claims.card.claimContacts", "Claim Contacts")}
                              claim={claim}
                            />
                          )}

                          {/* Homeowner & Property - WP1-57 merges the old Customer +
                  Host cards into one, so the homeowner, the property address
                  and the associated structure all read together. Vehicle/
                  vessel attrs are filtered out of the property rows; the
                  configured host display wins when present. */}
                          {claim.holding_uuid && (
                            <EditableDetailCard
                              key="homeowner-property"
                              folded={isFolded("homeowner-property")}
                              onToggleFold={() =>
                                toggleFold("homeowner-property")
                              }
                              title={t(
                                "claims.card.homeownerProperty",
                                "Homeowner & Property",
                              )}
                              icon={
                                linkedHolding?.host?.kind === "VEHICLE" ? (
                                  <Car className="h-3.5 w-3.5" />
                                ) : linkedHolding?.host?.kind === "VESSEL" ? (
                                  <Anchor className="h-3.5 w-3.5" />
                                ) : (
                                  <Home className="h-3.5 w-3.5" />
                                )
                              }
                              trailing={customerCardTrailing}
                              items={[
                                // The registration owns these; they are edited
                                // there, never from the claim.
                                ...displayOnlyCardItems([
                                  {
                                    key: "name",
                                    label: "Name",
                                    value: resolvedConsumerName,
                                  },
                                  {
                                    key: "email",
                                    label: "Email",
                                    value: resolvedConsumerEmail,
                                    link: !!resolvedConsumerEmail,
                                    onLinkClick: () =>
                                      (window.location.href = `mailto:${resolvedConsumerEmail}`),
                                  },
                                  {
                                    key: "phone",
                                    label: "Phone",
                                    value:
                                      formatPhoneDisplay(resolvedConsumerPhone) ||
                                      undefined,
                                  },
                                  // Property address is rendered by the configured
                                  // host display columns - never hardcoded here, so
                                  // the card display config stays the single source
                                  // of truth and the address never renders twice.
                                  ...(configuredHostRows.length > 0
                                    ? configuredHostRows.map((row) => ({
                                        key: `host_${row.key}`,
                                        label: row.label,
                                        value: row.value,
                                      }))
                                    : (claim.host_attributes ?? [])
                                        .filter(
                                          (a) =>
                                            ![
                                              "make",
                                              "model",
                                              "colour",
                                              "vin",
                                              "registration_number",
                                            ].includes(a.code_name),
                                        )
                                        .map((a) => ({
                                          key: `host_${a.code_name}`,
                                          label: a.display_name,
                                          value: a.value,
                                        }))),
                                ]),
                                // WPS-146 #4: attributes the schema routes here (the
                                // mailing address, the named contact), shown as before.
                                // WPS-203 AC5: the claim owns these, so they edit here.
                                ...routedHomeownerItems.map((item) => ({
                                  ...item,
                                  displayValue: formatDisplayValue(
                                    item.field,
                                    item.value,
                                    { locale, currency },
                                  ),
                                })),
                                ...displayOnlyCardItems([
                                  {
                                    key: "registration",
                                    label: "Registration",
                                    value: "View registration",
                                    link: true,
                                    onLinkClick: () =>
                                      navigate(
                                        registrationPathFromClaim(
                                          claim.holding_uuid,
                                          uuid,
                                        ),
                                      ),
                                  },
                                ]),
                              ]}
                              formatOptions={{ locale, currency }}
                              editButtonTestId="homeowner-property-edit-btn"
                              editConfig={
                                editConfig && routedHomeownerItems.length > 0
                                  ? {
                                      ...editConfig,
                                      onSave: handleClaimDetailsSave,
                                    }
                                  : undefined
                              }
                            />
                          )}

                          {/* WPS-146 #7 / AC5: "the Homeowner & Property section
                  appears in the top-right area of the page".
                  ``overview_pinned_cards`` floats that card to the head of the
                  right-hand column, but a pin only leads the column of its OWN
                  BAND - and the card sat in the second band, which starts some
                  670px down. So the band starts AFTER it: Homeowner & Property
                  now belongs to "The Claim" and the pin puts it top-right,
                  level with Defect Details.

                  Gate the band on holding-based claims (which always render the
                  contacts/contractor cards). Holdingless claims only have the
                  Supplier card, which can be empty, so they skip the band
                  heading to avoid an orphaned label.

                  Labelled "Contacts", not "People & Property": the property
                  left with the card above, and what remains is the list of
                  people to call. */}
                          {claim.holding_uuid ? (
                            <div data-reorder-divider>
                              <OverviewSectionHeading
                                label="Contacts"
                                className="mt-2"
                              />
                            </div>
                          ) : null}

                          {/* Claim Contacts - WP1-57 + WPS-141. Homeowner is the primary
                  contact; any other party already on the claim (inspection
                  contact, a group's property manager) is listed as a secondary.
                  Tenants with the `claim_contact` entity registered also get
                  a managed contacts list (team members + external parties). */}
                          {claim.holding_uuid && (
                            <ClaimContactsCard
                              key="claim-contacts"
                              data-card-title={t("claims.card.claimContacts", "Claim Contacts")}
                              claimUuid={claim.uuid}
                              orgUuid={orgUuid}
                              primary={{
                                role: "Homeowner",
                                name: resolvedConsumerName,
                                email: resolvedConsumerEmail,
                                phone: resolvedConsumerPhone,
                              }}
                              secondary={[
                                {
                                  role: "Inspection Contact",
                                  name:
                                    (claim.attribute_values
                                      ?.appointment_contact_name as
                                      string | undefined) || undefined,
                                  phone:
                                    (claim.attribute_values
                                      ?.appointment_contact_phone as
                                      string | undefined) || undefined,
                                },
                                {
                                  role: "Property Manager",
                                  name:
                                    claim.group?.manager_name ||
                                    undefined,
                                  email:
                                    claim.group?.manager_email ||
                                    undefined,
                                },
                              ]}
                              inspectionContact={{
                                name:
                                  (claim.attribute_values
                                    ?.appointment_contact_name as
                                    string | undefined) || "",
                                phone:
                                  (claim.attribute_values
                                    ?.appointment_contact_phone as
                                    string | undefined) || "",
                              }}
                              editConfig={
                                editConfig
                                  ? {
                                      isPending: editConfig.isPending,
                                      canEdit: editConfig.canEdit,
                                      onSave: handleClaimDetailsSave,
                                    }
                                  : undefined
                              }
                            />
                          )}

                          {/* Transfers covering this registration, so a reviewer sees
                  an owner change in progress. */}
                          {claim.holding_uuid && (
                            <ClaimTransfersCard
                              key="claim-transfers"
                              holdingUuid={claim.holding_uuid}
                            />
                          )}

                          {/* The contractor this claim concerns (WPS-163).
                  Inherited from the registration unless the claim overrides
                  it, and assignable here for the cases that genuinely differ:
                  a second contractor on a repair, or a claim with no
                  registration to inherit from. Renders the same profile the
                  registration does, so the two cannot drift. Same network gate
                  as the other branch, so the two cannot drift on that either. */}
                          {(claim.partner ||
                            tenantSettings?.has_downstream_partnerships) && (
                            <ClaimPartnerCard
                              key="claim-partner"
                              data-card-title={
                              claim.partner?.role_name ??
                              externalRoles[0]?.name ??
                              t("claims.card.partner", "Partner")
                            }
                              claimUuid={claim.uuid}
                              partner={claim.partner}
                              isInherited={claim.partner_is_inherited}
                              canEdit={canEdit && !isTerminal}
                              partnerLocked={partnerLock.locked}
                            />
                          )}

                          {/* Contractor - WP1-58. Editable so a resolution specialist can
                  capture the contractor's contact, phone, email and address on
                  the call. Business + Subcontractor auto-fill from the
                  registration (registration_contractor) the first time, then
                  persist on the claim once saved.

                  WPS-175 #2: no longer gated on `holding_uuid`. Job Stop used
                  to fold its contractor into the Supplier Information card,
                  which is why one card could name a contractor while this one
                  said there was none. Every field here is a claim attribute -
                  nothing on this card ever needed a holding - so a holdingless
                  claim gets the same editable card, which is also what lets an
                  RS capture the contractor after a fast intake. */}
                          {/* WPS-119: a workflow whose contractor is fixed by
                  config (BFS always settles with Integrated Interiors) has no
                  per-claim contractor contact to capture, and its schema
                  declares none of these attributes - so the card can never be
                  filled and renders as five permanent blanks beside the
                  populated Contractor card. Curated off that Overview via the
                  same `Workflow.metadata.overview_hidden_cards` config the
                  Decision card uses. Config, not a schema branch. */}
                          {!isOverviewCardHidden("contractor_contact") && (
                            <EditableDetailCard
                              key="contractor-contact"
                              editButtonTestId="contractor-contact-edit-btn"
                              title={t(
                                "claims.card.contractorContactThisClaim",
                                "Contractor contact (this claim)",
                              )}
                              icon={<UserRound className="h-3.5 w-3.5" />}
                              items={[
                                {
                                  field: defineField({
                                    code_name: "contractor_name",
                                    display_name: "Business",
                                    field_type: "TEXT",
                                  }),
                                  value:
                                    (claim.attribute_values?.contractor_name as
                                      string | undefined) ??
                                    claim.registration_contractor
                                      ?.business_name ??
                                    "",
                                },
                                {
                                  field: defineField({
                                    code_name: "contractor_contact_name",
                                    display_name: "Contact",
                                    field_type: "TEXT",
                                  }),
                                  value:
                                    (claim.attribute_values
                                      ?.contractor_contact_name as
                                      string | undefined) ?? "",
                                },
                                {
                                  field: defineField({
                                    code_name: "contractor_phone",
                                    display_name: "Phone",
                                    field_type: "PHONE",
                                  }),
                                  value:
                                    (claim.attribute_values
                                      ?.contractor_phone as
                                      string | undefined) ?? "",
                                },
                                {
                                  field: defineField({
                                    code_name: "contractor_email",
                                    display_name: "Email",
                                    field_type: "EMAIL",
                                  }),
                                  value:
                                    (claim.attribute_values
                                      ?.contractor_email as
                                      string | undefined) ?? "",
                                },
                                {
                                  field: defineField({
                                    code_name: "contractor_address",
                                    display_name: "Address",
                                    field_type: "TEXT",
                                  }),
                                  value:
                                    (claim.attribute_values
                                      ?.contractor_address as
                                      string | undefined) ?? "",
                                },
                                {
                                  field: defineField({
                                    code_name: "subcontractor_name",
                                    display_name: "Subcontractor",
                                    field_type: "TEXT",
                                  }),
                                  value:
                                    (claim.attribute_values
                                      ?.subcontractor_name as
                                      string | undefined) ??
                                    claim.registration_contractor
                                      ?.subcontractor_name ??
                                    "",
                                },
                              ]}
                              formatOptions={{ locale, currency }}
                              editConfig={
                                editConfig
                                  ? {
                                      ...editConfig,
                                      // Capturing contractor details on a call should be
                                      // one-click - no mandatory "edit reason" prompt.
                                      requireReason: false,
                                      onSave: handleClaimDetailsSave,
                                    }
                                  : undefined
                              }
                            />
                          )}

                          {claim.holding_uuid && (
                            <div data-reorder-divider>
                              <OverviewSectionHeading
                                label="Coverage"
                                className="mt-2"
                              />
                            </div>
                          )}

                          {/* Product - holding-based claims, gated by the
                            product entity view permission. */}
                          {showOverviewProductCard && (
                            <DetailCard
                              key="product"
                              folded={isFolded("product")}
                              onToggleFold={() => toggleFold("product")}
                              title={t("claims.card.product", "Product")}
                              icon={<Package className="h-3.5 w-3.5" />}
                              trailing={
                                hasProductIdentification &&
                                linkedHolding?.unidentified &&
                                (can("holdings.edit.global") ||
                                  can("holdings.edit.acl")) ? (
                                  <Button
                                    onClick={() => setIdentifyProductOpen(true)}
                                    variant="outline"
                                    size="xs"
                                    className="flex cursor-pointer items-center gap-1.5 rounded-[6px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] text-[10px] uppercase tracking-[0.06em] text-[var(--ink)] transition-all duration-150 hover:border-[var(--green)] hover:bg-[var(--green-bg)]"
                                  >
                                    <Package className="h-3 w-3" />
                                    Identify product
                                  </Button>
                                ) : claim.holding_uuid ? (
                                  // Edit product (and any other holding actions)
                                  // via the generic entity-action mechanism.
                                  // Renders nothing unless the tenant has seeded a
                                  // holding action AND the user holds its
                                  // permission (e.g. holding.edit_product_variant),
                                  // so it stays tenant-agnostic. Refetches the
                                  // claim on success since the product name/type
                                  // are denormalised onto the claim row.
                                  <EntityActionMenu
                                    entityTypeKey="holding"
                                    resourceUuid={claim.holding_uuid}
                                    excludeActionKeys={holdingActionExclusions}
                                    triggerLabel={t(
                                      "claims.productActions",
                                      "Product actions",
                                    )}
                                    onActionComplete={() => refetch()}
                                  />
                                ) : undefined
                              }
                            >
                              {overviewProductRows.map((row) => (
                                <FieldRow
                                  key={row.key}
                                  label={row.label}
                                  value={row.value}
                                />
                              ))}
                              {/* The selling dealer is a transaction fact, not a
                      product attribute - it lives in Claim Details, not
                      here. */}
                              {claim.product_uuid &&
                                !linkedHolding?.unidentified && (
                                  <FieldRow
                                    label="Record"
                                    value="View product"
                                    link
                                    onLinkClick={() =>
                                      navigate(
                                        `/manage/products/${claim.product_uuid}`,
                                      )
                                    }
                                  />
                                )}
                              {hasProductIdentification &&
                                linkedHolding?.unidentified &&
                                (can("holdings.edit.global") ||
                                  can("holdings.edit.acl")) && (
                                  <p className="px-4 py-[9px] text-[12px] text-[var(--ink-3)]">
                                    This claim was filed against an unidentified
                                    product.
                                  </p>
                                )}
                            </DetailCard>
                          )}

                          {claim.holding_uuid && (
                            <div key="identify-dialog" data-reorder-static>
                              <IdentifyProductDialog
                                holdingUuid={claim.holding_uuid}
                                open={identifyProductOpen}
                                onOpenChange={setIdentifyProductOpen}
                                onReassigned={refetch}
                              />
                            </div>
                          )}

                          {/* Warranty - holding-based claims, gated by the
                            warranty entity view permission. */}
                          {claim.holding_uuid && canViewWarrantyCard && (
                            <DetailCard
                              key="warranty"
                              folded={isFolded("warranty")}
                              onToggleFold={() => toggleFold("warranty")}
                              title={`Warranty · ${warrantyType}`}
                              icon={<Shield className="h-3.5 w-3.5" />}
                            >
                              {configuredWarrantyRows.length > 0 ? (
                                <RegistrationConfiguredFieldRows
                                  items={configuredWarrantyRows}
                                />
                              ) : (
                                <FieldRow
                                  label="Status"
                                  value={
                                    <StatusBadge status={warrantyStatus} />
                                  }
                                />
                              )}
                              {configuredWarrantyRows.length === 0 &&
                                warrantyForDuration && (
                                  <>
                                    <FieldRow
                                      label="Tier"
                                      value={warrantyTier}
                                    />
                                    <FieldRow
                                      label="Class"
                                      value={warrantyClassName}
                                    />
                                    <FieldRow
                                      label="Duration"
                                      value={durationLabel(warrantyForDuration)}
                                    />
                                    <FieldRow
                                      label="Active from"
                                      value={
                                        warrantyActiveFrom
                                          ? formatDate(warrantyActiveFrom) ||
                                            warrantyActiveFrom
                                          : undefined
                                      }
                                    />
                                    {!warrantyForDuration.is_lifetime && (
                                      <FieldRow
                                        label="Expires"
                                        value={
                                          warrantyExpires
                                            ? formatDate(warrantyExpires) ||
                                              warrantyExpires
                                            : undefined
                                        }
                                      />
                                    )}
                                  </>
                                )}
                              {configuredWarrantyRows.length === 0 &&
                                !warrantyForDuration && (
                                  <FieldRow label="Tier" value="-" />
                                )}
                              {warrantyDocuments.length > 0 && (
                                <div className="border-b-[0.5px] border-b-[var(--rule-light)] px-4 py-[9px]">
                                  <div className="mb-2 text-[9.5px] uppercase tracking-[0.16em] text-[var(--ink-3)]">
                                    Documents
                                  </div>
                                  {warrantyDocuments.map((document) =>
                                    document.url ? (
                                      <a
                                        key={document.uuid}
                                        href={document.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-1 flex items-center justify-between gap-3 text-[12px] hover:underline"
                                      >
                                        <span className="flex min-w-0 items-center gap-1.5 text-[var(--green)]">
                                          <ExternalLink className="h-3 w-3 shrink-0" />
                                          <span className="truncate">
                                            {document.label}
                                          </span>
                                        </span>
                                        <span className="font-mono text-[10px] text-[var(--ink-3)]">
                                          {document.kind}
                                        </span>
                                      </a>
                                    ) : (
                                      <div
                                        key={document.uuid}
                                        className="mt-1 flex items-center justify-between gap-3 text-[12px]"
                                      >
                                        <span className="truncate text-[var(--ink-3)]">
                                          {document.label}
                                        </span>
                                        <span className="font-mono text-[10px] text-[var(--ink-3)]">
                                          {document.kind}
                                        </span>
                                      </div>
                                    ),
                                  )}
                                </div>
                              )}
                            </DetailCard>
                          )}

                          {/* Follow-up Schedule (awaiting_docs only) flows as a masonry
                  card alongside the rest. */}
                          {currentStateKey === "awaiting_docs" &&
                            followUpMilestones && (
                              <DetailCard
                                key="followup"
                                folded={isFolded("followup")}
                                onToggleFold={() => toggleFold("followup")}
                                title={t(
                                  "claims.card.followUpSchedule",
                                  "Follow-up Schedule",
                                )}
                                icon={<Clock className="h-3.5 w-3.5" />}
                              >
                                {followUpMilestones.map((milestone) => (
                                  <FieldRow
                                    key={milestone.day}
                                    label={milestone.label}
                                    value={
                                      <span
                                        className={
                                          milestone.done
                                            ? "text-[var(--ink)]"
                                            : "text-[var(--ink-3)]"
                                        }
                                      >
                                        {formatDate(
                                          milestone.date.toISOString(),
                                        )}
                                        {milestone.done &&
                                          milestone.day > 0 && (
                                            <Check className="ml-1.5 inline-block h-3 w-3 text-[var(--green)]" />
                                          )}
                                      </span>
                                    }
                                  />
                                ))}
                              </DetailCard>
                            )}

                          {/* Notes and Activity flow as masonry cards too, so they pack
                  against the columns above instead of starting a new row.
                  Evidence moved to the Evidence tab. */}
                          <NotesPanel
                            key="notes"
                            data-card-title={t("notes.title", "Notes")}
                            recordId={claim.uuid}
                            recordType="claim"
                            canAddInternal
                            canAddCustomerVisible
                            allowAttachments
                            onNoteAdded={() => refetch()}
                          />
                          <div key="communications" data-card-title={t("claimComms.title", "Communications")}>{communicationsCard}</div>
                        </DraggableCardGrid>
                        
                      </>
                    </>
                  )}
                </>
              ) : (
                <>
                  {/* State guidance (e.g. "Claim Received") so a freshly opened
                claim greets the user on Overview with what to do next. */}
                  {currentStateAttributeValues.guidance_title &&
                  !panelShowsGuidance ? (
                    <div className="mb-4 flex items-start gap-3 rounded-2xl border-[0.5px] border-l-[3px] border-[var(--rule)] border-l-[#e67e22] bg-[var(--bg-light)] px-4 py-3">
                      <FileText className="mt-0.5 h-4 w-4 shrink-0 text-[#e67e22]" />
                      <div>
                        <p className="text-[13px] font-medium text-[var(--ink)]">
                          {currentStateAttributeValues.guidance_title as string}
                        </p>
                        {currentStateAttributeValues.guidance_description ? (
                          <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
                            {
                              currentStateAttributeValues.guidance_description as string
                            }
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                  {/* Hidden file input shared by the Evidence "Add" buttons. */}
                  <input
                    ref={evidenceInputRef}
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,video/mp4,video/quicktime"
                    className="hidden"
                    onChange={(event) => {
                      const files = Array.from(event.currentTarget.files ?? []);
                      if (files.length > 0) handleEvidenceFilesSelected(files);
                      event.currentTarget.value = "";
                    }}
                  />
                  {/* Scroll anchor for the "View" action on customer-evidence
                notifications - lands at the top of the detail cards, where
                the Claim Details card (and its evidence rows) begins. */}
                  <div ref={evidenceSectionRef} className="scroll-mt-4" />
                  {isGroupClaim ? (
                    /* Group claim Overview (the group card, Claim Details, Supplier,
                 Decision, Notes, Activity). Masonry columns pack the cards
                 tightly regardless of their height instead of leaving
                 whitespace beside a short card in a row-aligned grid. */
                    <>
                      <DraggableCardGrid
                        storageKey="claim-detail-umbrella-cards-v1"
                        locked={cardsView.pinned}
                        view={cardsView.layout}
                        summaries={overviewSummaries}
                        asideKeys={["communications", "notes"]}
                        asideExtras={overviewAside}
                        appendCards={overviewSections ? undefined : overviewAside}
                        hideable
                        aligned
                        pinnedColumns={overviewPinnedColumns}
                        defaultHiddenKeys={overviewDefaultHiddenCards}
                        hiddenScope={overviewCardScope}
                        className="columns-1 gap-6 md:columns-2"
                        itemClassName="mb-6 break-inside-avoid"
                      >
                        {/* WPS-145: the claim's communication hold. This grid carries no
                            "The Claim" band, so the hold leads the Overview here. Same control
                            and same rules as the banded layout - the tenant's label and the
                            permission gating the switch both come from the API. */}
                        {claim.communication_hold && !claim.communication_hold.active ? (
                          <div data-reorder-static>
                            <CommunicationHoldControl
                              claimUuid={claim.uuid}
                              hold={claim.communication_hold}
                            />
                          </div>
                        ) : null}
                        <EditableDetailCard
                          key="claim-details"
                          title={t("claims.card.claimDetails", "Claim Details")}
                          icon={<FileText className="h-3.5 w-3.5" />}
                          items={[
                            ...claimDetailItems,
                            // The group's member count lives on Claim Details (read-only).
                            ...(claim.group
                              ? [
                                  {
                                    field: defineField({
                                      code_name: "_group_members",
                                      display_name: groupMembersLabel,
                                      field_type: "TEXT",
                                    }),
                                    value: String(
                                      claim.group.member_count,
                                    ),
                                    readOnly: true,
                                  },
                                ]
                              : []),
                          ]}
                          formatOptions={{ locale, currency }}
                          editConfig={
                            editConfig
                              ? {
                                  ...editConfig,
                                  onSave: handleClaimDetailsSave,
                                }
                              : undefined
                          }
                        />

                        {claim.group ? (
                          <DetailCard
                            title={t(
                              "claims.card.group",
                              "{{groupNoun}}",
                            )}
                            icon={<Building className="h-3.5 w-3.5" />}
                          >
                            <FieldRow
                              label={t("claims.card.groupName", "Name")}
                              value={claim.group.name}
                            />
                            <FieldRow
                              label="Address"
                              value={claim.group.address || undefined}
                            />
                            <FieldRow
                              label="Property Manager"
                              value={
                                claim.group.manager_name ||
                                claim.group.manager_email ||
                                undefined
                              }
                            />
                          </DetailCard>
                        ) : null}

                        {/* WPS-160 Claim Team - internal-only; self-hides unless
                          the tenant is on advanced_routing with a slot
                          catalogue. */}
                        {!isPartnerAccess && (
                          <ClaimTeamPanel
                            key="claim-team"
                            data-card-title={t("claimTeam.title", "Claim Team")}
                            claimUuid={claim.uuid}
                            footer={partnerAssignFooter}
                          />
                        )}

                        <ClaimDocumentationCard
                          key="documentation"
                          data-card-title={t("claims.documentationTitle", "Documentation")}
                          claimType={claim.claim_schema_key ?? ""}
                          claimUuid={claim.uuid}
                          canEdit={canEdit && !isTerminal}
                        />

                        {canViewDecisionCards && (
                          <Fragment key="decision-group">
                            <SupplierContactsCard
                              key="supplier"
                              data-card-title={t("claims.card.claimContacts", "Claim Contacts")}
                              claim={claim}
                            />

                            <EditableDetailCard
                              key="decision"
                              title={t("claims.card.decision", "Decision")}
                              icon={<Shield className="h-3.5 w-3.5" />}
                              items={decisionCardItems}
                              formatOptions={{ locale, currency }}
                              editConfig={
                                editConfig
                                  ? {
                                      ...editConfig,
                                      onSave: handleDecisionSave,
                                    }
                                  : undefined
                              }
                            />
                          </Fragment>
                        )}

                        <NotesPanel
                          key="notes"
                          data-card-title={t("notes.title", "Notes")}
                          recordId={claim.uuid}
                          recordType="claim"
                          canAddInternal
                          canAddCustomerVisible
                          allowAttachments
                          onNoteAdded={() => refetch()}
                        />

                        <div key="communications" data-card-title={t("claimComms.title", "Communications")}>{communicationsCard}</div>
                      </DraggableCardGrid>
                      
                    </>
                  ) : (
                    <>
                      {/* Masonry columns (not a row-aligned grid) so a short card like
                Claim Details doesn't leave a tall whitespace gap beside a long
                Decision card. Each card packs against the previous one in its
                column; Notes flows in as just another card, same width and
                proportions as the rest. Cards are drag-reorderable (order
                persists per user, cached in the browser) - see DraggableCardGrid. */}
                      <DraggableCardGrid
                        storageKey="claim-detail-cards-v1"
                        locked={cardsView.pinned}
                        view={cardsView.layout}
                        summaries={overviewSummaries}
                        asideKeys={["communications", "notes"]}
                        asideExtras={overviewAsideWithNotes}
                        hideable
                        aligned
                        pinnedColumns={overviewPinnedColumns}
                        defaultHiddenKeys={overviewDefaultHiddenCards}
                        hiddenScope={overviewCardScope}
                        className="columns-1 gap-6 md:columns-2"
                        itemClassName="mb-6 break-inside-avoid"
                      >
                        {/* WPS-145: the claim's communication hold. This grid carries no
                            "The Claim" band, so the hold leads the Overview here. Same control
                            and same rules as the banded layout - the tenant's label and the
                            permission gating the switch both come from the API. */}
                        {claim.communication_hold && !claim.communication_hold.active ? (
                          <div data-reorder-static>
                            <CommunicationHoldControl
                              claimUuid={claim.uuid}
                              hold={claim.communication_hold}
                            />
                          </div>
                        ) : null}
                        {/* Partner branch cards (Assign to partner) - same size /
                  grid as the other Overview cards; only present once a partner
                  workflow has actually been initiated on the claim. */}
                        {partnerBranchCards}
                        {/* The group - the umbrella claim's "who/where" card, the
                  analogue of the customer/property card on a normal claim.
                  Same single-column size as the other Overview cards. */}
                        {claim.group ? (
                          <DetailCard
                            key="hoa-community"
                            folded={isFolded("hoa-community")}
                            onToggleFold={() => toggleFold("hoa-community")}
                            title={t(
                              "claims.card.group",
                              "{{groupNoun}}",
                            )}
                            icon={<Building className="h-3.5 w-3.5" />}
                          >
                            <FieldRow
                              label={t("claims.card.groupName", "Name")}
                              value={claim.group.name}
                            />
                            <FieldRow
                              label="Address"
                              value={claim.group.address || undefined}
                            />
                            <FieldRow
                              label="Property Manager"
                              value={
                                claim.group.manager_name ||
                                claim.group.manager_email ||
                                undefined
                              }
                            />
                            <FieldRow
                              label={groupMembersLabel}
                              value={String(
                                claim.group.member_count,
                              )}
                            />
                          </DetailCard>
                        ) : null}
                        {/* Claim Details */}
                        {claimDetailItems.length > 0 ? (
                          <EditableDetailCard
                            key="claim-details"
                            title={t(
                              "claims.card.claimDetails",
                              "Claim Details",
                            )}
                            icon={<FileText className="h-3.5 w-3.5" />}
                            items={claimDetailItems}
                            formatOptions={{ locale, currency }}
                            editConfig={
                              editConfig
                                ? {
                                    ...editConfig,
                                    onSave: handleClaimDetailsSave,
                                  }
                                : undefined
                            }
                          />
                        ) : (
                          <DetailCard
                            key="claim-details"
                            folded={isFolded("claim-details")}
                            onToggleFold={() => toggleFold("claim-details")}
                            title={t(
                              "claims.card.claimDetails",
                              "Claim Details",
                            )}
                            icon={<FileText className="h-3.5 w-3.5" />}
                          >
                            <div className="px-3 py-3 text-[11px] text-[var(--ink-3)]">
                              No claim details recorded
                            </div>
                          </DetailCard>
                        )}

                        {/* WPS-160 Claim Team - internal-only; self-hides
                            unless the tenant is on advanced_routing with a
                            slot catalogue. */}
                        {!isPartnerAccess && (
                          <ClaimTeamPanel
                            key="claim-team"
                            data-card-title={t("claimTeam.title", "Claim Team")}
                            claimUuid={claim.uuid}
                            footer={partnerAssignFooter}
                          />
                        )}

                        {/* Decision + pricing - internal-only, gated by
                            claims.view_decisions (partners never hold it). */}
                        {canViewDecisionCards && (
                          <div
                            key="decision"
                            data-card-title={t(
                              "claims.card.decision",
                              "Decision",
                            )}
                            className="flex flex-col gap-2.5 [&>:first-child]:flex-1"
                          >
                            <EditableDetailCard
                              title={t("claims.card.decision", "Decision")}
                              icon={<Shield className="h-3.5 w-3.5" />}
                              items={decisionCardItems}
                              formatOptions={{ locale, currency }}
                              editConfig={
                                editConfig
                                  ? {
                                      ...editConfig,
                                      onSave: handleDecisionSave,
                                    }
                                  : undefined
                              }
                            />
                            {/* Part of the Decision card: no grip or hide of its own. */}
                            <NestedCardBoundary>
                              <PricingGuidancePanel guidance={claim.pricing_guidance} />
                            </NestedCardBoundary>
                          </div>
                        )}

                        {/* Supplier Info - holdingless claims (Uninstalled / Job Stop) */}
                        {isHoldingless && (
                          <SupplierContactsCard
                            key="supplier"
                            data-card-title={t("claims.card.claimContacts", "Claim Contacts")}
                            claim={claim}
                          />
                        )}

                        {/* Customer - only shown for holding-based claims. This is
                  the registration's owner; for B2B tenants it's the
                  account/dealer, hence the themable label. */}
                        {claim.holding_uuid && (
                          <DetailCard
                            key="customer"
                            folded={isFolded("customer")}
                            onToggleFold={() => toggleFold("customer")}
                            title={customerCardLabel}
                            icon={<UserCircle className="h-3.5 w-3.5" />}
                            trailing={standardCustomerCardTrailing}
                          >
                            <FieldRow
                              label="Name"
                              value={resolvedConsumerName}
                            />
                            <FieldRow
                              label="Email"
                              value={resolvedConsumerEmail}
                              link={!!resolvedConsumerEmail}
                              onLinkClick={() =>
                                (window.location.href = `mailto:${resolvedConsumerEmail}`)
                              }
                            />
                            <FieldRow
                              label="Phone"
                              value={
                                formatPhoneDisplay(resolvedConsumerPhone) ||
                                undefined
                              }
                            />
                            <FieldRow
                              label="Address"
                              value={resolvedConsumerAddress}
                            />
                            <FieldRow
                              label="Registration"
                              value="View registration"
                              link
                              onLinkClick={() =>
                                navigate(
                                  registrationPathFromClaim(
                                    claim.holding_uuid,
                                    uuid,
                                  ),
                                )
                              }
                            />
                          </DetailCard>
                        )}

                        {/* Consumer (end-user) - renders only when the claim
                  captured end_user_* attributes, keeping the end consumer
                  distinct from the account/customer above. */}
                        {consumerRows.some(
                          (row) => !isBlankSummaryValue(row.value),
                        ) && (
                          <ConsumerInfoCard
                            key="consumer"
                            attrs={claim.attribute_values ?? {}}
                            label={consumerCardLabel}
                          />
                        )}

                        {/* Host (Property / Vehicle / etc - driven by entity config).
                  Hidden for tenants with no host concept (no entity
                  display columns, no payload data) so the card doesn't
                  render as an empty "Property" shell. */}
                        {claim.holding_uuid && showHostCard && (
                          <DetailCard
                            key="host"
                            folded={isFolded("host")}
                            onToggleFold={() => toggleFold("host")}
                            title={
                              linkedHolding?.host?.host_template?.name ||
                              hostEntity?.display_name ||
                              "Host"
                            }
                            icon={
                              linkedHolding?.host?.kind === "VEHICLE" ? (
                                <Car className="h-3.5 w-3.5" />
                              ) : linkedHolding?.host?.kind === "VESSEL" ? (
                                <Anchor className="h-3.5 w-3.5" />
                              ) : (
                                <Home className="h-3.5 w-3.5" />
                              )
                            }
                          >
                            {configuredHostRows.length > 0
                              ? configuredHostRows.map((row) => (
                                  <FieldRow
                                    key={row.key}
                                    label={row.label}
                                    value={row.value}
                                  />
                                ))
                              : (claim.host_attributes ?? []).map(
                                  (a: {
                                    code_name: string;
                                    display_name: string;
                                    value: string;
                                  }) => (
                                    <FieldRow
                                      key={a.code_name}
                                      label={a.display_name}
                                      value={a.value}
                                    />
                                  ),
                                )}
                          </DetailCard>
                        )}

                        {/* Product - holding-based claims, gated by the
                            product entity view permission. */}
                        {claim.holding_uuid && canViewProductCard && (
                          <DetailCard
                            key="product"
                            folded={isFolded("product")}
                            onToggleFold={() => toggleFold("product")}
                            title={t("claims.card.product", "Product")}
                            icon={<Package className="h-3.5 w-3.5" />}
                            trailing={
                              hasProductIdentification &&
                              linkedHolding?.unidentified &&
                              (can("holdings.edit.global") ||
                                can("holdings.edit.acl")) ? (
                                <Button
                                  onClick={() => setIdentifyProductOpen(true)}
                                  variant="outline"
                                  size="xs"
                                  className="flex cursor-pointer items-center gap-1.5 rounded-[6px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] text-[10px] uppercase tracking-[0.06em] text-[var(--ink)] transition-all duration-150 hover:border-[var(--green)] hover:bg-[var(--green-bg)]"
                                >
                                  <Package className="h-3 w-3" />
                                  Identify product
                                </Button>
                              ) : claim.holding_uuid ? (
                                // Edit product (and any other holding actions)
                                // via the generic entity-action mechanism.
                                // Renders nothing unless the tenant has seeded a
                                // holding action AND the user holds its
                                // permission (e.g. holding.edit_product_variant),
                                // so it stays tenant-agnostic. Refetches the
                                // claim on success since the product name/type
                                // are denormalised onto the claim row.
                                <EntityActionMenu
                                  entityTypeKey="holding"
                                  resourceUuid={claim.holding_uuid}
                                  excludeActionKeys={holdingActionExclusions}
                                  triggerLabel={t(
                                    "claims.productActions",
                                    "Product actions",
                                  )}
                                  onActionComplete={() => refetch()}
                                />
                              ) : undefined
                            }
                          >
                            {configuredProductRows.length > 0 ? (
                              configuredProductRows.map((row) => (
                                <FieldRow
                                  key={row.key}
                                  label={row.label}
                                  value={row.value}
                                />
                              ))
                            ) : (
                              <>
                                <FieldRow
                                  label="Product"
                                  value={
                                    claim.product_short_name ||
                                    claim.product_name
                                  }
                                />
                                {claim.product_template_name && (
                                  <FieldRow
                                    label="Template"
                                    value={claim.product_template_name}
                                  />
                                )}
                                {claim.product_type && (
                                  <FieldRow
                                    label="Type"
                                    value={
                                      claim.product_type
                                        .charAt(0)
                                        .toUpperCase() +
                                      claim.product_type.slice(1).toLowerCase()
                                    }
                                  />
                                )}
                                {claim.product_category_name && (
                                  <FieldRow
                                    label="Category"
                                    value={claim.product_category_name}
                                  />
                                )}
                                {claim.dealer_name && (
                                  <FieldRow
                                    label={dealerRowLabel}
                                    value={claim.dealer_name}
                                  />
                                )}
                              </>
                            )}
                            {/* Serial number of the specific unit being claimed /
                      recalled — a claim attribute, shown here on the
                      Product card regardless of which path rendered
                      above. Only appears when captured. */}
                            {(() => {
                              const serial =
                                claim.attribute_values?.serial_number;
                              const serialText =
                                serial === null ||
                                serial === undefined ||
                                serial === ""
                                  ? undefined
                                  : String(serial);
                              return serialText ? (
                                <FieldRow
                                  label="Serial Number"
                                  value={serialText}
                                />
                              ) : null;
                            })()}
                            {hasProductIdentification &&
                              linkedHolding?.unidentified &&
                              (can("holdings.edit.global") ||
                                can("holdings.edit.acl")) && (
                                <p className="px-4 py-[9px] text-[12px] text-[var(--ink-3)]">
                                  This claim was filed against an unidentified
                                  product.
                                </p>
                              )}
                          </DetailCard>
                        )}

                        {/* The partner this claim concerns, titled in the
                            tenant's own word for them (Contractor, Dealer,
                            ...). Previously rendered only under the `hoa`
                            branch, so every other tenant lost a card that was
                            already generic - the dealer was reachable only as
                            grey text in the page header.

                            Gated on the org actually having a partner network,
                            the same signal that hides the registration Dealer
                            picker: a tenant with no partnerships would
                            otherwise carry a permanently empty card on every
                            claim. An inherited or overridden partner still
                            shows it, so a claim never hides one it has. */}
                        {(claim.partner ||
                          tenantSettings?.has_downstream_partnerships) && (
                          <ClaimPartnerCard
                            key="claim-partner"
                            data-card-title={
                              claim.partner?.role_name ??
                              externalRoles[0]?.name ??
                              t("claims.card.partner", "Partner")
                            }
                            claimUuid={claim.uuid}
                            partner={claim.partner}
                            isInherited={claim.partner_is_inherited}
                            canEdit={canEdit && !isTerminal}
                            partnerLocked={partnerLock.locked}
                          />
                        )}

                        {/* Warranty - holding-based claims, gated by the
                            warranty entity view permission. */}
                        {claim.holding_uuid && canViewWarrantyCard && (
                          <DetailCard
                            key="warranty"
                            folded={isFolded("warranty")}
                            onToggleFold={() => toggleFold("warranty")}
                            title={`Warranty · ${warrantyType}`}
                            icon={<Shield className="h-3.5 w-3.5" />}
                          >
                            {configuredWarrantyRows.length > 0 ? (
                              <RegistrationConfiguredFieldRows
                                items={configuredWarrantyRows}
                              />
                            ) : (
                              <FieldRow
                                label="Status"
                                value={<StatusBadge status={warrantyStatus} />}
                              />
                            )}
                            {configuredWarrantyRows.length === 0 &&
                              warrantyForDuration && (
                                <>
                                  <FieldRow label="Tier" value={warrantyTier} />
                                  <FieldRow
                                    label="Class"
                                    value={warrantyClassName}
                                  />
                                  <FieldRow
                                    label="Duration"
                                    value={durationLabel(warrantyForDuration)}
                                  />
                                  <FieldRow
                                    label="Active from"
                                    value={
                                      warrantyActiveFrom
                                        ? formatDate(warrantyActiveFrom) ||
                                          warrantyActiveFrom
                                        : undefined
                                    }
                                  />
                                  {!warrantyForDuration.is_lifetime && (
                                    <FieldRow
                                      label="Expires"
                                      value={
                                        warrantyExpires
                                          ? formatDate(warrantyExpires) ||
                                            warrantyExpires
                                          : undefined
                                      }
                                    />
                                  )}
                                </>
                              )}
                            {configuredWarrantyRows.length === 0 &&
                              !warrantyForDuration && (
                                <FieldRow label="Tier" value="-" />
                              )}
                            {warrantyDocuments.length > 0 && (
                              <div className="border-b-[0.5px] border-b-[var(--rule-light)] px-4 py-[9px]">
                                <div className="mb-2 text-[9.5px] uppercase tracking-[0.16em] text-[var(--ink-3)]">
                                  Documents
                                </div>
                                {warrantyDocuments.map((document) =>
                                  document.url ? (
                                    <a
                                      key={document.uuid}
                                      href={document.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="mt-1 flex items-center justify-between gap-3 text-[12px] hover:underline"
                                    >
                                      <span className="flex min-w-0 items-center gap-1.5 text-[var(--green)]">
                                        <ExternalLink className="h-3 w-3 shrink-0" />
                                        <span className="truncate">
                                          {document.label}
                                        </span>
                                      </span>
                                      <span className="font-mono text-[10px] text-[var(--ink-3)]">
                                        {document.kind}
                                      </span>
                                    </a>
                                  ) : (
                                    <div
                                      key={document.uuid}
                                      className="mt-1 flex items-center justify-between gap-3 text-[12px]"
                                    >
                                      <span className="truncate text-[var(--ink-3)]">
                                        {document.label}
                                      </span>
                                      <span className="font-mono text-[10px] text-[var(--ink-3)]">
                                        {document.kind}
                                      </span>
                                    </div>
                                  ),
                                )}
                              </div>
                            )}
                          </DetailCard>
                        )}
                      </DraggableCardGrid>

                      {/* Parts / line items - claim-level ledger for workflows
                WITHOUT a settlement workspace (workspace flows render the
                same table inside their settlement tab instead). Appears
                once the tenant registers the ``settlement_line_item``
                entity; rows are picked from the product catalogue via the
                SKU search in the add dialog. Workflows with an assessment
                stage do their per-item working-out on the Assessment tab,
                so the ledger is suppressed there - one surface, no
                duplication. */}
                      {settlement?.visible &&
                        !hasSettlementWorkspace &&
                        !hasAssessmentWorkspace && (
                          <div className="mt-4">
                            <SettlementLineItemsTable
                              claimUuid={claim.uuid}
                              lineItems={settlement.line_items ?? []}
                              isEditable={
                                Boolean(settlement.line_items_editable) &&
                                canEdit
                              }
                              currency={currency}
                            />
                          </div>
                        )}

                      {/* Evidence + Notes - equal-width cards on the same row,
                          stretched to one height so each row lines up like
                          the grid above. */}
                      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 [&>*]:h-full">
                        <input
                          ref={evidenceInputRef}
                          type="file"
                          multiple
                          accept="image/jpeg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,video/mp4,video/quicktime"
                          className="hidden"
                          onChange={(event) => {
                            const files = Array.from(
                              event.currentTarget.files ?? [],
                            );
                            if (files.length > 0)
                              handleEvidenceFilesSelected(files);
                            event.currentTarget.value = "";
                          }}
                        />
                        {hasResolutionWorkspace ? (
                          // Workflows with a resolution workspace (e.g. OC) keep the
                          // attachments grid on the resolution tab; Overview shows the
                          // capture-field list unchanged.
                          <DetailCard
                            title={evidenceCardLabel}
                            icon={<Image className="h-3.5 w-3.5" />}
                            trailing={
                              canEdit && !isTerminal ? (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="xs"
                                  onClick={() =>
                                    evidenceInputRef.current?.click()
                                  }
                                  disabled={isEvidenceUploading}
                                  className="inline-flex cursor-pointer items-center gap-1 bg-transparent p-0 text-[10px] font-normal uppercase tracking-[0.1em] text-[var(--green)] transition-opacity duration-150 hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                  {isEvidenceUploading ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Plus className="h-3 w-3" />
                                  )}
                                  Add
                                </Button>
                              ) : undefined
                            }
                          >
                            {evidence.length === 0 ? (
                              <div className="px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
                                No evidence attached
                              </div>
                            ) : (
                              evidence.map((file) => (
                                <EvidenceFileRow
                                  key={file.rowKey}
                                  fileName={file.file_name}
                                  fileUuid={file.uuid}
                                  fileUrl={file.file_url}
                                  thumbnail={
                                    <AuthenticatedImage
                                      src={fileThumbnailUrl(file.file_url)}
                                      alt={file.file_name}
                                      className="h-full w-full object-cover"
                                      fallback={null}
                                    />
                                  }
                                  mimeType={file.mime_type}
                                  onPreview={
                                    file.uuid || file.file_url
                                      ? () =>
                                          openFile({
                                            file_uuid: file.uuid,
                                            file_url: file.file_url,
                                            file_name: file.file_name,
                                            mime_type: file.mime_type,
                                          })
                                      : undefined
                                  }
                                />
                              ))
                            )}
                          </DetailCard>
                        ) : (
                          // No resolution tab: surface session/iris uploads AND the
                          // capture-field/admin "+" files in one card.
                          <EvidenceCard
                            claimUuid={claim.uuid}
                            title={evidenceCardLabel}
                            canAdd={canEdit && !isTerminal}
                            isAdding={isEvidenceUploading}
                            onAdd={() => evidenceInputRef.current?.click()}
                            extraItems={attributeEvidence}
                          />
                        )}

                        {/* Notes - a card like any other, flowing in the same masonry
                  column at the same width and proportions as the rest. In the
                  sections view Notes, Communications and Activity sit in the
                  side panel instead. */}
                        {!overviewSections && (
                          <>
                            {claimNotesPanel}
                            {communicationsCard}
                            {activityCard}
                          </>
                        )}
                      </div>

                      {claim.holding_uuid && (
                        <IdentifyProductDialog
                          holdingUuid={claim.holding_uuid}
                          open={identifyProductOpen}
                          onOpenChange={setIdentifyProductOpen}
                          onReassigned={refetch}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </TabsContent>

            {/* ── Resolution Tab (standard only; OC merges it into Overview) ── */}
            {!sectionedDesign && hasResolutionWorkspace && (
              <TabsContent value="resolution">
                <ClaimResolutionPanel
                  claim={claim}
                  history={history}
                  claimDetailItems={claimDetailItems}
                  editConfig={editConfig}
                  currentStateKey={currentStateKey}
                  currentStateAttributeValues={currentStateAttributeValues}
                  currentStateEditableFields={
                    workflowInstance?.current_state_editable_fields ?? null
                  }
                  onClaimDetailsSave={handleClaimDetailsSave}
                  formatOptions={{ locale, currency }}
                  partnerLocked={partnerLock.locked}
                  propertyRows={configuredHostRows}
                  resolvedConsumer={{
                    name: resolvedConsumerName,
                    email: resolvedConsumerEmail,
                    phone: resolvedConsumerPhone,
                    address: resolvedConsumerAddress,
                  }}
                  warrantyLabel={`Warranty · ${warrantyType}`}
                  warrantyRows={
                    configuredWarrantyRows.length > 0
                      ? configuredWarrantyRows.map((r) => ({
                          key: r.key,
                          label: r.label,
                          value: r.displayValue ?? r.value,
                        }))
                      : warrantyForDuration
                        ? [
                            {
                              key: "status",
                              label: "Status",
                              value: warrantyStatus,
                            },
                            { key: "tier", label: "Tier", value: warrantyTier },
                            {
                              key: "class",
                              label: "Class",
                              value: warrantyClassName,
                            },
                            {
                              key: "duration",
                              label: "Duration",
                              value: durationLabel(warrantyForDuration),
                            },
                          ]
                        : []
                  }
                  onNavigateRegistration={
                    claim.holding_uuid
                      ? () =>
                          navigate(
                            registrationPathFromClaim(
                              claim.holding_uuid,
                              uuid,
                            ),
                          )
                      : undefined
                  }
                  onTimelineEntryClick={(entry) => setContactEntry(entry)}
                />
              </TabsContent>
            )}

            {/* ── Lab Tab ──────────────────────────────────── */}
            <IfEntity name="lab_analysis">
              <TabsContent value="lab">
                <ClaimLabPanel
                  claim={claim}
                  claimDetailItems={claimDetailItems}
                  classificationItems={classificationItems}
                  labFindingItems={labFindingItems}
                  editConfig={editConfig}
                  currentStateKey={currentStateKey}
                  currentStateAttributeValues={currentStateAttributeValues}
                  onClaimDetailsSave={handleClaimDetailsSave}
                  evidence={evidence}
                  formatOptions={{ locale, currency }}
                  // WPS-151 retest (Penny) C13: the Add Sample popup captures
                  // Plant Code from the SAME curated dropdown as the claim's
                  // ``plant_code`` SELECT, so the option list comes from the
                  // claim schema (data), never a hardcoded array here.
                  plantCodeOptions={
                    metadataMap.get("plant_code")?.configuration_data
                      ?.options as { value: string; label: string }[] | undefined
                  }
                  sampleItems={sampleItems}
                />
              </TabsContent>
            </IfEntity>

            {/* ── Settlement Tab ───────────────────────────── */}
            {/*
            Which panel the tab shows is the state's own ``settlement_panel``
            attribute (audit / compensate), authored with the workflow, so a
            tenant's settlement stages are data rather than state keys.
          */}
              {/* Payment ledger - one row per payment recorded against this
                claim, so a second payment no longer overwrites the first.
                Appears once the tenant registers the ``claim_payment``
                entity. A settlement workspace carries its own payments
                table on its tab, so this is suppressed there. */}
              {hasPaymentsTab && (
                <TabsContent value="payments">
                  <StageCardGrid storageKey="claim-detail-payments-cards-v1">
                    <SettlementPaymentsTable
                      key="payments"
                      data-card-wide
                      data-card-title={t("claims.card.payments", "Payments")}
                      claimUuid={claim.uuid}
                      payments={claimPayments ?? []}
                      isEditable={canEdit}
                      allowPaymentProcessing={allowPaymentProcessing}
                      currency={currency}
                      payeeCandidates={payeeCandidates}
                      paidThrough={claim.paid_by_parent?.claim_number ?? null}
                    />
                    {settlementVouchersCard}
                  </StageCardGrid>
                </TabsContent>
              )}

            <IfEntity name="settlement_line_item">
              <TabsContent value="settlement">
                {settlementPanel === "audit" && settlement?.visible ? (
                  <SettlementAuditPanel
                    settlement={settlement}
                    claimUuid={claim.uuid}
                    currency={currency}
                    allowPaymentProcessing={allowPaymentProcessing}
                    trailingCards={settlementVouchersCard}
                  />
                ) : settlementPanel === "compensate" && settlement?.visible ? (
                  <CompensatePanel
                    settlement={settlement}
                    claimUuid={claim.uuid}
                    currency={currency}
                    formatOptions={{ locale, currency }}
                    allowPaymentProcessing={allowPaymentProcessing}
                    trailingCards={settlementVouchersCard}
                  />
                ) : settlement?.visible ? (
                  <>
                  {settlement.settled_as_group && (
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-4 py-3">
                      <p className="text-[13px] leading-[1.5] text-[var(--ink-2)]">
                        {t(
                          "claims.groupSettlementNote",
                          "This claim's settlement is the total of its properties. Build each property's settlement on that property's own line; it is added up here.",
                        )}
                      </p>
                      {isUmbrellaParent && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="cursor-pointer"
                          onClick={() => setActiveTab("children")}
                        >
                          {t("claims.openProperties", "Open Properties")}
                        </Button>
                      )}
                    </div>
                  )}
                  <StageCardGrid storageKey="claim-detail-settlement-cards-v1">
                    {/* A group-settled claim has no lines of its
                      own - they live on its properties - so once its lines can
                      no longer be added, an empty Line Items card says nothing
                      the note above does not. */}
                    {!(
                      settlement.settled_as_group &&
                      settlement.line_items.length === 0 &&
                      !settlement.line_items_editable
                    ) && (
                      <SettlementLineItemsTable
                        key="line-items"
                        data-card-wide
                        data-card-title={t("claims.card.lineItems", "Line Items")}
                        claimUuid={claim.uuid}
                        lineItems={settlement.line_items}
                        isEditable={
                          settlement.line_items_editable &&
                          can("claims.rs_actions")
                        }
                        currency={currency}
                        summary={settlement.summary}
                        pricesRefreshedAt={settlement.prices_refreshed_at}
                        isLocked={settlement.prices_locked === true}
                        defaultPaymentType={settlement.default_payment_type}
                      />
                    )}
                    {/* WPS-146: the summary's cards are cards of the tab, so each one
                        moves and stretches to its row on its own. */}
                    <SettlementCostSummaryCard
                      key="cost-summary"
                      data-card-title={t("settlement.costSummary.title", "Cost Summary")}
                      summary={settlement.summary}
                      currency={currency}
                    />
                    <SettlementLedgerSplitCard
                      key="ledger-split"
                      data-card-title={t("settlement.ledgerSplit.title", "Ledger Split")}
                      lineItems={settlement.line_items}
                      currency={currency}
                    />
                    <SettlementProrationCard
                      key="proration"
                      data-card-title={t("settlement.proration.title", "Proration")}
                      summary={settlement.summary}
                      currency={currency}
                    />
                    <SettlementPaymentsTable
                      key="payments"
                      data-card-wide
                      data-card-title={t("claims.card.payments", "Payments")}
                      claimUuid={claim.uuid}
                      payments={settlement.payments}
                      isEditable={
                        settlement.payments_editable &&
                        can("claims.settlement_actions")
                      }
                      allowPaymentProcessing={allowPaymentProcessing}
                      currency={currency}
                      paidThrough={settlement.paid_through}
                    />
                    {settlementVouchersCard}
                  </StageCardGrid>
                  </>
                ) : (
                  <>
                    <GenericStationPanel label="Settlement" />
                    {settlementVouchersCard ? (
                      <div className="mt-4">{settlementVouchersCard}</div>
                    ) : null}
                  </>
                )}
                {/* WPS-175 #12: the "Settlement Documents" card was removed.
                  Claim documentation has one home - the Evidence tab - so a
                  document can never be filed in a place a reviewer does not
                  look. Nothing is lost: the Evidence tab's card is
                  unfiltered, so every attachment previously reachable here
                  (category section "settlement") still lists there, and the
                  upload affordance moves with it. */}
              </TabsContent>
            </IfEntity>

            {/* ── Inspection Tab ───────────────────────────── */}
            <IfEntity name="inspection">
              <TabsContent value="inspection">
                {/* WPS-175 #5: the reason and notes the request was made
                  with, first on the tab - it is the thing an inspector needs
                  before anything else about the visit. WPS-146: the tab's
                  cards share one grid, so each one moves. */}
                <ClaimInspectionPanel
                  leadingCards={
                    <InspectionRequestsCard
                      key="inspection-requests"
                      data-card-wide
                      data-card-title={t(
                        "claims.card.inspectionRequests",
                        "Inspection Requests",
                      )}
                      claimUuid={claim.uuid}
                    />
                  }
                  trailingCards={[
                    <EvidenceCard
                      key="inspection-documents"
                      data-card-wide
                      data-card-title={t(
                        "claims.card.inspectionDocuments",
                        "Inspection Documents",
                      )}
                      claimUuid={claim.uuid}
                      title={t(
                        "claims.card.inspectionDocuments",
                        "Inspection Documents",
                      )}
                      filterToSection="inspection"
                      canAdd={
                        canEdit &&
                        !isTerminal &&
                        (can("inspections.edit.global") ||
                          can("inspections.edit.acl"))
                      }
                    />,
                    <LinkedInspectionDocuments
                      key="linked-inspection-documents"
                      data-card-wide
                      data-card-title={t(
                        "claims.inspectionEvidence.title",
                        "Inspection Evidence",
                      )}
                      claimUuid={claim.uuid}
                    />,
                  ]}
                  claim={claim}
                  editConfig={editConfig}
                  onClaimDetailsSave={handleClaimDetailsSave}
                  onEvidenceFilesSelected={handleEvidenceFilesSelected}
                  isEvidenceUploading={isEvidenceUploading}
                  evidence={evidence}
                  canEdit={canEdit}
                  isTerminal={isTerminal}
                  currentStateAttributeValues={currentStateAttributeValues}
                  formatOptions={{ locale, currency }}
                />
              </TabsContent>
            </IfEntity>

            {/* ── Assessment Tab ──────────────────────────────
              Generic per-item coverage + amount stage. Editable only while the
              current state is tagged workspace_category="assessment"; read-only
              afterwards. Internal-only (gated by claims.view_decisions). */}
            {hasAssessmentTab && (
              <TabsContent value="assessment">
                <AssessmentLineItemsTable
                  claimUuid={claim.uuid}
                  editable={
                    currentStateAttributeValues.workspace_category ===
                    "assessment"
                  }
                />
              </TabsContent>
            )}

            {/* ── LOA Tab ──────────────────────────────────── */}
            {showLoaTab && (
              <TabsContent value="loa">
                {/* WP1-65: the Warranty vs Business Goodwill Levels-of-Approval
                  cascade lives here (was previously hoisted above the tabs,
                  where it read like a floating notification banner). */}
                {/* The approval track is a SIBLING of the review card, above
                  it: who the claim is waiting on is the headline of the tab.
                  Deliberately NOT a prop of ClaimLOAPanel - that component
                  returns null on a cascade claim (`suppressDecisionCard`,
                  where these per-rung records ARE the panel), so nesting the
                  track inside made the ladder vanish on exactly the claims it
                  exists for. `embedded` drops its own card chrome so the two
                  read as one surface, not two titled cards. */}
                {/* WPS-146: the tab's blocks share one card grid. */}
                <StageCardGrid storageKey="claim-detail-loa-cards-v1">
                {/* One card: the track is drawn without chrome so it reads as
                  the head of the review card beneath it, and splitting them
                  left it floating on its own. */}
                <div
                  key="loa-review"
                  data-card-wide
                  data-card-title={t("claims.card.loaReview", "LOA Review")}
                  className="flex flex-col gap-3"
                >
                <div>
                  <LOAApprovalPanel
                    loas={metadata.loas as LOADimension[] | undefined}
                    context={metadata.loa_context as LOAContext | undefined}
                    currency={currency}
                    locale={locale}
                    embedded
                  />
                </div>
                {/* A claim back for approval: what moved since the last
                  decision, before the full picture below. */}
                {metadata.approval_changes ? (
                  <div>
                    <ApprovalChangesSummary
                      changes={metadata.approval_changes as ApprovalChanges}
                    />
                  </div>
                ) : null}
                <ClaimLOAPanel
                  groupLines={isUmbrellaParent}
                  approvalChanges={
                    (metadata.approval_changes as ApprovalChanges | null) ??
                    null
                  }
                  claim={claim}
                  fieldMeta={fieldMeta}
                  currentStateKey={currentStateKey}
                  stateAttributes={currentStateAttributeValues}
                  availableActions={availableActions}
                  hasLabWorkspace={hasLabWorkspace}
                  settlement={settlement}
                  loa={settlement?.loa ?? null}
                  warrantyType={warrantyType}
                  productName={productNameWithoutVariant(
                    claim,
                    // WPS-175 #14: a holdingless claim (Job Stop) captures the
                    // product on the claim itself - an ENTITY_SEARCH value, so
                    // either a picked-resource object or a plain string.
                    resourceValueLabel(claim.attribute_values?.product_name),
                  )}
                  loaDimensions={
                    (metadata.loas as LOADimension[] | undefined) ?? []
                  }
                  formatOptions={{ locale, currency }}
                  semanticCodes={labSemanticCodes}
                  suppressDecisionCard={hasLoaCascade}
                  loaViewer={
                    (metadata.loa_viewer as LOAViewerState | null | undefined) ??
                    null
                  }
                />
                </div>
                </StageCardGrid>
                {/* WPS-175 #14: the LOA & Legal Documents card was removed
                  here. LOA and legal paperwork is evidence like any other and
                  now lives on the Evidence surface, so it is filed and found
                  in one place rather than in a section-specific card only the
                  LOA reviewer ever opens. The `legal_document` category is
                  retagged to the overview section in the same change. */}
              </TabsContent>
            )}

            {/* ── Children Tab (umbrella parent) ──────────────
              A group claim gets the property-lines surface; other umbrella types
              (multi-item) get the generic children list. */}
            {isUmbrellaParent && (
              <TabsContent value="children">
                {isGroupClaim ? (
                  <ChildClaimsTab
                    parentUuid={claim.uuid}
                    settledAsGroup={settlement?.settled_as_group === true}
                    acceptsNewLines={settlement?.accepts_new_lines !== false}
                    groupUuid={claim.group?.uuid}
                    childClaimType={claim.child_claim_schema_key ?? undefined}
                    defaultPayoutMode={
                      (claim.attribute_values?.settlement_mode as string) ===
                      "individual"
                        ? "per_claim"
                        : "combined"
                    }
                  />
                ) : (
                  <UmbrellaChildrenList parentUuid={claim.uuid} />
                )}
              </TabsContent>
            )}

            {/* ── Evidence Tab (WP1-56) ───────────────────────
              Single canonical home for all of a claim's evidence and
              documents. The redundant cards on Overview/Resolution/Lab
              were removed; the stage-filtered Documents cards on
              Inspection/Settlement/LOA remain as relevant subsets. */}
            {hasEvidenceTab && (
              <TabsContent value="evidence">
                {returnToAction && (
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-4 py-3">
                    <div className="flex items-start gap-2.5">
                      <FileUp className="mt-0.5 h-4 w-4 shrink-0 text-[var(--ink-3)]" />
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[13px] font-medium text-[var(--ink)]">
                          {t(
                            "claims.override.returnTitle",
                            "Add the document, then continue",
                          )}
                        </span>
                        <span className="text-[12px] font-light text-[var(--ink-2)]">
                          {t("claims.override.returnBody", {
                            defaultValue:
                              'You were on "{{action}}". Once the document is attached, pick up where you left off.',
                            action: returnToAction.label,
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setReturnToAction(null)}
                        className="text-[12px] text-[var(--ink-3)]"
                      >
                        {t("claims.override.returnDismiss", "Not now")}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          const resume = returnToAction;
                          setReturnToAction(null);
                          setActiveTab("settlement");
                          setActiveAction(resume);
                        }}
                        className="text-[12px]"
                      >
                        {t("claims.override.returnCta", {
                          defaultValue: "Back to {{action}}",
                          action: returnToAction.label,
                        })}
                      </Button>
                    </div>
                  </div>
                )}
                <EvidenceCard
                  claimUuid={claim.uuid}
                  canAdd={canEdit && !isTerminal}
                  extraItems={attributeEvidence}
                  autoOpenAdd={autoOpenEvidenceAdd}
                  onAutoOpenAddHandled={() => setAutoOpenEvidenceAdd(false)}
                />
              </TabsContent>
            )}
          </Tabs>
          </ClaimLayoutLockContext.Provider>
        </main>

        {/* ── Dialogs ──────────────────────────────────────── */}

        <Dialog open={dupDialogOpen} onOpenChange={handleDupDialogChange}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Mark duplicates reviewed</DialogTitle>
              <DialogDescription className="text-[13px] text-[var(--ink-2)]">
                Confirm you have reviewed the{" "}
                {(claim.potential_duplicates ?? []).length === 1
                  ? "matched claim"
                  : `${(claim.potential_duplicates ?? []).length} matched claims`}{" "}
                for this customer. The warning will collapse and re-appear only
                if a new unreviewed match is found later.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-1.5 py-2">
              <Label
                htmlFor="claim-dup-note"
                className="text-[11px] font-normal uppercase tracking-[0.08em] text-[var(--ink-3)]"
              >
                Review note
              </Label>
              <Input
                id="claim-dup-note"
                value={dupNote}
                onChange={(e) => setDupNote(e.target.value)}
                placeholder="Optional note (e.g. confirmed not a duplicate)"
                className="text-[13px]"
              />
            </div>
            <DialogFooter>
              <Button
                onClick={() => handleDupDialogChange(false)}
                variant="outline"
                className="px-4 py-2 text-[12px]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleResolveDuplicates}
                disabled={resolveDuplicates.isPending}
                className="px-4 py-2 text-[12px] disabled:opacity-70"
              >
                {resolveDuplicates.isPending ? "Saving..." : "Mark reviewed"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <DuplicateClaimPreviewDialog
          previewUuid={previewDupUuid}
          onClose={() => setPreviewDupUuid(null)}
          matchedOn={
            (claim.potential_duplicates ?? []).find(
              (dup) => dup.claim_uuid === previewDupUuid,
            )?.matched_on ?? []
          }
          onOpenFull={(dupUuid) => {
            setPreviewDupUuid(null);
            navigate(`/claims/${dupUuid}`);
          }}
        />

        <ActionEntryDialog
          entry={contactEntry}
          open={contactEntry !== null}
          onOpenChange={(open) => {
            if (!open) setContactEntry(null);
          }}
          roleLabels={SENDER_ROLE_LABELS}
          formatValue={(value, key) =>
            formatAttrValue(value, locale, key, formatCurrency, t)
          }
          formatTimestamp={(iso) => {
            const d = new Date(iso);
            if (isNaN(d.getTime())) return iso;
            return new Intl.DateTimeFormat(locale, {
              day: "numeric",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }).format(d);
          }}
          onReply={() => {
            setContactEntry(null);
            setContactCustomerOpen(true);
          }}
        />

        {/* Flag dialog */}
        <Dialog open={flagDialogOpen} onOpenChange={handleFlagDialogChange}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{flagActionLabel}</DialogTitle>
              <DialogDescription className="text-[13px] text-[var(--ink-2)]">
                {claim.is_flagged
                  ? "This will remove the flag from the claim."
                  : "Add an optional internal note explaining why this claim is being flagged."}
              </DialogDescription>
            </DialogHeader>
            {claim.is_flagged && (
              <div className="rounded-2xl border-[0.5px] border-[var(--status-error)] bg-[var(--status-error-bg-subtle)] px-4 py-3">
                <p className="text-[11px] font-normal uppercase tracking-[0.08em] text-[var(--status-error)]">
                  Current flag reason
                </p>
                <p className="mt-1 text-[13px] text-[var(--ink)]">
                  {claim.flag_reason || "No reason provided"}
                </p>
              </div>
            )}
            {!claim.is_flagged && (
              <div className="flex flex-col gap-1.5 py-2">
                <Label
                  htmlFor="claim-flag-reason"
                  className="text-[11px] font-normal uppercase tracking-[0.08em] text-[var(--ink-3)]"
                >
                  Flag reason
                </Label>
                <Input
                  id="claim-flag-reason"
                  value={flagReason}
                  onChange={(e) => setFlagReason(e.target.value)}
                  placeholder="Optional note"
                  className="text-[13px]"
                />
              </div>
            )}
            <DialogFooter>
              <Button
                onClick={() => handleFlagDialogChange(false)}
                variant="outline"
                className="px-4 py-2 text-[12px]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleFlagSubmit}
                disabled={toggleFlag.isPending}
                variant="destructive"
                className="bg-[var(--status-error)] px-4 py-2 text-[12px] text-[var(--bg)] disabled:opacity-70"
              >
                {toggleFlag.isPending ? "Processing..." : flagActionLabel}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ClaimActionDialog
          open={activeAction !== null}
          action={activeAction}
          claimUuid={claim.uuid}
          workflowInstanceUuid={metadata.workflow_instance?.uuid}
          attributeValues={claim.attribute_values}
          pricingGuidance={claim.pricing_guidance}
          currentStateKey={currentStateKey}
          payeeCandidates={payeeCandidates}
          approvalChanges={
            (metadata.approval_changes as ApprovalChanges | null) ?? null
          }
          settlementSummary={
            settlement?.summary
              ? {
                  material_subtotal:
                    Number(settlement.summary.material_subtotal) || 0,
                  labor_subtotal:
                    Number(settlement.summary.labor_subtotal) || 0,
                  other_subtotal:
                    Number(settlement.summary.other_subtotal) || 0,
                  settlement_total:
                    Number(settlement.summary.settlement_total) || 0,
                  material_proration_pct:
                    settlement.summary.material_proration_pct != null
                      ? Number(settlement.summary.material_proration_pct)
                      : null,
                  labor_proration_pct:
                    settlement.summary.labor_proration_pct != null
                      ? Number(settlement.summary.labor_proration_pct)
                      : null,
                  prorated_total:
                    settlement.summary.prorated_total != null
                      ? Number(settlement.summary.prorated_total)
                      : null,
                  // WPS-165 #12: the approval dialog's coverage_amount reads
                  // this, so it has to travel with the rest of the summary.
                  payable_total:
                    settlement.summary.payable_total != null
                      ? Number(settlement.summary.payable_total)
                      : null,
                  proration_formula_name:
                    settlement.summary.proration_formula_name ?? null,
                  // WP1-61: per-Item-Detail rows for the dialog summary.
                  item_detail_subtotals: (
                    settlement.summary.item_detail_subtotals ?? []
                  ).map((row) => ({
                    item_detail: row.item_detail,
                    label: row.label,
                    subtotal: Number(row.subtotal) || 0,
                  })),
                }
              : undefined
          }
          onOpenChange={(open) => {
            if (!open) setActiveAction(null);
          }}
          onAddDocuments={() => {
            // Held, not discarded - the Evidence tab offers the way back.
            setReturnToAction(activeAction);
            setActiveAction(null);
            setActiveTab(hasEvidenceTab ? "evidence" : "overview");
            // ...and open the upload dialog on arrival. The reviewer answered
            // "I will add the document" by pressing this button; landing them
            // on the tab and leaving them to find "+ Add" asks the same
            // question twice. Only when the Evidence tab is where they land -
            // the Overview fallback has no dialog to open.
            if (hasEvidenceTab) setAutoOpenEvidenceAdd(true);
          }}
          // Only offered when the workflow actually has a checklist action
          // available on this state, so the button can never be a dead end.
          onChecklistClaimant={
            checklistAction
              ? () => {
                  setActiveAction(checklistAction);
                }
              : undefined
          }
          onSuccess={async () => {
            // A workflow action can trigger an inspection side-effect (e.g.
            // "request inspection" during intake review), so refresh the
            // linked-inspections list and its aggregated evidence on the
            // Inspection tab immediately.
            queryClient.invalidateQueries({ queryKey: ["inspections"] });
            queryClient.invalidateQueries({
              queryKey: ["inspections-claim-evidence"],
            });
            const result = await refetch();
            const newInstance = result.data?.metadata?.workflow_instance;
            const newStateKey = newInstance?.current_state_key;
            if (newStateKey) {
              let targetTab = (
                newInstance?.current_state_attribute_values as
                  Record<string, unknown> | undefined
              )?.workspace_category as string | undefined;
              // OC has no Resolution tab (merged into Overview), so route those
              // resolution-stage states to Overview instead of a hidden tab.
              if (sectionedDesign && targetTab === "resolution") targetTab = "overview";
              if (targetTab) setActiveTab(targetTab);
            }
          }}
        />

        <ContactCustomerDialog
          open={contactCustomerOpen}
          onOpenChange={setContactCustomerOpen}
          customerName={claim.consumer_name || "the customer"}
          claimUuid={claim.uuid}
        />

        <SendPacketDialog
          open={sendPacketOpen}
          onOpenChange={setSendPacketOpen}
          claimUuid={claim.uuid}
          communicationHold={claim.communication_hold}
        />

        <ReissueUploadLinkDialog
          open={reissueOpen}
          onOpenChange={setReissueOpen}
          claimUuid={claim.uuid}
          defaultEmail={resolvedConsumerEmail}
          defaultPhone={resolvedConsumerPhone}
          recipientName={resolvedConsumerName}
        />

        {/* WP1-59: prominent "Request Additional Photos" checklist modal */}
        {requestPhotosOpen && (
          <RequestPhotosDialog
            claimUuid={claim.uuid}
            onClose={() => setRequestPhotosOpen(false)}
          />
        )}
      </AppShell>
    </FileViewerProvider>
  );
}
